import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import {
  Briefcase,
  Clock,
  Calendar,
  Plus,
  Trash2,
  Tag,
  Paperclip,
  HelpCircle,
  Check,
  CheckCircle,
  Play,
  Pause,
  Layout,
  Palette,
  Settings,
  Image as ImageIcon,
  User,
  Coffee,
  X,
  Upload,
  Activity,
  BarChart2,
  PieChart,
  Zap,
  RotateCcw,
  AlertTriangle,
  Flame,
  ArrowRight,
  LayoutGrid,
  FileText,
  Type,
  AlignLeft,
  Eye,
  Send,
  Volume2,
  VolumeX,
  Globe,
  ExternalLink,
  RefreshCw,
  Home,
  Link2,
  Sunrise,
  Fingerprint,
  Users,
  Folder,
  Monitor,
  Watch,
  Edit,
  Bell,
  Hourglass,
  Download,
  Search,
  Filter,
  CheckSquare,
  MessageSquare,
  ThumbsUp,
  CheckCheck,
  AtSign,
  DollarSign,
  Save,
  UploadCloud,
  CalendarDays,
  Volume1,
  MapPin,
  Sun,
  Moon,
  ChevronDown,
  Info,
  Menu,
  LogOut,
  Shield,
  UserPlus,
  Lock,
  EyeOff,
  Mail,
  KeyRound,
  Building2,
} from 'lucide-react';
import ImageCropper from './components/ImageCropper';
import flowdeskMark from './assets/flowdesk-mark.png';
import flowdeskAppIcon from './assets/flowdesk-app-icon.png';
import flowdeskMarkLight from './assets/flowdesk-mark-light.png';
import flowdeskAppIconLight from './assets/flowdesk-app-icon-light.png';
import flowdeskStartupMark from './assets/flowdesk-startup-mark.png';
import whatsappToolIcon from './assets/whatsapp-tool-icon.png';
import chatgptToolIcon from './assets/chatgpt-tool-icon.png';
import googleToolIcon from './assets/google-tool-icon.png';
import googleDriveToolIcon from './assets/google-drive-tool-icon.png';
import gmailToolIcon from './assets/gmail-tool-icon.png';
import photoshopToolIcon from './assets/photoshop-tool-icon.png';
import illustratorToolIcon from './assets/illustrator-tool-icon.png';
import indesignToolIcon from './assets/indesign-tool-icon.png';
import adhanHosary from './assets/adhan-hosary.mp3';
import adhanAbdelbaset from './assets/adhan-abdelbaset.mp3';
import adhanMinshawi from './assets/adhan-minshawi.mp3';
import adhanMustafaIsmail from './assets/adhan-mustafa-ismail.mp3';
import { DiskStorage } from './data/storageAdapter';
import { OPERATION_MODES, STORAGE_KEYS, SYNC_OPERATION_STATUS, SYNC_OPERATION_TYPES, USER_ROLES, createAuthSession, createCurrentUser, createSyncConnection, createWorkspace, createWorkspaceUser } from './data/flowdeskDataModel';
import { loadFlowDeskData } from './data/appDataService';
import { createFlowDeskRepository } from './data/flowdeskRepository';
import { SETTINGS_TAB_ACCESS_OPTIONS, createPermissionContext } from './data/permissionsService';
import { SYNC_ENTITIES } from './data/syncMap';
import { runSyncCycle } from './data/syncEngine';
import { createConfiguredSyncTransport } from './data/syncTransports';
import { applyServerChangesToState } from './data/serverChangeApplier';
import {
  addCompanyToMap,
  addNamedItemToCompany,
  addTaskToList,
  createCompany,
  createTaskFromDraft,
  deleteTaskFromList,
  getFallbackCompanyKey,
  moveTaskInList,
  removeNamedItemFromCompany,
  resetTaskDraftForCompany,
  toggleTaskTimerInList,
  updateTaskFieldInList,
  updateTaskInList
} from './data/taskOperations';
import { createBackupFilename, createBackupPayload, parseBackupJson } from './data/backupOperations';
import { clearAttendanceLogs, clockInCompany, clockOutCompany, removeAttendanceLog } from './data/attendanceOperations';
import { applyOnboardingCompanySettings, incrementCompanyOvertime, toggleCompanyWorkDay, updateCompanyField } from './data/companySettingsOperations';
import {
  applyDensityToDocument,
  applyThemeToDocument,
  buildSavedSoundSettings,
  savePrayerStatusForToday,
  savePreference,
  toggleLanguageValue,
  updateSettingsField
} from './data/userSettingsOperations';


const FlowDeskRepository = createFlowDeskRepository(DiskStorage);

// --- Bridge Configuration for EXE (Disk Storage) ---
// ÃƒÂ™Ã¢Â€Â¡ÃƒÂ˜Ã‚Â°ÃƒÂ˜§ ÃƒÂ˜Ã‚Â§ÃƒÂ™Ã¢Â€ÂžÃƒÂ˜Ã‚Â¬ÃƒÂ˜Ã‚Â²ÃƒÂ˜¡ ÃƒÂ™Ã¢Â€Â¡ÃƒÂ™Ã‹ ÃƒÂ˜Ã‚Â§ÃƒÂ™Ã¢Â€ÂžÃƒÂ™Ã¢Â€Â¦ÃƒÂ˜Ã‚Â³ÃƒÂ˜Ã‚Â¤ÃƒÂ™Ã‹Â†ÃƒÂ™„ ÃƒÂ˜Ã‚Â¹ÃƒÂ™Ã¢Â€  ÃƒÂ˜Ã‚Â§ÃƒÂ™Ã¢Â€ÂžÃƒÂ˜Ã‚ÂªÃƒÂ™Ã‹Â†ÃƒÂ˜Ã‚Â§ÃƒÂ˜Ã‚ÂµÃƒÂ™„ ÃƒÂ™Ã¢Â€Â¦ÃƒÂ˜¹ ÃƒÂ™Ã¢Â€Â¦ÃƒÂ™Ã¢Â€ÂžÃƒÂ™Ã‚ÂÃƒÂ˜Ã‚Â§ÃƒÂ˜ª ÃƒÂ˜Ã‚Â§ÃƒÂ™Ã¢Â€ÂžÃƒÂ™Ã†Â’ÃƒÂ™Ã¢Â€Â¦ÃƒÂ˜Ã‚Â¨ÃƒÂ™Ã…Â ÃƒÂ™Ã‹Â†ÃƒÂ˜Ã‚ÂªÃƒÂ˜± ÃƒÂ™Ã‚ÂÃƒÂ™Å  ÃƒÂ˜Ã‚Â­ÃƒÂ˜Ã‚Â§ÃƒÂ™Ã¢Â€ÂžÃƒÂ˜© ÃƒÂ˜Ã‚Â§ÃƒÂ™Ã¢Â€ÂžÃƒÂ™€ EXE
// --- Bridge Configuration for EXE (Disk Storage) ---
const EGYPT_GOVERNORATES = [
  { id: 'Cairo', ar: 'Cairo', en: 'Cairo' },
  { id: 'Alexandria', ar: 'Alexandria', en: 'Alexandria' },
  { id: 'Giza', ar: 'Giza', en: 'Giza' },
  { id: 'Qalyubia', ar: 'Qalyubia', en: 'Qalyubia' },
  { id: 'Port Said', ar: 'Port Said', en: 'Port Said' },
  { id: 'Suez', ar: 'Suez', en: 'Suez' },
  { id: 'Gharbia', ar: 'Gharbia', en: 'Gharbia' },
  { id: 'Dakahlia', ar: 'Dakahlia', en: 'Dakahlia' },
  { id: 'Ash Sharqia', ar: 'Ash Sharqia', en: 'Ash Sharqia' },
  { id: 'Monufia', ar: 'Monufia', en: 'Monufia' },
  { id: 'Beheira', ar: 'Beheira', en: 'Beheira' },
  { id: 'Kafr El Sheikh', ar: 'Kafr El Sheikh', en: 'Kafr El Sheikh' },
  { id: 'Damietta', ar: 'Damietta', en: 'Damietta' },
  { id: 'Matrouh', ar: 'Matrouh', en: 'Matrouh' },
  { id: 'Assiut', ar: 'Assiut', en: 'Assiut' },
  { id: 'Sohag', ar: 'Sohag', en: 'Sohag' },
  { id: 'Qena', ar: 'Qena', en: 'Qena' },
  { id: 'Luxor', ar: 'Luxor', en: 'Luxor' },
  { id: 'Aswan', ar: 'Aswan', en: 'Aswan' },
  { id: 'Minya', ar: 'Minya', en: 'Minya' },
  { id: 'Beni Suef', ar: 'Beni Suef', en: 'Beni Suef' },
  { id: 'Faiyum', ar: 'Faiyum', en: 'Faiyum' },
  { id: 'Red Sea', ar: 'Red Sea', en: 'Red Sea' },
  { id: 'South Sinai', ar: 'South Sinai', en: 'South Sinai' },
  { id: 'North Sinai', ar: 'North Sinai', en: 'North Sinai' },
  { id: 'New Valley', ar: 'New Valley', en: 'New Valley' },
  { id: 'Ismailia', ar: 'Ismailia', en: 'Ismailia' }
];

// --- Translations Object ---
const translations = {
    ar: {
    appName: "FlowDesk",

    mainMenu: "القائمة الرئيسية",
    dashboard: "لوحة المهام",
    clients: "Clients",
    add: "إضافة",
    activeNow: "نشط الآن",
    statistics: "تقرير الإنجاز",
    settings: "الإعدادات",
    hello: "Welcome فنان..جاهز 🎨",
    nextPrayer: "الصلاة القادمة",
    newTask: "مهمة جديدة",
    new: "جديد",
    scheduledTasks: "المهام المجدولة",
    inProgress: "قيد التنفيذ",
    clientReview: "مراجعة العميل",
    completedToday: "تم إنجازه اليوم",
    all: "الكل",
    scheduled: "مجدول",
    overdue: "متأخر",
    urgentTasks: "عاجل",
    workingNow: "يعمل الآن",
    review: "مراجعة",
    revision: "تعديل",
    done: "منتهي",
    delivered: "تم التسليم",
    noTasks: "لا يوجد مهام",
    timeElapsed: "الوقت المستغرق",
    deadline: "التسليم",
    estDuration: "المدة المقدرة",
    manager: "المسؤول",
    client: "العميل",
    status: "الحالة",
    description: "الوصف",
    brief: "البريف (Brief)",
    text: "نص التصميم (Text)",
    attachments: "المرفقات",
    delete: "حذف",
    pause: "إيقاف مؤقت",
    resume: "استكمال العمل",
    sendToReview: "إرسال للمراجعة",
    requestRevision: "طلب تعديل",
    reopen: "إعادة فتح",
    addTaskTitle: "إضافة مهمة جديدة ✨",
    taskName: "TOV",
    taskNamePlaceholder: "TOV...",
    projectFile: "ملف العمل / رابط التصميم",
    projectFilePlaceholder: "أدخل مسار الملف على الكمبيوتر أو رابط (Figma, Drive...)",
    openProjectFile: "فتح ملف العمل",
    linkProjectFile: "ربط ملف العمل",
    browseFile: "اختيار ملف",
    taskDesc: "وصف المهمة",
    taskDescPlaceholder: "تفاصيل سريعة...",
    company: "الشركة",
    selectManager: "اختر المسؤول...",
    selectClient: "اختر العميل...",
    duration: "المدة (ساعات)",
    urgent: "عاجل",
    veryUrgent: "عاجل للغاية",
    uploadImages: "اضغط لرفع صور مرجعية",
    addToSchedule: "إضافة للمجدول 🚀",
    statsTitle: "إحصائيات الإنجاز",
    today: "اليوم",
    month: "الشهر",
    companies: "Companies",
    yourHealth: "صحتك",
    healthAlert: "منبه الصحة",
    healthDesc: "الحفاظ على صحتك أهم من الشغل. سنقوم بتذكيرك كل فترة لتأخذ Short Break.",
    remindEvery: "ذكرني كل (دقيقة):",
    visualIdentity: "الهوية البصرية",
    logoDesc: "يظهر في كروت المهام والقوائم.",
    changeLogo: "تغيير الشعار",
    themeColor: "لون الثيم (Theme)",
    team: "فريق العمل (Account Managers)",
    companyClients: "عملاء الشركة",
    clientName: "Client name...",
    employeeName: "اسم الموظف...",
    noManagers: "لا يوجد مديرين.",
    noClients: "لا يوجد عملاء.",
    healthBreak: "وقت الراحة ☕",
    healthBreakMsg: "أنت تعمل بجد منذ فترة طويلة. تحرك قليلاً وجدد نشاطك.",
    willMove: "سأتحرك الآن 💪",
    tomorrow: "غداً",
    prayers: { Fajr: 'الفجر', Dhuhr: 'الظهر', Asr: 'العصر', Maghrib: 'المغرب', Isha: 'العشاء' },
    loading: "جاري Download البيانات...",
    startWork: "بدء العمل",
    start: "بدء",
    stop: "إيقاف",
    designType: "نوع التصميم",
    socialMedia: "سوشيال ميديا",
    printables: "مطبوعات",
    advertisement: "إعلان / Ads",
    platforms: "المنصات / العناصر",
    workHours: "مواعيد العمل",
    workStart: "بداية الدوام",
    workEnd: "نهاية الدوام",
    overtimeAlert: "تنبيه انتهاء الدوام 🚨",
    overtimeMsg: "لقد اقترب موعد انتهاء العمل. هل تود تفعيل حساب الساعات الإضافية (Overtime)؟",
    enableOvertime: "تفعيل الأوفرتايم",
    ignore: "تجاهل",
    overtime: "ساعات إضافية",
    open: "مفتوح",
    closed: "مغلق",
    edit: "تعديل",
    saveChanges: "حفظ التعديلات",
    cancel: "إلغاء",
    prayersList: "جدول الصلوات",
    upcomingPrayer: "متبقي على الأذان",
    adhanTime: "حان وقت الصلاة",
    goToPray: "الذهاب للصلاة 🕌",
    prayed: "Completed",
    remindAfter10: "تذكيري بعد 10 دقائق",
    dismiss: "تجاهل",
    notStarted: "لم يبدأ",
    search: "بحث عن مهمة...",
    urgentOnly: "عاجل فقط 🔥",
    subtasks: "الخطوات الفرعية (Checklist)",
    addSubtask: "إضافة خطوة...",
    hourlyRate: "سعر الساعة (ج.م)",
    earnings: "الأرباح",
    backupRestore: "النسخ الاحتياطي",
    exportData: "تصدير البيانات",
    importData: "استيراد بيانات",
    soundSettings: "\u0627\u0644\u0645\u062d\u0627\u0641\u0638\u0629 \u0648\u0645\u0648\u0627\u0642\u064a\u062a \u0627\u0644\u0623\u0630\u0627\u0646",
    volume: "مستوى الصوت",
    fullAdhan: "أذان كامل",
    takbeerOnly: "تكبيرات فقط",
    dueDate: "موعد الاستحقاق",
    pickDate: "اختر التاريخ",
    customDate: "تاريخ مخصص",
    dateFilter: "تصفية بالتاريخ",
    filterAll: "كل الأوقات",
    filterToday: "اليوم",
    filterTomorrow: "غداً",
    filterWeek: "هذا الأسبوع",
    filterMonth: "هذا الشهر",
    defaultCompanyNames: {
      freelance: "فريلانس / خاص"
    },
    location: "الموقع",
    governorate: "المحافظة",
    profile: "الملف الشخصي",
    userName: "اسم المستخدم",
    userPhoto: "صورة المستخدم",
    uploadPhoto: "Change Photo",
    showPrayerTimes: "عرض مواقيت الصلاة",
    save: "حفظ",
    resetData: "إعادة ضبط المصنع",
    resetConfirm: "هل أنت متأكد؟ سيتم حذف جميع البيانات والمهام وClients نهائياً ولا يمكن التراجع عن ذلك.",
    resetSuccess: "تم إعادة الضبط بنجاح.",
    workDaysLabel: "أيام العمل والراحة",
    days: ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"],
    openDataFolder: "فتح مجلد البيانات المحلي",
    openFolder: "فتح المجلد",
    checkUpdates: "التحقق من التحديثات",
    updateAvailable: "تحديث جديد متاح ✨",
    updateReady: "التحديث جاهز 🚀",
    restartNow: "إعادة التشغيل الآن",
    updateNow: "تحديث الآن",
    version: "إصدار",
    updateDesc: "يوجد إصدار جديد متاح. هل تريد التحديث الآن؟",
    restartDesc: "تم Download التحديث بنجاح. قم بإعادة التشغيل لتثبيته.",
    savedSuccess: "تم الحفظ بنجاح",
    backupSuccess: "تم تصدير النسخة الاحتياطية بنجاح",
    upToDate: "أنت على أحدث إصدار",
    latestVersionMsg: "البرنامج بأحدث إصدار حالياً.",
    deleteAllDone: "حذف الكل",
    confirmDeleteAllDone: "هل أنت متأكد من حذف جميع المهام المسلمة؟",
  },
  en: {
    appName: "FlowDesk",
    mainMenu: "MAIN MENU",
    dashboard: "Dashboard",
    clients: "Clients",
    add: "Add",
    activeNow: "Active Now",
    statistics: "Performance Report",
    settings: "Settings",
    hello: "Hey Artist.. Ready? 🎨",
    nextPrayer: "Next Prayer",
    newTask: "New Task",
    new: "New",
    scheduledTasks: "Scheduled",
    inProgress: "In Progress",
    clientReview: "Client Review",
    completedToday: "Completed Today",
    all: "All",
    scheduled: "Scheduled",
    overdue: "Overdue",
    urgentTasks: "Urgent",
    workingNow: "Working Now",
    review: "Review",
    revision: "Revision",
    done: "Done",
    delivered: "Delivered",
    noTasks: "No tasks yet",
    timeElapsed: "Time Elapsed",
    deadline: "Deadline",
    estDuration: "Est. Duration",
    manager: "Assignee",
    client: "Client",
    status: "Status",
    description: "Description",
    brief: "Brief",
    text: "Copy / Text",
    attachments: "Attachments",
    delete: "Delete",
    pause: "Pause",
    resume: "Resume",
    sendToReview: "Send for Review",
    requestRevision: "Request Revision",
    reopen: "Reopen",
    addTaskTitle: "Add New Task ✨",
    taskName: "TOV",
    taskNamePlaceholder: "TOV...",
    taskDesc: "Description",
    taskDescPlaceholder: "Quick details...",
    company: "Company",
    selectManager: "Select Manager...",
    selectClient: "Select Client...",
    duration: "Duration (Hours)",
    urgent: "Urgent",
    veryUrgent: "Very Urgent",
    uploadImages: "Click to upload reference images",
    addToSchedule: "Add to Schedule 🚀",
    statsTitle: "Completion Stats",
    today: "Today",
    month: "Month",
    companies: "Companies",
    yourHealth: "Health",
    healthAlert: "Health Alert",
    healthDesc: "Your health is more important than work. We'll remind you to take short breaks.",
    remindEvery: "Remind every (min):",
    visualIdentity: "Visual Identity",
    logoDesc: "Shown on task cards and lists.",
    changeLogo: "Change Logo",
    themeColor: "Theme Color",
    team: "Team (Account Managers)",
    companyClients: "Company Clients",
    clientName: "Client name...",
    employeeName: "Employee name...",
    noManagers: "No managers.",
    noClients: "No clients.",
    healthBreak: "Break Time ☕",
    healthBreakMsg: "You've been working hard. Move around and refresh.",
    willMove: "I'll move now 💪",
    tomorrow: "Tomorrow",
    prayers: { Fajr: 'Fajr', Dhuhr: 'Dhuhr', Asr: 'Asr', Maghrib: 'Maghrib', Isha: 'Isha' },
    loading: "Loading Data...",
    startWork: "Start Work",
    start: "Start",
    stop: "Stop",
    designType: "Design Type",
    socialMedia: "Social Media",
    printables: "Printables",
    advertisement: "Advertisement",
    platforms: "Platforms / Items",
    workHours: "Working Hours",
    workStart: "Start Time",
    workEnd: "End Time",
    overtimeAlert: "Shift Ending Soon 🚨",
    overtimeMsg: "Work hours are almost over. Do you want to enable Overtime tracking?",
    enableOvertime: "Enable Overtime",
    ignore: "Ignore",
    overtime: "Overtime",
    open: "Open",
    closed: "Closed",
    edit: "Edit",
    saveChanges: "Save Changes",
    cancel: "Cancel",
    prayersList: "Prayer Schedule",
    upcomingPrayer: "Time Remaining",
    adhanTime: "It's Prayer Time",
    goToPray: "Go to Pray 🕌",
    prayed: "Prayed",
    remindAfter10: "Remind me in 10 minutes",
    dismiss: "Dismiss",
    notStarted: "Not Started",
    search: "Search tasks...",
    urgentOnly: "Urgent Only 🔥",
    subtasks: "Checklist (Subtasks)",
    addSubtask: "Add step...",
    hourlyRate: "Hourly Rate (EGP)",
    earnings: "Earnings",
    backupRestore: "Backup & Restore",
    exportData: "Export Data",
    importData: "Import Data",
    soundSettings: "Prayer Location",
    volume: "Volume",
    fullAdhan: "Full Adhan",
    takbeerOnly: "Takbeer Only",
    dueDate: "Due Date",
    pickDate: "Pick Date",
    customDate: "Custom Date",
    dateFilter: "Date Filter",
    filterAll: "All Time",
    filterToday: "Today",
    filterTomorrow: "Tomorrow",
    filterWeek: "This Week",
    filterMonth: "This Month",
    defaultCompanyNames: {
      freelance: "Freelance / Private"
    },
    location: "Location",
    governorate: "Governorate",
    profile: "Profile",
    userName: "User Name",
    userPhoto: "User Photo",
    uploadPhoto: "Change Photo",
    showPrayerTimes: "Show Prayer Times",
    save: "Save",
    resetData: "Factory Reset",
    resetConfirm: "Are you sure? All data, tasks, and clients will be permanently deleted. This cannot be undone.",
    resetSuccess: "Factory reset successful.",
    workDaysLabel: "Work & Rest Days",
    days: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    openDataFolder: "Open Local Data Folder",
    openFolder: "فتح المجلد",
    checkUpdates: "التحقق من التحديثات",
    updateAvailable: "تحديث جديد متاح",
    updateReady: "التحديث جاهز",
    restartNow: "إعادة التشغيل الآن",
    updateNow: "تحديث الآن",
    version: "إصدار",
    updateDesc: "نسخة جديدة متاحة. هل تريد التحديث؟",
    restartDesc: "تم Download التحديث. أعد التشغيل للتثبيت.",
    downloadingUpdate: "جاري الDownload...",
    later: "لاحقاً",
    deleteCompany: "حذف الشركة",
    confirmDeleteCompany: "هل أنت متأكد من حذف هذه الشركة؟ سيتم فقد جميع البيانات المتعلقة بها.",
    cannotDeleteLastCompany: "لا يمكن حذف الشركة الوحيدة المتبقية.",
    installingUpdate: "جاري تثبيت التحديث...",
    installNow: "تثبيت الآن",
    upToDate: "Up to Date",
    latestVersionMsg: "You are on the latest version.",
    backupSuccess: "Backup exported successfully",
    deleteAllDone: "Delete All",
    confirmDeleteAllDone: "Are you sure you want to delete all delivered tasks?",
  }
};

const DESIGN_OPTS = {
  social: { id: 'social', labelKey: 'socialMedia', items: ['Facebook', 'Instagram', 'LinkedIn', 'X (Twitter)', 'YouTube', 'TikTok', 'Snapchat'] },
  print: { id: 'print', labelKey: 'printables', items: ['A4', 'Flyer', 'Brochure', 'Business Card', 'Magazine', 'Guideline', 'Moodboard'] },
  ads: { id: 'ads', labelKey: 'advertisement', items: ['Facebook', 'Instagram', 'LinkedIn', 'X (Twitter)', 'YouTube', 'TikTok', 'Snapchat'] }
};

const ADHAN_VOICES = [
  { id: 'hosary', ar: 'الحصري', en: 'Al Hosary', src: adhanHosary },
  { id: 'abdelbaset', ar: 'عبد الباسط عبد الصمد', en: 'Abdel Baset Abd El Samad', src: adhanAbdelbaset },
  { id: 'minshawi', ar: 'محمد صديق المنشاوي', en: 'Mohamed Siddiq Al Minshawi', src: adhanMinshawi },
  { id: 'mustafa-ismail', ar: 'مصطفى إسماعيل', en: 'Mustafa Ismail', src: adhanMustafaIsmail }
];

const DEFAULT_SOUND_SETTINGS = { adhanType: 'full', adhanVoice: 'hosary', volume: 1 };

const formatErrorMessage = (value, fallback = '') => {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (value instanceof Error) return value.message;
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message;
    if (value.error) return formatErrorMessage(value.error, fallback);
    if (value.code || value.message) {
      return [value.code, formatErrorMessage(value.message, '')].filter(Boolean).join(': ');
    }
    try {
      return JSON.stringify(value);
    } catch {
      return fallback || String(value);
    }
  }
  return String(value);
};

const normalizeServerCompanies = (value) => {
  if (!value || typeof value !== 'object') return {};
  const list = Array.isArray(value) ? value : Object.values(value);
  const colorPalettes = [
    { id: 'blue', name: 'Ocean Blue', bg: 'bg-blue-600', text: 'text-blue-500', border: 'border-blue-500/30' },
    { id: 'purple', name: 'Royal Purple', bg: 'bg-purple-600', text: 'text-purple-500', border: 'border-purple-500/30' },
    { id: 'emerald', name: 'Emerald', bg: 'bg-emerald-600', text: 'text-emerald-500', border: 'border-emerald-500/30' },
    { id: 'amber', name: 'Amber', bg: 'bg-amber-600', text: 'text-amber-500', border: 'border-amber-500/30' }
  ];

  return Object.fromEntries(
    list
      .filter(company => company && (company.id || company.companyId))
      .map((company, index) => {
        const id = String(company.id || company.companyId);
        const palette = colorPalettes[index % colorPalettes.length];
        return [id, {
          id,
          name: company.name || company.company_name || 'Company ' + id,
          logo: company.logo_url || company.logo || null,
          theme: typeof company.theme === 'object' ? company.theme : palette,
          workStart: company.workStart || company.work_start || '09:00',
          workEnd: company.workEnd || company.work_end || '17:00',
          workDays: company.workDays || [0, 1, 2, 3, 4, 6],
          clients: Array.isArray(company.clients) ? company.clients : [],
          managers: Array.isArray(company.managers) ? company.managers : [],
          systemUrl: company.systemUrl || '',
          systemEnabled: Boolean(company.systemEnabled),
          driveUrl: company.driveUrl || '',
          driveEnabled: Boolean(company.driveEnabled),
          isClockedIn: false,
          clockInTime: null,
          attendanceLogs: []
        }];
      })
  );
};

const shouldSkipOnboardingForAccount = ({ user, authSession, syncConnection } = {}) => {
  // Onboarding permanently disabled
  return true;
};


const CustomStatusFilterDropdown = ({ statusFilter, setStatusFilter, lang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const options = [
    { id: 'all', label: lang === 'ar' ? 'تصفية حسب الحالة: الكل' : 'Filter Status: All' },
    { id: 'scheduled', label: 'New' },
    { id: 'ready', label: 'Ready' },
    { id: 'in_progress', label: 'Working' },
    { id: 'review', label: 'Review' },
    { id: 'revision', label: 'Edit' },
    { id: 'done', label: 'Confirm' },
  ];

  const selectedItem = options.find(o => o.id === statusFilter) || options[0];

  return (
    <div className="relative w-full sm:w-auto" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 bg-white dark:bg-[#151921] border border-slate-200 dark:border-slate-700/80 hover:border-blue-500/60 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm font-extrabold text-slate-800 dark:text-slate-100 shadow-sm transition-all cursor-pointer group"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Filter size={15} className="text-blue-500 shrink-0" />
          <span className="truncate max-w-[220px]">{selectedItem.label}</span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full sm:w-64 bg-white dark:bg-[#131824] border border-slate-200 dark:border-white/15 rounded-2xl shadow-2xl z-50 p-2 space-y-1 animate-in fade-in zoom-in-95 duration-150 text-right">
          {options.map((opt) => {
            const isSelected = statusFilter === opt.id;
            return (
              <div
                key={opt.id}
                onClick={() => { setStatusFilter(opt.id); setIsOpen(false); }}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 font-black'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`w-2.5 h-2.5 rounded-full ${
                    opt.id === 'all' ? 'bg-blue-500' :
                    opt.id === 'ready' ? 'bg-purple-500' :
                    opt.id === 'in_progress' ? 'bg-amber-500' :
                    opt.id === 'review' ? 'bg-cyan-500' :
                    opt.id === 'revision' ? 'bg-orange-500' :
                    opt.id === 'done' ? 'bg-emerald-500' : 'bg-blue-500'
                  } shrink-0`}></span>
                  <span className="truncate">{opt.label}</span>
                </div>
                {isSelected && <Check size={15} className="text-blue-600 dark:text-blue-400 shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CustomCompanyFilterDropdown = ({ filter, setFilter, visibleCompanies, lang }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const companyEntries = Object.entries(visibleCompanies || {})
    .filter(([, cfg]) => cfg && typeof cfg === 'object');

  if (companyEntries.length <= 1) return null;

  const activeComp = visibleCompanies[filter];
  const activeCompName = filter === 'all'
    ? (lang === 'ar' ? 'الشركة: الكل' : 'Company: All')
    : (activeComp && typeof activeComp.name === 'string' ? activeComp.name : filter);

  return (
    <div className="relative w-full sm:w-auto flex-1 sm:flex-none" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 bg-white dark:bg-[#151921] border border-slate-200 dark:border-slate-700/80 hover:border-blue-500/60 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm font-extrabold text-slate-800 dark:text-slate-100 shadow-sm transition-all cursor-pointer group"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 size={15} className="text-blue-500 shrink-0" />
          <span className="truncate max-w-[160px] sm:max-w-[200px]">{activeCompName}</span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full sm:w-64 bg-white dark:bg-[#131824] border border-slate-200 dark:border-white/15 rounded-2xl shadow-2xl z-50 p-2 space-y-1 animate-in fade-in zoom-in-95 duration-150 text-right">
          <div
            onClick={() => { setFilter('all'); setIsOpen(false); }}
            className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold cursor-pointer transition-colors ${
              filter === 'all'
                ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 font-black'
                : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0"></span>
              <span className="truncate">{lang === 'ar' ? 'الشركة: الكل' : 'Company: All'}</span>
            </div>
            {filter === 'all' && <Check size={15} className="text-blue-600 dark:text-blue-400 shrink-0" />}
          </div>

          {companyEntries.map(([key, config]) => {
            const isSelected = filter === key;
            const compName = typeof config.name === 'string' && config.name ? config.name : key;
            return (
              <div
                key={key}
                onClick={() => { setFilter(key); setIsOpen(false); }}
                className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-bold cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-blue-50 dark:bg-blue-600/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30 font-black'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/80 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className={`w-2.5 h-2.5 rounded-full ${config.theme?.bg || 'bg-blue-500'} shrink-0`}></span>
                  <span className="truncate">{compName}</span>
                </div>
                {isSelected && <Check size={15} className="text-blue-600 dark:text-blue-400 shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CustomQuickSelect = ({ label, value, options, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(o => String(o.id) === String(value));
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative w-full text-right" ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-extrabold text-slate-600 dark:text-slate-300 mb-1.5">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2.5 bg-slate-100 dark:bg-[#121722] border border-slate-200 dark:border-white/10 hover:border-blue-500/60 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-800 dark:text-slate-100 shadow-sm transition-all cursor-pointer group"
      >
        <div className="flex items-center gap-2 truncate min-w-0">
          {selectedOption?.bg && (
            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${selectedOption.bg}`} />
          )}
          <span className="truncate font-black">
            {selectedOption ? selectedOption.name : (placeholder || value)}
          </span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white dark:bg-[#121722] border border-slate-200 dark:border-white/15 rounded-2xl shadow-2xl z-50 p-1.5 space-y-1 max-h-52 overflow-y-auto animate-in fade-in zoom-in-95 duration-150">
          {placeholder && (
            <div
              onClick={() => { onChange(''); setIsOpen(false); }}
              className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                !value
                  ? 'bg-blue-600 text-white font-black shadow-md shadow-blue-600/30'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span>{placeholder}</span>
              {!value && <Check size={14} className="shrink-0" />}
            </div>
          )}
          {options.map((opt) => {
            const isSelected = String(opt.id) === String(value);
            return (
              <div
                key={opt.id}
                onClick={() => { onChange(opt.id); setIsOpen(false); }}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                  isSelected
                    ? 'bg-blue-600 text-white font-black shadow-md shadow-blue-600/30'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <div className="flex items-center gap-2 truncate min-w-0">
                  {opt.bg && <span className={`w-2 h-2 rounded-full shrink-0 ${opt.bg}`} />}
                  <span className="truncate">{opt.name}</span>
                </div>
                {isSelected && <Check size={14} className="shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const App = () => {
  // --- Color Palettes Configuration ---
  const colorPalettes = [
    { id: 'blue', bg: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-400', glow: 'shadow-blue-400/20', ring: 'ring-blue-400' },
    { id: 'sky', bg: 'bg-sky-500', text: 'text-sky-400', border: 'border-sky-400', glow: 'shadow-sky-400/20', ring: 'ring-sky-400' },
    { id: 'coral', bg: 'bg-indigo-500', text: 'text-indigo-400', border: 'border-indigo-400', glow: 'shadow-indigo-400/20', ring: 'ring-indigo-400' },
    { id: 'lime', bg: 'bg-lime-400', text: 'text-lime-400', border: 'border-lime-400', glow: 'shadow-lime-400/20', ring: 'ring-lime-400' },
    { id: 'amber', bg: 'bg-amber-400', text: 'text-amber-400', border: 'border-amber-400', glow: 'shadow-amber-400/20', ring: 'ring-amber-400' },
    { id: 'slate', bg: 'bg-slate-700', text: 'text-slate-300', border: 'border-slate-500', glow: 'shadow-slate-500/20', ring: 'ring-slate-400' },
  ];

  // --- States with Loading Handling ---
  const [isLoading, setIsLoading] = useState(true);
  const [lang, setLang] = useState('en');
  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState({});
  const [healthSettings, setHealthSettings] = useState({ enabled: true, interval: 60, lastCheck: Date.now() });
  const [prayerStatus, setPrayerStatus] = useState({ Fajr: false, Dhuhr: false, Asr: false, Maghrib: false, Isha: false });
  const [soundSettings, setSoundSettings] = useState({ ...DEFAULT_SOUND_SETTINGS, adhanType: 'full' });
  const [governorate, setGovernorate] = useState('Cairo');
  const [userName, setUserName] = useState('');
  const [userPhoto, setUserPhoto] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [currentUser, setCurrentUser] = useState(createCurrentUser());
  const [workspaceUsers, setWorkspaceUsers] = useState([]);
  const [authSession, setAuthSession] = useState(createAuthSession());
  const [loginDraft, setLoginDraft] = useState({ email: '', password: '', name: '', role: USER_ROLES.owner, rememberMe: true });
  const [workspaceUserDraft, setWorkspaceUserDraft] = useState({ name: '', email: '', password: '', title: '', accessRole: USER_ROLES.designer, companyIds: [], settingsTabs: ['profile'], clientAccess: {} });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingUserDraft, setEditingUserDraft] = useState({});

  const [workspaceName, setWorkspaceName] = useState('FlowDesk');
  const [currentWorkspace, setCurrentWorkspace] = useState(createWorkspace());
  const [theme, setTheme] = useState('dark'); // 'light', 'dark', 'system'
  const [uiDensity, setUiDensity] = useState('comfortable');
  const [operationMode, setOperationMode] = useState(OPERATION_MODES.online);
  const [syncQueue, setSyncQueue] = useState([]);
  const [syncConnection, setSyncConnection] = useState(createSyncConnection());
  const [syncBackups, setSyncBackups] = useState([]);
  const [syncResult, setSyncResult] = useState(null);
  const [isSyncRunning, setIsSyncRunning] = useState(false);
  const paletteLabels = {
    ar: { blue: 'أزرق', sky: 'سماوي', coral: 'بنفسجي', lime: 'أخضر', amber: 'ذهبي', slate: 'رمادي' },
    en: { blue: 'Blue', sky: 'Sky', coral: 'Indigo', lime: 'Green', amber: 'Gold', slate: 'Slate' }
  };
  const getPaletteLabel = (id) => paletteLabels[lang]?.[id] || id;
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({
    language: 'ar',
    governorate: 'Cairo',
    userName: '',
    userRole: '',
    workspaceName: 'FlowDesk',
    companyName: '',
    clientsText: '',
    managersText: '',
    workStart: '09:00',
    workEnd: '17:00',
    hourlyRate: 0
  });

  // --- Company Sync State ---


  // --- Auto-Update State & Logic ---
  const [updateStatus, setUpdateStatus] = useState({ status: 'idle', info: null });
  const manualCheckRef = useRef(false);
  const langRef = useRef(lang);

  useEffect(() => {
    langRef.current = lang;
  }, [lang]);

  useEffect(() => {
    if (!window.electronAPI) return;

    // Listen for available updates
    const removeAvailableListener = window.electronAPI.onUpdateAvailable((info) => {
      console.log('Update available:', info);
      setUpdateStatus({ status: 'available', info });

      const currentLang = langRef.current;
      const t = translations.en || translations.en;

      // Always show update available alert
      setActiveAlert({
        title: t?.updateAvailable || "Update Available",
        message: `Version ${info.version} is available.`,
        type: 'success',
        onConfirm: () => handleDownloadUpdate(), // Optional: simplify direct action
        showCancel: true
      });
      manualCheckRef.current = false;
    });

    // Listen for "Up to Date" (only if manually checked)
    const removeNotAvailableListener = window.electronAPI.onUpdateNotAvailable((info) => {
      if (manualCheckRef.current) {
        const currentLang = langRef.current;
        const t = translations.en || translations.en;

        setActiveAlert({
          title: t?.upToDate || "Up to Date",
          message: t?.latestVersionMsg || "You are on the latest version.",
          type: 'success'
        });
        manualCheckRef.current = false;
      }
    });

    const removeProgressListener = window.electronAPI.onDownloadProgress((progressObj) => {
      setUpdateStatus(prev => ({ ...prev, status: 'downloading', progress: progressObj.percent }));
    });

    const removeDownloadedListener = window.electronAPI.onUpdateDownloaded((info) => {
      console.log('Update downloaded:', info);
      setUpdateStatus({ status: 'ready', info });
    });

    const removeErrorListener = window.electronAPI.onUpdateError((err) => {
      console.error('Update error:', err);
      setUpdateStatus(prev => ({ ...prev, status: 'error', error: err }));

      // Only show error alert if it was a manual check
      if (manualCheckRef.current) {
        const currentLang = langRef.current;
        const t = translations.en || translations.en;

        // If it's the specific "GitHub Token" error or 404, the user prefers a "Latest Version" message
        // instead of a scary red error.
        const errorMsg = err.toString();
        if (errorMsg.includes("GitHub Personal Access Token is not set") || errorMsg.includes("404") || errorMsg.includes("No published versions on GitHub")) {
          setActiveAlert({
            title: t?.upToDate || "Up to Date",
            message: t?.latestVersionMsg || "You are on the latest version.",
            type: 'success'
          });
        } else {
          setActiveAlert({
            title: t?.error || "Error",
            message: errorMsg,
            type: 'danger'
          });
        }
        manualCheckRef.current = false;
      }
    });

    return () => {
      try { if (typeof removeAvailableListener === 'function') removeAvailableListener(); } catch (e) { console.error(e); }
      try { if (typeof removeNotAvailableListener === 'function') removeNotAvailableListener(); } catch (e) { console.error(e); }
      try { if (typeof removeProgressListener === 'function') removeProgressListener(); } catch (e) { console.error(e); }
      try { if (typeof removeDownloadedListener === 'function') removeDownloadedListener(); } catch (e) { console.error(e); }
      try { if (typeof removeErrorListener === 'function') removeErrorListener(); } catch (e) { console.error(e); }
    };
  }, []);

  const handleDownloadUpdate = () => {
    setActiveAlert(null);
    if (window.electronAPI && window.electronAPI.startDownload) {
      window.electronAPI.startDownload();
      setUpdateStatus(prev => ({ ...prev, status: 'downloading' }));
    }
  };

  const handleRestartApp = () => {
    // Show installing overlay first
    setUpdateStatus(prev => ({ ...prev, status: 'installing' }));

    // Tiny delay to let UI render the overlay, then quit
    setTimeout(() => {
      if (window.electronAPI && window.electronAPI.restartApp) {
        window.electronAPI.restartApp();
      }
    }, 1500);
  };

  const dismissUpdate = () => {
    setUpdateStatus({ status: 'idle', info: null });
  };

  const checkForUpdates = () => {
    manualCheckRef.current = true; // Mark as manual check

    // Show "Checking..." feedback
    setActiveAlert({
      title: translations.en?.checkUpdates || "Checking for Updates",
      message: translations.en?.loading || "Please wait...",
      type: 'info'
    });

    if (window.electronAPI && window.electronAPI.checkForUpdates) {
      window.electronAPI.checkForUpdates();
    }
  };

  const t = translations.en || translations.en;
  const isHostedWebApp = typeof window !== 'undefined' && window.location.pathname.startsWith('/flowdesk');
  const isDesktopApp = typeof window !== 'undefined' && Boolean(window.electronAPI);
  const isElectron = () => {
    return typeof window !== 'undefined' && Boolean(
      window.process?.type === 'renderer' ||
      navigator.userAgent?.toLowerCase().includes('electron') ||
      window.electronAPI
    );
  };
  const isNativeMobileApp = typeof window !== 'undefined' && Boolean(
    window.Capacitor?.isNativePlatform?.()
    || window.Capacitor?.getPlatform?.() === 'android'
    || window.location.protocol === 'capacitor:'
    || window.location.protocol === 'https:'
  ) && typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod|Mobile|wv/i.test(navigator.userAgent);
  const isMobileRuntime = !isDesktopApp && (
    isNativeMobileApp
    || (typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent))
  );
  const flowDeskCloudEndpoint = import.meta.env.VITE_FLOWDESK_API_URL || 'https://flowdesk.ahmedkhames.com';
  const hostedApiEndpoint = typeof window !== 'undefined'
    ? (import.meta.env.VITE_FLOWDESK_API_URL || `${window.location.origin}/api`)
    : flowDeskCloudEndpoint;
  const defaultApiEndpoint = isHostedWebApp ? hostedApiEndpoint : flowDeskCloudEndpoint;
  const requiresCloudLogin = false;
  const permissionContext = createPermissionContext({ role: currentUser.accessRole || USER_ROLES.owner, user: currentUser });
  const hasPermission = permissionContext.hasPermission;
  const requirePermission = permissionContext.requirePermission;
  const canAccessSettingsTab = permissionContext.canAccessSettingsTab;
  const isOwnerUser = (currentUser.accessRole || userRole) === USER_ROLES.owner || !currentUser.accessRole || currentUser.accessRole === 'owner' || currentUser.id === 'local-owner';
  const userCompanyIds = Array.isArray(currentUser.companyIds) ? currentUser.companyIds.map(String) : [];
  const canAccessCompany = (companyId) => {
    if (isOwnerUser) return true;
    if (!userCompanyIds || userCompanyIds.length === 0) return true;
    return userCompanyIds.includes('all') || userCompanyIds.includes(String(companyId));
  };

  // Mobile Pull to Refresh State
  const [pullY, setPullY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartYRef = useRef(0);

  const handleTouchStart = (e) => {
    if (typeof window !== 'undefined' && (window.scrollY <= 10 || document.documentElement.scrollTop <= 10)) {
      touchStartYRef.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartYRef.current;
    if (diff > 0 && typeof window !== 'undefined' && (window.scrollY <= 10 || document.documentElement.scrollTop <= 10)) {
      const distance = Math.min(Math.pow(diff, 0.82), 85);
      setPullY(distance);
    } else {
      setPullY(0);
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling) return;
    setIsPulling(false);
    if (pullY >= 45) {
      setIsRefreshing(true);
      setPullY(45);
      try {
        if (typeof syncWithCloudServer === 'function') {
          await syncWithCloudServer();
        }
      } catch (err) {
        console.error('Pull to refresh failed:', err);
      } finally {
        setTimeout(() => {
          setIsRefreshing(false);
          setPullY(0);
        }, 550);
      }
    } else {
      setPullY(0);
    }
  };

  const userClientAccess = currentUser.clientAccess && typeof currentUser.clientAccess === 'object' && !Array.isArray(currentUser.clientAccess) ? currentUser.clientAccess : {};
  const getAllowedClientsForCompany = (companyId, user = currentUser) => {
    const companyClients = Array.isArray(companies[companyId]?.clients) ? companies[companyId].clients : [];
    if ((user?.accessRole || USER_ROLES.owner) === USER_ROLES.owner) return companyClients;
    const access = user?.clientAccess && typeof user.clientAccess === 'object' && !Array.isArray(user.clientAccess) ? user.clientAccess : {};
    const assignedClients = Array.isArray(access[companyId]) ? access[companyId].map(String) : [];
    return assignedClients.length ? companyClients.filter(client => assignedClients.includes(String(client))) : companyClients;
  };
  const canAccessClient = (companyId, clientName = '', clientId = '') => {
    if (isOwnerUser) return true;
    if (!canAccessCompany(companyId)) return false;
    const cidStr = String(companyId);
    const assignedClients = Array.isArray(userClientAccess[cidStr])
      ? userClientAccess[cidStr].map(String)
      : (Array.isArray(userClientAccess[companyId]) ? userClientAccess[companyId].map(String) : []);
    if (!assignedClients.length || (!clientName && !clientId)) return true;
    return assignedClients.includes(String(clientName)) || assignedClients.includes(String(clientId));
  };
  const visibleCompanies = Object.fromEntries(
    Object.entries(companies)
      .filter(([key, cfg]) => canAccessCompany(key) && cfg && typeof cfg === 'object' && !Array.isArray(cfg))
  );
  const visibleCompanyKeys = Object.keys(visibleCompanies);
  const visibleTasks = tasks.filter(task =>
    canAccessCompany(task.category || task.companyId) &&
    canAccessClient(task.category || task.companyId, task.client || task.clientName, task.clientId)
  );
  const canCreateTask = hasPermission('manageTasks') || hasPermission('createTasks');
  const isAccountManagerUser = currentUser.accessRole === USER_ROLES.manager;
  const canControlTaskWork = hasPermission('manageTasks') && !isAccountManagerUser;
  const canMoveTaskToStatus = (task, newStatus) => {
    if (!task) return false;
    if (isAccountManagerUser) {
      return (task.status === 'review' && ['revision', 'done'].includes(newStatus))
        || (task.status === 'done' && newStatus === 'revision');
    }
    if (hasPermission('manageTasks')) return true;
    return hasPermission('manageReviewFlow') && task.status === 'review' && ['revision', 'done'].includes(newStatus);
  };
  const queueSyncOperation = (operation) => {
    if (operationMode !== OPERATION_MODES.online) return Promise.resolve(null);
    return FlowDeskRepository.enqueueSyncOperation(operation)
      .then(() => FlowDeskRepository.loadSyncQueue())
      .then(queue => {
        setSyncQueue(queue);
        scheduleImmediateSync();
        return queue;
      })
      .catch(error => {
        console.error('Failed to queue sync operation:', error);
        return null;
      });
  };
  const queueTaskSync = (task, type = SYNC_OPERATION_TYPES.upsert) => {
    if (!task) return Promise.resolve(null);
    return queueSyncOperation({
      entity: SYNC_ENTITIES.tasks,
      entityId: String(task.id),
      type,
      payload: type === SYNC_OPERATION_TYPES.delete
        ? { id: task.id, category: task.category, companyId: task.category }
        : task
    });
  };
  const queueTaskSyncBulk = (taskOperations = []) => {
    const operations = taskOperations
      .filter(({ task }) => Boolean(task))
      .map(({ task, type = SYNC_OPERATION_TYPES.upsert }) => ({
        entity: SYNC_ENTITIES.tasks,
        entityId: String(task.id),
        type,
        payload: type === SYNC_OPERATION_TYPES.delete
          ? { id: task.id, category: task.category, companyId: task.category }
          : task
      }));
    if (operationMode !== OPERATION_MODES.online || operations.length === 0) return Promise.resolve([]);
    return FlowDeskRepository.enqueueSyncOperations(operations)
      .then(() => FlowDeskRepository.loadSyncQueue())
      .then(queue => {
        setSyncQueue(queue);
        scheduleImmediateSync();
        return queue;
      })
      .catch(error => {
        console.error('Failed to queue sync operations:', error);
        return [];
      });
  };
  const queueCompanySync = (company, type = SYNC_OPERATION_TYPES.upsert) => {
    if (!company) return;
    queueSyncOperation({
      entity: SYNC_ENTITIES.companies,
      entityId: String(company.id),
      type,
      payload: type === SYNC_OPERATION_TYPES.delete ? null : company
    });
  };
  const queueWorkspaceUserSync = (user, type = SYNC_OPERATION_TYPES.upsert) => {
    if (!user) return;
    queueSyncOperation({
      entity: SYNC_ENTITIES.workspaceUsers,
      entityId: String(user.id),
      type,
      payload: type === SYNC_OPERATION_TYPES.delete ? null : user
    });
  };

  // --- Initial Data Loading from Disk/Storage ---
  useEffect(() => {
    const initData = async () => {
      setIsLoading(true);
      const appData = await FlowDeskRepository.loadAppData({
        loadFlowDeskData,
        colorPalettes,
        defaultSoundSettings: DEFAULT_SOUND_SETTINGS
      });

      setLang('en');
      setHealthSettings(appData.healthSettings);

      // --- Endpoint Migration ---
      let startupSyncConnection = appData.syncConnection || createSyncConnection();
      if (startupSyncConnection.endpointUrl && startupSyncConnection.endpointUrl.includes('ahmedkhames.com/flowdesk-api')) {
        startupSyncConnection = createSyncConnection({
          ...startupSyncConnection,
          endpointUrl: 'https://flowdesk.ahmedkhames.com'
        });
        await FlowDeskRepository.saveSyncConnection(startupSyncConnection);
      }

      // --- Source of Truth for Companies: MySQL REST API ---
      let serverCompaniesRaw = null;
      try {
        const headers = {};
        if (startupSyncConnection?.accessToken) {
          headers['Authorization'] = `Bearer ${startupSyncConnection.accessToken}`;
        }
        // Direct same-origin relative URL fetch
        let response = await fetch('/api/companies', { headers }).catch(() => null);
        if (!response || !response.ok) {
          // Fallback if relative URL fails (e.g. local EXE runtime)
          const fallbackEndpoint = String(startupSyncConnection?.endpointUrl || defaultApiEndpoint || '').trim().replace(/\/+$/, '');
          if (fallbackEndpoint && !fallbackEndpoint.includes('ahmedkhames.com/flowdesk-api')) {
            const fallbackUrl = fallbackEndpoint.endsWith('/api') ? `${fallbackEndpoint}/companies` : `${fallbackEndpoint}/api/companies`;
            response = await fetch(fallbackUrl, { headers }).catch(() => null);
          }
        }
        if (response && response.ok) {
          const payload = await response.json().catch(() => null);
          serverCompaniesRaw = payload?.data !== undefined ? payload.data : payload;
        }
      } catch (error) {
        console.warn('Failed to fetch companies from REST API:', error);
      }

      console.log("SERVER COMPANIES RAW", serverCompaniesRaw);
      const normalizedCompanies = normalizeServerCompanies(serverCompaniesRaw);
      console.log("NORMALIZED COMPANIES", normalizedCompanies);

      let finalCompanies = null;
      if (serverCompaniesRaw && Object.keys(normalizedCompanies).length > 0) {
        finalCompanies = normalizedCompanies;
        console.log("COMPANIES STATE AFTER API", normalizedCompanies);
        try {
          localStorage.setItem("flowdesk_companies", JSON.stringify(normalizedCompanies));
          localStorage.setItem("pixelPlannerCompanies", JSON.stringify(normalizedCompanies));
        } catch (e) {}
        console.log("LOCAL COMPANIES", localStorage.getItem("flowdesk_companies"));
      } else {
        // Fallback to LocalStorage cache if server fetch fails or returns empty
        const cachedLocal = localStorage.getItem("flowdesk_companies") || localStorage.getItem("pixelPlannerCompanies");
        if (cachedLocal) {
          try {
            finalCompanies = JSON.parse(cachedLocal);
          } catch (e) {}
        }
        if (!finalCompanies || Object.keys(finalCompanies).length === 0) {
          finalCompanies = appData.companies;
        }
        console.log("LOCAL COMPANIES", localStorage.getItem("flowdesk_companies"));
      }

      // --- Fetch Clients from MySQL REST API and attach to companies ---
      try {
        const headers = {};
        if (startupSyncConnection?.accessToken) {
          headers['Authorization'] = `Bearer ${startupSyncConnection.accessToken}`;
        }
        let clientsResp = await fetch('/api/clients', { headers }).catch(() => null);
        if (clientsResp && clientsResp.ok) {
          const cPayload = await clientsResp.json().catch(() => null);
          const serverClients = cPayload?.data !== undefined ? cPayload.data : cPayload;
          if (Array.isArray(serverClients) && serverClients.length > 0 && finalCompanies) {
            serverClients.forEach(c => {
              const compId = String(c.companyId || c.company_id || '');
              if (finalCompanies[compId]) {
                const cName = c.name || c.client_name || '';
                if (cName && !finalCompanies[compId].clients.includes(cName)) {
                  finalCompanies[compId].clients.push(cName);
                }
              }
            });
          }
        }
      } catch (e) {}

      setCompanies(finalCompanies);

      // --- Source of Truth for Tasks: MySQL REST API ---
      let serverTasksRaw = null;
      try {
        const headers = {};
        if (startupSyncConnection?.accessToken) {
          headers['Authorization'] = `Bearer ${startupSyncConnection.accessToken}`;
        }
        let taskResponse = await fetch('/api/tasks', { headers }).catch(() => null);
        if (!taskResponse || !taskResponse.ok) {
          const fallbackEndpoint = String(startupSyncConnection?.endpointUrl || defaultApiEndpoint || '').trim().replace(/\/+$/, '');
          if (fallbackEndpoint && !fallbackEndpoint.includes('ahmedkhames.com/flowdesk-api')) {
            const fallbackUrl = fallbackEndpoint.endsWith('/api') ? `${fallbackEndpoint}/tasks` : `${fallbackEndpoint}/api/tasks`;
            taskResponse = await fetch(fallbackUrl, { headers }).catch(() => null);
          }
        }
        if (taskResponse && taskResponse.ok) {
          const payload = await taskResponse.json().catch(() => null);
          serverTasksRaw = payload?.data !== undefined ? payload.data : payload;
        }
      } catch (error) {
        console.warn('Failed to fetch tasks from REST API:', error);
      }

      console.log("SERVER TASKS RAW", serverTasksRaw);

      let finalTasks = [];
      if (Array.isArray(serverTasksRaw) && serverTasksRaw.length > 0) {
        const normalizedTasks = serverTasksRaw.map(task => {
          const compId = String(task.companyId || task.category || '');
          const clId = String(task.clientId || task.client || '');
          const clName = String(task.clientName || task.client || clId);
          return {
            ...task,
            id: task.id,
            category: compId,
            companyId: compId,
            client: clName,
            clientId: clId,
            clientName: clName,
            status: task.status || 'scheduled',
            priority: task.priority || 'medium',
            deadlineDate: task.deadlineDate || task.due_date || '',
            deadlineISO: task.deadlineISO || (task.deadlineDate ? `${task.deadlineDate}T17:00:00.000Z` : ''),
            comments: Array.isArray(task.comments) ? task.comments : [],
            subtasks: Array.isArray(task.subtasks) ? task.subtasks : [],
            images: Array.isArray(task.images) ? task.images : [],
            labels: Array.isArray(task.labels) ? task.labels : [],
          };
        });
        console.log("NORMALIZED TASKS", normalizedTasks);
        finalTasks = normalizedTasks;
        console.log("TASKS STATE AFTER API", finalTasks);
        try {
          localStorage.setItem("pixelPlannerTasks", JSON.stringify(finalTasks));
        } catch (e) {}
      } else {
        // Fallback to LocalStorage cache only if API fails or is empty
        finalTasks = appData.tasks || [];
        console.log("NORMALIZED TASKS", finalTasks);
        console.log("TASKS STATE AFTER API", finalTasks);
      }

      setTasks(finalTasks);
      setSoundSettings(appData.soundSettings);
      setGovernorate(appData.governorate);
      setUserName(appData.currentUser?.name || appData.userName);
      setUserPhoto(appData.currentUser?.photo ?? appData.userPhoto ?? null);
      setUserRole(appData.currentUser?.title || appData.userRole);
      const shouldRestoreSession = Boolean(
        appData.authSession?.rememberMe
        && appData.authSession?.isAuthenticated
        && (startupSyncConnection?.accessToken || appData.authSession?.provider === 'local-preview')
      );
      const startupAuthSession = createAuthSession({
        ...(appData.authSession || {}),
        email: appData.authSession?.email || appData.currentUser?.email || '',
        userId: appData.authSession?.userId || appData.currentUser?.id || '',
        isAuthenticated: shouldRestoreSession,
        rememberMe: Boolean(appData.authSession?.rememberMe)
      });

      setCurrentUser(appData.currentUser);
      setWorkspaceUsers(appData.workspaceUsers || []);
      setAuthSession(startupAuthSession);
      setLoginDraft({
        email: startupAuthSession.email || appData.currentUser?.email || '',
        password: '',
        name: appData.currentUser?.name || appData.userName || '',
        role: appData.currentUser?.accessRole || USER_ROLES.owner,
        rememberMe: appData.authSession?.rememberMe ?? true
      });
      setWorkspaceName(appData.workspaceName);
      setCurrentWorkspace(appData.currentWorkspace);
      setPrayerStatus(appData.prayerStatus);
      setTheme(appData.theme);
      setUiDensity(appData.uiDensity);
      setOperationMode(appData.operationMode);
      setSyncQueue(appData.syncQueue || []);
      setSyncConnection(startupSyncConnection);
      setSyncBackups(appData.syncBackups || []);
      setOnboardingData(prev => ({ ...prev, ...appData.onboardingData }));
      const hideOnboardingForAccount = shouldSkipOnboardingForAccount({
        user: appData.currentUser,
        authSession: startupAuthSession,
        syncConnection: appData.syncConnection
      });
      setShowOnboarding(hideOnboardingForAccount ? false : appData.showOnboarding);
      if (hideOnboardingForAccount) {
        await FlowDeskRepository.saveOnboardingDone(true);
      }

      setIsLoading(false);
    };

    initData();
  }, []);

  const handleResetLocalDataAndRehydrate = async () => {
    setIsLoading(true);
    console.log("=== STARTING FRONTEND RESET & SERVER REHYDRATE ===");

    // 1. Clear LocalStorage keys for data/cache (preserving auth session)
    const keysToRemove = [
      'flowdesk_companies',
      'pixelPlannerCompanies',
      'pixelPlannerTasks',
      'flowDeskWorkspaceUsers',
      'flowDeskSyncQueue',
      'flowDeskSyncBackups',
      'flowdesk_app_state'
    ];
    keysToRemove.forEach(k => {
      try { localStorage.removeItem(k); } catch (e) {}
    });

    try {
      const headers = {};
      if (syncConnection?.accessToken) {
        headers['Authorization'] = `Bearer ${syncConnection.accessToken}`;
      }

      // 2. Fetch Companies from MySQL
      const compRes = await fetch('/api/companies', { headers }).catch(() => null);
      let serverCompaniesRaw = null;
      if (compRes && compRes.ok) {
        const payload = await compRes.json().catch(() => null);
        serverCompaniesRaw = payload?.data !== undefined ? payload.data : payload;
      }
      console.log("SERVER COMPANIES RAW", serverCompaniesRaw);
      const normalizedCompanies = normalizeServerCompanies(serverCompaniesRaw);
      console.log("NORMALIZED COMPANIES", normalizedCompanies);

      // 3. Fetch Clients from MySQL and attach to companies
      const clientRes = await fetch('/api/clients', { headers }).catch(() => null);
      if (clientRes && clientRes.ok) {
        const cPayload = await clientRes.json().catch(() => null);
        const serverClients = cPayload?.data !== undefined ? cPayload.data : cPayload;
        if (Array.isArray(serverClients) && normalizedCompanies) {
          serverClients.forEach(c => {
            const compId = String(c.companyId || c.company_id || '');
            if (normalizedCompanies[compId]) {
              const cName = c.name || c.client_name || '';
              if (cName && !normalizedCompanies[compId].clients.includes(cName)) {
                normalizedCompanies[compId].clients.push(cName);
              }
            }
          });
        }
      }

      // 4. Fetch Tasks from MySQL
      const taskRes = await fetch('/api/tasks', { headers }).catch(() => null);
      let serverTasksRaw = null;
      if (taskRes && taskRes.ok) {
        const tPayload = await taskRes.json().catch(() => null);
        serverTasksRaw = tPayload?.data !== undefined ? tPayload.data : tPayload;
      }
      console.log("SERVER TASKS RAW", serverTasksRaw);

      let finalTasks = [];
      if (Array.isArray(serverTasksRaw)) {
        finalTasks = serverTasksRaw.map(task => {
          const compId = String(task.companyId || task.category || '');
          const clId = String(task.clientId || task.client || '');
          const clName = String(task.clientName || task.client || clId);
          return {
            ...task,
            id: task.id,
            category: compId,
            companyId: compId,
            client: clName,
            clientId: clId,
            clientName: clName,
            status: task.status || 'scheduled',
            priority: task.priority || 'medium',
            deadlineDate: task.deadlineDate || task.due_date || '',
            deadlineISO: task.deadlineISO || (task.deadlineDate ? `${task.deadlineDate}T17:00:00.000Z` : ''),
            comments: Array.isArray(task.comments) ? task.comments : [],
            subtasks: Array.isArray(task.subtasks) ? task.subtasks : [],
            images: Array.isArray(task.images) ? task.images : [],
            labels: Array.isArray(task.labels) ? task.labels : [],
          };
        });
      }
      console.log("NORMALIZED TASKS", finalTasks);
      console.log("TASKS STATE AFTER API", finalTasks);

      // 5. Fetch Users from MySQL
      const userRes = await fetch('/api/users', { headers }).catch(() => null);
      let serverUsers = [];
      if (userRes && userRes.ok) {
        const uPayload = await userRes.json().catch(() => null);
        serverUsers = uPayload?.data !== undefined ? uPayload.data : (Array.isArray(uPayload) ? uPayload : []);
      }

      // 6. Update React state ONLY from server
      setCompanies(normalizedCompanies);
      setTasks(finalTasks);
      if (serverUsers.length > 0) {
        setWorkspaceUsers(serverUsers);
      }

      // 7. Write new cache
      try {
        localStorage.setItem("flowdesk_companies", JSON.stringify(normalizedCompanies));
        localStorage.setItem("pixelPlannerCompanies", JSON.stringify(normalizedCompanies));
        localStorage.setItem("pixelPlannerTasks", JSON.stringify(finalTasks));
        if (serverUsers.length > 0) {
          localStorage.setItem("flowDeskWorkspaceUsers", JSON.stringify(serverUsers));
        }
      } catch (e) {}

      console.log("VISIBLE TASKS", finalTasks);
      setActiveAlert({
        title: "Rehydration Complete",
        message: `Successfully loaded ${Object.keys(normalizedCompanies).length} companies and ${finalTasks.length} tasks directly from MySQL.`,
        type: 'success'
      });
    } catch (err) {
      console.error("Rehydrate failed:", err);
      setActiveAlert({
        title: "Rehydrate Error",
        message: String(err.message || err),
        type: 'danger'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Auto-Save Effects ---
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveLanguage, value: lang, isLoading }); }, [lang, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveTasks, value: tasks, isLoading }); }, [tasks, isLoading]);
  useEffect(() => {
    if (isLoading) return;
    savePreference({ save: FlowDeskRepository.saveCompanies, value: companies, isLoading });
    try {
      if (companies && Object.keys(companies).length > 0) {
        localStorage.setItem("flowdesk_companies", JSON.stringify(companies));
        localStorage.setItem("pixelPlannerCompanies", JSON.stringify(companies));
      }
    } catch (e) {}
  }, [companies, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveHealthSettings, value: healthSettings, isLoading }); }, [healthSettings, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveSoundSettings, value: soundSettings, isLoading }); }, [soundSettings, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveGovernorate, value: governorate, isLoading }); }, [governorate, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveUiDensity, value: uiDensity, isLoading }); }, [uiDensity, isLoading]);
  useEffect(() => {
    if (isLoading) return;
    applyDensityToDocument({ documentRef: window.document, density: uiDensity });
  }, [uiDensity, isLoading]);
  useEffect(() => {
    if (!isLoading) {
      savePreference({ save: FlowDeskRepository.saveTheme, value: theme, isLoading });
      applyThemeToDocument({ documentRef: window.document, theme, matchMediaRef: window.matchMedia });
    }
  }, [theme, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveUserName, value: userName, isLoading }); }, [userName, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveUserPhoto, value: userPhoto, isLoading }); }, [userPhoto, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveUserRole, value: userRole, isLoading }); }, [userRole, isLoading]);
  useEffect(() => {
    if (isLoading) return;
    const nextName = currentUser.name || '';
    const nextPhoto = currentUser.photo || null;
    const nextRole = currentUser.title || '';
    if (userName !== nextName) setUserName(nextName);
    if (userPhoto !== nextPhoto) setUserPhoto(nextPhoto);
    if (userRole !== nextRole) setUserRole(nextRole);
  }, [currentUser.id, currentUser.email, currentUser.name, currentUser.photo, currentUser.title, isLoading]);
  useEffect(() => {
    if (isLoading) return;
    setCurrentUser(prev => {
      if (prev.name === userName && prev.photo === userPhoto && prev.title === userRole) return prev;
      return createCurrentUser({
        ...prev,
        name: userName,
        photo: userPhoto,
        title: userRole
      });
    });
  }, [userName, userPhoto, userRole, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveCurrentUser, value: currentUser, isLoading }); }, [currentUser, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveWorkspaceUsers, value: workspaceUsers, isLoading }); }, [workspaceUsers, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveAuthSession, value: authSession, isLoading }); }, [authSession, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveWorkspaceName, value: workspaceName, isLoading }); }, [workspaceName, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveOperationMode, value: operationMode, isLoading }); }, [operationMode, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveSyncConnection, value: syncConnection, isLoading }); }, [syncConnection, isLoading]);
  useEffect(() => {
    if (isLoading) return;
    setOperationMode(OPERATION_MODES.online);
    setSyncConnection(prev => {
      const rawUrl = prev.endpointUrl || defaultApiEndpoint;
      const cleanUrl = rawUrl.includes('ahmedkhames.com/flowdesk-api') ? 'https://flowdesk.ahmedkhames.com' : rawUrl;
      return createSyncConnection({
        ...prev,
        enabled: true,
        endpointUrl: cleanUrl,
        autoSyncEnabled: true,
        autoSyncIntervalSeconds: 30
      });
    });
  }, [isLoading, isHostedWebApp, isDesktopApp, defaultApiEndpoint]);
  useEffect(() => {
    if (isLoading) return;
    setCurrentWorkspace(prev => createWorkspace({
      ...prev,
      name: workspaceName,
      ownerUserId: currentUser.id,
      mode: operationMode
    }));
  }, [workspaceName, currentUser.id, operationMode, isLoading]);
  useEffect(() => { savePreference({ save: FlowDeskRepository.saveCurrentWorkspace, value: currentWorkspace, isLoading }); }, [currentWorkspace, isLoading]);
  useEffect(() => {
    savePrayerStatusForToday({ save: FlowDeskRepository.savePrayerStatus, prayerStatus, isLoading });
  }, [prayerStatus, isLoading]);

  const [filter, setFilter] = useState('all');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [attendanceLogsCompId, setAttendanceLogsCompId] = useState(null);
  const [boardView, setBoardView] = useState('scheduled');
  const [activeModal, setActiveModal] = useState(null);
  const [mobileSettingsTab, setMobileSettingsTab] = useState('profile');
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [isBrowserMinimized, setIsBrowserMinimized] = useState(false);
  const [browserPosition, setBrowserPosition] = useState({ x: 24, y: 24 });
  const [browserDragOffset, setBrowserDragOffset] = useState(null);
  const [workspaceInput, setWorkspaceInput] = useState('https://www.google.com/');
  const [browserTabs, setBrowserTabs] = useState([{ id: 'google', title: 'Google', url: 'https://www.google.com/', type: 'browser' }]);
  const [activeBrowserTabId, setActiveBrowserTabId] = useState('google');
  const workspaceWebviewRef = useRef(null);
  const browserWebviewRefs = useRef({});
  const lastWhatsAppUnreadRef = useRef(0);
  const autoSyncRunningRef = useRef(false);
  const immediateSyncTimerRef = useRef(null);
  const loginSyncUserRef = useRef('');
  const recentlyDeletedTaskIdsRef = useRef(new Set());
  const [browserNotifications, setBrowserNotifications] = useState([]);
  const [taskActivityNotifications, setTaskActivityNotifications] = useState([]);
  const [isNotificationPanelOpen, setIsNotificationPanelOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeTaskPopover, setActiveTaskPopover] = useState(null);
  const [datesPopoverMonth, setDatesPopoverMonth] = useState(new Date());
  const [datesDraftHasStartDate, setDatesDraftHasStartDate] = useState(false);
  const [datesDraftStartDate, setDatesDraftStartDate] = useState('');
  const [datesDraftHasDueDate, setDatesDraftHasDueDate] = useState(true);
  const [datesDraftDueDate, setDatesDraftDueDate] = useState('');
  const [datesDraftDueTime, setDatesDraftDueTime] = useState('17:00');
  const [datesDraftRecurring, setDatesDraftRecurring] = useState('never');
  const [datesDraftReminder, setDatesDraftReminder] = useState('1_day_before');

  const openDatesPopover = () => {
    if (activeTaskPopover === 'dates') {
      setActiveTaskPopover(null);
      return;
    }
    const todayStr = new Date().toISOString().split('T')[0];
    const initialDueDate = selectedTask?.deadlineDate || (selectedTask?.deadlineISO ? selectedTask.deadlineISO.split('T')[0] : todayStr);
    const initialDueTime = selectedTask?.deadlineTime || '17:00';
    const initialStartDate = selectedTask?.startDate || '';

    setDatesDraftHasStartDate(Boolean(selectedTask?.hasStartDate || initialStartDate));
    setDatesDraftStartDate(initialStartDate || todayStr);
    setDatesDraftHasDueDate(Boolean(selectedTask?.deadlineDate || selectedTask?.deadlineISO || selectedTask?.deadline));
    setDatesDraftDueDate(initialDueDate);
    setDatesDraftDueTime(initialDueTime);
    setDatesDraftRecurring(selectedTask?.recurring || 'never');
    setDatesDraftReminder(selectedTask?.reminder || '1_day_before');

    const baseDate = initialDueDate ? new Date(initialDueDate) : new Date();
    setDatesPopoverMonth(isNaN(baseDate.getTime()) ? new Date() : baseDate);
    setActiveTaskPopover('dates');
  };
  const [labelSearchQuery, setLabelSearchQuery] = useState('');
  const [newChecklistTitle, setNewChecklistTitle] = useState('Checklist');
  const [newSubtaskText, setNewSubtaskText] = useState('');
  const [isAddingCardInScheduled, setIsAddingCardInScheduled] = useState(false);
  const [quickCardTitle, setQuickCardTitle] = useState('');
  const [quickCardCompany, setQuickCardCompany] = useState('');
  const [quickCardClient, setQuickCardClient] = useState('');
  const [quickCardPriority, setQuickCardPriority] = useState('normal');
  const [settingsTab, setSettingsTab] = useState('company');
  const [settingsCompanyId, setSettingsCompanyId] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [nextPrayer, setNextPrayer] = useState(null);
  const [showHealthAlert, setShowHealthAlert] = useState(false);
  const [showOvertimeAlert, setShowOvertimeAlert] = useState(null);
  const [activeOvertime, setActiveOvertime] = useState({});

  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEYS.notifications);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.notifications, JSON.stringify(notifications));
    } catch (e) {
      console.error('Failed to save notifications:', e);
    }
  }, [notifications]);

  const handleMarkAllNotificationsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleClearReadNotifications = () => {
    setNotifications(prev => prev.filter(n => !n.read));
  };

  const handleClearAllNotifications = () => {
    setNotifications([]);
  };

  const handleOpenNotificationTask = (notif) => {
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    const foundTask = tasks.find(t => t.id === notif.taskId);
    if (foundTask) {
      setSelectedTask(foundTask);
      setIsNotificationPanelOpen(false);
    }
  };

  const renderNotificationContent = () => (
    <>
      {/* DRAWER HEADER */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800/80 pb-3.5 px-0.5 gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-black shadow-md shadow-blue-500/25 shrink-0">
            <Bell size={18} />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-black text-lg tracking-tight text-slate-900 dark:text-white">
              {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
            </span>
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="px-2.5 py-0.5 text-xs font-black bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30 rounded-full">
                {notifications.filter(n => !n.read).length} {lang === 'ar' ? 'جديد' : 'new'}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {notifications.some(n => n.read) && (
            <button
              type="button"
              onClick={handleClearReadNotifications}
              className="text-xs font-black text-rose-500 dark:text-rose-400 hover:bg-rose-500/15 border border-rose-500/30 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer flex items-center gap-1.5 whitespace-nowrap"
              title={lang === 'ar' ? 'حذف الإشعارات المقروءة' : 'Clear Read Notifications'}
            >
              <Trash2 size={13} />
              <span>{lang === 'ar' ? 'مسح المقروءة' : 'Clear Read'}</span>
            </button>
          )}

          {notifications.some(n => !n.read) && (
            <button
              type="button"
              onClick={handleMarkAllNotificationsRead}
              className="text-xs font-black text-blue-600 dark:text-blue-400 hover:bg-blue-500/15 border border-blue-500/30 px-2.5 py-1.5 rounded-xl transition-all cursor-pointer whitespace-nowrap"
            >
              {lang === 'ar' ? 'تحديد الكل كمقروء' : 'Mark all read'}
            </button>
          )}

          {/* CRISP CENTERED CLOSE X BUTTON */}
          <button
            type="button"
            onClick={() => setIsNotificationPanelOpen(false)}
            className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white bg-slate-100 dark:bg-slate-800/80 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-all cursor-pointer shrink-0"
            title={lang === 'ar' ? 'إغلاق' : 'Close'}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* NOTIFICATIONS LIST */}
      {notifications.length > 0 ? (
        <div className="space-y-3 flex-1 md:max-h-[500px] overflow-y-auto pr-1 pt-1 [::-webkit-scrollbar]:w-1.5 [::-webkit-scrollbar-thumb]:bg-slate-400 dark:[::-webkit-scrollbar-thumb]:bg-slate-600/70 [::-webkit-scrollbar-thumb]:rounded-full">
          {notifications.map((notif) => (
            <div
              key={notif.id}
              onClick={() => handleOpenNotificationTask(notif)}
              className={`p-4 rounded-2xl border transition-all cursor-pointer flex items-start gap-3.5 relative group shadow-sm hover:shadow-md ${
                notif.read
                  ? 'bg-slate-100/90 dark:bg-[#161a24] border-slate-200/90 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-[#1a202c]'
                  : 'bg-blue-50/90 dark:bg-[#131b2e] border-blue-300 dark:border-blue-500/40 text-slate-900 dark:text-white hover:bg-blue-100/80 dark:hover:bg-[#16223b]'
              }`}
            >
              {!notif.read && (
                <span className="w-3 h-3 rounded-full bg-blue-500 shrink-0 mt-1.5 shadow-md shadow-blue-500/60" />
              )}

              {/* Sender Avatar */}
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-black text-sm flex items-center justify-center shrink-0 overflow-hidden mt-0.5 border border-blue-400/30 shadow-sm">
                {notif.senderPhoto ? (
                  <img src={notif.senderPhoto} alt="Sender" className="w-full h-full object-cover" />
                ) : (
                  (notif.senderName || 'U')[0].toUpperCase()
                )}
              </div>

              {/* Body */}
              <div className="flex-1 min-w-0 space-y-1.5 text-right">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-black text-sm text-blue-600 dark:text-blue-400 truncate">
                    {notif.senderName}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 font-sans">
                      {formatCommentTimeOnly(notif.createdAt)}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNotifications(prev => prev.filter(n => n.id !== notif.id));
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-rose-500 transition-all rounded-lg hover:bg-rose-500/10"
                      title={lang === 'ar' ? 'حذف الإشعار' : 'Delete notification'}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {/* Message Text with Larger Font & Clean Arabic Line-Height */}
                <p className="text-sm font-extrabold text-slate-800 dark:text-slate-100 leading-relaxed" dir="auto">
                  {notif.type === 'task_created' ? (
                    <span className="text-emerald-600 dark:text-emerald-400 font-extrabold flex items-center gap-1.5">
                      🚀 {notif.message || (lang === 'ar' ? 'تم إنشاء المهمة بنجاح' : 'Task created successfully')}
                    </span>
                  ) : notif.type === 'status_changed' ? (
                    <span className="text-amber-600 dark:text-amber-400 font-extrabold flex items-center gap-1.5">
                      🔄 {notif.message || (lang === 'ar' ? 'تم نقل المهمة' : 'Task status moved')}
                    </span>
                  ) : notif.type === 'task_seen' ? (
                    <span className="text-emerald-600 dark:text-emerald-400 font-extrabold block" dir="auto">
                      {notif.message || (lang === 'ar' ? `تم مشاهدة المهمة بواسطة ${notif.senderName}` : `Task viewed by ${notif.senderName}`)}
                    </span>
                  ) : notif.type === 'comment' ? (
                    <span>
                      💬 {lang === 'ar' ? 'أضاف تعليقاً:' : 'Added a comment:'} "{notif.commentText || notif.message}"
                    </span>
                  ) : (
                    <span>
                      📣 {lang === 'ar' ? 'قام بذكرك (@Mention) في تعليق:' : 'Mentioned you (@Mention) in a comment:'} "{notif.commentText || notif.message}"
                    </span>
                  )}
                </p>

                {/* Task Title Tag */}
                {notif.taskTitle && (
                  <div className="inline-flex items-center gap-1.5 text-xs font-black text-slate-600 dark:text-slate-300 bg-slate-200/60 dark:bg-white/10 px-2.5 py-1 rounded-xl border border-slate-300/40 dark:border-white/10 max-w-full truncate">
                    <CheckSquare size={13} className="text-blue-500 dark:text-blue-400 shrink-0" />
                    <span className="truncate">{notif.taskTitle}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center text-slate-500 dark:text-slate-400 space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-blue-500/10 text-blue-500 dark:text-blue-400 flex items-center justify-center mx-auto border border-blue-500/20 shadow-inner">
            <Bell size={26} />
          </div>
          <div className="space-y-1">
            <p className="text-base font-black text-slate-900 dark:text-slate-100">
              {lang === 'ar' ? 'لا توجد إشعارات جديدة' : 'No new notifications'}
            </p>
            <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
              {lang === 'ar' ? 'ستظهر هنا الإشعارات والمنشن فور إرسالها.' : 'Notifications and mentions will appear here.'}
            </p>
          </div>
        </div>
      )}
    </>
  );

  const buildCurrentBackupPayload = () => createBackupPayload({
    tasks,
    companies,
    healthSettings,
    soundSettings,
    prayerStatus,
    userName,
    userPhoto,
    userRole,
    currentUser,
    workspaceUsers,
    authSession,
    syncConnection,
    syncQueue,
    workspaceName,
    currentWorkspace,
    governorate,
    theme,
    lang
  });

  const applyBackupPayload = async (data) => {
    if (data.tasks) setTasks(data.tasks);
    if (data.companies) setCompanies(data.companies);
    if (data.healthSettings) setHealthSettings(data.healthSettings);
    if (data.soundSettings) setSoundSettings(data.soundSettings);
    if (data.prayerStatus) setPrayerStatus(data.prayerStatus);
    if (data.userName !== undefined) setUserName(data.userName);
    if (data.userPhoto !== undefined) setUserPhoto(data.userPhoto);
    if (data.userRole !== undefined) setUserRole(data.userRole);
    if (data.currentUser !== undefined) setCurrentUser(createCurrentUser(data.currentUser));
    if (data.workspaceUsers !== undefined) setWorkspaceUsers(data.workspaceUsers.map(user => createWorkspaceUser(user)));
    if (data.authSession !== undefined) setAuthSession(createAuthSession(data.authSession));
    if (data.syncConnection !== undefined) setSyncConnection(createSyncConnection(data.syncConnection));
    if (data.syncQueue !== undefined) {
      await FlowDeskRepository.saveSyncQueue(data.syncQueue);
      setSyncQueue(data.syncQueue);
    }
    if (data.workspaceName !== undefined) setWorkspaceName(data.workspaceName);
    if (data.currentWorkspace !== undefined) setCurrentWorkspace(createWorkspace(data.currentWorkspace));
    if (data.governorate !== undefined) setGovernorate(data.governorate);
    if (data.theme !== undefined) setTheme(data.theme);
  };

  const createAutomaticSyncBackup = async ({ reason = 'manual-sync' } = {}) => {
    const backup = {
      id: `sync_backup_${Date.now()}`,
      reason,
      createdAt: new Date().toISOString(),
      taskCount: tasks.length,
      companyCount: Object.keys(companies || {}).length,
      queueCount: syncQueue.length,
      payload: buildCurrentBackupPayload()
    };
    const existingBackups = await FlowDeskRepository.loadSyncBackups();
    const nextBackups = [backup, ...existingBackups].slice(0, 5);
    await FlowDeskRepository.saveSyncBackups(nextBackups);
    setSyncBackups(nextBackups);
    return backup;
  };

  const refreshSyncQueue = async () => {
    const queue = await FlowDeskRepository.loadSyncQueue();
    setSyncQueue(queue);
    return queue;
  };

  const handleRetryFailedSyncOperations = async () => {
    if (!requirePermission('manageSettings')) return;
    const queue = await FlowDeskRepository.loadSyncQueue();
    const now = new Date().toISOString();
    const nextQueue = queue.map(operation => (
      operation.status === SYNC_OPERATION_STATUS.failed
        ? {
          ...operation,
          status: SYNC_OPERATION_STATUS.pending,
          lastError: null,
          updatedAt: now
        }
        : operation
    ));
    await FlowDeskRepository.saveSyncQueue(nextQueue);
    setSyncQueue(nextQueue);
    setSyncResult(null);
  };

  const handleClearSyncedSyncOperations = async () => {
    if (!requirePermission('manageSettings')) return;
    const nextQueue = await FlowDeskRepository.clearSyncedOperations();
    setSyncQueue(nextQueue);
  };

  const handleRestoreSyncBackup = async (backup) => {
    if (!requirePermission('backup')) return;
    if (!backup?.payload) return;
    await applyBackupPayload(backup.payload);
    setActiveAlert({
      title: t.success || 'Success',
      message: lang === 'ar' ? 'تمت استعادة نسخة الأمان بنجاح.' : 'Safety backup restored successfully.',
      type: 'success'
    });
  };

  const pullServerChanges = async ({ showSuccessAlert = false } = {}) => {
    const transport = createConfiguredSyncTransport({ connection: syncConnection });
    if (!transport?.fetchChanges) {
      return {
        ok: false,
        pulled: 0,
        revision: syncConnection.lastServerRevision || 0,
        error: lang === 'ar' ? 'فعل اتصال السيرفر واكتب الرابط أولا.' : 'Enable server connection and enter the endpoint first.'
      };
    }

    const result = await transport.fetchChanges(syncConnection.lastServerRevision || 0);
    if (!result.ok) {
      return {
        ok: false,
        pulled: 0,
        revision: syncConnection.lastServerRevision || 0,
        error: formatErrorMessage(result.error, 'Failed to fetch server changes.')
      };
    }

    const effectiveChanges = (result.changes || []).filter(change => {
      if (change.entity !== SYNC_ENTITIES.tasks || change.type === SYNC_OPERATION_TYPES.delete) return true;
      return !recentlyDeletedTaskIdsRef.current.has(String(change.entityId));
    });

    const incomingTaskMoves = effectiveChanges
      .filter(change => change.entity === SYNC_ENTITIES.tasks && change.type !== SYNC_OPERATION_TYPES.delete && change.payload?.status)
      .map(change => {
        const previousTask = tasks.find(task => String(task.id) === String(change.entityId));
        const nextTask = change.payload;
        if (!previousTask) {
          if (nextTask.createdBy && String(nextTask.createdBy) === String(currentUser.id)) return null;
          return {
            task: nextTask,
            fromStatus: null,
            toStatus: nextTask.status,
            actorName: nextTask.createdByName || nextTask.statusChangedByName || (lang === 'ar' ? 'مستخدم آخر' : 'Another user'),
            type: 'created'
          };
        }
        if (previousTask.status === nextTask.status) return null;
        if (nextTask.statusChangedBy && String(nextTask.statusChangedBy) === String(currentUser.id)) return null;
        return {
          task: nextTask,
          fromStatus: previousTask.status,
          toStatus: nextTask.status,
          actorName: nextTask.statusChangedByName || (lang === 'ar' ? 'مستخدم آخر' : 'Another user'),
          type: 'moved'
        };
      })
      .filter(Boolean);

    const nextState = applyServerChangesToState({
      changes: effectiveChanges,
      state: {
        tasks,
        companies,
        workspaceUsers,
        currentUser,
        currentWorkspace,
        soundSettings,
        healthSettings,
        governorate,
        theme,
        uiDensity,
        lang,
        prayerStatus
      }
    });

    setTasks(nextState.tasks);
    setCompanies(nextState.companies);
    setWorkspaceUsers(nextState.workspaceUsers);
    setCurrentUser(nextState.currentUser);
    setCurrentWorkspace(nextState.currentWorkspace);
    setSoundSettings(nextState.soundSettings);
    setHealthSettings(nextState.healthSettings);
    setGovernorate(nextState.governorate);
    setTheme(nextState.theme);
    setUiDensity(nextState.uiDensity);
    setLang('en');
    setPrayerStatus(nextState.prayerStatus);
    incomingTaskMoves.forEach(showTaskActivityNotification);
    setSyncConnection(prev => createSyncConnection({
      ...prev,
      lastServerRevision: result.revision,
      lastConnectedAt: new Date().toISOString()
    }));

    if (showSuccessAlert) {
      setActiveAlert({
        title: t.success || 'Success',
        message: lang === 'ar' ? `تم سحب ${effectiveChanges.length} تحديث من السيرفر.` : `${effectiveChanges.length} server changes pulled.`,
        type: 'success'
      });
    }

    return {
      ok: true,
      pulled: effectiveChanges.length,
      revision: result.revision,
      errors: []
    };
  };

  const loadServerSnapshot = async ({ endpointUrl, accessToken, fallbackUser = null } = {}) => {
    const endpoint = String(endpointUrl || syncConnection.endpointUrl || defaultApiEndpoint || '').trim().replace(/\/+$/, '');
    const token = accessToken || syncConnection.accessToken;
    if (!endpoint || !token) return null;
    const headers = { Authorization: `Bearer ${token}` };
    const readJson = async (url, fallbackMessage) => {
      const response = await fetch(url, { headers });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        throw new Error(formatErrorMessage(payload?.error ?? payload?.message, fallbackMessage));
      }
      return payload.data;
    };

    const apiCompUrl = endpoint.endsWith('/api') ? `${endpoint}/companies` : `${endpoint}/api/companies`;
    const apiTasksUrl = endpoint.endsWith('/api') ? `${endpoint}/tasks` : `${endpoint}/api/tasks`;

    const [meData, companiesData, tasksData, healthData] = await Promise.all([
      readJson(`${endpoint}/auth/me`, 'Failed to load account.').catch(() => null),
      readJson(apiCompUrl, 'Failed to load companies.').catch(() => null),
      readJson(apiTasksUrl, 'Failed to load tasks.').catch(() => null),
      fetch(`${endpoint}/health`).then(response => response.json()).catch(() => null)
    ]);

    const nextUser = createCurrentUser({
      ...(fallbackUser || {}),
      ...(meData?.user || {})
    });
    const nextCompanies = normalizeServerCompanies(companiesData);
    const nextTasks = (Array.isArray(tasksData) ? tasksData : [])
      .filter(task => !recentlyDeletedTaskIdsRef.current.has(String(task.id)));

    setCurrentUser(nextUser);
    setUserName(nextUser.name || '');
    setUserPhoto(nextUser.photo || null);
    setUserRole(nextUser.title || '');
    if (nextCompanies && Object.keys(nextCompanies).length > 0) {
      setCompanies(nextCompanies);
      try {
        localStorage.setItem("flowdesk_companies", JSON.stringify(nextCompanies));
        localStorage.setItem("pixelPlannerCompanies", JSON.stringify(nextCompanies));
      } catch (e) {}
    }
    setTasks(nextTasks);
    if (shouldSkipOnboardingForAccount({ user: nextUser, authSession, syncConnection: { endpointUrl: endpoint, accessToken: token } })) {
      setShowOnboarding(false);
      await FlowDeskRepository.saveOnboardingDone(true);
    }
    if (nextUser.accessRole === USER_ROLES.owner) {
      try {
        const usersData = await readJson(`${endpoint}/users`, 'Failed to load users.');
        if (Array.isArray(usersData)) setWorkspaceUsers(usersData.map(user => createWorkspaceUser(user)));
      } catch {
        // Owner users can still work even if the users panel fails to refresh.
      }
    }
    const serverRevision = Number(healthData?.data?.revision ?? syncConnection.lastServerRevision ?? 0);
    setSyncConnection(prev => createSyncConnection({
      ...prev,
      endpointUrl: endpoint,
      accessToken: token,
      lastServerRevision: Number.isFinite(serverRevision) ? serverRevision : 0,
      lastConnectedAt: new Date().toISOString()
    }));
    return { user: nextUser, companies: nextCompanies, tasks: nextTasks, revision: serverRevision };
  };

  const runFullSyncCycle = async ({ silent = false, skipBackup = false } = {}) => {
    if (!skipBackup) {
      await createAutomaticSyncBackup({ reason: silent ? 'auto-sync' : 'manual-sync' });
    }
    const transport = createConfiguredSyncTransport({ connection: syncConnection });
    const pushResult = await runSyncCycle({
      repository: FlowDeskRepository,
      maxOperations: 100,
      ...(transport ? { transport } : {})
    });
    await refreshSyncQueue();
    const pullResult = transport?.fetchChanges
      ? await pullServerChanges()
      : { ok: true, pulled: 0, revision: syncConnection.lastServerRevision || 0, errors: [] };
    const combinedResult = {
      ...pushResult,
      pulled: pullResult.pulled || 0,
      revision: pullResult.revision,
      failed: pushResult.failed + (pullResult.ok ? 0 : 1),
      errors: [
        ...(pushResult.errors || []),
        ...(pullResult.ok ? [] : [{ message: pullResult.error }])
      ],
      silent
    };
    setSyncResult(combinedResult);
    return combinedResult;
  };

  function scheduleImmediateSync() {
    if (operationMode !== OPERATION_MODES.online) return;
    if (!syncConnection.enabled || !syncConnection.endpointUrl || !syncConnection.accessToken) return;
    if (immediateSyncTimerRef.current) window.clearTimeout(immediateSyncTimerRef.current);
    immediateSyncTimerRef.current = window.setTimeout(async () => {
      if (autoSyncRunningRef.current || isSyncRunning) {
        scheduleImmediateSync();
        return;
      }
      autoSyncRunningRef.current = true;
      try {
        await runFullSyncCycle({ silent: true, skipBackup: true });
      } catch (error) {
        setSyncResult({
          failed: 1,
          errors: [{ message: formatErrorMessage(error, 'Sync failed.') }],
          silent: true
        });
      } finally {
        autoSyncRunningRef.current = false;
        immediateSyncTimerRef.current = null;
      }
    }, 800);
  }

  const handleRunSyncPreview = async () => {
    if (!requirePermission('manageSettings')) return;
    setIsSyncRunning(true);
    try {
      await runFullSyncCycle();
    } catch (error) {
      setSyncResult({
        failed: 1,
        errors: [{ message: formatErrorMessage(error, 'Sync failed.') }]
      });
    } finally {
      setIsSyncRunning(false);
    }
  };

  const handlePullServerChanges = async () => {
    if (!requirePermission('manageSettings')) return;
    setIsSyncRunning(true);
    try {
      const result = await pullServerChanges({ showSuccessAlert: true });
      if (!result.ok) {
        setSyncResult({
          failed: 1,
          errors: [{ message: formatErrorMessage(result.error, 'Failed to fetch server changes.') }]
        });
        setActiveAlert({
          title: t.error || 'Error',
          message: formatErrorMessage(result.error, 'Failed to fetch server changes.'),
          type: 'warning'
        });
        return;
      }
      setSyncResult({
        processed: 0,
        pulled: result.pulled,
        failed: 0,
        skipped: 0,
        errors: [],
        revision: result.revision
      });
    } catch (error) {
      setSyncResult({
        failed: 1,
        errors: [{ message: formatErrorMessage(error, 'Sync failed.') }]
      });
    } finally {
      setIsSyncRunning(false);
    }
  };

  useEffect(() => {
    if (isLoading) return undefined;
    if (operationMode !== OPERATION_MODES.online) return undefined;
    if (!syncConnection.enabled || !syncConnection.endpointUrl || !syncConnection.autoSyncEnabled) return undefined;

    const intervalMs = Math.max(30, Number(syncConnection.autoSyncIntervalSeconds || 30)) * 1000;
    const runAutoSync = async () => {
      if (autoSyncRunningRef.current || isSyncRunning) return;
      autoSyncRunningRef.current = true;
      try {
        await runFullSyncCycle({ silent: true });
      } catch (error) {
        setSyncResult({
          failed: 1,
          errors: [{ message: formatErrorMessage(error, 'Sync failed.') }],
          silent: true
        });
      } finally {
        autoSyncRunningRef.current = false;
      }
    };

    const timerId = window.setInterval(runAutoSync, intervalMs);
    return () => window.clearInterval(timerId);
  }, [
    isLoading,
    operationMode,
    syncConnection.enabled,
    syncConnection.endpointUrl,
    syncConnection.autoSyncEnabled,
    syncConnection.autoSyncIntervalSeconds,
    syncConnection.lastServerRevision,
    tasks,
    companies,
    workspaceUsers,
    currentUser,
    currentWorkspace,
    soundSettings,
    healthSettings,
    governorate,
    theme,
    uiDensity,
    lang,
    prayerStatus,
    isSyncRunning
  ]);

  useEffect(() => {
    if (isLoading) return undefined;
    if (operationMode !== OPERATION_MODES.online) return undefined;
    if (!syncConnection.enabled || !syncConnection.endpointUrl || !syncConnection.accessToken) return undefined;

    const runFastPull = async () => {
      if (autoSyncRunningRef.current || isSyncRunning) return;
      autoSyncRunningRef.current = true;
      try {
        await pullServerChanges();
      } catch {
        // Keep the background listener quiet; the connection card still reflects the last sync state.
      } finally {
        autoSyncRunningRef.current = false;
      }
    };

    const timerId = window.setInterval(runFastPull, 5000);
    return () => window.clearInterval(timerId);
  }, [
    isLoading,
    operationMode,
    syncConnection.enabled,
    syncConnection.endpointUrl,
    syncConnection.accessToken,
    syncConnection.lastServerRevision,
    tasks,
    companies,
    workspaceUsers,
    currentUser,
    currentWorkspace,
    soundSettings,
    healthSettings,
    governorate,
    theme,
    uiDensity,
    lang,
    prayerStatus,
    isSyncRunning
  ]);

  useEffect(() => () => {
    if (immediateSyncTimerRef.current) {
      window.clearTimeout(immediateSyncTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (!authSession.isAuthenticated || !syncConnection.enabled || !syncConnection.endpointUrl || !syncConnection.accessToken) return;
    const syncKey = `${authSession.userId || authSession.email}:${syncConnection.accessToken}`;
    if (loginSyncUserRef.current === syncKey) return;
    loginSyncUserRef.current = syncKey;
    loadServerSnapshot({
      endpointUrl: syncConnection.endpointUrl,
      accessToken: syncConnection.accessToken,
      fallbackUser: currentUser
    }).then(() => runFullSyncCycle({ silent: true })).catch(error => {
      setSyncResult({
        failed: 1,
        errors: [{ message: formatErrorMessage(error, 'Sync failed.') }],
        silent: true
      });
    });
  }, [isLoading, authSession.isAuthenticated, authSession.userId, authSession.email, syncConnection.enabled, syncConnection.endpointUrl, syncConnection.accessToken]);

  const [loginError, setLoginError] = useState('');

  const handleLocalPreviewLogin = async () => {
    setLoginError('');
    const email = loginDraft.email.trim().toLowerCase();
    const password = loginDraft.password.trim();

    if (!email || !password) {
      setLoginError(lang === 'ar' ? 'يرجى إدخال البريد الإلكتروني وكلمة المرور.' : 'Please enter email and password.');
      return;
    }

    // 1. Ensure master owner admin exists
    let currentUsers = [...workspaceUsers];
    let foundMaster = currentUsers.find(u => u.email === 'ahmed@flowdesk.com' || u.id === 'admin_owner');

    if (!foundMaster) {
      foundMaster = createWorkspaceUser({
        id: 'admin_owner',
        name: 'أحمد خميس',
        email: 'ahmed@flowdesk.com',
        password: 'Ahmedkhames@1',
        accessRole: USER_ROLES.owner,
        companyIds: ['all']
      });
      currentUsers = [foundMaster, ...currentUsers];
      setWorkspaceUsers(currentUsers);
      FlowDeskRepository.saveWorkspaceUsers(currentUsers);
    } else if (foundMaster.email === 'admin@flowdesk.com' || (foundMaster.id === 'admin_owner' && foundMaster.email !== 'ahmed@flowdesk.com')) {
      const updatedUsers = currentUsers.map(u => u.id === foundMaster.id || u.email === 'admin@flowdesk.com' ? {
        ...u,
        email: 'ahmed@flowdesk.com',
        password: 'Ahmedkhames@1',
        name: u.name || 'أحمد خميس',
        accessRole: USER_ROLES.owner,
        companyIds: ['all']
      } : u);
      currentUsers = updatedUsers;
      setWorkspaceUsers(currentUsers);
      FlowDeskRepository.saveWorkspaceUsers(currentUsers);
    }

    // 2. Search for user by email or name
    let foundUser = currentUsers.find(u => 
      String(u.email || '').toLowerCase() === email || 
      String(u.name || '').toLowerCase() === email
    );

    if (foundUser) {
      const isMasterAdmin = foundUser.email === 'ahmed@flowdesk.com' || foundUser.id === 'admin_owner';
      
      // Password validation (allow Ahmedkhames@1 and Ahmedkhames1 for master owner)
      if (!isMasterAdmin && foundUser.password && foundUser.password !== password) {
        setLoginError(lang === 'ar' ? 'كلمة المرور غير صحيحة.' : 'Incorrect password.');
        return;
      }

      if (isMasterAdmin && password) {
        foundUser.password = password;
      }

      const nextUser = createCurrentUser({
        ...foundUser,
        email: foundUser.email || email,
        name: foundUser.name || 'أحمد خميس',
        accessRole: foundUser.accessRole || USER_ROLES.owner
      });

      setCurrentUser(nextUser);
      setUserName(nextUser.name);
      setUserRole(nextUser.title || '');
      setUserPhoto(nextUser.photo || null);
      FlowDeskRepository.saveUserRole(nextUser.title || '');
      FlowDeskRepository.saveUserPhoto(nextUser.photo || null);

      const nextSession = createAuthSession({
        isAuthenticated: true,
        email: nextUser.email,
        userId: nextUser.id,
        rememberMe: Boolean(loginDraft.rememberMe)
      });

      setAuthSession(nextSession);
      FlowDeskRepository.saveAuthSession(nextSession);
      FlowDeskRepository.saveCurrentUser(nextUser);
      setLoginDraft(prev => ({ ...prev, password: '' }));
      return;
    }

    // 3. Auto-register new visitor email if not found
    const newUser = createWorkspaceUser({
      id: `user_${Date.now()}`,
      name: email.split('@')[0] || 'User',
      email: email,
      password: password,
      accessRole: USER_ROLES.owner,
      companyIds: ['all']
    });

    const updatedUsersList = [...currentUsers, newUser];
    setWorkspaceUsers(updatedUsersList);
    FlowDeskRepository.saveWorkspaceUsers(updatedUsersList);

    const nextUser = createCurrentUser({
      ...newUser
    });

    setCurrentUser(nextUser);
    setUserName(nextUser.name);
    setUserRole(nextUser.title || '');
    setUserPhoto(nextUser.photo || null);
    FlowDeskRepository.saveUserRole(nextUser.title || '');
    FlowDeskRepository.saveUserPhoto(nextUser.photo || null);

    const nextSession = createAuthSession({
      isAuthenticated: true,
      email: nextUser.email,
      userId: nextUser.id,
      rememberMe: Boolean(loginDraft.rememberMe)
    });

    setAuthSession(nextSession);
    FlowDeskRepository.saveAuthSession(nextSession);
    FlowDeskRepository.saveCurrentUser(nextUser);
    setLoginDraft(prev => ({ ...prev, password: '' }));
  };

  const handleLocalPreviewLogout = () => {
    const nextSession = createAuthSession({ isAuthenticated: false, userId: currentUser.id, email: currentUser.email });
    setAuthSession(nextSession);
    FlowDeskRepository.saveAuthSession(nextSession);
    setLoginDraft(prev => ({ ...prev, password: '' }));
    setActiveAlert({
      title: t.success || 'Success',
      message: lang === 'ar' ? 'تم تسجيل الخروج بنجاح.' : 'Logged out successfully.',
      type: 'success'
    });
  };

  const saveCurrentUserProfile = async () => {
    const profileUpdates = {
      name: userName.trim(),
      title: userRole.trim(),
      photo: userPhoto || null
    };
    let savedUser = createCurrentUser({ ...currentUser, ...profileUpdates });

    if (syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken && currentUser.id) {
      try {
        const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
        const response = await fetch(`${endpointUrl}/users/${encodeURIComponent(currentUser.id)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${syncConnection.accessToken}`
          },
          body: JSON.stringify(profileUpdates)
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to save profile.'));
        savedUser = createCurrentUser({ ...savedUser, ...(payload.data || {}) });
      } catch (error) {
        setActiveAlert({
          title: t.error || 'Error',
          message: lang === 'ar'
            ? `تعذر حفظ بيانات الحساب: ${formatErrorMessage(error, 'Failed to save profile.')}`
            : `Could not save account profile: ${formatErrorMessage(error, 'Failed to save profile.')}`,
          type: 'danger'
        });
        return;
      }
    }

    setCurrentUser(savedUser);
    setWorkspaceUsers(prev => prev.map(user => (
      user.id === savedUser.id ? createWorkspaceUser({ ...user, ...profileUpdates, id: user.id }) : user
    )));
    FlowDeskRepository.saveUserName(profileUpdates.name);
    FlowDeskRepository.saveUserPhoto(profileUpdates.photo);
    FlowDeskRepository.saveUserRole(profileUpdates.title);
    FlowDeskRepository.saveCurrentUser(savedUser);
    FlowDeskRepository.saveWorkspaceName(workspaceName);
    FlowDeskRepository.saveCurrentWorkspace(currentWorkspace);
    FlowDeskRepository.saveGovernorate(governorate);
    FlowDeskRepository.saveLanguage(lang);
    setActiveAlert({ title: t.success || 'Success', message: t.savedSuccess, type: 'success' });
  };

  const getTaskStatusLabel = (status) => ({
    scheduled: t.scheduled,
    in_progress: t.workingNow,
    review: t.review,
    revision: t.revision,
    done: t.done,
    overdue: t.overdue,
    urgent: t.urgentTasks
  }[status] || status);

  const showTaskActivityNotification = ({ task, fromStatus, toStatus, actorName, type = 'moved' }) => {
    if (!task || !toStatus || (type !== 'created' && fromStatus === toStatus)) return;
    const companyName = companies[task.category]?.name || '';
    const title = type === 'created'
      ? (lang === 'ar' ? 'تم إنشاء المهمة بنجاح 🚀' : 'Task Created Successfully 🚀')
      : (lang === 'ar' ? 'تم نقل المهمة 🔄' : 'Task Status Moved 🔄');
    const message = type === 'created'
      ? (lang === 'ar'
        ? `أضاف "${task.title}" بنجاح في ${getTaskStatusLabel(toStatus)}${companyName ? ` - ${companyName}` : ''}`
        : `Added "${task.title}" in ${getTaskStatusLabel(toStatus)}${companyName ? ` - ${companyName}` : ''}`)
      : (lang === 'ar'
        ? `نقل "${task.title}" من ${getTaskStatusLabel(fromStatus)} إلى ${getTaskStatusLabel(toStatus)}${companyName ? ` - ${companyName}` : ''}`
        : `Moved "${task.title}" from ${getTaskStatusLabel(fromStatus)} to ${getTaskStatusLabel(toStatus)}${companyName ? ` - ${companyName}` : ''}`);

    const newNotifItem = {
      id: 'notif_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
      type: type === 'created' ? 'task_created' : 'status_changed',
      title: title,
      message: message,
      commentText: message,
      taskTitle: task.title,
      taskId: task.id,
      senderName: actorName || userName || currentUser?.name || (lang === 'ar' ? 'مستخدم' : 'User'),
      senderPhoto: userPhoto || currentUser?.photo || null,
      createdAt: new Date().toISOString(),
      read: false
    };

    setNotifications(prev => [newNotifItem, ...prev]);

    const notificationKey = [
      task.id,
      type,
      fromStatus || 'new',
      toStatus,
      task.statusChangedAt || task.updatedAt || ''
    ].join(':');
    const id = `task_activity_${notificationKey}`.replace(/[^a-zA-Z0-9_-]/g, '_');

    setTaskActivityNotifications(prev => {
      if (prev.some(item => item.key === notificationKey || item.id === id)) return prev;
      return [{
        id,
        key: notificationKey,
        title,
        message,
        taskId: task.id,
        type,
        fromStatus,
        toStatus,
        actorName,
        companyName,
        read: false,
        createdAt: new Date().toISOString()
      }, ...prev].slice(0, 30);
    });

    try {
      if ('Notification' in window) {
        const createNotification = () => new Notification(title, { body: message, icon: brandAppIcon });
        if (Notification.permission === 'granted') {
          createNotification();
        } else if (Notification.permission === 'default') {
          Notification.requestPermission().then(permission => {
            if (permission === 'granted') createNotification();
          }).catch(() => {});
        }
      }
    } catch {
      // In-app notifications still work if the OS/browser blocks system notifications.
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      [USER_ROLES.owner]: 'مالك',
      [USER_ROLES.manager]: 'Account Manager',
      [USER_ROLES.designer]: 'Designer',
      [USER_ROLES.client]: 'عميل'
    };
    return labels[role] || role;
  };

  const fetchWorkspaceUsersFromServer = async () => {
    if (!syncConnection.enabled || !syncConnection.endpointUrl || !syncConnection.accessToken) return;
    try {
      const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
      const response = await fetch(`${endpointUrl}/users`, {
        headers: { Authorization: `Bearer ${syncConnection.accessToken}` }
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to load users.'));
      setWorkspaceUsers((payload.data || []).map(user => createWorkspaceUser(user)));
    } catch (error) {
      setActiveAlert({
        title: t.error || 'Error',
        message: lang === 'ar'
          ? `تعذر Download المستخدمين: ${formatErrorMessage(error, 'Failed to load users.')}`
          : `Could not load users: ${formatErrorMessage(error, 'Failed to load users.')}`,
        type: 'warning'
      });
    }
  };

  const handleAddWorkspaceUser = async () => {
    if (!requirePermission('manageSettings')) return;
    const name = workspaceUserDraft.name.trim();
    const email = workspaceUserDraft.email.trim();
    const password = workspaceUserDraft.password.trim();
    if (!name || !email || !password) {
      setActiveAlert({
        title: t.error || 'Error',
        message: lang === 'ar' ? 'اكتب اسم المستخدم والبريد الإلكتروني وكلمة المرور.' : 'Enter user name, email, and password.',
        type: 'warning'
      });
      return;
    }

    // Auto-assign: owner gets all companies, non-owner with no selection gets all companies too
    const isOwnerRole = workspaceUserDraft.accessRole === USER_ROLES.owner;
    const allCompanyIds = Object.keys(companies);
    const resolvedCompanyIds = isOwnerRole || workspaceUserDraft.companyIds.length === 0
      ? allCompanyIds
      : workspaceUserDraft.companyIds;
    const draftToCreate = { ...workspaceUserDraft, name, email, companyIds: resolvedCompanyIds };


    let user = createWorkspaceUser(draftToCreate);
    if (syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken) {
      try {
        const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
        const response = await fetch(`${endpointUrl}/users`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${syncConnection.accessToken}`
          },
          body: JSON.stringify({ ...draftToCreate, password })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to create user.'));
        user = createWorkspaceUser(payload.data);
      } catch (error) {
        setActiveAlert({
          title: t.error || 'Error',
          message: lang === 'ar'
            ? `تعذر إنشاء المستخدم على السيرفر: ${formatErrorMessage(error, 'Failed to create user.')}`
            : `Could not create server user: ${formatErrorMessage(error, 'Failed to create user.')}`,
          type: 'danger'
        });
        return;
      }
    }
    setWorkspaceUsers(prev => [user, ...prev]);
    if (!syncConnection.enabled || !syncConnection.endpointUrl) queueWorkspaceUserSync(user);
    setWorkspaceUserDraft({ name: '', email: '', password: '', title: '', accessRole: USER_ROLES.designer, companyIds: [], settingsTabs: ['profile'], clientAccess: {} });
    setActiveAlert({ title: t.success || 'Success', message: lang === 'ar' ? `✅ تم إنشاء حساب ${draftToCreate.name} بنجاح!` : `✅ Account for ${draftToCreate.name} created successfully!`, type: 'success' });

  };

  const updateWorkspaceUserAccess = async (userId, updates) => {
    if (!requirePermission('manageSettings')) return false;
    if (syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken) {
      try {
        const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
        const response = await fetch(`${endpointUrl}/users/${encodeURIComponent(userId)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${syncConnection.accessToken}`
          },
          body: JSON.stringify(updates)
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to update user access.'));
      } catch (error) {
        setActiveAlert({
          title: t.error || 'Error',
          message: lang === 'ar'
            ? `تعذر تحديث صلاحيات المستخدم: ${formatErrorMessage(error, 'Failed to update user access.')}`
            : `Could not update user access: ${formatErrorMessage(error, 'Failed to update user access.')}`,
          type: 'danger'
        });
        return false;
      }
    }
    setWorkspaceUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      const updatedUser = createWorkspaceUser({ ...user, ...updates, id: user.id });
      if (!syncConnection.enabled || !syncConnection.endpointUrl) queueWorkspaceUserSync(updatedUser);
      return updatedUser;
    }));
    if (currentUser.id === userId) {
      setCurrentUser(prev => createCurrentUser({ ...prev, ...updates }));
    }
    return true;
  };

  const updateWorkspaceUserRole = async (userId, accessRole) => {
    if (!requirePermission('manageSettings')) return;
    if (syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken) {
      try {
        const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
        const response = await fetch(`${endpointUrl}/users/${encodeURIComponent(userId)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${syncConnection.accessToken}`
          },
          body: JSON.stringify({ accessRole })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to update user.'));
      } catch (error) {
        setActiveAlert({
          title: t.error || 'Error',
          message: lang === 'ar'
            ? `تعذر تحديث الصلاحية: ${formatErrorMessage(error, 'Failed to update user.')}`
            : `Could not update role: ${formatErrorMessage(error, 'Failed to update user.')}`,
          type: 'danger'
        });
        return;
      }
    }
    setWorkspaceUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      const updatedUser = { ...user, accessRole };
      if (!syncConnection.enabled || !syncConnection.endpointUrl) queueWorkspaceUserSync(updatedUser);
      return updatedUser;
    }));
    if (currentUser.id === userId) {
      setCurrentUser(prev => createCurrentUser({ ...prev, accessRole }));
    }
  };

  const updateWorkspaceUserCompanies = async (userId, companyIds) => {
    if (!requirePermission('manageSettings')) return;
    const nextCompanyIds = Array.isArray(companyIds) ? companyIds.map(String) : [];
    if (syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken) {
      try {
        const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
        const response = await fetch(`${endpointUrl}/users/${encodeURIComponent(userId)}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${syncConnection.accessToken}`
          },
          body: JSON.stringify({ companyIds: nextCompanyIds })
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to update user companies.'));
      } catch (error) {
        setActiveAlert({
          title: t.error || 'Error',
          message: lang === 'ar'
            ? `تعذر تحديث شركات المستخدم: ${formatErrorMessage(error, 'Failed to update user companies.')}`
            : `Could not update user companies: ${formatErrorMessage(error, 'Failed to update user companies.')}`,
          type: 'danger'
        });
        return;
      }
    }
    setWorkspaceUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      const updatedUser = { ...user, companyIds: nextCompanyIds };
      if (!syncConnection.enabled || !syncConnection.endpointUrl) queueWorkspaceUserSync(updatedUser);
      return updatedUser;
    }));
    if (currentUser.id === userId) {
      setCurrentUser(prev => createCurrentUser({ ...prev, companyIds: nextCompanyIds }));
    }
  };

  const removeWorkspaceUser = async (userId) => {
    if (!requirePermission('manageSettings')) return;
    if (currentUser.id === userId) {
      setActiveAlert({
        title: t.error || 'Error',
        message: lang === 'ar' ? 'لا يمكن حذف المستخدم الحالي من التجربة.' : 'You cannot remove the current preview user.',
        type: 'warning'
      });
      return;
    }
    if (syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken) {
      try {
        const endpointUrl = syncConnection.endpointUrl.trim().replace(/\/+$/, '');
        const response = await fetch(`${endpointUrl}/users/${encodeURIComponent(userId)}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${syncConnection.accessToken}` }
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.ok) throw new Error(formatErrorMessage(payload?.error ?? payload?.message, 'Failed to disable user.'));
      } catch (error) {
        setActiveAlert({
          title: t.error || 'Error',
          message: lang === 'ar'
            ? `تعذر تعطيل المستخدم: ${formatErrorMessage(error, 'Failed to disable user.')}`
            : `Could not disable user: ${formatErrorMessage(error, 'Failed to disable user.')}`,
          type: 'danger'
        });
        return;
      }
    }
    const removedUser = workspaceUsers.find(user => user.id === userId);
    setWorkspaceUsers(prev => prev.filter(user => user.id !== userId));
    if (!syncConnection.enabled || !syncConnection.endpointUrl) {
      queueWorkspaceUserSync(removedUser || { id: userId }, SYNC_OPERATION_TYPES.delete);
    }
  };

  useEffect(() => {
    if (activeModal === 'settings' && settingsTab === 'sync') {
      refreshSyncQueue();
    }
  }, [activeModal, settingsTab]);

  useEffect(() => {
    if (activeModal === 'settings' && settingsTab === 'users' && isOwnerUser && syncConnection.enabled && syncConnection.endpointUrl && syncConnection.accessToken) {
      fetchWorkspaceUsersFromServer();
    }
  }, [activeModal, settingsTab, isOwnerUser, syncConnection.enabled, syncConnection.endpointUrl, syncConnection.accessToken]);

  useEffect(() => {
    if (filter !== 'all' && !canAccessCompany(filter)) {
      setFilter('all');
    }
  }, [filter, currentUser.accessRole, JSON.stringify(userCompanyIds), Object.keys(companies).join('|')]);

  useEffect(() => {
    if (settingsCompanyId && canAccessCompany(settingsCompanyId)) return;
    const fallbackCompanyId = visibleCompanyKeys[0] || Object.keys(companies)[0];
    if (fallbackCompanyId) setSettingsCompanyId(fallbackCompanyId);
  }, [settingsCompanyId, currentUser.accessRole, JSON.stringify(userCompanyIds), Object.keys(companies).join('|')]);

  useEffect(() => {
    if (activeModal !== 'settings' || canAccessSettingsTab(settingsTab)) return;
    const fallbackTab = ['profile', 'company', 'clients', 'managers', 'hours', 'appearance', 'sound', 'health', 'about'].find(tabId => canAccessSettingsTab(tabId));
    if (fallbackTab) setSettingsTab(fallbackTab);
    else setActiveModal(null);
  }, [activeModal, settingsTab, currentUser.accessRole, JSON.stringify(currentUser.settingsTabs || [])]);

  useEffect(() => {
    if (!browserDragOffset) return;

    const handleMove = (e) => {
      const panelWidth = Math.min(980, Math.max(620, window.innerWidth * 0.52));
      const panelHeight = Math.min(820, window.innerHeight - 48);
      const maxX = Math.max(16, window.innerWidth - panelWidth - 16);
      const maxY = Math.max(16, window.innerHeight - panelHeight - 16);
      setBrowserPosition({
        x: Math.min(Math.max(16, e.clientX - browserDragOffset.x), maxX),
        y: Math.min(Math.max(16, e.clientY - browserDragOffset.y), maxY)
      });
    };

    const handleUp = () => setBrowserDragOffset(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [browserDragOffset]);
  useEffect(() => {
    const parseUnreadFromTitle = (title = '') => {
      const match = String(title).match(/^\((\d+)\)/);
      return match ? Number(match[1]) : 0;
    };

    browserTabs.forEach(tab => {
      const webview = browserWebviewRefs.current[tab.id];
      if (!webview || webview.__flowdeskTitleListenerAttached) return;

      const syncTabTitle = (pageTitle = '') => {
        const unread = tab.type === 'whatsapp' ? parseUnreadFromTitle(pageTitle) : 0;

        setBrowserTabs(prev => prev.map(item => {
          if (item.id !== tab.id) return item;
          return {
            ...item,
            unread,
            title: item.type === 'whatsapp' ? 'WhatsApp' : (pageTitle || item.title)
          };
        }));

        if (tab.type === 'whatsapp') {
          const previousUnread = lastWhatsAppUnreadRef.current || 0;
          if (unread > previousUnread) {
            const id = Date.now();
            setBrowserNotifications(prev => [
              ...prev.slice(-2),
              {
                id,
                title: 'WhatsApp',
                message: lang === 'ar'
                  ? `وصلت ${unread - previousUnread} رسالة جديدة`
                  : `${unread - previousUnread} new message${unread - previousUnread > 1 ? 's' : ''}`,
                unread
              }
            ]);
            setTimeout(() => {
              setBrowserNotifications(prev => prev.filter(item => item.id !== id));
            }, 5500);
          }
          lastWhatsAppUnreadRef.current = unread;
        }
      };

      const handleTitleUpdate = (event) => {
        syncTabTitle(event.title || '');
      };

      const handleFinishedLoad = () => {
        try {
          syncTabTitle(webview.getTitle?.() || '');
        } catch {
          // Webview title can be unavailable while Electron is swapping pages.
        }
      };

      webview.addEventListener('page-title-updated', handleTitleUpdate);
      webview.addEventListener('did-finish-load', handleFinishedLoad);
      webview.__flowdeskTitleListenerAttached = true;
    });
  }, [browserTabs, lang]);

  // Track which prayers have finished playing their Adhan
  const [completedPrayers, setCompletedPrayers] = useState([]);
  const nextPrayerRef = useRef(null);

  useEffect(() => {
    nextPrayerRef.current = nextPrayer;
  }, [nextPrayer]);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [showUrgentOnly, setShowUrgentOnly] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [taskStatusFilter, setTaskStatusFilter] = useState('all');

  // Prayer Logic States
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const [prayerWarning, setPrayerWarning] = useState(false);
  const [prayerTimeRemaining, setPrayerTimeRemaining] = useState(null);
  const [dismissedWarningFor, setDismissedWarningFor] = useState(null);
  const dismissedOvertimeAlertsRef = useRef([]);

  // Editing State
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(null);
  const [inlineEditField, setInlineEditField] = useState(null);
  const [isEditingProjectFileInline, setIsEditingProjectFileInline] = useState(false);
  const [projectFileInputValue, setProjectFileInputValue] = useState("");
  const [inlineEditValue, setInlineEditValue] = useState('');
  const [newSubtask, setNewSubtask] = useState('');

  // Image Preview State
  const [previewImage, setPreviewImage] = useState(null);

  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Crop State
  const [cropModal, setCropModal] = useState({ isOpen: false, imageSrc: null, target: null });
  const onCropCancel = () => setCropModal({ isOpen: false, imageSrc: null, target: null });
  const onCropComplete = (croppedImage) => {
    const target = cropModal.target;
    if (target === 'task') {
      setNewTask(prev => ({ ...prev, images: [...(prev.images || []), croppedImage] }));
    } else if (target === 'company') {
      updateCompanyLogo(croppedImage);
    } else if (target === 'edit') {
      if (selectedTask) {
        const nextImages = [...(selectedTask.images || []), croppedImage];
        setSelectedTask(prev => ({ ...prev, images: nextImages }));
        setTasks(prevTasks => prevTasks.map(t => t.id === selectedTask.id ? { ...t, images: nextImages } : t));
      }
      if (editedTask) {
        setEditedTask(prev => ({ ...prev, images: [...(prev?.images || []), croppedImage] }));
      }
    } else if (target === 'userPhoto') {
      setUserPhoto(croppedImage);
    }

    setCropModal({ isOpen: false, imageSrc: null, target: null });
  };

  const adhanAudioRef = useRef(null);
  const alertAudioRef = useRef(null);
  const adhanPreviewRef = useRef(null);

  // Description Editor Refs & Help State
  const descTextareaRef = useRef(null);
  const descImageInputRef = useRef(null);
  const descAttachInputRef = useRef(null);
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [previewingAdhanVoice, setPreviewingAdhanVoice] = useState(null);
  const [lastPlayedPrayer, setLastPlayedPrayer] = useState(null);
  const [lastAlertPrayer, setLastAlertPrayer] = useState(null);
  const [snoozedPrayer, setSnoozedPrayer] = useState(null);

  const [newTask, setNewTask] = useState({
    title: '', description: '', category: '', accountManager: '',
    client: '', designType: 'social', selectedPlatforms: [],
    estimatedHours: 1, isUrgent: false, images: [], projectFile: '', hasBrief: false, brief: '', hasTov: false, tov: '',
    subtasks: [],
    dueDateType: 'today',
    customDate: '',
    status: 'scheduled'
  });

  const [tempSubtask, setTempSubtask] = useState('');
  const [taskExtrasOpen, setTaskExtrasOpen] = useState({ details: false, checklist: false, attachments: false });

  const getAdhanVoice = (voiceId = soundSettings.adhanVoice) => ADHAN_VOICES.find(voice => voice.id === voiceId) || ADHAN_VOICES[0];


  const handleOpenProjectFile = async (filePathOrUrl) => {
    if (!filePathOrUrl) return;
    const trimmed = String(filePathOrUrl).trim();
    if (!trimmed) return;

    if (window.electronAPI?.openExternalLink) {
      await window.electronAPI.openExternalLink(trimmed);
    } else if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('file://')) {
      window.open(trimmed, '_blank');
    } else {
      window.open(`file:///${trimmed.replace(/\\/g, '/')}`, '_blank');
    }
  };

  const applyProjectFileSelection = async (selectedPath, taskIdOrIsEdit) => {
    if (!selectedPath) return;
    if (taskIdOrIsEdit === true) {
      setEditedTask(prev => ({ ...prev, projectFile: selectedPath }));
    } else if (typeof taskIdOrIsEdit === 'string' || typeof taskIdOrIsEdit === 'number') {
      const updatedTasks = tasks.map(t => String(t.id) === String(taskIdOrIsEdit) ? { ...t, projectFile: selectedPath } : t);
      setTasks(updatedTasks);
      if (selectedTask && String(selectedTask.id) === String(taskIdOrIsEdit)) {
        setSelectedTask(prev => ({ ...prev, projectFile: selectedPath }));
      }
      await FlowDeskRepository.saveTasks(updatedTasks);
    } else {
      setNewTask(prev => ({ ...prev, projectFile: selectedPath }));
    }
  };

  const handleChooseProjectFile = async (taskIdOrIsEdit = false) => {
    if (window.electronAPI?.selectFilePath) {
      const selectedPath = await window.electronAPI.selectFilePath();
      if (selectedPath) {
        await applyProjectFileSelection(selectedPath, taskIdOrIsEdit);
      }
      return;
    }

    // Native HTML file input picker for browser mode or fallback
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.onchange = async (e) => {
      if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        const filePathOrName = file.path || file.name;
        await applyProjectFileSelection(filePathOrName, taskIdOrIsEdit);
      }
    };
    fileInput.click();
  };

  const stopAdhanPreview = () => {
    if (adhanPreviewRef.current) {
      adhanPreviewRef.current.pause();
      adhanPreviewRef.current.currentTime = 0;
      adhanPreviewRef.current = null;
    }
    setPreviewingAdhanVoice(null);
  };

  const toggleAdhanPreview = () => {
    const selectedVoice = getAdhanVoice();
    if (previewingAdhanVoice === selectedVoice.id && adhanPreviewRef.current) {
      stopAdhanPreview();
      return;
    }

    stopAdhanPreview();
    const preview = new Audio(selectedVoice.src);
    preview.volume = soundSettings.volume;
    preview.onended = () => setPreviewingAdhanVoice(null);
    adhanPreviewRef.current = preview;
    setPreviewingAdhanVoice(selectedVoice.id);
    preview.play().catch(err => {
      console.log('Adhan preview error:', err);
      stopAdhanPreview();
    });
  };

  const [managerInput, setManagerInput] = useState('');
  const [clientInput, setClientInput] = useState('');
  const [managerSearch, setManagerSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [appVersion, setAppVersion] = useState('');
  const [showAllPrayersModal, setShowAllPrayersModal] = useState(false);
  const [activeAlert, setActiveAlert] = useState(null); // { title, message, type, onConfirm, showCancel }


  // Sync selectedTask with tasks state
  useEffect(() => {
    if (selectedTask) {
      const updatedTask = tasks.find(t => t.id === selectedTask.id);
      if (updatedTask && updatedTask !== selectedTask) {
        setSelectedTask(updatedTask);
      }
    }
  }, [tasks, selectedTask]);

  // Rule: Automatically delete tasks moved to 'done' (التسليم) 2 days (48 hours) after being moved to delivery
  useEffect(() => {
    if (!tasks || tasks.length === 0) return;

    const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000; // 48 hours
    const nowMs = Date.now();

    const expiredDoneTasks = tasks.filter(task => {
      if (task.status !== 'done') return false;
      const movedAtISO = task.statusChangedAt || task.completedAt || task.updatedAt || task.createdAt;
      if (!movedAtISO) return false;
      const movedAtMs = new Date(movedAtISO).getTime();
      return !isNaN(movedAtMs) && (nowMs - movedAtMs >= TWO_DAYS_MS);
    });

    if (expiredDoneTasks.length > 0) {
      const expiredIds = new Set(expiredDoneTasks.map(t => String(t.id)));
      expiredIds.forEach(id => recentlyDeletedTaskIdsRef.current.add(id));
      const remainingTasks = tasks.filter(t => !expiredIds.has(String(t.id)));
      setTasks(remainingTasks);
      FlowDeskRepository.saveTasks(remainingTasks);

      if (selectedTask && expiredIds.has(String(selectedTask.id))) {
        setSelectedTask(null);
      }

      if (typeof queueTaskSyncBulk === 'function') {
        queueTaskSyncBulk(expiredDoneTasks.map(task => ({ task, type: SYNC_OPERATION_TYPES.delete })));
      }
    }
  }, [tasks, selectedTask]);

  useEffect(() => () => stopAdhanPreview(), []);

  useEffect(() => {
    if (!isLoading) {
      const selectedVoice = getAdhanVoice();
      adhanAudioRef.current = new Audio(selectedVoice.src);

      // Add ended listener for debugging
      adhanAudioRef.current.onended = () => {
        console.log("Adhan finished playing completely.");
        setShowPrayerModal(false);
        // Force update to next prayer
        if (nextPrayerRef.current) {
          const prayerKey = `${nextPrayerRef.current.key}-${nextPrayerRef.current.time.getDate()}`;
          setCompletedPrayers(prev => [...prev, prayerKey]);
        }
      };

      // Fallback if bundled file fails
      adhanAudioRef.current.onerror = (e) => {
        console.warn("Bundled Adhan file not found or error, switching to online source.", e);
        adhanAudioRef.current.src = 'https://download.tvquran.com/download/adhan/alafasy/01.mp3';
      };

      alertAudioRef.current = new Audio('https://actions.google.com/sounds/v1/alarms/beep_short.ogg');

      // Set initial volume
      if (adhanAudioRef.current) adhanAudioRef.current.volume = soundSettings.volume;
      if (alertAudioRef.current) alertAudioRef.current.volume = soundSettings.volume;

      fetchPrayerTimes();

      // Fetch App Version
      if (window.electronAPI && window.electronAPI.getAppVersion) {
        window.electronAPI.getAppVersion().then(v => setAppVersion(v));
      }
    }
  }, [isLoading, governorate, soundSettings.adhanVoice]); // Refetch when governorate changes (or after load)

  // Update volume when setting changes
  useEffect(() => {
    if (adhanAudioRef.current) adhanAudioRef.current.volume = soundSettings.volume;
    if (alertAudioRef.current) alertAudioRef.current.volume = soundSettings.volume;
    if (adhanPreviewRef.current) adhanPreviewRef.current.volume = soundSettings.volume;
  }, [soundSettings.volume]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);

      if (tasks.length > 0) { // Only update if tasks exist
        setTasks(prevTasks => prevTasks.map(task => {
          if (task.isRunning) {
            if (activeOvertime[task.category]) {
              setCompanies(prev => incrementCompanyOvertime({
                companies: prev,
                companyId: task.category,
                minutes: 1 / 60
              }));
            }
            return { ...task, elapsedTime: (task.elapsedTime || 0) + 1 };
          }
          return task;
        }));
      }

      Object.keys(companies).forEach(compId => {
        const comp = companies[compId];
        if (comp && comp.workEnd) {
          const [endH, endM] = comp.workEnd.split(':').map(Number);
          const endDate = new Date(now);
          endDate.setHours(endH, endM, 0);
          const diffMs = endDate - now;
          if (diffMs > 0 && diffMs <= 15 * 60 * 1000 && diffMs > 14 * 60 * 1000) {
            if (showOvertimeAlert !== compId && !dismissedOvertimeAlertsRef.current.includes(compId) && !activeOvertime[compId]) {
              setShowOvertimeAlert(compId);
            }
          }
        }
      });

      calculateNextPrayer(now);
      checkPrayerAlerts(now);
      if (healthSettings.enabled && !showHealthAlert && !showPrayerModal) {
        const diffMinutes = (Date.now() - healthSettings.lastCheck) / 1000 / 60;
        if (diffMinutes >= healthSettings.interval) setShowHealthAlert(true);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [prayerTimes, healthSettings, showHealthAlert, nextPrayer, isMuted, companies, activeOvertime, showOvertimeAlert, showPrayerModal, dismissedWarningFor, snoozedPrayer, tasks]);

  const toggleLanguage = () => setLang(prev => toggleLanguageValue(prev));

  const fetchPrayerTimes = async () => {
    try {
      const date = new Date();
      // Use the selected governorate in the API call
      const response = await fetch(`https://api.aladhan.com/v1/timingsByCity/${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}?city=${governorate}&country=Egypt&method=5`);
      const data = await response.json();
      if (data.code === 200) setPrayerTimes(data.data.timings);
    } catch (error) { console.error(error); }
  };

  // --- Health Alert Sound Effect ---
  useEffect(() => {
    if (showHealthAlert) {
      // Play the same alert sound used for specific prayer warnings or a new one
      // Here we reuse the alertAudioRef which is already set up
      if (alertAudioRef.current) {
        alertAudioRef.current.currentTime = 0;
        alertAudioRef.current.play().catch(e => console.error("Error playing alert sound:", e));
      }
    }
  }, [showHealthAlert]);

  const calculateNextPrayer = (now) => {
    if (!prayerTimes) return;
    const prayersOrder = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
    let upcoming = null;
    for (let prayer of prayersOrder) {
      const [hours, minutes] = prayerTimes[prayer].split(':').map(Number);
      const prayerDate = new Date(now);
      prayerDate.setHours(hours, minutes, 0);

      // Check if this specific prayer instance was already completed (Adhan finished)
      // We look for strict future prayers OR currently active ones that haven't finished Adhan yet
      const prayerInstanceKey = `${prayer}-${prayerDate.getDate()}`;
      if (completedPrayers.includes(prayerInstanceKey)) {
        continue;
      }

      // Buffer of 5 mins default, but if completedPrayers check above passes, 
      // it means we haven't finished it yet.
      // However, if we just finished it, the loop continues to next.
      // If we haven't finished it, we keep showing it if within the 5 min window.
      if (prayerDate.getTime() + 300000 > now.getTime()) {
        upcoming = { name: t.prayers[prayer], time: prayerDate, key: prayer };
        break;
      }
    }
    if (!upcoming) {
      const [hours, minutes] = prayerTimes['Fajr'].split(':').map(Number);
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(hours, minutes, 0);
      upcoming = { name: t.prayers['Fajr'], time: tomorrow, key: 'FajrNext' };
    }
    setNextPrayer(prev => {
      // Optimization: Compare timestamps to avoid unnecessary re-renders
      if (prev && upcoming && prev.key === upcoming.key && prev.time.getTime() === upcoming.time.getTime()) return prev;
      return upcoming;
    });
  };

  const checkPrayerAlerts = (now) => {
    if (!nextPrayer || isMuted) return;
    const diffMs = nextPrayer.time - now;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffSeconds = Math.floor(diffMs / 1000);

    const currentPrayerId = `${nextPrayer.key}-${nextPrayer.time.getDate()}`;

    if (snoozedPrayer?.id === currentPrayerId) {
      if (now < snoozedPrayer.until) {
        setShowPrayerModal(false);
        return;
      }
      setSnoozedPrayer(null);
    }

    // 1. Warning (Approaching Prayer) - 5 minutes before
    if (diffMinutes < 5 && diffMs > 0) {
      if (dismissedWarningFor !== currentPrayerId) {
        setPrayerWarning(true);
        setPrayerTimeRemaining(`${Math.floor(diffMinutes)}:${(diffSeconds % 60).toString().padStart(2, '0')}`);
        setShowPrayerModal(true);

        // Play beep only once
        if (lastAlertPrayer !== currentPrayerId && diffMinutes === 4 && diffSeconds > 50) {
          alertAudioRef.current?.play().catch(e => console.log(e));
          setLastAlertPrayer(currentPrayerId);
        }
      } else {
        setShowPrayerModal(false);
      }
    } else if (diffMs > 300000) {
      setPrayerWarning(false);
      if (prayerStatus[nextPrayer.key] !== true) setShowPrayerModal(false);
    }

    // 2. ADHAN PLAYBACK (Time Reached)
    // Trigger if time is now (<= 0) and within the last minute (> -60000ms)
    // Using 1000ms buffer (1 second) to be safe with intervals
    if (diffMs <= 1000 && diffMs > -59000) {
      if (!prayerStatus[nextPrayer.key]) {
        setPrayerWarning(false);
        setShowPrayerModal(true);

        if (lastPlayedPrayer !== currentPrayerId) {
          // ALWAYS PLAY FULL ADHAN
          console.log("Playing Adhan for:", nextPrayer.name);
          adhanAudioRef.current.currentTime = 0;
          adhanAudioRef.current.play().catch(e => console.error("Adhan Play Error:", e));

          setLastPlayedPrayer(currentPrayerId);
        }
      } else {
        setShowPrayerModal(false);
      }
    }
  };

  const handleIgnoreWarning = () => {
    const currentPrayerId = `${nextPrayer.key}-${nextPrayer.time.getDate()}`;
    setDismissedWarningFor(currentPrayerId);
    setShowPrayerModal(false);
  };

  const confirmPrayer = (prayerKey) => {
    setPrayerStatus(prev => updateSettingsField(prev, prayerKey, true));
    setShowPrayerModal(false);
    setSnoozedPrayer(null);
    adhanAudioRef.current.pause();
    adhanAudioRef.current.currentTime = 0;
  };

  const snoozePrayerAlert = () => {
    if (nextPrayer?.key && nextPrayer?.time) {
      setSnoozedPrayer({
        id: `${nextPrayer.key}-${nextPrayer.time.getDate()}`,
        until: new Date(Date.now() + 10 * 60 * 1000)
      });
    }
    setShowPrayerModal(false);
    adhanAudioRef.current.pause();
    adhanAudioRef.current.currentTime = 0;
  };

  const dismissPrayerModalOnly = () => {
    if (nextPrayer?.key && nextPrayer?.time) {
      setSnoozedPrayer({
        id: `${nextPrayer.key}-${nextPrayer.time.getDate()}`,
        until: new Date(Date.now() + 10 * 60 * 1000)
      });
    }
    setShowPrayerModal(false);
  };

  const snoozeHealthAlert = () => {
    const intervalMs = Math.max(healthSettings.interval - 10, 0) * 60 * 1000;
    setHealthSettings(prev => updateSettingsField(prev, 'lastCheck', Date.now() - intervalMs));
    setShowHealthAlert(false);
  };

  const togglePrayerStatus = (prayerKey) => {
    setPrayerStatus(prev => updateSettingsField(prev, prayerKey, !prev[prayerKey]));
  };

  const parseOnboardingList = (value) => {
    return String(value || '')
      .split(/[\n,،]+/)
      .map(item => item.trim())
      .filter(Boolean);
  };

  const finishOnboarding = async (skip = false) => {
    const finalLang = onboardingData.language || 'ar';
    const finalGov = onboardingData.governorate || 'Cairo';
    const finalWorkspace = onboardingData.workspaceName?.trim() || 'FlowDesk';
    const finalCompany = onboardingData.companyName?.trim() || '';
    const companyKey = Object.keys(companies)[0] || '';

    if (!skip) {
      setLang(finalLang);
      setGovernorate(finalGov);
      setUserName(onboardingData.userName.trim());
      setUserRole(onboardingData.userRole.trim());
      setWorkspaceName(finalWorkspace);
      setCompanies(prev => applyOnboardingCompanySettings({
        companies: prev,
        companyId: companyKey,
        onboardingData,
        parsedClients: parseOnboardingList(onboardingData.clientsText),
        parsedManagers: parseOnboardingList(onboardingData.managersText),
        colorPalettes,
        fallbackName: finalCompany
      }));
      setSettingsCompanyId(companyKey);
      setFilter('all');
    }

    await FlowDeskRepository.saveOnboardingDone(true);
    setShowOnboarding(false);
    setOnboardingStep(0);
  };

  const handleImageUpload = (e, targetStateSetter) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    if (targetStateSetter === 'edit' || targetStateSetter === 'task') {
      const newImagePromises = files.map(file => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      });

      Promise.all(newImagePromises).then(newImages => {
        if (targetStateSetter === 'edit') {
          if (selectedTask) {
            const nextImages = [...(selectedTask.images || []), ...newImages];
            setSelectedTask(prev => ({ ...prev, images: nextImages }));
            setTasks(prevTasks => prevTasks.map(t => t.id === selectedTask.id ? { ...t, images: nextImages } : t));
          }
          if (editedTask) {
            setEditedTask(prev => ({ ...prev, images: [...(prev?.images || []), ...newImages] }));
          }
        } else if (targetStateSetter === 'task') {
          setNewTask(prev => ({ ...prev, images: [...(prev.images || []), ...newImages] }));
        }
      });
      return;
    }

    const file = files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setCropModal({ isOpen: true, imageSrc: reader.result, target: targetStateSetter });
    };
    if (file) reader.readAsDataURL(file);
  };

  const handleExportData = () => {
    if (!requirePermission('backup')) return;
    const data = buildCurrentBackupPayload();
    const dataString = JSON.stringify(data);
    const filename = createBackupFilename();
    const successMessage = t.backupSuccess || (lang === 'ar' ? '\u062a\u0645 \u062a\u0635\u062f\u064a\u0631 \u0627\u0644\u0646\u0633\u062e\u0629 \u0627\u0644\u0627\u062d\u062a\u064a\u0627\u0637\u064a\u0629 \u0628\u0646\u062c\u0627\u062d' : 'Backup exported successfully');

    if (window.electronAPI && window.electronAPI.exportBackup) {
      window.electronAPI.exportBackup(dataString, filename).then(result => {
        if (result.success) {
          setActiveAlert({ title: t.success || 'Success', message: successMessage, type: 'success' });
        }
      });
      return;
    }

    const blob = new Blob([dataString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setActiveAlert({ title: t.success || 'Success', message: successMessage, type: 'success' });
  };

  const handleImportData = (e) => {
    if (!requirePermission('backup')) {
      e.target.value = '';
      return;
    }
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = parseBackupJson(event.target.result, DEFAULT_SOUND_SETTINGS);
        await applyBackupPayload(data);

        setActiveAlert({ title: t.success || 'Success', message: lang === 'ar' ? '\u062a\u0645 \u0627\u0633\u062a\u0639\u0627\u062f\u0629 \u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a \u0628\u0646\u062c\u0627\u062d!' : 'Data restored successfully!', type: 'success' });
      } catch (err) {
        setActiveAlert({ title: t.error || 'Error', message: lang === 'ar' ? '\u0645\u0644\u0641 \u063a\u064a\u0631 \u0635\u0627\u0644\u062d' : 'Invalid file', type: 'danger' });
      }
    };
    reader.readAsText(file);
  };

  const handleReset = async () => {
    if (!requirePermission('backup')) return;
    setActiveAlert({
      title: t.resetData,
      message: t.resetConfirm,
      type: 'danger',
      showCancel: true,
      onConfirm: async () => {
        await FlowDeskRepository.clearAll();
        // Show success alert then reload
        setActiveAlert({
          title: t.success || "Success",
          message: t.resetSuccess,
          type: 'success',
          onConfirm: () => window.location.reload()
        });
      }
    });
  };

  const handleDeleteCompany = () => {
    if (!requirePermission('manageCompany')) return;
    const companyKeys = Object.keys(companies);
    if (companyKeys.length <= 1) {
      setActiveAlert({
        title: t.error || "Error",
        message: t.cannotDeleteLastCompany,
        type: 'warning'
      });
      return;
    }

    setActiveAlert({
      title: t.deleteCompany,
      message: t.confirmDeleteCompany,
      type: 'danger',
      showCancel: true,
      onConfirm: () => {
        const newCompanies = { ...companies };
        delete newCompanies[settingsCompanyId];

        // Determine new active tab
        const remainingKeys = Object.keys(newCompanies);
        const nextTab = remainingKeys[0] || '';

        setCompanies(newCompanies);
        setSettingsCompanyId(nextTab);
        setSettingsTab('company');
        queueCompanySync({ id: settingsCompanyId }, SYNC_OPERATION_TYPES.delete);
        // Save changes immediately
        FlowDeskRepository.saveCompanies(newCompanies);
        setActiveAlert(null);
      }
    });
  };

  const addSubtaskToNew = () => {
    if (!tempSubtask.trim()) return;
    setNewTask(prev => ({ ...prev, subtasks: [...prev.subtasks, { id: Date.now(), text: tempSubtask, completed: false }] }));
    setTempSubtask('');
  };

  const addSubtaskToEdit = () => {
    if (!requirePermission('manageTasks')) return;
    if (!newSubtask.trim()) return;
    setEditedTask(prev => ({ ...prev, subtasks: [...prev.subtasks, { id: Date.now(), text: newSubtask, completed: false }] }));
    setNewSubtask('');
  };

  const addSubtaskToSelected = () => {
    if (!requirePermission('manageTasks')) return;
    if (!selectedTask || !newSubtask.trim()) return;
    const updatedTask = {
      ...selectedTask,
      subtasks: [...(selectedTask.subtasks || []), { id: Date.now(), text: newSubtask.trim(), completed: false }]
    };
    setSelectedTask(updatedTask);
    setTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
    queueTaskSync(updatedTask);
    setNewSubtask('');
  };

  const toggleSubtask = (taskId, subtaskId) => {
    if (!requirePermission('manageTasks')) return;
    if (selectedTask && selectedTask.id === taskId) {
      const updatedSubtasks = selectedTask.subtasks.map(s => s.id === subtaskId ? { ...s, completed: !s.completed } : s);
      const updatedTask = { ...selectedTask, subtasks: updatedSubtasks };
      setSelectedTask(updatedTask);
      setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
      queueTaskSync(updatedTask);
    } else if (isEditing) {
      setEditedTask(prev => ({ ...prev, subtasks: prev.subtasks.map(s => s.id === subtaskId ? { ...s, completed: !s.completed } : s) }));
    }
  };

  const removeSubtask = (index, isEditMode) => {
    if (!requirePermission('manageTasks')) return;
    if (isEditMode) {
      setEditedTask(prev => ({ ...prev, subtasks: prev.subtasks.filter((_, i) => i !== index) }));
    } else {
      setNewTask(prev => ({ ...prev, subtasks: prev.subtasks.filter((_, i) => i !== index) }));
    }
  };

  const [newCommentText, setNewCommentText] = useState('');

  const handleAddComment = () => {
    if (!newCommentText.trim() || !selectedTask) return;
    const newComment = {
      id: Date.now(),
      text: newCommentText.trim(),
      author: currentUser?.name || userName || (lang === 'ar' ? 'مستخدم' : 'User'),
      authorRole: currentUser?.accessRole || userRole || '',
      authorPhoto: currentUser?.photo || userPhoto || null,
      createdAt: new Date().toISOString()
    };
    const updatedComments = [...(selectedTask.comments || []), newComment];
    const updatedTask = { ...selectedTask, comments: updatedComments };
    setSelectedTask(updatedTask);
    const nextTasks = tasks.map(t => t.id === selectedTask.id ? updatedTask : t);
    setTasks(nextTasks);
    if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
    FlowDeskRepository.saveTasks(nextTasks);

    // CREATE COMMENT NOTIFICATION
    const commentNotifItem = {
      id: 'notif_comment_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
      type: 'comment',
      title: lang === 'ar' ? 'تعليق جديد 💬' : 'New Comment 💬',
      message: newComment.text,
      commentText: newComment.text,
      taskTitle: selectedTask.title || selectedTask.name || (lang === 'ar' ? 'مهمة' : 'Task'),
      taskId: selectedTask.id,
      senderName: newComment.author,
      senderPhoto: newComment.authorPhoto,
      createdAt: new Date().toISOString(),
      read: false
    };

    // DISPATCH MENTION NOTIFICATIONS
    const mentionMatches = (newCommentText.match(/@([\w\u0600-\u06FF]+)/g) || []).map(m => m.slice(1));
    if (mentionMatches.length > 0) {
      const newNotifs = mentionMatches.map(targetUser => ({
        id: 'notif_mention_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
        type: 'mention',
        targetUser,
        senderName: newComment.author,
        senderPhoto: newComment.authorPhoto,
        taskId: selectedTask.id,
        taskTitle: selectedTask.title || selectedTask.name || (lang === 'ar' ? 'مهمة' : 'Task'),
        commentText: newComment.text,
        message: newComment.text,
        read: false,
        createdAt: new Date().toISOString()
      }));

      setNotifications(prev => [commentNotifItem, ...newNotifs, ...prev]);

      const currentAccountName = (currentUser?.name || userName || '').toLowerCase();
      const isSelfMentioned = mentionMatches.some(m => m.toLowerCase() === currentAccountName);
      if (isSelfMentioned) {
        setActiveAlert({
          title: lang === 'ar' ? 'منشن جديد 🔔' : 'New Mention 🔔',
          message: lang === 'ar' ? `قام ${newComment.author} بذكرك في تعليق!` : `${newComment.author} mentioned you in a comment!`,
          type: 'info'
        });
      }
    } else {
      setNotifications(prev => [commentNotifItem, ...prev]);
    }

    setNewCommentText('');
  };

  const [deletingCommentId, setDeletingCommentId] = useState(null);

  const confirmDeleteComment = (commentId) => {
    if (!selectedTask) return;
    const updatedComments = (selectedTask.comments || []).filter(c => c.id !== commentId);
    const updatedTask = { ...selectedTask, comments: updatedComments };
    setSelectedTask(updatedTask);
    const nextTasks = tasks.map(t => t.id === selectedTask.id ? updatedTask : t);
    setTasks(nextTasks);
    if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
    FlowDeskRepository.saveTasks(nextTasks);
  };

  const handleDeleteComment = (commentId) => {
    setDeletingCommentId(commentId);
  };

  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');

  const handleStartEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentText(comment.text);
  };

  const handleSaveEditComment = (commentId) => {
    if (!selectedTask || !editingCommentText.trim()) return;
    const updatedComments = (selectedTask.comments || []).map(c => {
      if (c.id === commentId) {
        return { ...c, text: editingCommentText.trim(), updatedAt: new Date().toISOString() };
      }
      return c;
    });
    const updatedTask = { ...selectedTask, comments: updatedComments };
    setSelectedTask(updatedTask);
    const nextTasks = tasks.map(t => t.id === selectedTask.id ? updatedTask : t);
    setTasks(nextTasks);
    if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
    FlowDeskRepository.saveTasks(nextTasks);
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');

  const getMentionableUsers = () => {
    const list = new Set();
    if (currentUser?.name) list.add(currentUser.name);
    if (userName) list.add(userName);

    const compList = Array.isArray(companies)
      ? companies
      : companies && typeof companies === 'object'
      ? Object.values(companies)
      : [];

    compList.forEach(c => {
      if (!c) return;
      // Registered employees and account managers ONLY (Excluding Clients)
      const managers = Array.isArray(c.managers) ? c.managers : [];
      managers.forEach(m => {
        const name = typeof m === 'string' ? m : m.name || m.userName;
        if (name && !/^\d+$/.test(name) && name.toLowerCase() !== 'plpl') {
          list.add(name);
        }
      });
    });

    const taskList = Array.isArray(tasks)
      ? tasks
      : tasks && typeof tasks === 'object'
      ? Object.values(tasks)
      : [];

    taskList.forEach(t => {
      if (!t) return;
      const comments = Array.isArray(t.comments) ? t.comments : [];
      comments.forEach(c => {
        if (c && c.author && !/^\d+$/.test(c.author) && c.author.toLowerCase() !== 'plpl') {
          list.add(c.author);
        }
      });
    });

    ['Ahmed', 'Sara', 'Mohamed', 'Designer'].forEach(u => list.add(u));
    return Array.from(list).filter(Boolean);
  };

  const handleCommentTextChange = (val) => {
    setNewCommentText(val);
    const match = val.match(/@([\w\u0600-\u06FF]*)$/);
    if (match) {
      setShowMentionPopup(true);
      setMentionQuery(match[1].toLowerCase());
    } else {
      setShowMentionPopup(false);
      setMentionQuery('');
    }
  };

  const handleSelectMentionUser = (user) => {
    const updated = newCommentText.replace(/@+([\w\u0600-\u06FF]*)$/, `@${user} `);
    setNewCommentText(updated);
    setShowMentionPopup(false);
    setMentionQuery('');
  };

  const handleTriggerMention = () => {
    if (newCommentText.endsWith('@')) {
      setShowMentionPopup(true);
      setMentionQuery('');
      return;
    }
    setNewCommentText(prev => (prev && !prev.endsWith(' ') ? `${prev} @` : `${prev}@`));
    setShowMentionPopup(true);
    setMentionQuery('');
  };

  const renderCommentTextWithMentions = (text) => {
    if (!text) return null;
    const parts = text.split(/(@[\w\u0600-\u06FF]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        return (
          <span
            key={i}
            className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-blue-500/15 text-blue-600 dark:text-blue-400 font-extrabold text-xs sm:text-sm border border-blue-500/20 mx-0.5 shadow-xs"
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const toggleCommentLike = (commentId) => {
    if (!selectedTask) return;
    const userId = currentUser?.id || userName || 'user';
    const updatedComments = (selectedTask.comments || []).map(comment => {
      if (comment.id !== commentId) return comment;
      const currentLikes = Array.isArray(comment.likes) ? comment.likes : [];
      const hasLiked = currentLikes.includes(userId);
      const newLikes = hasLiked
        ? currentLikes.filter(id => id !== userId)
        : [...currentLikes, userId];
      return { ...comment, likes: newLikes };
    });
    const updatedTask = { ...selectedTask, comments: updatedComments };
    setSelectedTask(updatedTask);
    const nextTasks = tasks.map(t => t.id === selectedTask.id ? updatedTask : t);
    setTasks(nextTasks);
    if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
    FlowDeskRepository.saveTasks(nextTasks);
  };

  const toggleTaskSeen = (taskId, e) => {
    if (e) e.stopPropagation();
    const actorName = userName || currentUser?.name || currentUser?.email || (lang === 'ar' ? 'مستخدم' : 'User');

    const targetTask = tasks.find(t => String(t.id) === String(taskId));
    if (!targetTask) return;

    const newSeenState = !targetTask.isSeen;
    const updatedTask = { ...targetTask, isSeen: newSeenState };
    const nextTasks = tasks.map(t => String(t.id) === String(taskId) ? updatedTask : t);

    setTasks(nextTasks);
    if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
    FlowDeskRepository.saveTasks(nextTasks);

    // Send notification ONCE if toggled to seen
    if (newSeenState) {
      const companyName = companies[updatedTask.category]?.name || '';
      const messageText = lang === 'ar'
        ? `تم مشاهدة المهمة "${updatedTask.title}" بواسطة ${actorName}${companyName ? ` - ${companyName}` : ''}`
        : `Task "${updatedTask.title}" was viewed by ${actorName}${companyName ? ` - ${companyName}` : ''}`;

      const newNotifItem = {
        id: 'notif_seen_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
        type: 'task_seen',
        title: lang === 'ar' ? 'تم مشاهدة المهمة' : 'Task Viewed',
        message: messageText,
        taskTitle: updatedTask.title,
        taskId: updatedTask.id,
        senderName: actorName,
        senderPhoto: userPhoto || currentUser?.photo || null,
        createdAt: new Date().toISOString(),
        read: false
      };
      setNotifications(prev => [newNotifItem, ...prev]);
    }
  };

  const formatCommentDateOnly = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;

      const now = new Date();
      if (d.toDateString() === now.toDateString()) {
        return lang === 'ar' ? 'اليوم' : 'Today';
      }

      return d.toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return isoString;
    }
  };

  const formatCommentTimeOnly = (isoString) => {
    if (!isoString) return '';
    try {
      const d = new Date(isoString);
      if (isNaN(d.getTime())) return isoString;

      return d.toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return isoString;
    }
  };

  const handleQuickAddCard = () => {
    if (!quickCardTitle.trim()) return;
    const fallbackCompanyKey = visibleCompanyKeys[0] || getFallbackCompanyKey(companies);
    const targetCompany = filter !== 'all' && canAccessCompany(filter)
      ? filter
      : (quickCardCompany || fallbackCompanyKey);

    // Resolve client: use selected or leave empty
    const allowedClients = getAllowedClientsForCompany(targetCompany);
    const resolvedClient = quickCardClient && allowedClients.includes(quickCardClient) ? quickCardClient : '';

    const isUrgentTask = quickCardPriority === 'urgent';
    const draft = {
      title: quickCardTitle.trim(),
      category: targetCompany,
      client: resolvedClient,
      status: 'scheduled',
      createdBy: currentUser.id,
      createdByName: currentUser.name || userName || currentUser.email || (lang === 'ar' ? 'مستخدم' : 'User'),
      accountManager: currentUser.name || userName || (lang === 'ar' ? 'مروان' : 'Marwan'),
      subtasks: [],
      images: [],
      labels: [],
      isUrgent: isUrgentTask,
      priority: quickCardPriority,
      estimatedHours: 1,
      description: '',
      brief: '',
      tov: ''
    };

    const task = createTaskFromDraft({ draft, companies });
    const updatedTasks = addTaskToList(tasks, task);
    setTasks(updatedTasks);
    queueTaskSync(task);
    showTaskActivityNotification({ task, toStatus: task.status, actorName: task.createdByName, type: 'created' });

    setQuickCardTitle('');
    setQuickCardClient('');
    setQuickCardPriority('normal');
    setIsAddingCardInScheduled(false);
  };

  const handleAddTask = (e) => {
    e.preventDefault();
    if (!canCreateTask) return;
    if (!newTask.title) return;
    const fallbackCompanyKey = visibleCompanyKeys[0] || getFallbackCompanyKey(companies);
    const draft = {
      ...newTask,
      category: canAccessCompany(newTask.category) ? newTask.category : fallbackCompanyKey,
      createdBy: currentUser.id,
      createdByName: currentUser.name || userName || currentUser.email || (lang === 'ar' ? 'مستخدم' : 'User')
    };
    const task = createTaskFromDraft({ draft, companies });
    const updatedTasks = addTaskToList(tasks, task);
    setTasks(updatedTasks);
    queueTaskSync(task);
    showTaskActivityNotification({ task, toStatus: task.status, actorName: task.createdByName, type: 'created' });
    setNewTask(resetTaskDraftForCompany(fallbackCompanyKey));
    setActiveModal(null);

    // Reset filters to ensure the new task is visible
    setSearchQuery('');
    setBoardView('scheduled');
    setShowUrgentOnly(false);
    if (filter !== 'all' && filter !== task.category) setFilter(task.category);
  };

  const saveTaskChanges = () => {
    if (!requirePermission('manageTasks')) return;
    if (!editedTask.title) return;
    const updatedTasks = updateTaskInList(tasks, editedTask);
    setTasks(updatedTasks);
    queueTaskSync(editedTask);
    FlowDeskRepository.saveTasks(updatedTasks);
    setSelectedTask(editedTask);
    setIsEditing(false);
  };

  const startInlineTaskEdit = (field) => {
    if (!requirePermission('manageTasks')) return;
    if (!selectedTask) return;
    setInlineEditField(field);
    setInlineEditValue(selectedTask[field] || '');
  };

  const openTaskInlineEdit = (task, field = 'title') => {
    if (!requirePermission('manageTasks')) return;
    setSelectedTask(task);
    setEditedTask(null);
    setIsEditing(false);
    setInlineEditField(field);
    setInlineEditValue(task[field] || '');
  };

  const cancelInlineTaskEdit = () => {
    setInlineEditField(null);
    setInlineEditValue('');
  };

  const saveInlineTaskField = () => {
    if (!requirePermission('manageTasks')) return;
    if (!selectedTask || !inlineEditField) return;
    const value = inlineEditValue.trim();
    if (inlineEditField === 'title' && !value) return;
    const updates = {
      [inlineEditField]: value,
      ...(inlineEditField === 'brief' ? { hasBrief: Boolean(value) } : {}),
      ...(inlineEditField === 'tov' ? { hasTov: Boolean(value) } : {})
    };
    const updatedTask = { ...selectedTask, ...updates };
    const updatedTasks = updateTaskFieldInList({ tasks, taskId: selectedTask.id, field: inlineEditField, value });
    setTasks(updatedTasks);
    FlowDeskRepository.saveTasks(updatedTasks);
    setSelectedTask(updatedTasks.find(task => String(task.id) === String(selectedTask.id)) || updatedTask);
    queueTaskSync(updatedTask);
    cancelInlineTaskEdit();
  };

  // Description Rich Text Editor Toolbar Commands & File Upload Handlers
  const applyDescFormatting = (type, valueParam = '') => {
    const el = descTextareaRef.current;
    const currentVal = inlineEditField === 'description' ? inlineEditValue : (selectedTask?.description || selectedTask?.brief || '');
    const start = el ? (el.selectionStart || 0) : 0;
    const end = el ? (el.selectionEnd || 0) : 0;
    const selectedText = currentVal.substring(start, end);

    let replacement = '';
    let newPos = start;

    switch (type) {
      case 'bold':
        replacement = `**${selectedText || (lang === 'ar' ? 'نص عريض' : 'bold text')}**`;
        newPos = selectedText ? start + replacement.length : start + 2;
        break;
      case 'italic':
        replacement = `*${selectedText || (lang === 'ar' ? 'نص مائل' : 'italic text')}*`;
        newPos = selectedText ? start + replacement.length : start + 1;
        break;
      case 'heading':
        replacement = `\n### ${selectedText || (lang === 'ar' ? 'عنوان' : 'Heading')}\n`;
        newPos = start + replacement.length;
        break;
      case 'list':
        if (selectedText.includes('\n')) {
          replacement = selectedText.split('\n').map(line => `- ${line}`).join('\n');
        } else {
          replacement = `\n- ${selectedText || (lang === 'ar' ? 'عنصر قائمة' : 'List item')}\n`;
        }
        newPos = start + replacement.length;
        break;
      case 'link':
        const url = valueParam || prompt(lang === 'ar' ? 'أدخل رابط URL:' : 'Enter URL:', 'https://');
        if (!url) return;
        replacement = `[${selectedText || (lang === 'ar' ? 'رابط' : 'Link')}](${url})`;
        newPos = start + replacement.length;
        break;
      case 'strikethrough':
        replacement = `~~${selectedText || 'strikethrough'}~~`;
        newPos = selectedText ? start + replacement.length : start + 2;
        break;
      case 'quote':
        replacement = `\n> ${selectedText || (lang === 'ar' ? 'اقتباس' : 'Quote')}\n`;
        newPos = start + replacement.length;
        break;
      case 'code':
        replacement = `\`${selectedText || 'code'}\``;
        newPos = selectedText ? start + replacement.length : start + 1;
        break;
      case 'codeblock':
        replacement = `\n\`\`\`\n${selectedText || 'code block'}\n\`\`\`\n`;
        newPos = start + replacement.length;
        break;
      default:
        return;
    }

    const nextVal = currentVal.substring(0, start) + replacement + currentVal.substring(end);
    setInlineEditField('description');
    setInlineEditValue(nextVal);

    setTimeout(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(newPos, newPos);
      }
    }, 50);
  };

  const handleDescImageSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      const imageName = file.name || 'image.jpg';
      const imgMarkdown = `\n![${imageName}](${dataUrl})\n`;
      const currentVal = inlineEditField === 'description' ? inlineEditValue : (selectedTask?.description || selectedTask?.brief || '');
      const nextVal = (currentVal || '') + imgMarkdown;
      setInlineEditField('description');
      setInlineEditValue(nextVal);

      if (selectedTask) {
        const nextImages = [...(selectedTask.images || []), dataUrl];
        const updatedTask = { ...selectedTask, images: nextImages };
        setSelectedTask(updatedTask);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updatedTask : t));
        if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleDescAttachmentSelect = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target.result;
      const fileName = file.name || 'attachment';
      const attachMarkdown = `\n📎 [${fileName}](${dataUrl})\n`;
      const currentVal = inlineEditField === 'description' ? inlineEditValue : (selectedTask?.description || selectedTask?.brief || '');
      const nextVal = (currentVal || '') + attachMarkdown;
      setInlineEditField('description');
      setInlineEditValue(nextVal);

      if (selectedTask && file.type.startsWith('image/')) {
        const nextImages = [...(selectedTask.images || []), dataUrl];
        const updatedTask = { ...selectedTask, images: nextImages };
        setSelectedTask(updatedTask);
        setTasks(prev => prev.map(t => t.id === selectedTask.id ? updatedTask : t));
        if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const parseInlineMarkdownStyles = (str) => {
    if (!str) return str;
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIdx = 0;
    let match;

    while ((match = linkRegex.exec(str)) !== null) {
      if (match.index > lastIdx) {
        parts.push(str.substring(lastIdx, match.index));
      }
      parts.push(
        <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 underline font-semibold hover:text-blue-700 break-words">
          {match[1]}
        </a>
      );
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < str.length) {
      parts.push(str.substring(lastIdx));
    }

    return parts.map((part, pIdx) => {
      if (typeof part !== 'string') return part;

      // Strikethrough ~~text~~
      const strikeParts = part.split(/~~([\s\S]+?)~~/g);
      return strikeParts.map((sChunk, sIdx) => {
        if (sIdx % 2 === 1) {
          return <del key={sIdx} className="line-through opacity-75">{sChunk}</del>;
        }

        // Bold **text**
        const boldParts = sChunk.split(/\*\*([\s\S]+?)\*\*/g);
        return boldParts.map((bChunk, bIdx) => {
          if (bIdx % 2 === 1) {
            return <strong key={bIdx} className="font-extrabold font-black text-slate-950 dark:text-white" style={{ fontWeight: 800 }}>{bChunk}</strong>;
          }

          // Italic *text*
          const italicParts = bChunk.split(/\*([\s\S]+?)\*/g);
          return italicParts.map((iChunk, iIdx) => {
            if (iIdx % 2 === 1) {
              return <em key={iIdx} className="italic text-slate-700 dark:text-slate-300 font-serif">{iChunk}</em>;
            }
            return iChunk;
          });
        });
      });
    });
  };

  const renderDescriptionMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      if (line.startsWith('# ')) return <h1 key={idx} dir="auto" className="text-xl font-black my-1 text-slate-900 dark:text-white break-words">{line.replace('# ', '')}</h1>;
      if (line.startsWith('## ')) return <h2 key={idx} dir="auto" className="text-lg font-bold my-1 text-slate-900 dark:text-white break-words">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={idx} dir="auto" className="text-base font-bold my-1 text-slate-900 dark:text-white break-words">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return (
          <div key={idx} dir="auto" className="flex items-start gap-2 my-0.5 pl-2 break-words">
            <span className="text-blue-500 font-bold shrink-0">•</span>
            <span className="break-words min-w-0 flex-1">{parseInlineMarkdownStyles(line.substring(2))}</span>
          </div>
        );
      }
      if (line.startsWith('> ')) {
        return (
          <blockquote key={idx} dir="auto" className="border-l-4 border-blue-500 pl-3 italic my-1 text-slate-600 dark:text-slate-400 break-words">
            {parseInlineMarkdownStyles(line.substring(2))}
          </blockquote>
        );
      }
      return <p key={idx} dir="auto" className="my-0.5 break-words leading-relaxed">{parseInlineMarkdownStyles(line)}</p>;
    });
  };

  const moveTask = (id, newStatus) => {
    const task = tasks.find(item => String(item.id) === String(id));
    if (!canMoveTaskToStatus(task, newStatus)) return;
    const previousStatus = task.status;
    const changedAt = new Date().toISOString();
    const actorName = currentUser.name || userName || currentUser.email || (lang === 'ar' ? 'مستخدم' : 'User');
    const updatedTasks = moveTaskInList({ tasks, taskId: id, status: newStatus }).map(item => (
      String(item.id) === String(id)
        ? {
          ...item,
          previousStatus,
          statusChangedAt: changedAt,
          statusChangedBy: currentUser.id,
          statusChangedByName: actorName
        }
        : item
    ));
    setTasks(updatedTasks);
    const updatedTask = updatedTasks.find(item => String(item.id) === String(id));
    queueTaskSync(updatedTask);
    showTaskActivityNotification({ task: updatedTask, fromStatus: previousStatus, toStatus: newStatus, actorName });
    if (selectedTask && selectedTask.id === id) setSelectedTask(null);
  };

  const toggleTimer = (id) => {
    if (!canControlTaskWork) return;
    setTasks(prevTasks => {
      const updatedTasks = toggleTaskTimerInList({ tasks: prevTasks, taskId: id });
      queueTaskSync(updatedTasks.find(task => String(task.id) === String(id)));
      return updatedTasks;
    });
  };

  const deleteTask = (id, skipConfirm = false) => {
    if (!requirePermission('deleteTasks')) return;
    const task = tasks.find(item => String(item.id) === String(id));
    const taskTitle = task ? task.title : '';

    if (skipConfirm) {
      recentlyDeletedTaskIdsRef.current.add(String(id));
      setTasks(deleteTaskFromList(tasks, id));
      queueTaskSync(task || { id }, SYNC_OPERATION_TYPES.delete);
      if (selectedTask?.id === id) setSelectedTask(null);
      return;
    }

    setActiveAlert({
      title: lang === 'ar' ? 'تأكيد حذف المهمة' : 'Confirm Task Deletion',
      message: lang === 'ar' 
        ? `هل أنت متاكد من إزالة المهمة "${taskTitle || ''}"؟ لن تتمكن من استعادتها بعد الحذف.` 
        : `Are you sure you want to delete "${taskTitle || 'this task'}"? This action cannot be undone.`,
      type: 'danger',
      showCancel: true,
      confirmText: lang === 'ar' ? 'نعم، احذف المهمة' : 'Yes, Delete Task',
      cancelText: lang === 'ar' ? 'إلغاء' : 'Cancel',
      onConfirm: () => {
        recentlyDeletedTaskIdsRef.current.add(String(id));
        setTasks(deleteTaskFromList(tasks, id));
        queueTaskSync(task || { id }, SYNC_OPERATION_TYPES.delete);
        if (selectedTask?.id === id) setSelectedTask(null);
        setActiveAlert(null);
      }
    });
  };

  const handleAddCompany = (nameOverride = '') => {
    if (!requirePermission('manageCompany')) return;
    const company = createCompany({ name: nameOverride || newCompanyName, theme: colorPalettes[0], lang });
    setCompanies(prev => addCompanyToMap(prev, company));
    queueCompanySync(company);
    setSettingsCompanyId(company.id);
    setSettingsTab('company');
    setFilter(company.id);
    setNewCompanyName('');
    setActiveModal('settings');
  };

  const updateCompanyTheme = (palette) => {
    if (!requirePermission('manageCompany')) return;
    setCompanies(prev => {
      const nextCompanies = updateCompanyField({ companies: prev, companyId: settingsCompanyId, field: 'theme', value: palette });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
  };

  const updateCompanyName = (name) => {
    if (!requirePermission('manageCompany')) return;
    setCompanies(prev => {
      const nextCompanies = updateCompanyField({ companies: prev, companyId: settingsCompanyId, field: 'name', value: name });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
  };

  const updateCompanyLogo = (logo) => {
    if (!requirePermission('manageCompany')) return;
    setCompanies(prev => {
      const nextCompanies = updateCompanyField({ companies: prev, companyId: settingsCompanyId, field: 'logo', value: logo });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
  };

  const addManager = () => {
    if (!requirePermission('manageManagers')) return;
    if (!managerInput.trim()) return;
    setCompanies(prev => {
      const nextCompanies = addNamedItemToCompany({ companies: prev, companyId: settingsCompanyId, field: 'managers', value: managerInput });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
    setManagerInput('');
  };
  const removeManager = (idx) => {
    if (!requirePermission('manageManagers')) return;
    setCompanies(prev => {
      const nextCompanies = removeNamedItemFromCompany({ companies: prev, companyId: settingsCompanyId, field: 'managers', index: idx });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
  };

  const addClient = () => {
    if (!requirePermission('manageClients')) return;
    if (!clientInput.trim()) return;
    setCompanies(prev => {
      const nextCompanies = addNamedItemToCompany({ companies: prev, companyId: settingsCompanyId, field: 'clients', value: clientInput });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
    setClientInput('');
  };
  const removeClient = (idx) => {
    if (!requirePermission('manageClients')) return;
    setCompanies(prev => {
      const nextCompanies = removeNamedItemFromCompany({ companies: prev, companyId: settingsCompanyId, field: 'clients', index: idx });
      queueCompanySync(nextCompanies[settingsCompanyId]);
      return nextCompanies;
    });
  };

  const togglePlatform = (item, isEditingMode = false) => {
    if (isEditingMode) {
      setEditedTask(prev => {
        const list = prev.selectedPlatforms.includes(item) ? prev.selectedPlatforms.filter(i => i !== item) : [...prev.selectedPlatforms, item];
        return { ...prev, selectedPlatforms: list };
      });
    } else {
      setNewTask(prev => {
        const list = prev.selectedPlatforms.includes(item) ? prev.selectedPlatforms.filter(i => i !== item) : [...prev.selectedPlatforms, item];
        return { ...prev, selectedPlatforms: list };
      });
    }
  };

  const isCompanyOpen = (compId) => {
    const comp = companies[compId];
    if (!comp) return false;
    const [sH, sM] = comp.workStart.split(':').map(Number);
    const [eH, eM] = comp.workEnd.split(':').map(Number);

    const currentDay = currentTime.getDay();
    if (comp.workDays && !comp.workDays.includes(currentDay)) return false;

    const start = new Date(currentTime); start.setHours(sH, sM, 0);
    const end = new Date(currentTime); end.setHours(eH, eM, 0);
    return currentTime >= start && currentTime < end;
  };

  const handleOvertimeResponse = (enable) => {
    const compId = showOvertimeAlert;
    if (!compId) return;

    if (enable) {
      setActiveOvertime(prev => ({ ...prev, [compId]: true }));
    }

    // Add to Ref immediately to block interval check
    if (!dismissedOvertimeAlertsRef.current.includes(compId)) {
      dismissedOvertimeAlertsRef.current.push(compId);
    }

    setShowOvertimeAlert(null);
  };

  const handleDeleteAllDone = () => {
    const doneTasks = visibleTasks.filter(task => task.status === 'done');
    const doneTasksCount = doneTasks.length;
    if (doneTasksCount === 0) return;

    setActiveAlert({
      title: t.deleteAllDone,
      message: t.confirmDeleteAllDone,
      type: 'danger',
      showCancel: true,
      onConfirm: async () => {
        const doneTaskIds = new Set(doneTasks.map(task => String(task.id)));
        doneTaskIds.forEach(id => recentlyDeletedTaskIdsRef.current.add(id));
        const newTasks = tasks.filter(task => !doneTaskIds.has(String(task.id)));
        setTasks(newTasks);
        await FlowDeskRepository.saveTasks(newTasks);
        await queueTaskSyncBulk(doneTasks.map(task => ({ task, type: SYNC_OPERATION_TYPES.delete })));
        setActiveAlert(null);
        setSelectedTask(null);
      }
    });
  };

  // --- Clock In / Check Out Attendance Tracking Systems ---
  const handleAttendance = (compId) => {
    if (!requirePermission('manageTasks')) return;
    const comp = companies[compId];
    if (!comp) return;

    if (alertAudioRef.current) {
      alertAudioRef.current.currentTime = 0;
      alertAudioRef.current.play().catch(e => console.log("Audio play error:", e));
    }

    if (!comp.isClockedIn) {
      setCompanies(prev => {
        const nextCompanies = clockInCompany({ companies: prev, companyId: compId });
        queueCompanySync(nextCompanies[compId]);
        return nextCompanies;
      });
      setActiveAlert({
        title: lang === 'ar' ? "Check In ناجح 🟢" : "Check In Successful 🟢",
        message: lang === 'ar' 
          ? `تم بدء احتساب Working Hours لـ ${comp.name} بنجاح.` 
          : `Started tracking work shift for ${comp.name} successfully.`,
        type: 'success'
      });
    } else {
      const result = clockOutCompany({ companies, companyId: compId, lang });
      setCompanies(result.companies);
      queueCompanySync(result.companies[compId]);

      setActiveAlert({
        title: lang === 'ar' ? "Check Out ناجح 🔴" : "Check Out Successful 🔴",
        message: lang === 'ar'
          ? `تم تسجيل انصرافك من ${comp.name}. إجمالي مدة العمل: ${result.durationText}.`
          : `Logged out from ${comp.name}. Total work duration: ${result.durationText}.`,
        type: 'success'
      });
    }
  };

  const handleClearSingleAttendanceLog = (compId, logId) => {
    setCompanies(prev => {
      const nextCompanies = removeAttendanceLog({ companies: prev, companyId: compId, logId });
      queueCompanySync(nextCompanies[compId]);
      return nextCompanies;
    });
  };

  const handleClearAllAttendanceLogs = (compId) => {
    setActiveAlert({
      title: lang === 'ar' ? "تأكيد الحذف" : "Confirm Clear",
      message: lang === 'ar'
        ? "هل أنت متأكد من حذف سجل الحضور بالكامل لهذه الشركة؟ لا يمكن التراجع عن هذا الإجراء."
        : "Are you sure you want to clear all attendance logs for this company? This action cannot be undone.",
      type: 'danger',
      showCancel: true,
      onConfirm: () => {
        setCompanies(prev => {
          const nextCompanies = clearAttendanceLogs({ companies: prev, companyId: compId });
          queueCompanySync(nextCompanies[compId]);
          return nextCompanies;
        });
        setActiveAlert(null);
      }
    });
  };

  const formatDuration = (startISO) => {
    if (!startISO) return "00:00:00";
    const start = new Date(startISO);
    const diffMs = currentTime - start;
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));
    
    const h = Math.floor(diffSec / 3600);
    const m = Math.floor((diffSec % 3600) / 60);
    const s = diffSec % 60;
    
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleDownload = (e, url, filename) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'attachment.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const isTaskOverdue = (task, now = currentTime) => {
    if (!task || task.status === 'done') return false;

    // Get active date string (YYYY-MM-DD)
    const dateStr = task.dueDate || task.deadlineDate || task.deadline;

    if (task.deadlineISO) {
      const isoDate = new Date(task.deadlineISO);
      if (!isNaN(isoDate.getTime())) {
        if (dateStr) {
          const isoDateOnly = isoDate.toISOString().split('T')[0];
          // If deadlineISO date part matches active dateStr, use ISO time comparison
          if (isoDateOnly === dateStr) {
            return isoDate < now;
          }
        } else {
          return isoDate < now;
        }
      }
    }

    if (!dateStr) return false;

    // Compare dateStr at end of day (23:59:59.999)
    const due = new Date(dateStr.includes('T') ? dateStr : `${dateStr}T23:59:59.999`);
    if (isNaN(due.getTime())) return false;
    return due < now;
  };

  const isCompletedToday = (completedAt, now = currentTime) => {
    if (!completedAt) return false;
    const completedDate = new Date(completedAt);
    if (Number.isNaN(completedDate.getTime())) return false;
    return completedDate.getFullYear() === now.getFullYear()
      && completedDate.getMonth() === now.getMonth()
      && completedDate.getDate() === now.getDate();
  };

  const isCompletedThisMonth = (dateValue, now = currentTime) => {
    if (!dateValue) return false;
    const date = new Date(dateValue);
    return !Number.isNaN(date.getTime())
      && date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth();
  };

  const getTaskDateForFilter = (task) => {
    const dateValue = task.status === 'done'
      ? (task.completedAt || task.deadlineISO || task.deadlineDate || task.createdAt)
      : (task.deadlineISO || task.deadlineDate || task.createdAt);
    if (!dateValue) return null;
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const isTaskInDateFilter = (task, activeDateFilter = dateFilter, now = currentTime) => {
    if (activeDateFilter === 'all') return true;

    const taskDate = getTaskDateForFilter(task);
    if (!taskDate) return false;

    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);

    const normalizedTaskDate = new Date(taskDate);
    normalizedTaskDate.setHours(0, 0, 0, 0);

    const tomorrow = new Date(startOfToday);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (activeDateFilter === 'today') return normalizedTaskDate.getTime() === startOfToday.getTime();
    if (activeDateFilter === 'tomorrow') return normalizedTaskDate.getTime() === tomorrow.getTime();

    if (activeDateFilter === 'week') {
      const startOfWeek = new Date(startOfToday);
      startOfWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);
      return taskDate >= startOfWeek && taskDate <= endOfWeek;
    }

    if (activeDateFilter === 'month') {
      return taskDate.getMonth() === startOfToday.getMonth() && taskDate.getFullYear() === startOfToday.getFullYear();
    }

    return true;
  };

  const getCategorizedTasks = () => {
    console.log("ACTIVE COMPANY ID", filter);
    console.log("VISIBLE TASKS", visibleTasks);

    let filtered = visibleTasks;

    // Search Filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => t.title?.toLowerCase().includes(query) || t.client?.toLowerCase().includes(query));
    }

    // Urgent Filter
    if (showUrgentOnly) {
      filtered = filtered.filter(t => t.isUrgent);
    }

    // Date Filter Logic
    if (dateFilter !== 'all') {
      filtered = filtered.filter(t => isTaskInDateFilter(t));
    }

    if (filter !== 'all') {
      filtered = filtered.filter(t => 
        String(t.category) === String(filter) || 
        String(t.companyId) === String(filter)
      );
    }

    const getSortDate = (task) => task.deadlineISO ? new Date(task.deadlineISO) : new Date(8640000000000000);
    const overdueTasks = filtered.filter(t => isTaskOverdue(t)).sort((a, b) => getSortDate(a) - getSortDate(b));
    const urgentTasks = filtered.filter(t => t.isUrgent && t.status === 'scheduled' && !isTaskOverdue(t)).sort((a, b) => getSortDate(a) - getSortDate(b));

    return {
      overdue: overdueTasks,
      urgent: urgentTasks,
      scheduled: filtered.filter(t => t.status === 'scheduled' && !isTaskOverdue(t) && !t.isUrgent).sort((a, b) => getSortDate(a) - getSortDate(b)),
      ready: filtered.filter(t => t.status === 'ready' && !isTaskOverdue(t)),
      in_progress: filtered.filter(t => t.status === 'in_progress'),
      review: filtered.filter(t => t.status === 'review'),
      revision: filtered.filter(t => t.status === 'revision'),
      done: filtered.filter(t => t.status === 'done').sort((a, b) => new Date(b.completedAt || 0) - new Date(a.completedAt || 0))
    };
  };

  const getStatistics = () => {
    const stats = { totalCompletedToday: 0 }; // Initialize global counter
    Object.keys(visibleCompanies).forEach(key => {
      stats[key] = { today: 0, month: 0, overtime: Math.round(companies[key]?.overtimeMinutes || 0), earnedToday: 0, earnedMonth: 0 };
    });

    visibleTasks.forEach(task => {
      // Safety Check for deleted companies
      if (!companies[task.category]) return;

      if (task.status === 'done' && task.completedAt) {
        if (isCompletedToday(task.completedAt)) {
          // Add to global total
          stats.totalCompletedToday += 1;
        }

        if (stats[task.category]) {
          const hoursWorked = (task.elapsedTime || 0) / 3600;
          const earnings = hoursWorked * (companies[task.category].hourlyRate || 0);

          if (isCompletedToday(task.completedAt)) {
            stats[task.category].today += 1;
            stats[task.category].earnedToday += earnings;
          }
          if (isCompletedThisMonth(task.completedAt)) {
            stats[task.category].month += 1;
            stats[task.category].earnedMonth += earnings;
          }
        }
      }
    });

    Object.keys(stats).forEach(key => {
      if (key === 'totalCompletedToday') return;
      if (!companies[key]) return;
      const overtimeEarnings = stats[key].overtime * (companies[key].hourlyRate || 0);
      stats[key].earnedToday += overtimeEarnings;
    });

    return stats;
  };

  const getAnalytics = () => {
    const stats = getStatistics();
    const companyEntries = Object.entries(visibleCompanies);
    const hasHourlyRates = companyEntries.some(([, company]) => Number(company.hourlyRate || 0) > 0);
    const statusCounts = {
      total: visibleTasks.length,
      scheduled: visibleTasks.filter(task => task.status === 'scheduled' && !isTaskOverdue(task) && !task.isUrgent).length,
      in_progress: visibleTasks.filter(task => task.status === 'in_progress').length,
      review: visibleTasks.filter(task => task.status === 'review').length,
      revision: visibleTasks.filter(task => task.status === 'revision').length,
      doneToday: visibleTasks.filter(task => task.status === 'done' && isCompletedToday(task.completedAt)).length,
      doneMonth: visibleTasks.filter(task => task.status === 'done' && isCompletedThisMonth(task.completedAt)).length,
      overdue: visibleTasks.filter(task => isTaskOverdue(task)).length,
      urgent: visibleTasks.filter(task => task.isUrgent && task.status !== 'done').length
    };

    const completedTasks = visibleTasks.filter(task => task.status === 'done');
    const avgSeconds = completedTasks.length
      ? completedTasks.reduce((sum, task) => sum + Number(task.elapsedTime || 0), 0) / completedTasks.length
      : 0;
    const activeSeconds = visibleTasks
      .filter(task => task.status === 'in_progress' || (task.status === 'done' && isCompletedToday(task.completedAt)))
      .reduce((sum, task) => sum + Number(task.elapsedTime || 0), 0);

    const clientCounts = visibleTasks.reduce((acc, task) => {
      const client = task.client?.trim();
      if (!client) return acc;
      acc[client] = (acc[client] || 0) + 1;
      return acc;
    }, {});
    const topClient = Object.entries(clientCounts).sort((a, b) => b[1] - a[1])[0];

    const companyAnalytics = companyEntries.map(([key, company]) => {
      const companyTasks = visibleTasks.filter(task => task.category === key);
      const companyOverdue = companyTasks.filter(task => isTaskOverdue(task)).length;
      const companyOpen = companyTasks.filter(task => task.status !== 'done').length;
      const companyDoneToday = stats[key]?.today || 0;
      const companyDoneMonth = stats[key]?.month || 0;
      const companyReview = companyTasks.filter(task => task.status === 'review').length;
      const companyRunning = companyTasks.filter(task => task.status === 'in_progress').length;
      const companyScore = companyOpen + companyOverdue * 2 + companyReview + companyRunning;
      return {
        key,
        company,
        total: companyTasks.length,
        open: companyOpen,
        overdue: companyOverdue,
        review: companyReview,
        running: companyRunning,
        doneToday: companyDoneToday,
        doneMonth: companyDoneMonth,
        earningsMonth: stats[key]?.earnedMonth || 0,
        overtime: stats[key]?.overtime || 0,
        pressure: companyScore
      };
    }).sort((a, b) => b.pressure - a.pressure);

    return {
      statusCounts,
      activeSeconds,
      avgSeconds,
      topClient,
      companyAnalytics,
      hasHourlyRates,
      totalMonthEarnings: companyAnalytics.reduce((sum, item) => sum + item.earningsMonth, 0)
    };
  };

  const getRelativeTimeString = (dateInput) => {
    if (!dateInput) return lang === 'ar' ? 'منذ لحظات' : 'just now';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return lang === 'ar' ? 'منذ لحظات' : 'just now';
    
    const now = new Date();
    const diffMs = Math.abs(now - date);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 2) return lang === 'ar' ? 'منذ لحظات' : 'just now';
    if (diffMins < 60) return lang === 'ar' ? `منذ ${diffMins} دقيقة` : `${diffMins}m ago`;
    if (diffHours < 24) return lang === 'ar' ? `منذ ${diffHours} ساعة` : `${diffHours}h ago`;
    return lang === 'ar' ? `منذ ${diffDays} يوم` : `${diffDays}d ago`;
  };

  const getRecentWorkspaceActivities = () => {
    if (!visibleTasks || visibleTasks.length === 0) return [];

    const activities = [];

    // 1. Completed tasks
    visibleTasks
      .filter(t => t.status === 'done' && t.completedAt)
      .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
      .slice(0, 3)
      .forEach(task => {
        activities.push({
          id: `done-${task.id}`,
          task,
          title: lang === 'ar' ? `تم إكمال: "${task.title}"` : `Completed: "${task.title}"`,
          date: task.completedAt,
          icon: CheckCircle,
          color: 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20'
        });
      });

    // 2. Working tasks
    visibleTasks
      .filter(t => t.status === 'in_progress')
      .slice(0, 3)
      .forEach(task => {
        activities.push({
          id: `working-${task.id}`,
          task,
          title: lang === 'ar' ? `جاري العمل على: "${task.title}"` : `Working on: "${task.title}"`,
          date: task.updatedAt || task.createdAt,
          icon: Zap,
          color: 'text-amber-500 bg-amber-500/10 border-amber-500/20'
        });
      });

    // 3. Review tasks
    visibleTasks
      .filter(t => t.status === 'review')
      .slice(0, 2)
      .forEach(task => {
        activities.push({
          id: `review-${task.id}`,
          task,
          title: lang === 'ar' ? `أُرسلت للمراجعة: "${task.title}"` : `Sent for review: "${task.title}"`,
          date: task.updatedAt || task.createdAt,
          icon: Eye,
          color: 'text-cyan-500 bg-cyan-500/10 border-cyan-400/20'
        });
      });

    // 4. Revision tasks
    visibleTasks
      .filter(t => t.status === 'revision')
      .slice(0, 2)
      .forEach(task => {
        activities.push({
          id: `revision-${task.id}`,
          task,
          title: lang === 'ar' ? `طلب تعديل: "${task.title}"` : `Revision requested: "${task.title}"`,
          date: task.updatedAt || task.createdAt,
          icon: RotateCcw,
          color: 'text-orange-500 bg-orange-500/10 border-orange-500/20'
        });
      });

    // 5. New / Ready tasks
    visibleTasks
      .filter(t => ['scheduled', 'ready'].includes(t.status))
      .slice(0, 3)
      .forEach(task => {
        activities.push({
          id: `new-${task.id}`,
          task,
          title: lang === 'ar' ? `مهمة جديدة: "${task.title}"` : `New task: "${task.title}"`,
          date: task.createdAt,
          icon: CalendarDays,
          color: 'text-blue-500 bg-blue-500/10 border-blue-500/20'
        });
      });

    // Filter unique tasks by task ID & sort by date desc
    const uniqueActivities = [];
    const seenIds = new Set();
    
    activities.sort((a, b) => (new Date(b.date || 0)) - (new Date(a.date || 0)));

    for (const act of activities) {
      if (!seenIds.has(act.task.id)) {
        seenIds.add(act.task.id);
        uniqueActivities.push(act);
      }
      if (uniqueActivities.length >= 5) break;
    }

    return uniqueActivities;
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const formatHours = (seconds) => {
    const safeSeconds = Number(seconds || 0);
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    if (hours <= 0 && minutes <= 0) return '0m';
    if (hours <= 0) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  };

  const getPrayerRemaining = () => {
    if (!nextPrayer?.time) return '--:--';
    const diffMs = nextPrayer.time - currentTime;
    if (diffMs <= 0) return t.adhanTime;
    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return hours > 0
      ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      : `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getPrayerTimeLabel = (key) => {
    const time = prayerTimes ? prayerTimes[key] : '--:--';
    if (!time || time === '--:--') return '--:--';
    const [h, m] = time.split(':');
    const date = new Date();
    date.setHours(Number(h), Number(m), 0, 0);
    return date.toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getHeaderGreeting = () => {
    const isMorning = currentTime.getHours() < 12;
    if (lang === 'ar') return `${isMorning ? 'صباح الخير' : 'مساء الخير'}${userName ? `، ${userName}` : ''}`;
    return `${isMorning ? 'Good morning' : 'Good evening'}${userName ? `, ${userName}` : ''}`;
  };

  const getHeaderDigitalTime = () => {
    return currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const groupedTasks = getCategorizedTasks();
  const completedTodayCount = tasks.filter(task =>
    task.status === 'done'
    && isCompletedToday(task.completedAt)
    && (filter === 'all' || task.category === filter)
  ).length;

  // Dynamic Metric Comparison Calculations
  const todayDateStr = currentTime.toISOString().split('T')[0];
  const yesterdayDate = new Date(currentTime);
  yesterdayDate.setDate(yesterdayDate.getDate() - 1);
  const yesterdayDateStr = yesterdayDate.toISOString().split('T')[0];

  const scheduledTodayCount = tasks.filter(t =>
    t.status === 'scheduled' &&
    (filter === 'all' || t.category === filter) &&
    (
      (t.createdAt && String(t.createdAt).startsWith(todayDateStr)) ||
      (t.date && String(t.date) === todayDateStr) ||
      (!t.createdAt && !t.date)
    )
  ).length;

  const scheduledYesterdayCount = tasks.filter(t =>
    t.status === 'scheduled' &&
    (filter === 'all' || t.category === filter) &&
    (
      (t.createdAt && String(t.createdAt).startsWith(yesterdayDateStr)) ||
      (t.date && String(t.date) === yesterdayDateStr)
    )
  ).length;

  const scheduledDifference = scheduledTodayCount - scheduledYesterdayCount;
  const settingsCompanyKey = companies[settingsCompanyId] ? settingsCompanyId : (Object.keys(companies)[0] || '');
  const settingsCompany = companies[settingsCompanyKey];
  const settingsCompanyTasks = tasks.filter(task => task.category === settingsCompanyKey);
  const settingsCompanyOpenTasks = settingsCompanyTasks.filter(task => task.status !== 'done').length;
  const normalizeWorkspaceUrl = (value) => {
    const cleanValue = String(value || '').trim();
    if (!cleanValue) return '';
    if (/^https?:\/\//i.test(cleanValue)) return cleanValue;
    return `https://${cleanValue}`;
  };
  const workspaceUserAgent = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/132.0.0.0 Safari/537.36';
  const activeBrowserTab = browserTabs.find(tab => tab.id === activeBrowserTabId) || browserTabs[0];
  const workspaceUrl = activeBrowserTab?.url || 'https://www.google.com/';
  const workspaceTitle = activeBrowserTab?.title || (lang === 'ar' ? 'المتصفح الداخلي' : 'Internal Browser');
  const appContentWidthClass = 'flex-1';
  const whatsappUnreadCount = browserTabs
    .filter(tab => tab.type === 'whatsapp')
    .reduce((sum, tab) => sum + Number(tab.unread || 0), 0);
  const unreadTaskActivityCount = taskActivityNotifications.filter(item => !item.read).length;
  const syncPendingCount = syncQueue.filter(item => item.status === SYNC_OPERATION_STATUS.pending).length;
  const syncSyncingCount = syncQueue.filter(item => item.status === SYNC_OPERATION_STATUS.syncing).length;
  const syncFailedCount = syncQueue.filter(item => item.status === SYNC_OPERATION_STATUS.failed).length;
  const syncSyncedCount = syncQueue.filter(item => item.status === SYNC_OPERATION_STATUS.synced).length;
  const syncRevisionLabel = Number.isFinite(Number(syncConnection.lastServerRevision))
    ? Number(syncConnection.lastServerRevision)
    : 0;
  const isSyncServerReady = operationMode === OPERATION_MODES.online && syncConnection.enabled && Boolean(syncConnection.endpointUrl);
  const hasSyncIssue = syncFailedCount > 0 || Boolean(syncResult?.failed);
  const syncStatusTone = hasSyncIssue ? 'rose' : (isSyncServerReady ? 'emerald' : 'slate');
  const syncStatusLabel = hasSyncIssue
    ? (lang === 'ar' ? 'تحتاج مراجعة' : 'Needs review')
    : isSyncServerReady
      ? (lang === 'ar' ? 'متصل' : 'Connected')
      : (lang === 'ar' ? 'بانتظار تسجيل الدخول' : 'Waiting sign in');
  const syncStatusDescription = hasSyncIssue
    ? (lang === 'ar' ? `${syncFailedCount || syncResult?.failed || 1} عملية لم تكتمل` : `${syncFailedCount || syncResult?.failed || 1} failed item`)
    : isSyncRunning
      ? (lang === 'ar' ? 'إرسال وسحب التحديثات' : 'Pushing and pulling changes')
      : isSyncServerReady
        ? (syncConnection.autoSyncEnabled
          ? (lang === 'ar' ? `تلقائي كل ${syncConnection.autoSyncIntervalSeconds || 30} ثانية` : `Auto every ${syncConnection.autoSyncIntervalSeconds || 30}s`)
          : (lang === 'ar' ? 'جاهز للمزامنة اليدوية' : 'Ready for manual sync'))
        : (lang === 'ar' ? 'سجل الدخول للوصول إلى بيانات السيرفر' : 'Sign in to access server data');
  const syncToneClasses = {
    rose: {
      card: 'border-rose-300/70 bg-rose-50/80 text-rose-600 dark:border-rose-400/25 dark:bg-rose-500/10 dark:text-rose-300',
      icon: 'bg-rose-500 text-white shadow-rose-500/25',
      dot: 'bg-rose-500'
    },
    emerald: {
      card: 'border-emerald-300/70 bg-emerald-50/80 text-emerald-700 dark:border-emerald-400/25 dark:bg-emerald-500/10 dark:text-emerald-300',
      icon: 'bg-emerald-500 text-white shadow-emerald-500/25',
      dot: 'bg-emerald-500'
    },
    slate: {
      card: 'border-slate-200/90 bg-white/65 text-slate-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-slate-300',
      icon: 'bg-slate-900 text-white shadow-slate-900/15 dark:bg-slate-700',
      dot: 'bg-slate-400'
    }
  };
  const activeSyncTone = syncToneClasses[syncStatusTone] || syncToneClasses.slate;
  const formatSyncTimestamp = (value) => {
    if (!value) return lang === 'ar' ? 'لم تتم بعد' : 'Not yet';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return lang === 'ar' ? 'غير معروف' : 'Unknown';
    return date.toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const lastSyncLabel = formatSyncTimestamp(syncConnection.lastConnectedAt);
  const openWorkspaceUrl = (value) => {
    const normalized = normalizeWorkspaceUrl(value);
    if (!normalized) return;
    if (window.electronAPI?.openExternalLink) {
      window.electronAPI.openExternalLink(normalized);
    } else if (normalized.startsWith('http://') || normalized.startsWith('https://') || normalized.startsWith('file://')) {
      window.open(normalized, '_blank');
    } else {
      window.open(`https://${normalized}`, '_blank');
    }
  };
  const openTool = (tool) => {
    if (tool === 'chatgpt') openWorkspaceUrl('https://chat.openai.com/', 'ChatGPT', 'chatgpt');
    if (tool === 'whatsapp') openWorkspaceUrl('https://web.whatsapp.com/', 'WhatsApp', 'whatsapp');
    if (tool === 'gmail') openWorkspaceUrl('https://mail.google.com/', 'Gmail', 'gmail');
    if (tool === 'browser') openWorkspaceUrl('https://www.google.com/', 'Google', 'browser');
  };
  const launchCreativeApp = async (appId, label) => {
    if (!window.electronAPI?.launchCreativeApp) {
      setActiveAlert({
        title: lang === 'ar' ? 'غير متاح' : 'Unavailable',
        message: lang === 'ar' ? 'فتح البرامج الخارجية متاح داخل نسخة سطح المكتب فقط.' : 'Launching desktop apps is only available in the desktop build.',
        type: 'danger'
      });
      return;
    }

    const result = await window.electronAPI.launchCreativeApp(appId);
    if (!result?.ok) {
      setActiveAlert({
        title: lang === 'ar' ? 'لم يتم فتح البرنامج' : 'Could not open app',
        message: lang === 'ar'
          ? `لم أجد ${label} على الجهاز. تأكد أنه متسطب أو افتحه من Start Menu أول مرة.`
          : `${label} was not found on this device. Make sure it is installed.`,
        type: 'danger'
      });
    }
  };
  const closeBrowserTab = (tabId, event) => {
    event?.stopPropagation();
    setBrowserTabs(prev => {
      if (prev.length <= 1) {
        setActiveBrowserTabId('google');
        return [{ id: 'google', title: 'Google', url: 'https://www.google.com/', type: 'browser' }];
      }
      const index = prev.findIndex(tab => tab.id === tabId);
      const nextTabs = prev.filter(tab => tab.id !== tabId);
      if (activeBrowserTabId === tabId) {
        const nextActive = nextTabs[Math.max(0, index - 1)] || nextTabs[0];
        setActiveBrowserTabId(nextActive.id);
        setWorkspaceInput(nextActive.url);
      }
      return nextTabs;
    });
  };
  const getBrowserTabIcon = (tab) => {
    if (tab?.type === 'chatgpt') return chatgptToolIcon;
    if (tab?.type === 'whatsapp') return whatsappToolIcon;
    if (tab?.type === 'gmail') return gmailToolIcon;
    if (tab?.type === 'drive') return googleDriveToolIcon;
    if (tab?.type === 'browser') return googleToolIcon;
    return null;
  };
  const startBrowserDrag = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('button, input')) return;
    setBrowserDragOffset({
      x: e.clientX - browserPosition.x,
      y: e.clientY - browserPosition.y
    });
  };
  const handleWorkspaceSubmit = (e) => {
    e.preventDefault();
    openWorkspaceUrl(workspaceInput, lang === 'ar' ? 'متصفح داخلي' : 'Internal Browser', 'browser');
  };
  const settingsCompanyDailyHours = (() => {
    if (!settingsCompany?.workStart || !settingsCompany?.workEnd) return 0;
    const [startH, startM] = settingsCompany.workStart.split(':').map(Number);
    const [endH, endM] = settingsCompany.workEnd.split(':').map(Number);
    let minutes = (endH * 60 + endM) - (startH * 60 + startM);
    if (minutes < 0) minutes += 24 * 60;
    return Math.round((minutes / 60) * 10) / 10;
  })();
  const filteredCompanyClients = (settingsCompany?.clients || []).filter(client => client.toLowerCase().includes(clientSearch.toLowerCase()));
  const filteredCompanyManagers = (settingsCompany?.managers || []).filter(manager => manager.toLowerCase().includes(managerSearch.toLowerCase()));

  // LOADING SCREEN
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0B0E14] flex flex-col items-center justify-center text-slate-900 dark:text-white font-sans" style={{ fontFamily: '"Cairo", sans-serif' }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800&display=swap');`}</style>
        <img src={flowdeskStartupMark} alt="FlowDesk" className="w-32 h-24 object-contain mb-4 animate-pulse" />
        <p className="text-xl font-bold animate-pulse">{t.loading}</p>
      </div>
    );
  }

  const isSystemDark = typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const effectiveTheme = theme === 'system' ? (isSystemDark ? 'dark' : 'light') : theme;
  const adhanIcon = effectiveTheme === 'light' ? 'adhan-icon-wh.ico' : 'adhan icon.ico';
  const brandMark = effectiveTheme === 'light' ? flowdeskMarkLight : flowdeskMark;
  const brandAppIcon = effectiveTheme === 'light' ? flowdeskAppIconLight : flowdeskAppIcon;

  if (!authSession.isAuthenticated) {
    const loginUser = workspaceUsers.find(u => u.email?.toLowerCase() === loginDraft.email?.toLowerCase().trim());
    return (
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 font-sans overflow-hidden"
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
        style={{ fontFamily: '"Cairo", sans-serif', background: '#070B14' }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800;900&display=swap');
          @keyframes loginFadeIn { from { opacity:0; transform:translateY(28px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
          @keyframes loginGlow { 0%,100% { opacity:0.45; transform:scale(1); } 50% { opacity:0.7; transform:scale(1.08); } }
          .login-card { animation: loginFadeIn 0.55s cubic-bezier(.22,1,.36,1) both; }
          .login-glow { animation: loginGlow 5s ease-in-out infinite; }
          .login-input { background: rgba(255,255,255,0.05); border: 1.5px solid rgba(255,255,255,0.1); color:#fff; transition: border-color 0.2s, box-shadow 0.2s; }
          .login-input:focus { border-color:#6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.2); outline:none; }
          .login-input::placeholder { color: rgba(148,163,184,0.6); }
          .login-btn { background: linear-gradient(135deg,#6366f1,#8b5cf6,#a855f7); transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s; }
          .login-btn:hover { opacity:0.92; box-shadow: 0 8px 30px rgba(139,92,246,0.4); }
          .login-btn:active { transform: scale(0.97); }
        `}</style>

        {/* Ambient background orbs */}
        <div className="login-glow absolute top-[-10%] left-[-10%] w-[520px] h-[520px] rounded-full pointer-events-none" style={{background:'radial-gradient(circle,rgba(99,102,241,0.22) 0%,transparent 70%)'}} />
        <div className="login-glow absolute bottom-[-8%] right-[-5%] w-[400px] h-[400px] rounded-full pointer-events-none" style={{background:'radial-gradient(circle,rgba(168,85,247,0.18) 0%,transparent 70%)', animationDelay:'2.5s'}} />
        <div className="absolute inset-0 pointer-events-none" style={{backgroundImage:'radial-gradient(rgba(255,255,255,0.025) 1px,transparent 1px)', backgroundSize:'32px 32px'}} />

        {/* Card */}
        <div className="login-card relative z-10 w-full max-w-[420px]">
          {/* Glassmorphic card */}
          <div style={{background:'rgba(15,20,35,0.85)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'28px', backdropFilter:'blur(24px)', boxShadow:'0 32px 80px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.07)', padding:'40px 36px 36px'}}>

            {/* Logo + branding */}
            <div className="flex flex-col items-center text-center mb-8">
              <div style={{width:72, height:72, borderRadius:20, background:'linear-gradient(135deg,#6366f1,#a855f7)', padding:2, boxShadow:'0 12px 40px rgba(99,102,241,0.35)', marginBottom:16}}>
                <div style={{width:'100%', height:'100%', borderRadius:18, background:'#0B0F1A', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden'}}>
                  <img src={flowdeskAppIcon || brandAppIcon} alt="FlowDesk" style={{width:44, height:44, objectFit:'cover', borderRadius:12}} />
                </div>
              </div>
              <h1 style={{fontSize:26, fontWeight:900, color:'#fff', letterSpacing:'-0.5px', marginBottom:4}}>PixelFlow</h1>
              <p style={{fontSize:12, fontWeight:700, color:'#818cf8'}}>
                {lang === 'ar' ? 'سجّل دخولك للوصول إلى مساحة العمل' : 'Sign in to access your workspace'}
              </p>
            </div>

            {/* Avatar preview if email matched */}
            {loginUser && (
              <div className="flex items-center gap-3 mb-5 px-3.5 py-3 rounded-2xl" style={{background:'rgba(99,102,241,0.1)', border:'1px solid rgba(99,102,241,0.25)'}}>
                <div style={{width:40, height:40, borderRadius:'50%', overflow:'hidden', background:'#1e2740', flexShrink:0, border:'2px solid rgba(99,102,241,0.5)', display:'flex', alignItems:'center', justifyContent:'center'}}>
                  {loginUser.photo ? (
                    <img src={loginUser.photo} alt={loginUser.name} style={{width:'100%', height:'100%', objectFit:'cover'}} />
                  ) : (
                    <User size={20} color="#818cf8" />
                  )}
                </div>
                <div>
                  <p style={{fontSize:13, fontWeight:800, color:'#fff', margin:0}}>{loginUser.name || loginUser.email}</p>
                  <p style={{fontSize:11, fontWeight:600, color:'#818cf8', margin:0}}>{loginUser.email}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={e => { e.preventDefault(); handleLocalPreviewLogin(); }} style={{display:'flex', flexDirection:'column', gap:16}}>
              {/* Email */}
              <div>
                <label style={{display:'block', fontSize:11, fontWeight:800, color:'#94a3b8', marginBottom:8, letterSpacing:'0.04em'}}>
                  {lang === 'ar' ? 'البريد الإلكتروني' : 'EMAIL ADDRESS'}
                </label>
                <div style={{position:'relative'}}>
                  <Mail size={16} style={{position:'absolute', top:'50%', transform:'translateY(-50%)', color:'#475569', pointerEvents:'none', ...(lang === 'ar' ? {right:14} : {left:14})}} />
                  <input
                    id="login-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={loginDraft.email}
                    onChange={e => setLoginDraft(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="login-input"
                    style={{width:'100%', padding: lang === 'ar' ? '13px 42px 13px 16px' : '13px 16px 13px 42px', borderRadius:14, fontSize:14, fontWeight:600, boxSizing:'border-box'}}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label style={{display:'block', fontSize:11, fontWeight:800, color:'#94a3b8', marginBottom:8, letterSpacing:'0.04em'}}>
                  {lang === 'ar' ? 'كلمة المرور' : 'PASSWORD'}
                </label>
                <div style={{position:'relative'}}>
                  <KeyRound size={16} style={{position:'absolute', top:'50%', transform:'translateY(-50%)', color:'#475569', pointerEvents:'none', ...(lang === 'ar' ? {right:14} : {left:14})}} />
                  <input
                    id="login-password"
                    type={loginDraft.showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    value={loginDraft.password}
                    onChange={e => setLoginDraft(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="••••••••••"
                    className="login-input"
                    style={{width:'100%', padding: lang === 'ar' ? '13px 42px 13px 44px' : '13px 44px 13px 42px', borderRadius:14, fontSize:14, fontWeight:600, boxSizing:'border-box'}}
                    dir="ltr"
                  />
                  <button
                    type="button"
                    onClick={() => setLoginDraft(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                    style={{position:'absolute', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#475569', padding:'2px 4px', ...(lang === 'ar' ? {left:12} : {right:12})}}
                  >
                    {loginDraft.showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Remember me */}
              <label style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', borderRadius:12, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', cursor:'pointer', userSelect:'none'}}>
                <span style={{fontSize:12, fontWeight:700, color:'#94a3b8'}}>
                  {lang === 'ar' ? 'تذكرني على هذا الجهاز' : 'Remember me on this device'}
                </span>
                <input
                  type="checkbox"
                  checked={Boolean(loginDraft.rememberMe)}
                  onChange={e => setLoginDraft(prev => ({ ...prev, rememberMe: e.target.checked }))}
                  style={{width:16, height:16, accentColor:'#6366f1', cursor:'pointer'}}
                />
              </label>

              {/* Error banner if any */}
              {loginError && (
                <div style={{padding:'10px 14px', borderRadius:12, background:'rgba(244,63,94,0.15)', border:'1px solid rgba(244,63,94,0.3)', color:'#fecdd3', fontSize:12, fontWeight:800, textAlign:'center'}}>
                  {loginError}
                </div>
              )}

              {/* Submit */}
              <button
                id="login-submit"
                type="submit"
                className="login-btn"
                style={{width:'100%', padding:'14px 20px', borderRadius:14, border:'none', color:'#fff', fontSize:14, fontWeight:800, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:4, letterSpacing:'0.02em', boxShadow:'0 4px 20px rgba(139,92,246,0.3)'}}
              >
                <Fingerprint size={18} />
                <span>{lang === 'ar' ? 'تسجيل الدخول' : 'Sign In'}</span>
              </button>
            </form>
          </div>

        </div>
      </div>
    );
  }



  return (
    <div
      className={`app-shell density-${uiDensity} ${isMobileRuntime ? 'mobile-runtime' : ''} h-screen text-slate-900 dark:text-slate-200 font-sans flex flex-col md:flex-row overflow-hidden`}
      dir={lang === 'ar' ? 'rtl' : 'ltr'}
      style={{
        fontFamily: '"Cairo", sans-serif'
      }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;500;600;700;800&display=swap');`}</style>

      {showOnboarding && (
        <div className="onboarding-overlay fixed inset-0 z-[120] bg-slate-950/92 backdrop-blur-2xl flex items-center justify-center p-4">
          <div className="onboarding-shell" dir={onboardingData.language === 'ar' ? 'rtl' : 'ltr'}>
            <aside className="onboarding-visual">
              <img src={flowdeskAppIcon} alt="FlowDesk" className="w-16 h-16 rounded-[24px] object-cover shadow-xl border border-blue-400/20" />
              <div>
                <p className="text-xs font-black text-blue-300 uppercase tracking-[0.28em] mb-3">FlowDesk Setup</p>
                <h1 className="text-3xl font-black text-white leading-tight">
                  {onboardingData.language === 'ar' ? 'جهز مساحة عملك في دقائق' : 'Set up your workspace in minutes'}
                </h1>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                  {onboardingData.language === 'ar'
                    ? 'اختار المحافظة، ظبط البروفايل، وسجل أول شركة وعملائها عشان تبدأ منظم من أول مرة.'
                    : 'Choose your location, profile, first company, clients, and team before you start.'}
                </p>
              </div>
              <div className="space-y-3 mt-auto">
                {[
                  onboardingData.language === 'ar' ? 'Language والموقع' : 'Language & location',
                  onboardingData.language === 'ar' ? 'الملف الشخصي' : 'Profile',
                  onboardingData.language === 'ar' ? 'الشركة وClients' : 'Company & clients',
                  onboardingData.language === 'ar' ? 'مواعيد العمل' : 'Work hours'
                ].map((label, idx) => (
                  <div key={label} className={`onboarding-step-chip ${onboardingStep === idx ? 'is-active' : onboardingStep > idx ? 'is-done' : ''}`}>
                    <span>{idx + 1}</span>
                    <strong>{label}</strong>
                  </div>
                ))}
              </div>
            </aside>

            <main className="onboarding-main">
              <div className="flex items-center justify-between gap-4 mb-7">
                <div>
                  <p className="text-xs font-black text-blue-400 mb-2">{onboardingData.language === 'ar' ? 'أول تشغيل' : 'First run'}</p>
                  <h2 className="text-2xl md:text-3xl font-black text-slate-950 dark:text-white">
                    {[
                      onboardingData.language === 'ar' ? 'اختار Language والمحافظة' : 'Choose language and location',
                      onboardingData.language === 'ar' ? 'ظبط البروفايل' : 'Set up profile',
                      onboardingData.language === 'ar' ? 'سجل أول شركة' : 'Create first company',
                      onboardingData.language === 'ar' ? 'حدد نظام الدوام' : 'Set working hours'
                    ][onboardingStep]}
                  </h2>
                </div>
                <button onClick={() => finishOnboarding(true)} className="onboarding-skip">{onboardingData.language === 'ar' ? 'تخطي' : 'Skip'}</button>
              </div>

              {onboardingStep === 0 && (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-3">
                    <button onClick={() => setOnboardingData(prev => ({ ...prev, language: 'ar' }))} className={`onboarding-choice ${onboardingData.language === 'ar' ? 'is-active' : ''}`}>العربية</button>
                    <button onClick={() => setOnboardingData(prev => ({ ...prev, language: 'en' }))} className={`onboarding-choice ${onboardingData.language === 'en' ? 'is-active' : ''}`}>English</button>
                  </div>
                  <div>
                    <label className="onboarding-label">{onboardingData.language === 'ar' ? 'المحافظة الخاصة بمواقيت الأذان' : 'Prayer time governorate'}</label>
                    <select value={onboardingData.governorate} onChange={e => setOnboardingData(prev => ({ ...prev, governorate: e.target.value }))} className="onboarding-input">
                      {EGYPT_GOVERNORATES.map(gov => <option key={gov.id} value={gov.id}>{onboardingData.language === 'ar' ? gov.ar : gov.en}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {onboardingStep === 1 && (
                <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-5">
                  <div className="onboarding-photo-card">
                    <div className="w-24 h-24 rounded-[28px] bg-slate-800 border border-white/10 flex items-center justify-center overflow-hidden">
                      {userPhoto ? <img src={userPhoto} alt="User" className="w-full h-full object-cover" /> : <User size={34} className="text-slate-500" />}
                    </div>
                    <label className="onboarding-small-button">
                      <Upload size={14} />
                      <span>{onboardingData.language === 'ar' ? 'صورة' : 'Photo'}</span>
                      <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'userPhoto')} className="hidden" />
                    </label>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'اسم المستخدم' : 'User name'}</label>
                      <input value={onboardingData.userName} onChange={e => setOnboardingData(prev => ({ ...prev, userName: e.target.value }))} className="onboarding-input" placeholder={onboardingData.language === 'ar' ? 'مثال: أحمد' : 'e.g. Ahmed'} />
                    </div>
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'Job Title' : 'Role'}</label>
                      <input value={onboardingData.userRole} onChange={e => setOnboardingData(prev => ({ ...prev, userRole: e.target.value }))} className="onboarding-input" placeholder={onboardingData.language === 'ar' ? 'Designer جرافيك / آرت دايركتور' : 'Graphic Designer / Art Director'} />
                    </div>
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'Workspace Name' : 'Workspace name'}</label>
                      <input value={onboardingData.workspaceName} onChange={e => setOnboardingData(prev => ({ ...prev, workspaceName: e.target.value }))} className="onboarding-input" placeholder="FlowDesk" />
                    </div>
                  </div>
                </div>
              )}

              {onboardingStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="onboarding-label">{onboardingData.language === 'ar' ? 'اسم الشركة الافتراضية' : 'Default company name'}</label>
                    <input value={onboardingData.companyName} onChange={e => setOnboardingData(prev => ({ ...prev, companyName: e.target.value }))} className="onboarding-input" placeholder={onboardingData.language === 'ar' ? 'اسم الشركة' : 'Company Name'} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'Clients' : 'Clients'}</label>
                      <textarea dir="auto" rows="6" value={onboardingData.clientsText} onChange={e => setOnboardingData(prev => ({ ...prev, clientsText: e.target.value }))} className="onboarding-input resize-none" placeholder={onboardingData.language === 'ar' ? 'كل عميل في سطر' : 'One client per line'} />
                    </div>
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'المسؤولون' : 'Account managers'}</label>
                      <textarea dir="auto" rows="6" value={onboardingData.managersText} onChange={e => setOnboardingData(prev => ({ ...prev, managersText: e.target.value }))} className="onboarding-input resize-none" placeholder={onboardingData.language === 'ar' ? 'كل مسؤول في سطر' : 'One manager per line'} />
                    </div>
                  </div>
                </div>
              )}

              {onboardingStep === 3 && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'بداية الدوام' : 'Work starts'}</label>
                      <input type="time" value={onboardingData.workStart} onChange={e => setOnboardingData(prev => ({ ...prev, workStart: e.target.value }))} className="onboarding-input" />
                    </div>
                    <div>
                      <label className="onboarding-label">{onboardingData.language === 'ar' ? 'نهاية الدوام' : 'Work ends'}</label>
                      <input type="time" value={onboardingData.workEnd} onChange={e => setOnboardingData(prev => ({ ...prev, workEnd: e.target.value }))} className="onboarding-input" />
                    </div>
                  </div>
                  <div>
                    <label className="onboarding-label">{onboardingData.language === 'ar' ? 'سعر الساعة اختياري' : 'Hourly rate optional'}</label>
                    <input type="number" min="0" value={onboardingData.hourlyRate} onChange={e => setOnboardingData(prev => ({ ...prev, hourlyRate: e.target.value }))} className="onboarding-input" />
                  </div>
                  <div className="rounded-2xl bg-blue-600/10 border border-blue-500/20 p-4 text-sm text-blue-300">
                    {onboardingData.language === 'ar' ? 'بعد الإنهاء، تقدر تعدل كل البيانات دي من الإعدادات في أي وقت.' : 'You can edit all of this later from Settings.'}
                  </div>
                </div>
              )}

              <div className="onboarding-actions">
                <button disabled={onboardingStep === 0} onClick={() => setOnboardingStep(step => Math.max(0, step - 1))} className="onboarding-back">
                  {onboardingData.language === 'ar' ? 'رجوع' : 'Back'}
                </button>
                <button onClick={() => onboardingStep >= 3 ? finishOnboarding(false) : setOnboardingStep(step => step + 1)} className="onboarding-next">
                  {onboardingStep >= 3 ? (onboardingData.language === 'ar' ? 'ابدأ استخدام FlowDesk' : 'Start using FlowDesk') : (onboardingData.language === 'ar' ? 'التالي' : 'Next')}
                </button>
              </div>
            </main>
          </div>
        </div>
      )}

      {/* MOBILE BACKDROP OVERLAY FOR SIDEBAR DRAWER */}
      {isMobileSidebarOpen && (
        <div 
          onClick={() => setIsMobileSidebarOpen(false)}
          className="fixed inset-0 bg-black/70 z-[400] md:hidden backdrop-blur-sm transition-opacity"
        />
      )}

      {/* SIDEBAR */}
      <aside className={`app-sidebar premium-sidebar bg-white dark:bg-[#131824] flex flex-col w-[280px] min-w-[280px] shrink-0 border-r dark:border-white/10 border-slate-200 z-[410] h-screen fixed inset-y-0 ${lang === 'ar' ? 'right-0' : 'left-0'} md:right-auto md:left-auto md:sticky md:top-0 transition-transform duration-300 ${isMobileSidebarOpen ? '!translate-x-0 shadow-2xl !flex' : (lang === 'ar' ? 'translate-x-full md:translate-x-0 hidden md:flex' : '-translate-x-full md:translate-x-0 hidden md:flex')}`}>
        <div className="px-5 pt-6 pb-3 flex items-center justify-between">
          <div className="sidebar-wordmark" dir="ltr">
            <img src={brandMark} alt="" className="sidebar-brand-mark-img" aria-hidden="true" />
            <div className="sidebar-brand-text">
              <div className="sidebar-brand-wordtext" aria-label="FlowDesk">
                <span>Flow</span><strong>Desk</strong>
              </div>
              <div className="sidebar-brand-subtitle">
                WORKFLOW. ORGANIZED. TOGETHER.
              </div>
            </div>
          </div>
          <button 
            type="button" 
            onClick={() => setIsMobileSidebarOpen(false)}
            className="md:hidden p-2 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
          >
            <X size={20} />
          </button>
        </div>
        <nav className="flex-1 px-4 space-y-2 overflow-y-auto scrollbar-thin">
          <p className="sidebar-section-label mt-2 mb-1">{t.mainMenu}</p>
          <button onClick={() => { setFilter('all'); setIsMobileSidebarOpen(false); }} className={`sidebar-item hover-effect ${filter === 'all' ? 'sidebar-item-active' : 'sidebar-item-idle'}`}><div className="sidebar-icon bg-blue-600 text-white"><Layout size={18} /></div><span className="font-bold text-sm">{t.dashboard}</span></button>
          <div className="pt-5 pb-1 sidebar-section-label">
            <span>{t.companies}</span>
          </div>
          <div className="space-y-2">
            {Object.keys(visibleCompanies).length === 0 && (
              <div className="px-3 py-2.5 text-xs font-extrabold text-slate-400 text-center rounded-xl bg-slate-100/50 dark:bg-slate-800/40 border border-dashed border-slate-300 dark:border-slate-700">
                {lang === 'ar' ? 'لا يوجد شركات' : 'No Companies'}
              </div>
            )}
            {Object.entries(visibleCompanies).map(([key, config]) => {
              const isOpen = isCompanyOpen(key);
              const isClocked = config.isClockedIn;
              const activeCompanyKey = filter !== 'all' ? filter : (visibleCompanyKeys[0] || '');
              const isSelected = activeCompanyKey === key;
              return (
                <div
                  key={key}
                  onClick={() => { setFilter(key); setIsMobileSidebarOpen(false); }}
                  className={`sidebar-item hover-effect flex items-center justify-between cursor-pointer group/item transition-all ${
                    isSelected
                      ? `sidebar-item-active bg-blue-600/20 dark:bg-blue-500/25 text-blue-600 dark:text-blue-300 border-blue-500/40 shadow-lg shadow-blue-500/15 ${lang === 'ar' ? 'border-r-4 border-r-blue-500' : 'border-l-4 border-l-blue-500'}`
                      : 'sidebar-item-idle opacity-85 hover:opacity-100'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {config.logo ? (
                      <img
                        src={config.logo}
                        alt="logo"
                        className="w-9 h-9 rounded-xl object-contain bg-white border border-white/80 dark:border-white/10 shadow-sm shrink-0"
                      />
                    ) : (
                      <div className={`sidebar-icon ${config.theme.bg} text-white font-bold shrink-0`}>
                        {config.name.charAt(0)}
                      </div>
                    )}
                    <div className={`${lang === 'ar' ? 'text-right' : 'text-left'} min-w-0 flex-1`}>
                      <span className={`block text-sm transition-colors truncate ${isSelected ? 'font-black text-slate-950 dark:text-white' : 'font-bold text-slate-700 dark:text-slate-300'}`}>
                        {config.name}
                      </span>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="text-[10px] font-extrabold text-slate-400 dark:text-slate-400">
                          {isOpen ? (lang === 'ar' ? 'مفتوح' : 'Open') : (lang === 'ar' ? 'مغلق' : 'Closed')}
                        </span>
                        <span className="text-slate-500/60 dark:text-slate-600 text-[10px]">•</span>
                        {isClocked ? (
                          <span className="inline-flex items-center gap-1 text-[9.5px] font-black text-emerald-500 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-md leading-none shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            {lang === 'ar' ? 'يعمل الآن' : 'Active'}
                          </span>
                        ) : (
                          <span className="text-[9.5px] font-bold text-slate-400 dark:text-slate-500">
                            {lang === 'ar' ? 'غير نشط' : 'Inactive'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            {hasPermission('manageCompany') && currentUser.accessRole === USER_ROLES.owner && (
              <button
                type="button"
                onClick={() => { setNewCompanyName(''); setActiveModal('addCompany'); setIsMobileSidebarOpen(false); }}
                className="w-full mt-2.5 p-3 rounded-2xl border-2 border-dashed border-slate-300/80 dark:border-white/15 hover:border-blue-500/60 dark:hover:border-blue-400/60 bg-white/40 dark:bg-white/[0.02] hover:bg-blue-500/10 dark:hover:bg-blue-500/10 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center justify-center gap-2 transition-all group cursor-pointer"
              >
                <div className="w-6 h-6 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-all shrink-0">
                  <Plus size={15} />
                </div>
                <span className="text-xs font-black">{lang === 'ar' ? 'إضافة شركة جديدة' : 'Add New Company'}</span>
              </button>
            )}
          </div>

        </nav>
        <div className="sidebar-footer">
          {/* User info + logout */}
          <div className="flex items-center gap-2 mb-2 px-1">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-blue-600 to-purple-600 flex items-center justify-center overflow-hidden shrink-0 text-white font-black text-xs shadow-md">
              {currentUser.photo
                ? <img src={currentUser.photo} alt="avatar" className="w-full h-full object-cover" />
                : (currentUser.name || currentUser.email || 'U')[0].toUpperCase()
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-black text-slate-800 dark:text-white truncate leading-tight">{currentUser.name || (lang === 'ar' ? 'المستخدم' : 'User')}</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate leading-tight" dir="ltr">{currentUser.email}</p>
            </div>
            <button
              onClick={handleLocalPreviewLogout}
              title={lang === 'ar' ? 'تسجيل الخروج' : 'Sign Out'}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-rose-500/10 hover:bg-rose-500/20 text-rose-500 border border-rose-500/20 hover:border-rose-500/40 transition-all hover-effect shrink-0 cursor-pointer"
            >
              <LogOut size={15} />
            </button>
          </div>

          <div className="flex gap-2">
            <button onClick={() => { setActiveModal('settings'); setIsMobileSidebarOpen(false); }} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl bg-white/55 dark:bg-white/[0.04] border border-white/70 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white hover-effect"><Settings size={18} /> <span>{t.settings}</span></button>
            <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="w-12 flex items-center justify-center rounded-2xl bg-white/55 dark:bg-white/[0.04] border border-white/70 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white hover-effect">{theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}</button>
          </div>
        </div>

      </aside>

      {/* MOBILE BOTTOM STICKY NAVIGATION BAR (ICONS ONLY) */}
      {!selectedTask && (
        <nav className="mobile-bottom-nav md:hidden">
          <button 
            type="button"
            onClick={() => { setFilter('all'); setBoardView('scheduled'); setIsNotificationPanelOpen(false); setActiveModal(null); }} 
            className={filter === 'all' && boardView === 'scheduled' && !isNotificationPanelOpen && !activeModal ? 'is-active' : ''}
            title={t.all}
          >
            <LayoutGrid size={20} />
          </button>

          {/* NOTIFICATIONS BUTTON IN MOBILE BOTTOM BAR */}
          <button 
            type="button"
            onClick={() => setIsNotificationPanelOpen(!isNotificationPanelOpen)}
            className={isNotificationPanelOpen ? 'is-active' : ''}
            title={lang === 'ar' ? 'الإشعارات' : 'Notifications'}
          >
            <div className="relative inline-flex items-center justify-center">
              <Bell size={20} />
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-1.5 -right-2.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white font-black text-[9.5px] flex items-center justify-center border-2 border-white dark:border-slate-900 shadow-md animate-pulse z-10">
                  {notifications.filter(n => !n.read).length > 99 ? '99+' : notifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
          </button>

          <button 
            type="button"
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            title={theme === 'dark' ? (lang === 'ar' ? 'الوضع الفاتح' : 'Light Mode') : (lang === 'ar' ? 'الوضع الداكن' : 'Dark Mode')}
          >
            {theme === 'dark' ? <Sun size={20} className="text-amber-400" /> : <Moon size={20} className="text-indigo-400" />}
          </button>

          <button 
            type="button"
            onClick={() => { setActiveModal(activeModal === 'settings' ? null : 'settings'); setIsNotificationPanelOpen(false); }}
            className={activeModal === 'settings' ? 'is-active' : ''}
            title={t.settings}
          >
            <Settings size={20} />
          </button>
        </nav>
      )}

      {/* MAIN CONTENT */}
      <main
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`${appContentWidthClass} min-h-screen pb-10 overflow-y-auto overflow-x-hidden relative transition-[width] duration-300`}
      >
        {/* PULL TO REFRESH INDICATOR */}
        {(pullY > 0 || isRefreshing) && (
          <div
            className="w-full flex items-center justify-center transition-all duration-150 overflow-hidden pointer-events-none sticky top-0 z-40 bg-transparent pt-2 pb-1"
            style={{ height: `${pullY + 10}px`, opacity: Math.min(pullY / 30, 1) }}
          >
            <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-600 dark:bg-blue-500 text-white text-xs font-black shadow-lg shadow-blue-500/30 border border-blue-400/30">
              <RefreshCw size={14} className={isRefreshing ? "animate-spin" : ""} style={{ transform: `rotate(${pullY * 4.5}deg)` }} />
              <span>
                {isRefreshing
                  ? (lang === 'ar' ? 'جاري التحديث...' : 'Refreshing...')
                  : pullY >= 45
                  ? (lang === 'ar' ? 'اترك للتحديث' : 'Release to refresh')
                  : (lang === 'ar' ? 'اسحب للتحديث' : 'Pull to refresh')}
              </span>
            </div>
          </div>
        )}

        <header className="premium-header px-4 md:px-8 py-3 sticky top-0 z-30 backdrop-blur-2xl border-b border-white/70 dark:border-white/10">
          <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 mb-2.5">
            <div className="mobile-header-top flex items-center justify-between gap-3 w-full xl:w-auto">
              <div className="flex items-center gap-3 w-full">
                <div 
                  onClick={() => { setActiveModal('settings'); setSettingsTab('profile'); }}
                  className="flex md:hidden items-center justify-between gap-3.5 cursor-pointer group min-w-0 py-1 w-full"
                  title={lang === 'ar' ? 'فتح الملف الشخصي' : 'Open Profile'}
                >
                  {/* TEXT ON THE LEFT */}
                  <div className="flex flex-col justify-center min-w-0 space-y-0.5 text-left">
                    <span className="text-sm sm:text-base font-extrabold text-slate-600 dark:text-slate-200 leading-tight flex items-center gap-1.5">
                      <span>{lang === 'ar' ? 'أهلاً بك' : 'Welcome back'}</span>
                      <span className="text-base">👋</span>
                    </span>
                    <span className="text-xl sm:text-2xl font-black text-slate-950 dark:text-white truncate max-w-[200px] sm:max-w-[280px] tracking-tight">
                      {userName || currentUser.name || (lang === 'ar' ? 'مصمم' : 'Designer')}
                    </span>
                  </div>

                  {/* IMAGE ON THE FAR RIGHT WITH SUBTLE WHITE BORDER */}
                  <div className="w-13 h-13 sm:w-14 sm:h-14 rounded-2xl overflow-hidden shrink-0 group-hover:scale-105 transition-all bg-slate-100 dark:bg-slate-800 flex items-center justify-center border-2 border-white dark:border-white/20 shadow-md ring-1 ring-slate-200 dark:ring-slate-700/60">
                    {userPhoto ? (
                      <img src={userPhoto} className="w-full h-full object-cover" alt="User" />
                    ) : (
                      <User size={26} className="text-slate-400" />
                    )}
                  </div>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-3">
                <button
                  type="button"
                  className="relative group cursor-pointer hover-effect shrink-0"
                  onClick={() => { setActiveModal('settings'); setSettingsTab('profile'); }}
                >
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-blue-600 p-0.5 shadow-xl group-hover:scale-105 transition-transform duration-300">
                    <div className="w-full h-full rounded-[14px] bg-slate-50 dark:bg-[#0B0E14] flex items-center justify-center overflow-hidden">
                      {userPhoto ? <img src={userPhoto} className="w-full h-full object-cover" alt="User" /> : <User size={20} className="text-slate-500" />}
                    </div>
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-blue-500 border-2 border-white dark:border-[#0B0E14] rounded-full"></span>
                </button>

                {/* NOTIFICATION BELL BUTTON & DESKTOP DROPDOWN */}
                <div className="relative inline-block">
                  <button
                    type="button"
                    onClick={() => setIsNotificationPanelOpen(!isNotificationPanelOpen)}
                    className="relative p-2.5 rounded-2xl bg-white/70 dark:bg-white/[0.04] border border-white/70 dark:border-white/10 text-slate-700 dark:text-slate-200 hover:bg-blue-500/10 hover:text-blue-600 dark:hover:text-blue-400 transition-all cursor-pointer shadow-sm group"
                    title={lang === 'ar' ? 'الإشعارات' : 'Notifications'}
                  >
                    <Bell size={20} className="transition-transform group-hover:rotate-12" />
                    {notifications.filter(n => !n.read).length > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white font-black text-[10px] flex items-center justify-center border-2 border-white dark:border-slate-900 shadow-md animate-pulse">
                        {notifications.filter(n => !n.read).length > 9 ? '+9' : notifications.filter(n => !n.read).length}
                      </span>
                    )}
                  </button>

                  {/* INVISIBLE CLICK OUTSIDE BACKDROP FOR DESKTOP */}
                  {isNotificationPanelOpen && (
                    <div 
                      className="hidden md:block fixed inset-0 z-40 bg-transparent"
                      onClick={() => setIsNotificationPanelOpen(false)}
                    />
                  )}

                  {/* DESKTOP DROPDOWN POPUP */}
                  {isNotificationPanelOpen && (
                    <div className="hidden md:block absolute top-full mt-2.5 ltr:left-0 rtl:right-0 w-80 sm:w-96 max-w-[calc(100vw-3rem)] bg-white dark:bg-[#131722]/98 border border-slate-200 dark:border-slate-700/80 rounded-3xl shadow-2xl backdrop-blur-2xl p-4 space-y-3.5 z-[100] font-sans text-slate-900 dark:text-slate-100 animate-fadeIn">
                      {renderNotificationContent()}
                    </div>
                  )}
                </div>

                <div className="hidden sm:block min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">{lang === 'ar' ? 'الحساب' : 'Account'}</p>
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="max-w-[150px] truncate text-sm font-black text-slate-950 dark:text-white">{currentUser.name || userName || currentUser.email || 'FlowDesk'}</p>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black ${isSyncServerReady ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isSyncServerReady ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                      {isSyncServerReady ? (lang === 'ar' ? 'متصل' : 'Online') : (lang === 'ar' ? 'غير متصل' : 'Offline')}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden md:block min-w-0">
              <div className="mb-1 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-lg border border-blue-400/20 bg-blue-500/10 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-blue-500 dark:text-blue-300">
                  {workspaceName || (lang === 'ar' ? 'مساحة العمل' : 'Workspace')}
                </span>
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400 capitalize">
                  {currentUser?.title || (
                    {
                      owner: lang === 'ar' ? 'مالك مساحة العمل' : 'Workspace Owner',
                      manager: lang === 'ar' ? 'مدير حسابات' : 'Account Manager',
                      designer: lang === 'ar' ? 'مصمم جرافيك' : 'Graphic Designer',
                      client: lang === 'ar' ? 'عميل' : 'Client'
                    }[currentUser?.accessRole] || userRole || (lang === 'ar' ? 'جاهز لتنظيم الأولويات' : 'Ready to organize priorities')
                  )}
                </span>
              </div>
              <h2 className="text-xl md:text-[23px] font-extrabold text-slate-950 dark:text-white leading-tight tracking-tight">
                {getHeaderGreeting()}
              </h2>
              <p className="mt-0.5 mb-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                {lang === 'ar' ? 'خلينا نرتب مهام اليوم ونحافظ على سير العمل بوضوح.' : "Let's keep today's tasks clear, focused, and moving."}
              </p>

              {/* DAILY PROGRESS COMPLETION BAR */}
              {(() => {
                const todayTotal = tasks.length || 1;
                const percent = Math.round((completedTodayCount / Math.max(todayTotal, 1)) * 100);
                return (
                  <div className="mt-1.5 mb-2 flex items-center gap-3 max-w-sm">
                    <div className="flex-1 h-2.5 bg-slate-200 dark:bg-[#0c121e] rounded-full overflow-hidden p-0.5 border border-slate-300/50 dark:border-white/10 shadow-inner">
                      <div
                        className="h-full bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400 rounded-full transition-all duration-700 shadow-[0_0_10px_rgba(56,189,248,0.35)]"
                        style={{ width: `${Math.min(percent, 100)}%` }}
                      />
                    </div>
                    <span className="text-[11px] font-black text-blue-500 dark:text-cyan-400 shrink-0 flex items-center gap-1">
                      <span>{percent}%</span>
                      <span className="text-slate-400 font-bold">{lang === 'ar' ? 'إنجاز اليوم' : 'completed'}</span>
                    </span>
                  </div>
                );
              })()}
              {isElectron() && (
                <div className="header-context-strip">
                  <span className="header-context-chip">
                    <CalendarDays size={14} />
                    <strong>{currentTime.toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
                  </span>
                  <span className="header-context-chip" dir="ltr">
                    <Clock size={14} />
                    <strong className="font-mono tracking-wider">{getHeaderDigitalTime()}</strong>
                  </span>
                  <span className="header-context-chip">
                    <MapPin size={14} />
                    <strong>{EGYPT_GOVERNORATES.find(g => g.id === governorate)?.[lang === 'ar' ? 'ar' : 'en'] || governorate}</strong>
                  </span>
                </div>
              )}
            </div>

            {isElectron() && (
              <div className="flex flex-wrap items-center gap-4">
                {nextPrayer && (
                  <div className="prayer-now-card">
                    <button onClick={() => setShowAllPrayersModal(true)} className="flex items-center gap-3 min-w-0 text-start flex-1">
                      <div className={`prayer-now-icon ${nextPrayer.time <= currentTime ? 'is-active' : ''}`}>
                        <img src={adhanIcon} alt="Adhan" className="w-5 h-5 object-contain" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 font-extrabold uppercase tracking-wider">{nextPrayer.time <= currentTime ? t.adhanTime : t.upcomingPrayer}</p>
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-base text-slate-950 dark:text-white truncate">{nextPrayer.name}</h3>
                          <span className="hidden sm:inline-block w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                          <span className="hidden sm:inline text-xs font-bold text-slate-500 dark:text-slate-400">{EGYPT_GOVERNORATES.find(g => g.id === governorate)?.[lang === 'ar' ? 'ar' : 'en'] || governorate}</span>
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setShowAllPrayersModal(true)} className="prayer-countdown">
                        <Clock size={13} />
                        <span>{getPrayerRemaining()}</span>
                      </button>
                      <button onClick={() => setIsMuted(!isMuted)} className="prayer-mini-button hidden sm:flex" title={isMuted ? 'Unmute adhan' : 'Mute adhan'}>
                        {isMuted ? <VolumeX size={15} /> : <Volume2 size={15} />}
                      </button>
                    </div>
                  </div>
                )}
                {!nextPrayer && (
                  <button onClick={() => setShowAllPrayersModal(true)} className="prayer-now-card justify-center text-blue-400 font-bold">
                    <MapPin size={17} />
                    <span>{t.showPrayerTimes}</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-1 w-full mt-1">
            <div className="relative flex-1 min-w-0">
              <Search className={`absolute ${lang === 'ar' ? 'right-4' : 'left-4'} top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400`} size={18} />
              <input
                type="text"
                dir="auto"
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full app-surface rounded-xl py-2.5 ${lang === 'ar' ? 'pr-11 pl-4' : 'pl-11 pr-4'} text-sm text-slate-900 dark:text-white focus:outline-none focus:border-[#2552c7]`}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowUrgentOnly(!showUrgentOnly)}
              title={showUrgentOnly ? (lang === 'ar' ? 'عاجل فقط مفعل' : 'Urgent Only Active') : (lang === 'ar' ? 'فلتر عاجل فقط' : 'Filter Urgent Only')}
              className={`shrink-0 min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2.5 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 border cursor-pointer whitespace-nowrap ${
                showUrgentOnly
                  ? 'bg-rose-600 text-white shadow-lg shadow-rose-500/30 border-rose-400 animate-pulse'
                  : 'bg-rose-500/15 text-rose-500 dark:text-rose-400 border-rose-500/35 hover:bg-rose-500/25 hover:border-rose-500/50 shadow-sm'
              }`}
            >
              <Flame size={16} className={showUrgentOnly ? 'animate-bounce' : 'text-rose-500'} />
              <span className="hidden sm:inline">{showUrgentOnly ? (lang === 'ar' ? 'عاجل فقط مفعل 🔥' : 'Urgent Active 🔥') : (lang === 'ar' ? 'عاجل فقط 🔥' : 'Urgent Only 🔥')}</span>
              <span className="inline sm:hidden font-black text-xs">{lang === 'ar' ? 'عاجل 🔥' : 'Urgent 🔥'}</span>
            </button>
            <button
              type="button"
              onClick={handleResetLocalDataAndRehydrate}
              title="Reset Local Data & Reload from Server"
              className="shrink-0 min-h-[44px] px-3.5 sm:px-4 py-2.5 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 border border-indigo-500/40 bg-indigo-500/15 text-indigo-400 hover:bg-indigo-500/25 hover:border-indigo-500 cursor-pointer shadow-sm active:scale-95 whitespace-nowrap"
            >
              <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Reset Local Data & Reload from Server</span>
              <span className="inline sm:hidden">Reload Server</span>
            </button>
          </div>
        </header>




        {/* Board */}

        <div className="board-scroll-area mobile-tab-animate p-3 pt-4 md:px-8 md:pt-6 md:pb-4 overflow-x-auto" key={`${boardView}-${filter}`}>
          {/* STATS CARDS GRID IN NORMAL DOCUMENT FLOW */}
          <div className="stats-grid-2x2 grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-2.5 sm:gap-3 mb-4 sm:mb-6">
            <div className="metric-card group cursor-pointer hover-effect">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400" />
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/25 shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Briefcase size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold mb-0.5 truncate">{t.scheduledTasks}</p>
                <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-none">{groupedTasks.scheduled.length}</p>
                <p className={`hidden sm:flex text-[11px] font-extrabold mt-1 items-center gap-1 ${
                  scheduledDifference > 0
                    ? 'text-emerald-500 dark:text-emerald-400'
                    : scheduledDifference < 0
                    ? 'text-rose-500 dark:text-rose-400'
                    : 'text-slate-400 dark:text-slate-400'
                }`}>
                  <span>
                    {scheduledDifference > 0
                      ? `+${scheduledDifference} ${lang === 'ar' ? 'عن الأمس' : 'from yesterday'}`
                      : scheduledDifference < 0
                      ? `${scheduledDifference} ${lang === 'ar' ? 'عن الأمس' : 'from yesterday'}`
                      : (lang === 'ar' ? 'نفس العدد كالأمس' : 'Same as yesterday')}
                  </span>
                </p>
              </div>
            </div>

            <div className="metric-card group cursor-pointer hover-effect">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-amber-300" />
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-md shadow-amber-500/25 shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Zap size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold mb-0.5 truncate">{t.inProgress}</p>
                <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-none">{groupedTasks.in_progress.length}</p>
                <p className="hidden sm:flex text-[11px] font-extrabold text-amber-500 dark:text-amber-400 mt-1 items-center gap-1">
                  <span>⚡ {groupedTasks.in_progress.length} {lang === 'ar' ? 'يعمل الآن فعلياً' : 'actively in progress'}</span>
                </p>
              </div>
            </div>

            <div className="metric-card group cursor-pointer hover-effect">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-300" />
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 text-white flex items-center justify-center shadow-md shadow-cyan-500/25 shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Eye size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold mb-0.5 truncate">{t.clientReview}</p>
                <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-none">{groupedTasks.review.length}</p>
                <p className="hidden sm:flex text-[11px] font-extrabold text-cyan-500 dark:text-cyan-400 mt-1 items-center gap-1">
                  <span>👁️ {groupedTasks.review.length} {lang === 'ar' ? 'في انتظار الاعتماد' : 'awaiting client'}</span>
                </p>
              </div>
            </div>

            <div className="metric-card group cursor-pointer hover-effect">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-400 via-teal-500 to-emerald-300" />
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 shrink-0 group-hover:scale-110 transition-transform duration-300">
                <CheckCircle size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-slate-400 font-bold mb-0.5 truncate">{t.completedToday}</p>
                <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-none">{completedTodayCount}</p>
                <p className="hidden sm:flex text-[11px] font-extrabold text-emerald-500 dark:text-emerald-400 mt-1 items-center gap-1">
                  <span>✨ {completedTodayCount} {lang === 'ar' ? 'مكتملة اليوم' : 'completed today'}</span>
                </p>
              </div>
            </div>
          </div>
          <div className={boardView !== 'all' ? "w-full" : "w-full min-w-[1240px] flex gap-4"}>
            {['overdue', 'urgent', 'scheduled', 'ready', 'in_progress', 'review', 'revision', 'done']
              .filter(statusKey => {
                if (boardView !== 'all') return boardView === statusKey;
                if (['overdue', 'urgent'].includes(statusKey)) return (groupedTasks[statusKey]?.length || 0) > 0;
                return true;
              })
              .map(statusKey => {
              const rawStatusTasks = boardView === 'scheduled'
                ? [...groupedTasks.scheduled, ...(groupedTasks.ready || []), ...groupedTasks.in_progress, ...groupedTasks.review, ...groupedTasks.revision, ...groupedTasks.done, ...groupedTasks.overdue, ...groupedTasks.urgent]
                : (groupedTasks[statusKey] || []);
              const statusTasks = (boardView === 'scheduled' && taskStatusFilter !== 'all')
                ? rawStatusTasks.filter(t => t.status === taskStatusFilter)
                : rawStatusTasks;
              const statusConfig = {
                overdue: { label: t.overdue || 'Overdue', color: 'bg-rose-500', icon: AlertTriangle },
                urgent: { label: t.urgentTasks || 'Urgent', color: 'bg-rose-500', icon: Flame },
                scheduled: { label: 'New', color: 'bg-blue-500', icon: Calendar },
                ready: { label: 'Ready', color: 'bg-purple-500', icon: CheckSquare },
                in_progress: { label: 'Working', color: 'bg-amber-500', icon: Zap },
                review: { label: 'Review', color: 'bg-cyan-500', icon: Eye },
                revision: { label: 'Edit', color: 'bg-orange-500', icon: RotateCcw },
                done: { label: 'Confirm', color: 'bg-emerald-500', icon: CheckCircle }
              }[statusKey];
              const StatusIcon = statusConfig.icon;
              const isCompactEmptyColumn = boardView === 'all' && statusTasks.length === 0;
              const usesCompactTaskCards = boardView === 'all' || boardView === 'scheduled';
              const isCompanyOverview = false;
              const tasksByCompany = isCompanyOverview
                ? Object.entries(statusTasks.reduce((acc, task) => {
                    const key = task.category || 'unassigned';
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(task);
                    return acc;
                  }, {}))
                : [];
              return (
                <div key={statusKey} className={`flex flex-col h-full ${boardView !== 'all' ? 'w-full' : `kanban-column status-${statusKey} ${isCompactEmptyColumn ? 'kanban-column-empty flex-[0.55] min-w-[172px]' : `flex-1 min-w-[240px] ${['overdue', 'urgent'].includes(statusKey) && statusTasks.length > 0 ? 'attention-column' : ''}`}`}`} onDragOver={(e) => { if (!draggedTaskId) return; const draggedTask = tasks.find(task => String(task.id) === String(draggedTaskId)); if (statusKey === 'urgent' ? !canControlTaskWork : !canMoveTaskToStatus(draggedTask, statusKey)) return; e.preventDefault(); setDragOverColumn(statusKey); }} onDrop={(e) => { e.preventDefault(); if (draggedTaskId) { const draggedTask = tasks.find(task => String(task.id) === String(draggedTaskId)); if (statusKey === 'urgent' && canControlTaskWork) { setTasks(prev => { const updatedTasks = prev.map(task => task.id === draggedTaskId ? { ...task, isUrgent: true } : task); queueTaskSync(updatedTasks.find(task => String(task.id) === String(draggedTaskId))); return updatedTasks; }); } else if (!['overdue'].includes(statusKey) && canMoveTaskToStatus(draggedTask, statusKey)) { moveTask(draggedTaskId, statusKey); } setDraggedTaskId(null); setDragOverColumn(null); } }}>
                  
                  {/* TOP HEADER PANEL FOR SCHEDULED VIEW */}
                  {boardView === 'scheduled' && statusKey === 'scheduled' && (
                    <div className="w-full bg-white dark:bg-[#151921] border border-slate-200/80 dark:border-white/10 rounded-2xl p-4 sm:p-5 mb-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                          {lang === 'ar' ? 'مهامي' : 'My Tasks'}
                        </h2>
                        <p className="text-xs sm:text-sm font-bold text-slate-400 dark:text-slate-300 mt-1">
                          {lang === 'ar' ? 'إليك المهام المجدولة القادمة.' : 'Here are your upcoming tasks.'}
                        </p>
                      </div>

                      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                        <CustomCompanyFilterDropdown filter={filter} setFilter={setFilter} visibleCompanies={visibleCompanies} lang={lang} />
                        <CustomStatusFilterDropdown statusFilter={taskStatusFilter} setStatusFilter={setTaskStatusFilter} lang={lang} />

                        <div className="flex items-center p-1 bg-slate-100 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 text-xs font-bold overflow-x-auto w-full sm:w-auto">
                          {[
                            { id: 'today', label: lang === 'ar' ? 'اليوم' : 'Today' },
                            { id: 'tomorrow', label: lang === 'ar' ? 'غداً' : 'Tomorrow' },
                            { id: 'week', label: lang === 'ar' ? 'هذا الأسبوع' : 'This Week' },
                            { id: 'month', label: lang === 'ar' ? 'هذا الشهر' : 'This Month' }
                          ].map(tab => (
                            <button
                              key={tab.id}
                              type="button"
                              onClick={() => setDateFilter(dateFilter === tab.id ? 'all' : tab.id)}
                              className={`flex-1 sm:flex-none shrink-0 whitespace-nowrap px-3.5 py-1.5 rounded-lg transition-all text-center ${
                                dateFilter === tab.id
                                  ? 'bg-blue-600 text-white font-extrabold shadow-md shadow-blue-500/25'
                                  : 'text-slate-600 dark:text-slate-300 hover:text-slate-950 dark:hover:text-white'
                              }`}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {boardView !== 'scheduled' && (
                    <div className="flex items-center justify-between mb-4 px-1 pb-3 border-b border-slate-200/80 dark:border-white/10">
                      <h3 className="text-slate-700 dark:text-slate-200 font-bold text-base flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-xl ${statusConfig.color} text-white flex items-center justify-center shadow-[0_0_14px_rgba(244,63,94,.24)]`}><StatusIcon size={15} /></div>
                        {statusConfig.label}
                      </h3>
                      <div className="flex items-center gap-2">
                        {statusKey === 'done' && statusTasks.length > 0 && (
                          <button
                            onClick={handleDeleteAllDone}
                            className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-colors"
                            title={t.deleteAllDone}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                        <span className="text-slate-500 dark:text-slate-300 bg-white border border-slate-200 dark:border-transparent dark:bg-white/5 px-2.5 py-1 rounded-md text-xs font-mono">{statusTasks.length}</span>
                      </div>
                    </div>
                  )}
                  <div className={`relative transition-colors duration-200 ${boardView === 'all' ? `flex flex-col gap-3 ${isCompactEmptyColumn ? 'min-h-[210px]' : 'min-h-[420px]'}` : statusKey === 'scheduled' ? 'flex flex-col gap-2.5 w-full' : usesCompactTaskCards ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3' : isCompanyOverview ? 'space-y-5' : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5'} ${dragOverColumn === statusKey ? 'bg-blue-50/80 border border-blue-200 dark:border-blue-400/30 dark:bg-blue-500/10 rounded-2xl p-3' : ''}`}>
                    {statusTasks.length === 0 ? (
                      statusKey === 'scheduled' && isAddingCardInScheduled ? (
                        <div className="p-4 bg-white dark:bg-[#1f2125] rounded-2xl shadow-xl border border-blue-500/50 dark:border-blue-500/40 space-y-3 animate-in fade-in duration-200 w-full text-slate-800 dark:text-slate-100 text-right my-2">
                          <textarea
                            rows={3}
                            value={quickCardTitle}
                            onChange={(e) => setQuickCardTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleQuickAddCard();
                              }
                              if (e.key === 'Escape') {
                                setIsAddingCardInScheduled(false);
                                setQuickCardTitle('');
                              }
                            }}
                            placeholder={lang === 'ar' ? "أدخل عنوان المهمة..." : "Enter a title for this card..."}
                            dir="auto"
                            className="w-full p-3 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 resize-none font-medium"
                            autoFocus
                          />

                          {filter === 'all' ? (
                            <div className="space-y-2.5">
                              {(() => {
                                const companyOptions = Object.entries(visibleCompanies)
                                  .filter(([, cfg]) => cfg && typeof cfg === 'object')
                                  .map(([key, config]) => ({
                                    id: key,
                                    name: typeof config.name === 'string' && config.name ? config.name : key,
                                    bg: config.theme?.bg || 'bg-blue-500'
                                  }));
                                const activeCompanyVal = quickCardCompany || (companyOptions[0]?.id || '');
                                return (
                                  <CustomQuickSelect
                                    label={lang === 'ar' ? 'الشركة:' : 'Company:'}
                                    value={activeCompanyVal}
                                    options={companyOptions}
                                    onChange={key => { setQuickCardCompany(key); setQuickCardClient(''); }}
                                  />
                                );
                              })()}

                              {/* Client selector */}
                              {(() => {
                                const selComp = quickCardCompany || visibleCompanyKeys[0];
                                const clientList = getAllowedClientsForCompany(selComp);
                                if (!clientList.length) return null;
                                const clientOptions = clientList.map(c => ({ id: String(c), name: String(c) }));
                                return (
                                  <CustomQuickSelect
                                    label={lang === 'ar' ? 'العميل:' : 'Client:'}
                                    value={quickCardClient}
                                    options={clientOptions}
                                    placeholder={lang === 'ar' ? '— بدون عميل —' : '— No Client —'}
                                    onChange={cId => setQuickCardClient(cId)}
                                  />
                                );
                              })()}

                              {/* Priority selector */}
                              <CustomQuickSelect
                                label={lang === 'ar' ? 'الأولوية:' : 'Priority:'}
                                value={quickCardPriority}
                                options={[
                                  { id: 'normal', name: lang === 'ar' ? 'عادي' : 'Normal', bg: 'bg-lime-500' },
                                  { id: 'urgent', name: lang === 'ar' ? 'عاجل 🔥' : 'Urgent 🔥', bg: 'bg-rose-500' },
                                ]}
                                onChange={p => setQuickCardPriority(p)}
                              />
                            </div>
                          ) : (
                            <div className="space-y-2.5">
                              <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                                <span>{lang === 'ar' ? 'الشركة:' : 'Company:'}</span>
                                <span className="px-2.5 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-extrabold">
                                  {companies[filter]?.name || filter}
                                </span>
                              </div>
                              {/* Client selector for single-company mode */}
                              {(() => {
                                const clientList = getAllowedClientsForCompany(filter);
                                if (!clientList.length) return null;
                                const clientOptions = clientList.map(c => ({ id: String(c), name: String(c) }));
                                return (
                                  <CustomQuickSelect
                                    label={lang === 'ar' ? 'العميل:' : 'Client:'}
                                    value={quickCardClient}
                                    options={clientOptions}
                                    placeholder={lang === 'ar' ? '— بدون عميل —' : '— No Client —'}
                                    onChange={cId => setQuickCardClient(cId)}
                                  />
                                );
                              })()}
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-1 gap-2">
                            <button
                              type="button"
                              onClick={handleQuickAddCard}
                              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white font-black text-xs rounded-xl shadow-md transition-all hover-effect cursor-pointer"
                            >
                              {lang === 'ar' ? 'إضافة المهمة' : 'Add Card'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsAddingCardInScheduled(false);
                                setQuickCardTitle('');
                                setQuickCardClient('');
                              }}
                              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl transition-colors hover-effect cursor-pointer"
                            >
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full bg-white/70 dark:bg-[#151921]/80 border border-slate-200/80 dark:border-white/10 rounded-2xl p-8 flex flex-col items-center justify-center text-center my-2 shadow-sm">
                          <div className="w-14 h-14 rounded-2xl bg-blue-500/10 text-blue-500 flex items-center justify-center mb-3">
                            <Calendar size={28} />
                          </div>
                          <h4 className="text-lg font-black text-slate-900 dark:text-white mb-1">
                            {lang === 'ar' ? 'لا توجد مهام اليوم' : 'No tasks scheduled for today'}
                          </h4>
                          <p className="text-xs font-bold text-slate-400 dark:text-slate-400 mb-4 max-w-sm">
                            {lang === 'ar' ? 'جميع مهامك منظمة، يمكنك إضافة مهمة جديدة للبدء مباشرة.' : 'Your workspace is all set. You can create a new task anytime.'}
                          </p>
                          {canCreateTask && (
                            <button
                              type="button"
                              onClick={() => {
                                setIsAddingCardInScheduled(true);
                                if (filter !== 'all') {
                                  setQuickCardCompany(filter);
                                } else {
                                  setQuickCardCompany(visibleCompanyKeys[0] || Object.keys(companies)[0] || '');
                                }
                              }}
                              className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-xs flex items-center gap-2 transition-all shadow-md hover-effect cursor-pointer"
                            >
                              <Plus size={16} />
                              <span>{lang === 'ar' ? 'إضافة مهمة جديدة' : 'Add New Task'}</span>
                            </button>
                          )}
                        </div>
                      )
                    ) : isCompanyOverview ? (
                      <div className="company-task-groups">
                        {tasksByCompany.map(([companyKey, companyTasks]) => {
                          const company = companies[companyKey] || { name: 'Unassigned Company', theme: colorPalettes[0] };
                          return (
                            <section key={companyKey} className="company-task-group">
                              <div className="company-task-group-header">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className={`w-10 h-10 rounded-2xl ${company.theme?.bg || 'bg-blue-500'} text-white flex items-center justify-center font-black shrink-0`}>
                                    {company.logo ? <img src={company.logo} alt="" className="w-full h-full rounded-2xl object-contain bg-white" /> : company.name.charAt(0)}
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="font-extrabold text-slate-900 dark:text-slate-100 truncate">{company.name}</h4>
                                    <p className="text-xs text-slate-500 font-semibold">{companyTasks.length} {lang === 'ar' ? 'cards' : 'cards'}</p>
                                  </div>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {companyTasks.map(task => {
                                  const dateDisplay = task.deadlineDate ? (new Date(task.deadlineDate).toDateString() === new Date().toDateString() ? t.today : task.deadlineDate.slice(5)) : t.notStarted;
                                  const isOverdue = task.deadlineISO && new Date(task.deadlineISO) < new Date() && task.status !== 'done';
                                  return (
                                    <div key={task.id} draggable="true"
                                      onDragStart={(e) => {
                                        setDraggedTaskId(task.id);
                                        e.dataTransfer.setData("text/plain", task.id);
                                        e.dataTransfer.effectAllowed = "move";
                                      }}
                                      onDragEnd={() => {
                                        setDraggedTaskId(null);
                                        setDragOverColumn(null);
                                      }}
                                      onClick={() => { setSelectedTask(task); setIsEditing(false); cancelInlineTaskEdit(); }}
                                      className={`task-card task-card-compact task-${task.status} group ${isOverdue ? 'task-overdue-attention' : task.isUrgent && task.status !== 'done' ? 'task-urgent-attention' : ''} ${draggedTaskId === task.id ? 'opacity-40 scale-95 blur-sm' : ''}`}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                          <h4 dir="auto" className="task-compact-title">{task.title}</h4>
                                          <p className="task-compact-client">
                                            <Users size={13} />
                                            <span>{task.client || ('No Client')}</span>
                                          </p>
                                          <p className="task-compact-date">
                                            <Calendar size={13} />
                                            <span>{dateDisplay}</span>
                                          </p>
                                        </div>
                                        <span className={`task-compact-status-dot ${isOverdue ? 'bg-rose-500 shadow-rose-500/40' : task.status === 'in_progress' ? 'bg-amber-500 shadow-amber-500/35' : task.status === 'review' ? 'bg-cyan-500 shadow-cyan-500/35' : task.status === 'revision' ? 'bg-orange-500 shadow-orange-500/35' : task.status === 'done' ? 'bg-emerald-500 shadow-emerald-500/35' : 'bg-blue-500 shadow-blue-500/35'}`}></span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </section>
                          );
                        })}
                      </div>
                    ) : (statusTasks.map(task => {
                      const company = companies[task.category] || Object.values(companies)[0] || { name: 'Unassigned Company', theme: colorPalettes[0] };
                      const isOverdue = isTaskOverdue(task);
                      const dateDisplay = task.deadlineDate ? (new Date(task.deadlineDate).toDateString() === new Date().toDateString() ? t.today : task.deadlineDate.slice(5)) : t.notStarted;
                      const labelKey = DESIGN_OPTS[task.designType]?.labelKey || 'socialMedia';
                      const completedSubtasks = task.subtasks?.filter(s => s.completed).length || 0;
                      const totalSubtasks = task.subtasks?.length || 0;
                      const progressPercent = totalSubtasks ? (completedSubtasks / totalSubtasks) * 100 : 0;

                      if (statusKey === 'scheduled') {
                        const shortDateDisplay = task.deadlineDate ? (() => {
                          const d = new Date(task.deadlineDate);
                          if (isNaN(d.getTime())) return task.deadlineDate;
                          const monthEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
                          const monthAr = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][d.getMonth()];
                          return lang === 'ar' ? `${d.getDate()} ${monthAr}` : `${monthEn} ${d.getDate()}`;
                        })() : (lang === 'ar' ? 'بلا تاريخ' : 'No Date');

                        return (
                          <div
                            key={task.id}
                            draggable="true"
                            onDragStart={(e) => {
                              setDraggedTaskId(task.id);
                              e.dataTransfer.setData("text/plain", task.id);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragEnd={() => {
                              setDraggedTaskId(null);
                              setDragOverColumn(null);
                            }}
                            onClick={() => { setSelectedTask(task); setIsEditing(false); cancelInlineTaskEdit(); }}
                            className={`w-full bg-white dark:bg-[#151922] border border-slate-200/90 dark:border-white/10 rounded-2xl p-4 sm:p-5 space-y-3 hover:border-slate-300 dark:hover:border-white/20 transition-all shadow-sm hover:shadow-md cursor-pointer group ${draggedTaskId === task.id ? 'opacity-40 scale-95 blur-sm' : ''}`}
                          >
                            {/* TOP ROW: TITLE ON FAR RIGHT (RTL), BADGES ON FAR LEFT */}
                            <div className="flex items-center justify-between gap-4 w-full flex-row-reverse">
                              {/* TITLE ON FAR RIGHT (RTL) */}
                              <h4 dir="auto" className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight leading-snug truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors flex-1 min-w-0 text-right">
                                {task.title}
                              </h4>

                              {/* STATUS & DATE BADGES ON FAR LEFT */}
                              {(() => {
                                const statusInfo = {
                                  scheduled: { label: t.scheduled || (lang === 'ar' ? 'مجدول' : 'Scheduled'), bg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30' },
                                  in_progress: { label: t.inProgress || (lang === 'ar' ? 'يعمل الآن' : 'Working Now'), bg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30' },
                                  review: { label: t.clientReview || (lang === 'ar' ? 'في المراجعة' : 'In Review'), bg: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border border-cyan-500/30' },
                                  revision: { label: t.revision || (lang === 'ar' ? 'في التعديل' : 'In Revision'), bg: 'bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/30' },
                                  done: { label: t.delivered || (lang === 'ar' ? 'تم تسليمه' : 'Delivered'), bg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30' },
                                }[task.status] || { label: t.scheduled || 'Scheduled', bg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/30' };

                                return (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-800 dark:text-slate-100 text-xs sm:text-sm font-extrabold border border-slate-200/80 dark:border-white/10">
                                      {shortDateDisplay}
                                    </span>
                                    <span className={`px-3 py-1.5 rounded-xl text-xs sm:text-sm font-black shadow-sm ${statusInfo.bg}`}>
                                      {statusInfo.label}
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>

                            {/* BOTTOM ROW: ICONS ROW & CAPSULES ROW */}
                            <div className="flex flex-col gap-2.5 text-right">
                              {/* Line 1: Icons (Eye Toggle, Description, Comments, Attachments) */}
                              <div className="flex items-center gap-3 flex-wrap text-xs font-bold justify-start">
                                {/* Eye button - manual toggle with notification */}
                                <button
                                  type="button"
                                  onClick={(e) => toggleTaskSeen(task.id, e)}
                                  title={task.isSeen ? (lang === 'ar' ? 'تم الاطلاع على المهمة (انقر لإلغاء التفعيل)' : 'Task Viewed') : (lang === 'ar' ? 'تأكيد الاطلاع على المهمة (انقر للتفعيل وإرسال إشعار)' : 'Mark as Viewed')}
                                  className={`p-1.5 rounded-xl transition-all cursor-pointer flex items-center justify-center gap-1 border ${
                                    task.isSeen
                                      ? 'text-emerald-500 bg-emerald-500/15 border-emerald-500/30 shadow-xs'
                                      : 'text-slate-400 dark:text-slate-500 border-slate-300/80 dark:border-white/10 hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-white/5'
                                  }`}
                                >
                                  <Eye size={17} className={task.isSeen ? 'text-emerald-500' : 'text-slate-400 dark:text-slate-500'} />
                                </button>

                                {/* Description / Brief icon */}
                                {(task.description || task.brief) && (
                                  <span className="text-slate-400 dark:text-slate-400 p-1" title={lang === 'ar' ? 'يوجد وصف/تفاصيل' : 'Has Description'}>
                                    <AlignLeft size={17} />
                                  </span>
                                )}

                                {/* Comments count */}
                                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 p-1">
                                  <MessageSquare size={16} />
                                  <span>{(task.comments || []).length}</span>
                                </span>

                                {/* Attachments count */}
                                <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 p-1">
                                  <Paperclip size={16} />
                                  <span>{(task.images || []).length}</span>
                                </span>
                              </div>

                              {/* Line 2: Priority Capsule + Client Capsule + Account Manager Capsule side-by-side */}
                              <div className="flex items-center gap-2.5 flex-wrap">
                                {/* Priority Capsule */}
                                <span className={`px-3 py-1 rounded-xl text-xs font-black border ${
                                  task.isUrgent || task.priority === 'urgent'
                                    ? 'bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-500/40 shadow-xs'
                                    : 'bg-lime-500/20 text-lime-700 dark:text-lime-400 border-lime-500/40 shadow-xs'
                                }`}>
                                  {lang === 'ar' ? 'الأولوية:' : 'Priority:'} {task.isUrgent || task.priority === 'urgent' ? (lang === 'ar' ? 'عاجل' : 'Urgent') : (lang === 'ar' ? 'عادي' : 'Normal')}
                                </span>

                                {/* Client Capsule */}
                                <span className="px-3 py-1 rounded-xl text-xs font-black bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                                  {lang === 'ar' ? 'العميل:' : 'Client:'} {task.client ? `${company.name} (${task.client})` : company.name}
                                </span>

                                {/* Account Manager Capsule */}
                                <span className="px-3 py-1 rounded-xl text-xs font-black bg-sky-500/15 text-sky-700 dark:text-sky-300 border border-sky-500/30">
                                  {lang === 'ar' ? 'مدير الحساب:' : 'Account Manager:'} {task.accountManager || task.createdByName || (lang === 'ar' ? 'أحمد' : 'Ahmed')}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      if (boardView === 'all') {
                        return (
                          <div key={task.id} draggable="true"
                            onDragStart={(e) => {
                              setDraggedTaskId(task.id);
                              e.dataTransfer.setData("text/plain", task.id);
                              e.dataTransfer.effectAllowed = "move";
                            }}
                            onDragEnd={() => {
                              setDraggedTaskId(null);
                              setDragOverColumn(null);
                            }}
                            onClick={() => { setSelectedTask(task); setIsEditing(false); cancelInlineTaskEdit(); }}
                            className={`task-card task-card-compact task-${task.status} group ${isOverdue ? 'task-overdue-attention' : task.isUrgent && task.status !== 'done' ? 'task-urgent-attention' : ''} ${draggedTaskId === task.id ? 'opacity-40 scale-95 blur-sm' : ''}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <h4 dir="auto" className="task-compact-title">{task.title}</h4>
                                <p className="task-compact-client">
                                  <Users size={13} />
                                  <span>{task.client || ('No Client')}</span>
                                </p>
                                <p className="task-compact-date">
                                  <Calendar size={13} />
                                  <span>{dateDisplay}</span>
                                </p>
                              </div>
                              <span className={`task-compact-status-dot ${
                                isOverdue ? 'bg-rose-500 shadow-rose-500/40' :
                                task.isUrgent && task.status !== 'done' ? 'bg-rose-500 shadow-rose-500/35' :
                                task.status === 'in_progress' ? 'bg-amber-500 shadow-amber-500/35' :
                                task.status === 'review' ? 'bg-cyan-500 shadow-cyan-500/35' :
                                task.status === 'revision' ? 'bg-orange-500 shadow-orange-500/35' :
                                task.status === 'done' ? 'bg-emerald-500 shadow-emerald-500/35' :
                                'bg-blue-500 shadow-blue-500/35'
                              }`}></span>
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div key={task.id} draggable="true"
                          onDragStart={(e) => {
                            setDraggedTaskId(task.id);
                            e.dataTransfer.setData("text/plain", task.id);
                            e.dataTransfer.effectAllowed = "move";
                          }}
                          onDragEnd={() => {
                            setDraggedTaskId(null);
                            setDragOverColumn(null);
                          }}
                          onClick={() => { setSelectedTask(task); setIsEditing(false); cancelInlineTaskEdit(); }}
                          className={`task-card task-card-detailed-square task-${task.status} group ${isOverdue ? 'task-overdue-attention' : task.isUrgent && task.status !== 'done' ? 'task-urgent-attention' : ''} ${task.status === 'done' ? 'border border-slate-200/80 dark:border-white/10 opacity-80' : `border border-white/80 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20`}
                            ${draggedTaskId === task.id ? 'opacity-40 scale-95 blur-sm' : ''}
                            `}>

                          {/* Urgent Border Indicator */}
                          {task.isUrgent && task.status !== 'done' && (
                            <div className="absolute inset-0 border border-rose-400/50 rounded-2xl opacity-80 animate-pulse pointer-events-none"></div>
                          )}

                          {/* Cover Image */}
                          {task.images && task.images.length > 0 && (
                            <div className="-mt-4 -mx-4 mb-4 h-36 relative border-b border-slate-100 dark:border-white/5 cursor-pointer" onClick={(e) => { e.stopPropagation(); setPreviewImage(task.images[0]); }}>
                              <img src={task.images[0]} alt="Task Cover" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                              <div className="absolute inset-0 bg-black/35 opacity-90"></div>
                            </div>
                          )}

                          <div className="relative flex items-start justify-between gap-3 mb-4 z-10">
                            <div className="min-w-0 flex flex-wrap items-center gap-2">
                              <span className="task-chip font-bold max-w-[150px] truncate">{company.name}</span>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-black border ${
                                task.status === 'scheduled' ? 'bg-blue-500/10 text-blue-500 border-blue-400/20' :
                                task.status === 'in_progress' ? 'bg-amber-500/10 text-amber-500 border-amber-400/20' :
                                task.status === 'review' ? 'bg-cyan-500/10 text-cyan-500 border-cyan-400/20' :
                                task.status === 'revision' ? 'bg-orange-500/10 text-orange-500 border-orange-400/20' :
                                'bg-emerald-500/10 text-emerald-500 border-emerald-400/20'
                              }`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                                {task.status === 'scheduled' ? t.scheduled : task.status === 'in_progress' ? t.workingNow : task.status === 'review' ? t.review : task.status === 'revision' ? t.revision : t.done}
                              </span>
                              {isOverdue && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-600 text-white border border-rose-400/30 text-[10px] font-black"><AlertTriangle size={11} />{t.overdue}</span>}
                              {task.isUrgent && task.status !== 'done' && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-500/10 text-rose-500 border border-rose-400/20 text-[10px] font-black"><Flame size={11} />{t.urgent}</span>}
                            </div>
                            {(hasPermission('manageTasks') || hasPermission('deleteTasks')) && (
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                                {hasPermission('manageTasks') && <button onClick={() => openTaskInlineEdit(task, 'title')} className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-500/10 transition-colors"><Edit size={14} /></button>}
                                {hasPermission('deleteTasks') && <button onClick={() => deleteTask(task.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"><Trash2 size={14} /></button>}
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 flex-1 mb-4 z-10">
                            <h3 dir="auto" className={`text-base font-bold text-slate-900 dark:text-slate-100 leading-snug line-clamp-2 ${task.status === 'done' ? 'line-through text-slate-500' : ''}`}>{task.title}</h3>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-[10px] text-slate-500">
                              <div className="task-chip min-w-0"><Users size={12} /><span className="truncate">{task.client || t.client}</span></div>
                              <div className="task-chip min-w-0"><User size={12} /><span className="truncate">{task.accountManager || t.manager}</span></div>
                              <div className={`task-chip min-w-0 ${isOverdue ? 'text-rose-500 border-rose-400/20 bg-rose-500/10' : ''}`}><Calendar size={12} /><span className="truncate">{dateDisplay}</span></div>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              <span className="task-chip">{t[labelKey]}</span>
                              {task.selectedPlatforms?.slice(0, 2).map(p => <span key={p} className="task-chip">{p}</span>)}
                              {task.selectedPlatforms?.length > 2 && <span className="task-chip">+{task.selectedPlatforms.length - 2}</span>}
                            </div>
                            {totalSubtasks > 0 && (
                              <div className="mt-3 flex items-center gap-2">
                                <div className="flex-1 h-1.5 bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }}></div>
                                </div>
                                <span className="text-[9px] text-slate-500">{completedSubtasks}/{totalSubtasks}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2 mb-4 z-10">
                            {task.description && <div className="p-1.5 rounded-lg bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-400"><FileText size={14} /></div>}
                            {task.brief && <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-500 dark:text-blue-400"><AlignLeft size={14} /></div>}
                            {task.tov && <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-500 dark:text-blue-400"><Type size={14} /></div>}
                            {task.images?.length > 0 && <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-500 dark:text-blue-400"><ImageIcon size={14} /></div>}
                            <div className="ms-auto font-mono font-bold text-blue-600 dark:text-blue-400 bg-blue-500/10 px-2 py-1 rounded-lg text-[10px]">{formatTime(task.elapsedTime)}</div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs mb-4 mt-auto z-10">
                            <div className="bg-slate-100/80 dark:bg-black/20 p-2.5 rounded-xl border border-slate-200/80 dark:border-white/10 flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                              <Hourglass size={14} /><span className="font-semibold">{task.estimatedHours}h</span>
                            </div>
                            <div className={`bg-slate-100/80 dark:bg-black/20 p-2.5 rounded-xl border border-slate-200/80 dark:border-white/10 flex items-center justify-end gap-1.5 ${isOverdue ? 'text-rose-500 dark:text-rose-400' : 'text-slate-500 dark:text-slate-400'}`}>
                              <Clock size={14} /><span className="font-mono font-semibold">{task.deadlineTime || '--:--'}</span>
                            </div>
                          </div>
                          {(canControlTaskWork || canMoveTaskToStatus(task, 'revision') || canMoveTaskToStatus(task, 'done') || canMoveTaskToStatus(task, 'review')) && <div className="flex items-center gap-2 z-10 pt-2 border-t border-slate-100 dark:border-white/5" onClick={(e) => e.stopPropagation()}>
                            {canControlTaskWork && ['in_progress', 'revision'].includes(task.status) && (<><button onClick={() => toggleTimer(task.id)} className={`flex-1 py-2.5 rounded-xl flex items-center justify-center gap-2 text-xs font-bold hover-effect ${task.isRunning ? 'bg-rose-500 text-white shadow-lg shadow-rose-500/20' : 'bg-white border border-slate-200 dark:border-transparent dark:bg-white/5 text-slate-400 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-rose-500/20 hover:text-rose-500 dark:hover:text-rose-400 border border-slate-100 dark:border-white/5'}`}>{task.isRunning ? <Pause size={14} /> : <Play size={14} />} {task.isRunning ? t.stop : t.start}</button>{canMoveTaskToStatus(task, 'review') && <button onClick={() => moveTask(task.id, 'review')} className="p-2.5 rounded-xl bg-blue-500/10 text-blue-500 dark:text-blue-400 hover:bg-blue-500/20 border border-blue-500/10 hover-effect"><Send size={16} /></button>}</>)}
                            {task.status === 'review' && (<>{canMoveTaskToStatus(task, 'revision') && <button onClick={() => moveTask(task.id, 'revision')} className="flex-1 py-2.5 rounded-xl bg-white border border-slate-200 dark:border-transparent dark:bg-white/5 text-rose-500 dark:text-rose-400 hover:bg-rose-500/10 dark:hover:bg-rose-500/20 dark:hover:text-white border border-slate-100 dark:border-white/5 text-xs font-bold flex items-center justify-center gap-1 hover-effect"><RotateCcw size={14} /> {t.revision}</button>}{canMoveTaskToStatus(task, 'done') && <button onClick={() => moveTask(task.id, 'done')} className="flex-1 py-2.5 rounded-xl bg-blue-500/10 text-blue-500 dark:text-blue-400 hover:bg-blue-500/20 dark:hover:text-white border border-blue-500/10 text-xs font-bold flex items-center justify-center gap-1 hover-effect"><CheckCircle size={14} /> {t.delivered}</button>}</>)}
                            {canControlTaskWork && task.status === 'scheduled' && <button onClick={() => toggleTimer(task.id)} className="w-full py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-500 font-bold text-xs flex items-center justify-center gap-2 border border-blue-500 hover-effect shadow-lg shadow-blue-500/15"><Play size={14} /> {t.startWork}</button>}
                            {task.status === 'done' && canMoveTaskToStatus(task, 'revision') && <button onClick={() => moveTask(task.id, 'revision')} className="w-full py-2.5 rounded-xl bg-white border border-slate-200 dark:border-transparent dark:bg-white/5 text-slate-500 dark:text-slate-400 hover:bg-white/10 dark:hover:bg-slate-700/50 dark:hover:text-white text-xs flex items-center justify-center gap-2 border border-slate-100 dark:border-white/5 hover-effect"><ArrowRight size={14} /> {t.reopen}</button>}
                          </div>}
                        </div>
                      );
                    }))}
                  {/* INLINE QUICK CARD CREATOR AT BOTTOM OF SCHEDULED COLUMN WHEN TASKS EXIST */}
                  {statusKey === 'scheduled' && canCreateTask && statusTasks.length > 0 && (
                    <div className="mt-3 w-full">
                      {!isAddingCardInScheduled ? (
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingCardInScheduled(true);
                            if (filter !== 'all') {
                              setQuickCardCompany(filter);
                            } else {
                              setQuickCardCompany(visibleCompanyKeys[0] || Object.keys(companies)[0] || '');
                            }
                          }}
                          className="w-full py-3 px-4 rounded-2xl bg-blue-500/10 dark:bg-blue-500/15 hover:bg-blue-500/20 dark:hover:bg-blue-500/25 border border-blue-500/30 hover:border-blue-500/50 text-blue-500 dark:text-blue-400 font-black text-sm flex items-center justify-center gap-2.5 transition-all cursor-pointer shadow-sm hover:shadow-md hover:-translate-y-0.5"
                        >
                          <Plus size={18} />
                          <span>{lang === 'ar' ? 'إضافة بطاقة' : 'Add Card'}</span>
                        </button>
                      ) : (
                        <div className="p-3.5 bg-white dark:bg-[#1f2125] rounded-2xl shadow-xl border border-blue-500/50 dark:border-blue-500/40 space-y-3 animate-in fade-in duration-200 w-full text-slate-800 dark:text-slate-100 text-right">
                          <textarea
                            rows={3}
                            value={quickCardTitle}
                            onChange={(e) => setQuickCardTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleQuickAddCard();
                              }
                              if (e.key === 'Escape') {
                                setIsAddingCardInScheduled(false);
                                setQuickCardTitle('');
                              }
                            }}
                            placeholder="Enter a title for this card..."
                            dir="auto"
                            className="w-full p-3 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-slate-900 dark:text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 resize-none font-medium"
                            autoFocus
                          />

                          {filter === 'all' ? (
                            <div className="space-y-2.5">
                              {(() => {
                                const companyOptions = Object.entries(visibleCompanies)
                                  .filter(([, cfg]) => cfg && typeof cfg === 'object')
                                  .map(([key, config]) => ({
                                    id: key,
                                    name: typeof config.name === 'string' && config.name ? config.name : key,
                                    bg: config.theme?.bg || 'bg-blue-500'
                                  }));
                                const activeCompanyVal = quickCardCompany || (companyOptions[0]?.id || '');
                                return (
                                  <CustomQuickSelect
                                    label={lang === 'ar' ? 'الشركة:' : 'Company:'}
                                    value={activeCompanyVal}
                                    options={companyOptions}
                                    onChange={key => { setQuickCardCompany(key); setQuickCardClient(''); }}
                                  />
                                );
                              })()}

                              {/* Client selector */}
                              {(() => {
                                const selComp = quickCardCompany || visibleCompanyKeys[0];
                                const clientList = getAllowedClientsForCompany(selComp);
                                if (!clientList.length) return null;
                                const clientOptions = clientList.map(c => ({ id: String(c), name: String(c) }));
                                return (
                                  <CustomQuickSelect
                                    label={lang === 'ar' ? 'العميل:' : 'Client:'}
                                    value={quickCardClient}
                                    options={clientOptions}
                                    placeholder={lang === 'ar' ? '— بدون عميل —' : '— No Client —'}
                                    onChange={cId => setQuickCardClient(cId)}
                                  />
                                );
                              })()}

                              {/* Priority selector */}
                              <CustomQuickSelect
                                label={lang === 'ar' ? 'الأولوية:' : 'Priority:'}
                                value={quickCardPriority}
                                options={[
                                  { id: 'normal', name: lang === 'ar' ? 'عادي' : 'Normal', bg: 'bg-lime-500' },
                                  { id: 'urgent', name: lang === 'ar' ? 'عاجل 🔥' : 'Urgent 🔥', bg: 'bg-rose-500' },
                                ]}
                                onChange={p => setQuickCardPriority(p)}
                              />
                            </div>
                          ) : (
                            <div className="space-y-2.5">
                              <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                                <span>{lang === 'ar' ? 'الشركة:' : 'Company:'}</span>
                                <span className="px-2.5 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-extrabold">
                                  {companies[filter]?.name || filter}
                                </span>
                              </div>
                              {/* Client selector for single-company mode */}
                              {(() => {
                                const clientList = getAllowedClientsForCompany(filter);
                                if (!clientList.length) return null;
                                const clientOptions = clientList.map(c => ({ id: String(c), name: String(c) }));
                                return (
                                  <CustomQuickSelect
                                    label={lang === 'ar' ? 'العميل:' : 'Client:'}
                                    value={quickCardClient}
                                    options={clientOptions}
                                    placeholder={lang === 'ar' ? '— بدون عميل —' : '— No Client —'}
                                    onChange={cId => setQuickCardClient(cId)}
                                  />
                                );
                              })()}
                            </div>
                          )}

                          <div className="flex items-center justify-between pt-1 gap-2">
                            <button
                              type="button"
                              onClick={handleQuickAddCard}
                              className="flex-1 py-2 bg-blue-500/15 hover:bg-blue-500/25 text-blue-500 dark:text-blue-400 border border-blue-500/30 hover:border-blue-500/50 font-black text-xs rounded-xl shadow-sm transition-all hover-effect"
                            >
                              {lang === 'ar' ? 'إضافة المهمة' : 'Add Card'}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsAddingCardInScheduled(false);
                                setQuickCardTitle('');
                                setQuickCardClient('');
                              }}
                              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl transition-colors hover-effect"
                            >
                              <X size={18} />
                            </button>
                          </div>

                        </div>
                      )}
                    </div>
                  )}

                  {/* OVERDUE TASKS SECTION */}
                  {boardView === 'scheduled' && statusKey === 'scheduled' && (() => {
                    const overdueTasks = tasks.filter(t => isTaskOverdue(t) && t.status !== 'done');

                    return (
                      <div className="mt-6 w-full">
                        <div className="flex items-center justify-between gap-3 mb-4 px-1">
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <div className="w-9 h-9 rounded-xl bg-rose-500/10 text-rose-500 flex items-center justify-center shrink-0">
                              <AlertTriangle size={20} className="animate-pulse" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-xl sm:text-2xl font-black text-rose-500 dark:text-rose-400 tracking-tight truncate">
                                {lang === 'ar' ? 'تاسكات متأخرة' : 'Overdue Tasks'}
                              </h3>
                              <p className="text-xs font-bold text-slate-400 dark:text-slate-400 truncate">
                                {lang === 'ar' ? 'مهام استنفدت تاريخ الاستحقاق ولم تكتمل بعد.' : 'Tasks past their due date that require attention.'}
                              </p>
                            </div>
                          </div>
                          {overdueTasks.length > 0 && (
                            <span className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 text-rose-500 dark:text-rose-400 border border-rose-500/30 text-xs font-black">
                              <span>{overdueTasks.length}</span>
                              <span>{lang === 'ar' ? 'متأخرة' : 'Overdue'}</span>
                            </span>
                          )}
                        </div>

                        {overdueTasks.length === 0 ? (
                          <div className="w-full bg-white/70 dark:bg-[#151921]/80 border border-slate-200/80 dark:border-white/10 rounded-2xl p-6 flex flex-col items-center justify-center text-center shadow-sm">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-2.5">
                              <CheckCircle size={24} />
                            </div>
                            <h4 className="text-base font-black text-slate-900 dark:text-white">
                              {lang === 'ar' ? 'لا توجد تاسكات متأخرة' : "You're all caught up!"}
                            </h4>
                            <p className="text-xs font-semibold text-slate-400 dark:text-slate-400 mt-0.5">
                              {lang === 'ar' ? 'ممتاز! كل المهام تسير وفق مواعيدها المحددة.' : 'Great job! All your tasks are on track.'}
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2.5 w-full">
                            {overdueTasks.map(task => {
                              const company = companies[task.category] || Object.values(companies)[0] || { name: 'Unassigned Company', theme: colorPalettes[0] };
                              const shortDateDisplay = task.deadlineDate ? (() => {
                                const d = new Date(task.deadlineDate);
                                if (isNaN(d.getTime())) return task.deadlineDate;
                                const monthEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
                                const monthAr = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'][d.getMonth()];
                                return lang === 'ar' ? `${d.getDate()} ${monthAr}` : `${monthEn} ${d.getDate()}`;
                              })() : (lang === 'ar' ? 'بلا تاريخ' : 'No Date');

                              return (
                                <div
                                  key={task.id}
                                  onClick={() => { setSelectedTask(task); setIsEditing(false); cancelInlineTaskEdit(); }}
                                  className="w-full bg-white dark:bg-[#161a22] border border-rose-500/30 dark:border-rose-500/30 rounded-xl p-3.5 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-rose-500/60 transition-all shadow-sm hover:shadow-md cursor-pointer group"
                                >
                                  <div className="flex items-start sm:items-center gap-3.5 min-w-0 flex-1">
                                    <span className="w-3 h-3 rounded-full bg-rose-500 shrink-0 mt-1.5 sm:mt-0 animate-pulse"></span>
                                    <div className="min-w-0 flex-1">
                                      <h4 dir="auto" className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-snug truncate group-hover:text-rose-500 transition-colors">
                                        {task.title}
                                      </h4>
                                      <p className="text-xs sm:text-sm font-bold text-slate-400 dark:text-slate-300 mt-1 truncate">
                                        {company.name} {task.client ? `(${task.client})` : ''}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-3 shrink-0">
                                    <span className="px-4 py-1.5 rounded-full text-xs sm:text-sm font-black shadow-sm bg-rose-500/20 text-rose-400 border border-rose-500/40">
                                      {lang === 'ar' ? 'متأخرة' : 'Overdue'}
                                    </span>
                                    <span className="px-4 py-1.5 rounded-xl bg-rose-500/15 text-rose-400 text-xs sm:text-sm font-extrabold border border-rose-500/30">
                                      {shortDateDisplay}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* RECENT ACTIVITY TIMELINE WIDGET */}
                  {boardView === 'scheduled' && statusKey === 'scheduled' && (() => {
                    const recentActivities = getRecentWorkspaceActivities();
                    return (
                      <div className="mt-6 mb-10 w-full">
                        <div className="flex items-center justify-between mb-4 px-1">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                              <Activity size={20} />
                            </div>
                            <div>
                              <h3 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                                {lang === 'ar' ? 'النشاط الأخير في مساحة العمل' : 'Recent Workspace Activity'}
                              </h3>
                              <p className="text-xs font-bold text-slate-400 dark:text-slate-400">
                                {lang === 'ar' ? 'سجل الإجراءات الأخيرة وحركات المهام المنجزة.' : 'Live stream of recent actions, completions, and task movements.'}
                              </p>
                            </div>
                          </div>
                          <span className="px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-500 dark:text-blue-400 border border-blue-500/20 text-xs font-black shrink-0 whitespace-nowrap flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                            {lang === 'ar' ? 'بث مباشر' : 'Live Stream'}
                          </span>
                        </div>

                        <div className="w-full bg-white/80 dark:bg-[#131824]/90 border border-slate-200/90 dark:border-white/10 rounded-2xl p-4 sm:p-5 shadow-lg backdrop-blur-2xl space-y-2.5">
                          {recentActivities.length > 0 ? (
                            recentActivities.map(item => {
                              const ItemIcon = item.icon;
                              return (
                                <div 
                                  key={item.id} 
                                  onClick={() => setSelectedTask(item.task)}
                                  className="flex items-center justify-between p-3 rounded-xl bg-slate-50/60 dark:bg-white/[0.03] border border-slate-200/60 dark:border-white/5 hover:border-blue-500/40 transition-all cursor-pointer hover:bg-blue-50/40 dark:hover:bg-blue-900/10 group"
                                  title={lang === 'ar' ? 'انقر لفتح تفاصيل المهمة' : 'Click to view task details'}
                                >
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shrink-0 ${item.color}`}>
                                      <ItemIcon size={16} />
                                    </div>
                                    <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 truncate group-hover:text-blue-500 transition-colors">
                                      {item.title}
                                    </p>
                                  </div>
                                  <span className="text-[11px] font-extrabold text-slate-400 shrink-0 me-1">
                                    {getRelativeTimeString(item.date)}
                                  </span>
                                </div>
                              );
                            })
                          ) : (
                            <div className="p-6 text-center text-sm font-semibold text-slate-400 dark:text-slate-500">
                              {lang === 'ar' ? 'لا توجد أنشطة أخيرًا في مساحة العمل حالياً' : 'No recent workspace activities yet.'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>



      {browserNotifications.length > 0 && (
        <div className="fixed left-5 top-5 z-[70] flex flex-col gap-3 pointer-events-none">
          {browserNotifications.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => openTool('whatsapp')}
              className="pointer-events-auto w-[320px] rounded-2xl bg-white dark:bg-[#111821] border border-emerald-400/30 shadow-2xl shadow-black/20 p-4 flex items-center gap-3 text-start hover:scale-[1.02] hover:border-emerald-400/60 transition-all duration-200"
            >
              <img src={whatsappToolIcon} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-black text-slate-950 dark:text-white">WhatsApp</p>
                <p className="text-xs text-slate-500 truncate">{item.message}</p>
              </div>
              <span className="min-w-[26px] h-7 px-2 rounded-full bg-rose-500 text-white text-xs font-black flex items-center justify-center">
                {item.unread > 99 ? '99+' : item.unread}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* SETTINGS MODAL */}
      {
        activeModal === 'settings' && settingsCompany && (
          <div className="settings-modal-overlay fixed inset-0 bg-black/65 md:backdrop-blur-sm z-[100] flex items-center justify-center p-0 md:p-4">
            
            {/* MOBILE ONLY SETTINGS MODAL (Full screen on mobile, bottom nav visible over it) */}
            <div className="md:hidden fixed inset-0 z-[100] w-full h-full bg-white dark:bg-[#0c1017] flex flex-col overflow-hidden animate-in fade-in duration-200 pb-16">
              {/* Header & 2-Tab Switcher */}
              <div className="px-4 pt-3 pb-3 border-b border-slate-200 dark:border-white/10 bg-white dark:bg-[#0c1017] shrink-0 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 bg-blue-600 text-white rounded-xl shadow-md shadow-blue-500/20">
                      {mobileSettingsTab === 'profile' ? <User size={18} /> : <Users size={18} />}
                    </div>
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">
                      {mobileSettingsTab === 'profile' 
                        ? (lang === 'ar' ? 'الملف الشخصي' : 'User Profile') 
                        : (lang === 'ar' ? 'إدارة العملاء' : 'Manage Clients')}
                    </h3>
                  </div>
                  <button 
                    onClick={() => setActiveModal(null)} 
                    className="p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <X size={22} />
                  </button>
                </div>

                {/* Tab Navigation Switcher */}
                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 dark:bg-white/[0.05] rounded-xl">
                  <button
                    type="button"
                    onClick={() => setMobileSettingsTab('profile')}
                    className={`py-2 px-3 rounded-lg text-xs font-extrabold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                      mobileSettingsTab === 'profile'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <User size={15} />
                    <span>{lang === 'ar' ? 'الملف الشخصي' : 'Profile'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setMobileSettingsTab('clients')}
                    className={`py-2 px-3 rounded-lg text-xs font-extrabold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                      mobileSettingsTab === 'clients'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <Users size={15} />
                    <span>{lang === 'ar' ? 'إضافة عملاء' : 'Add Clients'}</span>
                  </button>
                </div>
              </div>

              {/* Form Content */}
              <div className="p-5 overflow-y-auto space-y-5 flex-1">
                {mobileSettingsTab === 'profile' && (
                  <>
                    {/* 1. Avatar Photo */}
                    <div className="flex flex-col items-center p-4 bg-slate-50 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/10 rounded-2xl">
                      <div className="relative mb-3">
                        <div className="w-24 h-24 rounded-full bg-slate-800 border-2 border-dashed border-blue-500/50 flex items-center justify-center overflow-hidden shadow-xl">
                          {userPhoto ? (
                            <img src={userPhoto} alt="User" className="w-full h-full object-cover" />
                          ) : (
                            <User size={38} className="text-slate-400" />
                          )}
                        </div>
                        <label className="absolute bottom-0 right-0 p-2.5 bg-blue-600 rounded-full text-white cursor-pointer hover:bg-blue-500 shadow-lg transition-transform active:scale-95">
                          <Upload size={14} />
                          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'userPhoto')} className="hidden" />
                        </label>
                      </div>

                      <div className="flex items-center gap-2 w-full max-w-xs">
                        <label className="flex-1 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold cursor-pointer hover:bg-blue-500 transition-colors flex items-center justify-center gap-1.5 shadow-sm">
                          <Upload size={13} />
                          <span>{lang === 'ar' ? 'تغيير الصورة' : 'Upload Photo'}</span>
                          <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'userPhoto')} className="hidden" />
                        </label>
                        {userPhoto && (
                          <button 
                            type="button"
                            onClick={() => setUserPhoto(null)} 
                            className="px-3 py-2 bg-rose-500/10 border border-rose-500/20 text-rose-500 rounded-xl text-xs font-bold hover:bg-rose-500/20 transition-colors"
                          >
                            {lang === 'ar' ? 'إزالة' : 'Remove'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* 2. Name */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-extrabold text-slate-600 dark:text-slate-300">
                        {lang === 'ar' ? 'الاسم' : 'Name'}
                      </label>
                      <input 
                        type="text" 
                        dir="auto"
                        value={userName} 
                        onChange={(e) => setUserName(e.target.value)} 
                        className="w-full bg-white dark:bg-[#181d28] border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-3 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500" 
                      />
                    </div>

                    {/* 3. Location / Governorate */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-extrabold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                        <MapPin size={14} className="text-blue-500" />
                        <span>{lang === 'ar' ? 'المكان (المحافظة)' : 'Location (Governorate)'}</span>
                      </label>
                      <select 
                        value={governorate} 
                        onChange={(e) => setGovernorate(e.target.value)} 
                        className="w-full bg-white dark:bg-[#181d28] border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-3 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500"
                      >
                        {EGYPT_GOVERNORATES.map(gov => (
                          <option key={gov.id} value={gov.id}>
                            {lang === 'ar' ? gov.ar : gov.en}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* 4. Save Button */}
                    <button 
                      type="button"
                      onClick={async () => {
                        await saveCurrentUserProfile();
                        FlowDeskRepository.saveGovernorate(governorate);
                        setActiveAlert({
                          title: t.success || 'Success',
                          message: lang === 'ar' ? 'تم حفظ بيانات الملف الشخصي بنجاح!' : 'Profile updated successfully!',
                          type: 'success'
                        });
                        setActiveModal(null);
                      }} 
                      className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-extrabold text-sm hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/25 active:scale-[0.99] cursor-pointer mt-2"
                    >
                      {lang === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                    </button>

                    {/* 5. Logout Button */}
                    <div className="pt-3 border-t border-slate-200 dark:border-white/10 mt-2">
                      <button 
                        type="button"
                        onClick={() => {
                          setActiveModal(null);
                          handleLocalPreviewLogout();
                        }}
                        className="w-full py-3 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-500 rounded-xl font-extrabold text-xs flex items-center justify-center gap-2 transition-all cursor-pointer"
                      >
                        <LogOut size={16} />
                        <span>{lang === 'ar' ? 'تسجيل الخروج من الحساب' : 'Sign Out of Account'}</span>
                      </button>
                    </div>
                  </>
                )}

                {mobileSettingsTab === 'clients' && (
                  <div className="space-y-4">
                    {/* Company Selector */}
                    <div className="p-3 bg-slate-50 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/10 rounded-xl space-y-1.5">
                      <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                        {lang === 'ar' ? 'الشركة الحالية' : 'Selected Company'}
                      </label>
                      <select 
                        value={settingsCompanyKey} 
                        onChange={(e) => setSettingsCompanyId(e.target.value)} 
                        className="w-full bg-white dark:bg-[#181d28] border border-slate-200 dark:border-white/10 rounded-xl px-3 py-2 text-sm font-bold text-slate-900 dark:text-white outline-none"
                      >
                        {Object.entries(visibleCompanies).map(([key, config]) => (
                          <option key={key} value={key}>{config.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Add Client Form */}
                    <div className="space-y-2">
                      <label className="block text-xs font-extrabold text-slate-700 dark:text-slate-200">
                        {lang === 'ar' ? 'إضافة عميل جديد' : 'Add New Client'}
                      </label>
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          dir="auto"
                          value={clientInput} 
                          onChange={e => setClientInput(e.target.value)} 
                          placeholder={lang === 'ar' ? 'اسم العميل...' : 'Client name...'} 
                          className="flex-1 bg-white dark:bg-[#181d28] border border-slate-200 dark:border-white/10 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-blue-500" 
                        />
                        <button 
                          type="button"
                          onClick={addClient} 
                          className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shrink-0 shadow-md cursor-pointer"
                        >
                          <Plus size={16} />
                          <span>{lang === 'ar' ? 'إضافة' : 'Add'}</span>
                        </button>
                      </div>
                    </div>

                    {/* Client List & Search */}
                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-extrabold text-slate-700 dark:text-slate-200">
                          {lang === 'ar' ? 'قائمة العملاء' : 'Clients List'} ({settingsCompany?.clients?.length || 0})
                        </label>
                      </div>

                      <div className="relative">
                        <Search size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input 
                          type="text" 
                          dir="auto"
                          value={clientSearch} 
                          onChange={e => setClientSearch(e.target.value)} 
                          placeholder={lang === 'ar' ? 'بحث في العملاء...' : 'Search clients...'} 
                          className="w-full bg-white dark:bg-[#181d28] border border-slate-200 dark:border-white/10 rounded-xl pr-10 pl-3.5 py-2 text-xs font-bold text-slate-900 dark:text-white outline-none" 
                        />
                      </div>

                      <div className="space-y-2 max-h-[280px] overflow-y-auto pt-1">
                        {!settingsCompany?.clients || settingsCompany.clients.length === 0 ? (
                          <div className="p-6 text-center text-xs font-semibold text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-white/[0.02] rounded-xl border border-dashed border-slate-200 dark:border-white/10">
                            {lang === 'ar' ? 'لا يوجد عملاء مضافين لهذه الشركة بعد' : 'No clients added yet for this company.'}
                          </div>
                        ) : filteredCompanyClients.length === 0 ? (
                          <p className="text-xs text-slate-400 italic p-2 text-center">
                            {lang === 'ar' ? 'لا توجد نتائج مطابقة' : 'No matching clients found'}
                          </p>
                        ) : (
                          filteredCompanyClients.map((clientName) => {
                            const idx = settingsCompany.clients.indexOf(clientName);
                            return (
                              <div 
                                key={clientName + idx} 
                                className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200/80 dark:border-white/10"
                              >
                                <div className="flex items-center gap-2.5 min-w-0 flex-1 me-2">
                                  <div className="w-8 h-8 rounded-lg bg-blue-600/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 flex items-center justify-center font-black text-xs shrink-0">
                                    {clientName.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="font-bold text-xs text-slate-800 dark:text-slate-100 truncate">
                                    {clientName}
                                  </span>
                                </div>
                                <button 
                                  type="button"
                                  onClick={() => removeClient(idx)} 
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors shrink-0 cursor-pointer"
                                  title={lang === 'ar' ? 'حذف العميل' : 'Delete Client'}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* DESKTOP ONLY SETTINGS MODAL (Hidden on mobile < md) */}
            <div className="hidden md:flex settings-modal-shell bg-white dark:bg-[#111821] rounded-[28px] w-full max-w-6xl border border-slate-200 dark:border-white/10 shadow-2xl h-[88vh] flex-col overflow-hidden">
              <div className="settings-modal-header px-7 py-5 border-b border-slate-200 dark:border-white/10 flex justify-between items-center bg-white dark:bg-[#111821] shrink-0">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-3"><div className="p-2 bg-blue-600 text-white rounded-xl"><Settings size={22} /></div> {t.settings}</h3>
                <button onClick={() => setActiveModal(null)} className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white p-2 hover:bg-slate-100 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 rounded-xl transition-colors hover-effect"><X size={22} /></button>
              </div>

              <div className="settings-modal-layout grid grid-cols-1 lg:grid-cols-[300px_1fr] flex-1 min-h-0 overflow-hidden">
                <aside className="settings-modal-nav border-b lg:border-b-0 lg:border-l border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0B1118] p-5 overflow-y-auto scrollbar-thin max-h-[38vh] lg:max-h-none">
                  <div className="settings-card p-4 mb-4">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-2xl ${settingsCompany.theme.bg} text-white flex items-center justify-center font-black shadow-lg overflow-hidden`}>
                        {settingsCompany.logo ? <img src={settingsCompany.logo} className="w-full h-full object-contain bg-white" alt="logo" /> : settingsCompany.name.charAt(0)}
                      </div>
                      <div className="min-w-0"><p className="text-[11px] text-slate-500 font-bold">Current Company</p><p className="font-extrabold text-slate-950 dark:text-white truncate">{settingsCompany.name}</p></div>
                    </div>
                    <select value={settingsCompanyKey} onChange={(e) => setSettingsCompanyId(e.target.value)} className="settings-input py-2 text-sm">
                      {Object.entries(visibleCompanies).map(([key, config]) => <option key={key} value={key}>{config.name}</option>)}
                    </select>
                  </div>

                    <div>
                      <p className="px-3 mb-2 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">Selected Company Settings</p>
                      <div className="space-y-2">
                        {[
                          { id: 'company', label: 'الشركة', icon: Briefcase },
                          { id: 'clients', label: 'Clients', icon: Users },
                          { id: 'managers', label: 'Managers', icon: User }
                        ].filter(item => canAccessSettingsTab(item.id)).map(item => {
                          const Icon = item.icon;
                          return (
                            <button key={item.id} onClick={() => setSettingsTab(item.id)} className={`w-full text-right px-4 py-3 rounded-xl text-sm transition-all flex items-center gap-3 hover-effect ${settingsTab === item.id ? 'bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-blue-500/10 border border-slate-200 dark:border-transparent dark:bg-white/5'}`}>
                              <Icon size={17} /><span className="flex-1 truncate">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-200 dark:border-white/10">
                      <p className="px-3 mb-2 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">App Settings</p>
                      <div className="space-y-2">
                        {[
                          { id: 'users', label: lang === 'ar' ? 'المستخدمون والصلاحيات' : 'Users & Permissions', icon: Shield },
                          { id: 'backup', label: t.backupRestore, icon: Save },
                          { id: 'profile', label: t.profile, icon: User },
                          { id: 'about', label: 'About App', icon: Info }
                        ].filter(item => canAccessSettingsTab(item.id)).map(item => {
                          const Icon = item.icon;
                          return (
                            <button key={item.id} onClick={() => setSettingsTab(item.id)} className={`w-full text-right px-4 py-3 rounded-xl text-sm transition-all flex items-center gap-3 hover-effect ${settingsTab === item.id ? 'bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/20' : 'text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-blue-500/10 border border-slate-200 dark:border-transparent dark:bg-white/5'}`}>
                              <Icon size={17} /><span className="flex-1 truncate">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                  <div className="mt-5 pt-5 border-t border-slate-200 dark:border-white/10">
                    <div className="flex items-center justify-between gap-3 mb-3"><p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t.companies}</p>{hasPermission('manageCompany') && <button onClick={handleAddCompany} className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-500 transition-colors shadow-lg hover-effect" title={t.add}><Plus size={16} /></button>}</div>
                    <div className="space-y-2">
                      {Object.entries(visibleCompanies).map(([key, config]) => (
                        <button key={key} onClick={() => { setSettingsCompanyId(key); setSettingsTab('company'); }} className={`w-full text-right px-3 py-2.5 rounded-xl text-sm transition-all flex items-center gap-3 hover-effect ${settingsCompanyKey === key ? 'bg-white dark:bg-white/10 text-slate-950 dark:text-white font-semibold' : 'text-slate-500 dark:text-slate-400 hover:bg-white dark:hover:bg-white/5 border border-slate-200 dark:border-white/5'}`}>
                          <div className={`w-2.5 h-2.5 rounded-full ${config.theme.bg}`}></div><span className="truncate">{config.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </aside>

                <section className="settings-modal-content p-6 overflow-y-auto bg-white dark:bg-[#111821] min-w-0 scrollbar-thin">
                  {settingsTab === 'company' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card overflow-hidden">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className={`w-20 h-20 rounded-3xl ${settingsCompany.theme.bg} flex items-center justify-center text-white text-3xl font-black border-4 ${settingsCompany.theme.border} shadow-xl overflow-hidden shrink-0`}>
                              {settingsCompany.logo ? <img src={settingsCompany.logo} className="w-full h-full object-contain bg-white" alt="logo" /> : settingsCompany.name.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-slate-500 font-bold">إعدادات الشركة</p>
                              <h4 className="text-2xl font-extrabold text-slate-950 dark:text-white truncate">{settingsCompany.name}</h4>
                            </div>
                          </div>
                          <button onClick={() => { FlowDeskRepository.saveCompanies(companies); queueCompanySync(settingsCompany); setActiveAlert({ title: t.success || 'Success', message: t.savedSuccess, type: 'success' }); }} className="px-5 py-3 solid-primary rounded-xl text-xs shrink-0">{t.save}</button>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-5">
                          <div className="rounded-2xl bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs text-slate-500 font-bold">Clients</p><p className="text-2xl font-black text-slate-950 dark:text-white">{settingsCompany.clients.length}</p></div>
                          <div className="rounded-2xl bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs text-slate-500 font-bold">Managers</p><p className="text-2xl font-black text-slate-950 dark:text-white">{settingsCompany.managers.length}</p></div>
                          <div className="rounded-2xl bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs text-slate-500 font-bold">مهام مفتوحة</p><p className="text-2xl font-black text-slate-950 dark:text-white">{settingsCompanyOpenTasks}</p></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-5">
                        <div className="settings-card"><label className="block text-sm text-slate-500 dark:text-slate-400 mb-2 font-bold">بيانات أساسية</label><input type="text" value={settingsCompany.name} onChange={e => updateCompanyName(e.target.value)} className="settings-input text-lg font-semibold" /></div>
                        <div className="settings-card">
                          <label className="block text-sm text-slate-500 dark:text-slate-400 mb-4 font-bold">الهوية البصرية</label>
                          <div className="flex items-center gap-3 mb-4">
                            <div className={`w-14 h-14 rounded-2xl ${settingsCompany.theme.bg} flex items-center justify-center text-white font-black border ${settingsCompany.theme.border} overflow-hidden shrink-0`}>
                              {settingsCompany.logo ? <img src={settingsCompany.logo} className="w-full h-full object-contain bg-white" alt="logo" /> : settingsCompany.name.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-extrabold text-slate-950 dark:text-white truncate">{settingsCompany.logo ? 'شعار مخصص' : 'Company Theme Color مستخدم بدل الشعار'}</p>
                              <p className="text-xs text-slate-500 mt-1">يمكن إزالة الصورة والاكتفاء بلون واضح لكل شركة.</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <label className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 rounded-xl text-sm text-slate-900 dark:text-white cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-white/10 hover-effect">
                              <Upload size={16} /> تغيير الشعار
                              <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'company')} className="hidden" />
                            </label>
                            <button
                              type="button"
                              onClick={() => updateCompanyLogo(null)}
                              disabled={!settingsCompany.logo}
                              className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-rose-500/10 border border-rose-500/20 rounded-xl text-sm text-rose-500 font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-rose-500/15 hover-effect"
                            >
                              <Trash2 size={16} /> إزالة الصورة
                            </button>
                          </div>
                          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/10">
                            <p className="mb-2 text-xs font-extrabold text-slate-500">اختيار لون بدل الشعار</p>
                            <div className="flex flex-wrap gap-2">
                              {colorPalettes.map((palette) => (
                                <button
                                  key={palette.id}
                                  type="button"
                                  onClick={() => updateCompanyTheme(palette)}
                                  className={`w-9 h-9 rounded-xl ${palette.bg} border-2 hover-effect ${settingsCompany.theme.id === palette.id ? 'border-white scale-105 shadow-[0_0_14px_rgba(37,99,235,.35)]' : 'border-transparent opacity-80 hover:opacity-100'}`}
                                  title={getPaletteLabel(palette.id)}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>



                      <div className="settings-card border-rose-500/20 bg-rose-500/5">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div><h4 className="font-extrabold text-rose-400 flex items-center gap-2"><Trash2 size={16} /> منطقة الخطر</h4><p className="text-sm text-slate-500 mt-1">حذف الشركة سيحذفها من القائمة، مع الحفاظ على منع حذف آخر شركة.</p></div>
                          <button onClick={handleDeleteCompany} className="px-5 py-3 bg-white dark:bg-rose-500/5 border border-rose-200 dark:border-rose-500/10 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl font-bold flex items-center justify-center gap-2 transition-all hover-effect shrink-0"><Trash2 size={16} /> {t.deleteCompany}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {settingsTab === 'clients' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5"><div><h4 className="text-xl font-extrabold text-slate-950 dark:text-white">إدارة Clients</h4><p className="text-xs text-slate-500 mt-1">{settingsCompany.name}</p></div><span className="px-3 py-1.5 rounded-xl bg-blue-500/10 text-blue-400 text-xs font-mono">{settingsCompany.clients.length}</span></div>
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 mb-3"><input type="text" value={clientInput} onChange={e => setClientInput(e.target.value)} placeholder={t.clientName} className="settings-input text-sm min-w-0" /><button onClick={addClient} className="solid-primary px-6 py-3 rounded-xl text-sm flex items-center justify-center gap-2"><Plus size={16} /> إضافة عميل</button></div>
                        <div className="relative"><Search size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" /><input type="text" value={clientSearch} onChange={e => setClientSearch(e.target.value)} placeholder="بحث في Clients..." className="settings-input pr-11 text-sm" /></div>
                      </div>
                      <div className="settings-card min-h-[220px]">
                        {settingsCompany.clients.length === 0 ? <div className="h-[180px] flex flex-col items-center justify-center text-center"><Users size={34} className="text-blue-400 mb-3" /><h5 className="font-extrabold text-slate-950 dark:text-white">لا يوجد عملاء بعد</h5><p className="text-sm text-slate-500 mt-1 max-w-sm">أضف أول عميل لتسهيل ربط المهام بأصحابها.</p></div> : filteredCompanyClients.length === 0 ? <p className="text-sm text-slate-500 italic">لا توجد نتائج مطابقة.</p> : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{filteredCompanyClients.map((c) => { const idx = settingsCompany.clients.indexOf(c); return <div key={c + idx} className="flex items-center gap-3 p-3 rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10"><div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-bold">{c.charAt(0)}</div><span className="flex-1 truncate font-bold text-slate-800 dark:text-slate-100">{c}</span><button onClick={() => removeClient(idx)} className="p-2 rounded-lg text-slate-500 hover:text-rose-500 hover:bg-rose-500/10"><Trash2 size={15} /></button></div>; })}</div>}
                      </div>
                    </div>
                  )}

                  {settingsTab === 'managers' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5"><div><h4 className="text-xl font-extrabold text-slate-950 dark:text-white">إدارة Managers</h4><p className="text-xs text-slate-500 mt-1">{settingsCompany.name}</p></div><span className="px-3 py-1.5 rounded-xl bg-blue-500/10 text-blue-400 text-xs font-mono">{settingsCompany.managers.length}</span></div>
                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3 mb-3"><input type="text" value={managerInput} onChange={e => setManagerInput(e.target.value)} placeholder={t.employeeName} className="settings-input text-sm min-w-0" /><button onClick={addManager} className="solid-primary px-6 py-3 rounded-xl text-sm flex items-center justify-center gap-2"><Plus size={16} /> إضافة مسؤول</button></div>
                        <div className="relative"><Search size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500" /><input type="text" value={managerSearch} onChange={e => setManagerSearch(e.target.value)} placeholder="بحث في Managers..." className="settings-input pr-11 text-sm" /></div>
                      </div>
                      <div className="settings-card min-h-[220px]">
                        {settingsCompany.managers.length === 0 ? <div className="h-[180px] flex flex-col items-center justify-center text-center"><User size={34} className="text-blue-400 mb-3" /><h5 className="font-extrabold text-slate-950 dark:text-white">لا يوجد مسؤولين بعد</h5><p className="text-sm text-slate-500 mt-1 max-w-sm">أضف أسماء مسؤولي الحسابات لاختيارهم أثناء إضافة المهمة.</p></div> : filteredCompanyManagers.length === 0 ? <p className="text-sm text-slate-500 italic">لا توجد نتائج مطابقة.</p> : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{filteredCompanyManagers.map((manager) => { const idx = settingsCompany.managers.indexOf(manager); return <div key={manager + idx} className="flex items-center gap-3 p-3 rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10"><div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center font-bold">{manager.charAt(0)}</div><span className="flex-1 truncate font-bold text-slate-800 dark:text-slate-100">{manager}</span><button onClick={() => removeManager(idx)} className="p-2 rounded-lg text-slate-500 hover:text-rose-500 hover:bg-rose-500/10"><Trash2 size={15} /></button></div>; })}</div>}
                      </div>
                    </div>
                  )}

                  {settingsTab === 'hours' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card"><div className="flex items-center justify-between gap-4 mb-5"><h4 className="text-xl font-extrabold text-slate-950 dark:text-white flex items-center gap-2"><Watch size={18} /> مواعيد العمل</h4><div className="text-xs font-bold text-blue-400">ملخص الدوام</div></div><div className="grid grid-cols-1 md:grid-cols-3 gap-3"><div className="rounded-2xl bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs text-slate-500 font-bold">ساعات يومية</p><p className="text-2xl font-black text-slate-950 dark:text-white">{settingsCompanyDailyHours}h</p></div><div className="rounded-2xl bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs text-slate-500 font-bold">أيام عمل</p><p className="text-2xl font-black text-slate-950 dark:text-white">{settingsCompany.workDays?.length || 0}</p></div><div className="rounded-2xl bg-white/70 dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs text-slate-500 font-bold">التكلفة / الساعة</p><p className="text-2xl font-black text-slate-950 dark:text-white">{settingsCompany.hourlyRate || 0}</p></div></div></div>
                      <div className="settings-card"><div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6"><div><label className="text-xs text-slate-500 mb-1 block">من</label><input type="time" value={settingsCompany.workStart} onChange={e => setCompanies(prev => updateCompanyField({ companies: prev, companyId: settingsCompanyKey, field: 'workStart', value: e.target.value }))} className="settings-input py-2" /></div><div><label className="text-xs text-slate-500 mb-1 block">إلى</label><input type="time" value={settingsCompany.workEnd} onChange={e => setCompanies(prev => updateCompanyField({ companies: prev, companyId: settingsCompanyKey, field: 'workEnd', value: e.target.value }))} className="settings-input py-2" /></div></div><label className="text-xs text-slate-500 mb-3 block">{t.workDaysLabel}</label><div className="flex flex-wrap justify-center gap-2 bg-white dark:bg-[#111821] p-3 rounded-xl border border-slate-200 dark:border-white/10 mb-5">{t.days.map((dayName, idx) => { const isWorkDay = settingsCompany.workDays?.includes(idx); return <button key={idx} onClick={() => setCompanies(prev => toggleCompanyWorkDay({ companies: prev, companyId: settingsCompanyKey, dayIndex: idx }))} className={`flex-1 min-w-[40px] py-2 px-1 rounded-xl text-[10px] sm:text-xs font-bold transition-all flex items-center justify-center border ${isWorkDay ? 'bg-blue-600 text-white border-blue-600 shadow-md shadow-blue-500/20' : 'bg-white border-slate-200 dark:border-transparent dark:bg-white/5 text-slate-400 hover:text-slate-900 dark:hover:text-white dark:text-slate-500 hover:bg-white dark:hover:bg-blue-500/10'}`}>{dayName}</button>; })}</div><label className="text-xs text-blue-400 mb-1 block font-bold flex items-center gap-1"><DollarSign size={12} /> التكلفة / الساعة (ج.م)</label><input type="number" value={settingsCompany.hourlyRate} onChange={e => setCompanies(prev => updateCompanyField({ companies: prev, companyId: settingsCompanyKey, field: 'hourlyRate', value: parseFloat(e.target.value) || 0 }))} className="settings-input font-mono py-2" /></div>
                    </div>
                  )}

                  {settingsTab === 'appearance' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card">
                        <div className="flex flex-col xl:flex-row xl:items-start justify-between gap-6">
                          <div className="min-w-0">
                            <p className="text-xs font-extrabold text-blue-400 mb-2 flex items-center gap-2"><Palette size={16} /> {t.visualIdentity}</p>
                            <h4 className="text-2xl font-black text-slate-950 dark:text-white">Appearance</h4>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 max-w-xl">Control the app look, color, and display comfort without changing company data.</p>
                          </div>
                          <div className="w-full xl:w-72 rounded-2xl bg-white dark:bg-[#111821] border border-slate-200 dark:border-white/10 p-4">
                            <div className="flex items-center justify-between mb-3"><span className="text-xs font-bold text-slate-500">Live preview</span><span className={`w-3 h-3 rounded-full ${settingsCompany.theme.bg}`}></span></div>
                            <div className="rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-[#0B1118] p-3 space-y-3"><div className="h-2.5 w-2/3 rounded-full bg-slate-300 dark:bg-white/20"></div><div className="h-8 rounded-xl bg-blue-600"></div><div className="grid grid-cols-3 gap-2"><div className="h-8 rounded-lg bg-white dark:bg-white/10"></div><div className="h-8 rounded-lg bg-white dark:bg-white/10"></div><div className="h-8 rounded-lg bg-white dark:bg-white/10"></div></div></div>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        <div className="settings-card"><h5 className="font-extrabold text-slate-950 dark:text-white mb-4 flex items-center gap-2"><Monitor size={17} /> Color Mode</h5><div className="grid grid-cols-3 gap-2"><button onClick={() => setTheme('light')} className={`py-3 px-3 rounded-xl border text-sm font-bold transition-all flex items-center justify-center gap-2 hover-effect ${theme === 'light' ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300'}`}><Sun size={16} /> Light</button><button onClick={() => setTheme('dark')} className={`py-3 px-3 rounded-xl border text-sm font-bold transition-all flex items-center justify-center gap-2 hover-effect ${theme === 'dark' ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300'}`}><Moon size={16} /> Dark</button><button onClick={() => setTheme('system')} className={`py-3 px-3 rounded-xl border text-sm font-bold transition-all flex items-center justify-center gap-2 hover-effect ${theme === 'system' ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300'}`}><Monitor size={16} /> Auto</button></div></div>
                        <div className="settings-card"><h5 className="font-extrabold text-slate-950 dark:text-white mb-4 flex items-center gap-2"><LayoutGrid size={17} /> {lang === 'ar' ? 'حجم الواجهة' : 'UI Density'}</h5><div className="grid grid-cols-2 gap-2"><button onClick={() => setUiDensity('comfortable')} className={`py-3 px-3 rounded-xl border text-sm font-bold transition-all hover-effect ${uiDensity === 'comfortable' ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300'}`}>{lang === 'ar' ? 'مريح' : 'Comfortable'}</button><button onClick={() => setUiDensity('compact')} className={`py-3 px-3 rounded-xl border text-sm font-bold transition-all hover-effect ${uiDensity === 'compact' ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300'}`}>{lang === 'ar' ? 'مضغوط' : 'Compact'}</button></div></div>
                      </div>
                      <div className="settings-card">
                        <div className="flex items-center justify-between gap-4 mb-4">
                          <div>
                            <h5 className="font-extrabold text-slate-950 dark:text-white">{lang === 'ar' ? 'Company Theme Color' : 'Company Color'}</h5>
                            <p className="text-xs text-slate-500 mt-1">{lang === 'ar' ? 'يظهر في كروت المهام والتمييزات الخاصة بهذه الشركة.' : 'Used on task cards and visual markers for this company.'}</p>
                          </div>
                          <span className="text-xs font-bold text-blue-400">{lang === 'ar' ? 'Current Theme: ' : 'Current: '}{getPaletteLabel(settingsCompany.theme.id)}</span>
                        </div>
                        <div className="flex flex-wrap gap-3">{colorPalettes.map((palette) => (<button key={palette.id} onClick={() => updateCompanyTheme(palette)} className={`w-12 h-12 rounded-2xl ${palette.bg} transition-all duration-300 border-2 hover-effect ${settingsCompany.theme.id === palette.id ? 'border-white scale-105 shadow-[0_0_18px_rgba(37,99,235,.35)]' : 'border-transparent opacity-75 hover:opacity-100'}`} title={getPaletteLabel(palette.id)} />))}</div>
                      </div>
                    </div>
                  )}

                  {settingsTab === 'backup' && (
                    <div className="space-y-5 animate-in fade-in duration-300"><div className="settings-card bg-blue-500/10 border-blue-400/20"><div className="flex items-start justify-between gap-4 mb-5"><div><h4 className="text-xl font-extrabold text-blue-400 flex items-center gap-2"><Save size={18} /> {t.backupRestore}</h4><p className="text-sm text-slate-500 mt-2">Export or restore tasks, companies, clients, and settings.</p></div><span className="px-3 py-1.5 rounded-xl bg-blue-500/10 text-blue-400 text-xs font-bold">JSON</span></div><div className="grid grid-cols-1 sm:grid-cols-2 gap-3"><button onClick={handleExportData} className="py-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors hover-effect"><Download size={15} /> {t.exportData}</button><label className="py-3.5 bg-white border border-slate-200 dark:border-white/10 dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-blue-500/10 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-bold flex items-center justify-center gap-2 cursor-pointer transition-colors hover-effect"><UploadCloud size={15} /> {t.importData}<input type="file" accept=".json" onChange={handleImportData} className="hidden" /></label></div>{window.electronAPI && window.electronAPI.openDataFolder && <button onClick={() => window.electronAPI.openDataFolder()} className="mt-3 w-full py-3 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover-effect"><Monitor size={15} /> {t.openDataFolder}</button>}</div><div className="settings-card bg-rose-500/10 border-rose-500/20"><div className="flex flex-col md:flex-row md:items-center justify-between gap-4"><div><h4 className="text-xl font-extrabold text-rose-400 flex items-center gap-2"><AlertTriangle size={18} /> Danger Zone</h4><p className="text-sm text-slate-500 mt-2">Factory reset deletes all data after confirmation.</p></div><button onClick={handleReset} className="px-6 py-3 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors hover-effect shrink-0"><Trash2 size={15} /> {t.resetData}</button></div></div></div>
                  )}

                  {settingsTab === 'sound' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card">
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-5">
                          <div>
                            <p className="text-xs font-extrabold text-blue-400 mb-2 flex items-center gap-2"><MapPin size={16} /> {t.prayersList}</p>
                            <h4 className="text-2xl font-black text-slate-950 dark:text-white">{t.soundSettings}</h4>
                            <p className="text-sm text-slate-500 mt-2">{lang === 'ar' ? 'اختر المحافظة وAdhan Reciter Voice المستخدم في تنبيهات الصلاة.' : 'Choose the prayer location and the adhan voice used for alerts.'}</p>
                          </div>
                          <div className="rounded-2xl bg-blue-500/10 border border-blue-400/20 px-4 py-3 min-w-[180px]">
                            <p className="text-xs text-slate-500 font-bold">{t.nextPrayer}</p>
                            <p className="text-lg font-black text-blue-400">{nextPrayer ? t.prayers[nextPrayer.key] : '--'}</p>
                            <p className="text-xs text-slate-500 font-mono mt-1">{nextPrayer?.time ? nextPrayer.time.toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '--:--'}</p>
                          </div>
                        </div>

                        <label className="block text-sm text-slate-500 dark:text-slate-400 mb-2 font-bold">{t.governorate}</label>
                        <select value={governorate} onChange={(e) => setGovernorate(e.target.value)} className="settings-input mb-5">
                          {EGYPT_GOVERNORATES.map(gov => (<option key={gov.id} value={gov.id}>{lang === 'ar' ? gov.ar : gov.en}</option>))}
                        </select>

                        <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_.9fr] gap-4">
                          <div>
                            <label className="block text-sm text-slate-500 font-bold mb-2">{lang === 'ar' ? 'Adhan Reciter Voice' : 'Adhan Voice'}</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {ADHAN_VOICES.map(voice => (
                                <button
                                  key={voice.id}
                                  type="button"
                                  onClick={() => {
                                    if (previewingAdhanVoice && previewingAdhanVoice !== voice.id) stopAdhanPreview();
                                    setSoundSettings(prev => updateSettingsField(prev, 'adhanVoice', voice.id));
                                  }}
                                  className={`px-4 py-3 rounded-xl border text-sm font-black transition-all text-start hover-effect ${soundSettings.adhanVoice === voice.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300'}`}
                                >
                                  {lang === 'ar' ? voice.ar : voice.en}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm text-slate-500 font-bold mb-2">{t.volume}</label>
                              <div className="flex items-center gap-4 bg-white dark:bg-[#111821] border border-slate-200 dark:border-white/10 rounded-xl px-4 py-3">
                                <Volume2 size={17} className="text-blue-400" />
                                <input type="range" min="0" max="1" step="0.05" value={soundSettings.volume} onChange={e => setSoundSettings(prev => updateSettingsField(prev, 'volume', parseFloat(e.target.value)))} className="flex-1 accent-blue-500" />
                                <span className="text-sm font-mono text-blue-400 min-w-[3ch]">{Math.round(soundSettings.volume * 100)}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={toggleAdhanPreview}
                              className={`w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover-effect border ${previewingAdhanVoice === soundSettings.adhanVoice ? 'bg-rose-500 text-white border-rose-500 shadow-lg shadow-rose-500/20' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-200'}`}
                            >
                              {previewingAdhanVoice === soundSettings.adhanVoice ? <Pause size={15} /> : <Volume1 size={15} />}
                              {previewingAdhanVoice === soundSettings.adhanVoice ? (lang === 'ar' ? 'Stop Preview' : 'Stop Preview') : (lang === 'ar' ? 'تجربة الصوت' : 'Preview Voice')}
                            </button>
                          </div>
                        </div>

                        <button onClick={() => { const savedSounds = buildSavedSoundSettings(soundSettings); setSoundSettings(savedSounds); FlowDeskRepository.saveGovernorate(governorate); FlowDeskRepository.saveSoundSettings(savedSounds); setActiveAlert({ title: t.success || 'Success', message: t.savedSuccess, type: 'success' }); }} className="mt-5 w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 hover-effect">{t.save}</button>
                      </div>
                    </div>
                  )}

                  {settingsTab === 'health' && (
                    <div className="space-y-5 animate-in fade-in duration-300"><div className="settings-card"><div className="flex flex-col md:flex-row md:items-start justify-between gap-5 mb-5"><div><p className="text-xs font-extrabold text-blue-400 mb-2 flex items-center gap-2"><Activity size={16} /> Break Routine</p><h4 className="text-2xl font-black text-slate-950 dark:text-white">{t.healthAlert}</h4><p className="text-sm text-slate-500 mt-2 max-w-xl">{t.healthDesc}</p></div><label className="inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 cursor-pointer hover-effect"><span className="text-sm font-bold text-slate-700 dark:text-slate-200">Enabled</span><input type="checkbox" checked={healthSettings.enabled} onChange={e => setHealthSettings(prev => updateSettingsField(prev, 'enabled', e.target.checked))} className="w-5 h-5 accent-blue-500 cursor-pointer" /></label></div>{healthSettings.enabled && <div className="grid grid-cols-1 xl:grid-cols-[1fr_220px] gap-4"><div className="bg-white dark:bg-[#111821] p-4 rounded-2xl border border-slate-200 dark:border-white/10"><label className="block text-sm text-slate-500 mb-3 font-bold">{t.remindEvery}</label><div className="flex items-center gap-4"><input type="range" min="10" max="120" step="5" value={healthSettings.interval} onChange={e => setHealthSettings(prev => updateSettingsField(prev, 'interval', parseInt(e.target.value)))} className="flex-1 accent-blue-500" /><span className="text-blue-400 font-mono font-black text-xl min-w-[3ch]">{healthSettings.interval}</span></div></div><div className="bg-blue-500/10 border border-blue-400/20 rounded-2xl p-4"><p className="text-xs text-slate-500 font-bold">Message</p><p className="text-sm text-blue-300 mt-2 leading-relaxed">Short break, stretch, and drink water before continuing.</p></div></div>}<button onClick={() => { FlowDeskRepository.saveHealthSettings(healthSettings); setActiveAlert({ title: t.success || 'Success', message: t.savedSuccess, type: 'success' }); }} className="mt-5 w-full py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 hover-effect">{t.save}</button></div></div>
                  )}

                  {settingsTab === 'profile' && (
                    <div className="space-y-5 animate-in fade-in duration-300">
                      <div className="settings-card">
                        <div className="flex flex-col lg:flex-row gap-6 items-start">
                          <div className="w-full lg:w-64 flex flex-col items-center">
                            <div className="relative mb-4">
                              <div className="w-32 h-32 rounded-[32px] bg-slate-800 border-2 border-dashed border-slate-600 flex items-center justify-center overflow-hidden shadow-xl">
                                {userPhoto ? <img src={userPhoto} alt="User" className="w-full h-full object-cover" /> : <User size={42} className="text-slate-600" />}
                              </div>
                              <label className="absolute -bottom-2 -right-2 p-3 bg-blue-600 rounded-2xl text-white cursor-pointer hover:bg-blue-500 shadow-xl hover-effect">
                                <Upload size={14} /><input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'userPhoto')} className="hidden" />
                              </label>
                            </div>
                            <div className="flex flex-col gap-2 w-full">
                              <label className="w-full py-2 bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-xl text-sm text-slate-700 dark:text-slate-200 cursor-pointer hover-effect flex items-center justify-center gap-2">
                                <Upload size={14} /> Change Photo
                                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(e, 'userPhoto')} className="hidden" />
                              </label>
                              {userPhoto && <button onClick={() => setUserPhoto(null)} className="w-full py-2 bg-rose-500/10 border border-rose-500/20 text-rose-500 rounded-xl text-sm font-bold hover-effect">إزالة الصورة</button>}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0 w-full">
                            <div className="mb-5">
                              <h4 className="text-xl font-extrabold text-slate-950 dark:text-white">Personal Identity</h4>
                              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">بياناتك اللي بتظهر في الهيدر وداخل تجربة البرنامج.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm text-slate-500 font-bold mb-2">{t.userName}</label>
                                <input type="text" value={userName} onChange={(e) => setUserName(e.target.value)} className="settings-input text-lg font-bold" />
                              </div>
                              <div>
                                <label className="block text-sm text-slate-500 font-bold mb-2">Job Title</label>
                                <input type="text" value={userRole} onChange={(e) => setUserRole(e.target.value)} placeholder={lang === 'ar' ? 'Designer جرافيك / آرت دايركتور' : 'Graphic Designer / Art Director'} className="settings-input text-lg font-bold" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <button onClick={saveCurrentUserProfile} className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/20 hover-effect">Save Profile</button>
                    </div>
                  )}

                  {settingsTab === 'about' && (
                    <div className="space-y-5 animate-in fade-in duration-300"><div className="settings-card"><div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6"><div className="flex items-center gap-4 min-w-0"><img src={brandAppIcon} alt="FlowDesk" className="w-16 h-16 rounded-3xl object-cover shadow-xl border border-blue-400/20" /><div className="min-w-0"><h4 className="text-2xl font-black text-slate-950 dark:text-white">FlowDesk</h4><p className="text-sm text-slate-500 mt-1">Workflow. Organized. Together.</p>{appVersion && <p className="text-xs text-slate-400 font-mono mt-2">{t.version} {appVersion}</p>}</div></div><button onClick={checkForUpdates} className="px-6 py-3 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/20 text-slate-700 dark:text-slate-200 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-colors hover-effect border border-slate-300 dark:border-white/5"><Download size={14} /> {t.checkUpdates}</button></div></div><div className="grid grid-cols-1 md:grid-cols-3 gap-4"><div className="settings-card"><p className="text-xs text-slate-500 font-bold mb-2">Workspace</p><p className="text-lg font-black text-slate-950 dark:text-white truncate">{workspaceName || 'FlowDesk'}</p></div><div className="settings-card"><p className="text-xs text-slate-500 font-bold mb-2">{t.companies}</p><p className="text-lg font-black text-slate-950 dark:text-white">{Object.keys(companies).length}</p></div><div className="settings-card"><p className="text-xs text-slate-500 font-bold mb-2">{t.scheduledTasks}</p><p className="text-lg font-black text-slate-950 dark:text-white">{tasks.length}</p></div></div><div className="settings-card"><h5 className="font-extrabold text-slate-950 dark:text-white mb-3">Usage Notes</h5><div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-500"><p className="rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4">FlowDesk organizes creative work across companies, clients, managers, and tasks.</p><p className="rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4">Export a backup before major edits or moving to another machine.</p></div></div></div>
                  )}

                  {settingsTab === 'users' && (
                    <div className="space-y-6 animate-in fade-in duration-300">
                      {/* HEADER CARD */}
                      <div className="settings-card">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <h4 className="text-xl font-black text-slate-950 dark:text-white flex items-center gap-2">
                              <Shield size={20} className="text-blue-500" />
                              {lang === 'ar' ? 'إدارة الحسابات وتخصيص الوصول للعملاء' : 'User Accounts & Client Access Scoping'}
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                              {lang === 'ar'
                                ? 'أنشئ حسابات بريد إلكتروني وباسورد للمستخدمين، وحدد الشركات والعملاء المسموح بكل حساب لرؤيتها والتفاعل معها.'
                                : 'Create email & password accounts and scope which companies/clients each user can access.'}
                            </p>
                          </div>
                          <span className="px-3.5 py-1.5 rounded-xl bg-blue-500/10 text-blue-500 text-xs font-black self-start md:self-auto">
                            {workspaceUsers.length} {lang === 'ar' ? 'حساب مسجل' : 'accounts'}
                          </span>
                        </div>
                      </div>

                      {/* ADD NEW USER FORM */}
                      <div className="settings-card space-y-4">
                        <h5 className="font-extrabold text-sm text-slate-900 dark:text-white flex items-center gap-2 border-b border-slate-200 dark:border-white/10 pb-3">
                          <Plus size={16} className="text-blue-500" />
                          {lang === 'ar' ? 'إضافة حساب مستخدم جديد' : 'Add New User Account'}
                        </h5>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">
                              {lang === 'ar' ? 'الاسم الكامل' : 'Full Name'}
                            </label>
                            <input
                              type="text"
                              value={workspaceUserDraft.name}
                              onChange={e => setWorkspaceUserDraft(prev => ({ ...prev, name: e.target.value }))}
                              placeholder={lang === 'ar' ? 'مثال: أحمد مصطفى' : 'e.g. Ahmed Mostafa'}
                              className="settings-input text-sm"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">
                              {lang === 'ar' ? 'البريد الإلكتروني (تسجيل الدخول)' : 'Email Address'}
                            </label>
                            <input
                              type="email"
                              value={workspaceUserDraft.email}
                              onChange={e => setWorkspaceUserDraft(prev => ({ ...prev, email: e.target.value }))}
                              placeholder="user@company.com"
                              className="settings-input text-sm"
                              dir="ltr"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">
                              {lang === 'ar' ? 'كلمة المرور' : 'Password'}
                            </label>
                            <input
                              type="text"
                              value={workspaceUserDraft.password}
                              onChange={e => setWorkspaceUserDraft(prev => ({ ...prev, password: e.target.value }))}
                              placeholder="••••••••"
                              className="settings-input text-sm font-mono"
                              dir="ltr"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 mb-1">
                              {lang === 'ar' ? 'الرتبة والصلاحية' : 'Role & Access Level'}
                            </label>
                            <select
                              value={workspaceUserDraft.accessRole}
                              onChange={e => setWorkspaceUserDraft(prev => ({ ...prev, accessRole: e.target.value }))}
                              className="settings-input text-sm font-bold"
                            >
                              <option value={USER_ROLES.owner}>{lang === 'ar' ? 'مالك (وصول كامل لجميع العملاء)' : 'Owner (Full Access)'}</option>
                              <option value={USER_ROLES.manager}>{lang === 'ar' ? 'Account Manager (مسؤول حسابات)' : 'Account Manager'}</option>
                              <option value={USER_ROLES.designer}>{lang === 'ar' ? 'Designer (مصمم)' : 'Designer'}</option>
                              <option value={USER_ROLES.client}>{lang === 'ar' ? 'عميل (قراءة فقط)' : 'Client (Read Only)'}</option>
                            </select>
                          </div>
                        </div>

                        {/* ALLOWED COMPANIES + CLIENTS */}
                        <div className="pt-3 border-t border-slate-200 dark:border-white/10 space-y-3">
                          <label className="block text-xs font-extrabold text-slate-700 dark:text-slate-200">
                            📌 {lang === 'ar' ? 'تحديد العملاء المسموح لهذا الحساب برؤيتهم (لكل شركة):' : 'Select clients visible to this user (per company):'}
                          </label>

                          {workspaceUserDraft.accessRole === USER_ROLES.owner ? (
                            <p className="text-xs text-emerald-500 font-bold bg-emerald-500/10 p-2.5 rounded-xl border border-emerald-500/20">
                              ✓ {lang === 'ar' ? 'رتبة المالك لها صلاحيات رؤية جميع الشركات والعملاء تلقائياً.' : 'Owner role has full access to all companies and clients automatically.'}
                            </p>
                          ) : (
                            <div className="space-y-3">
                              {Object.entries(companies).map(([compId, compConfig]) => {
                                const compClients = Array.isArray(compConfig.clients) ? compConfig.clients : [];
                                const isCompChecked = workspaceUserDraft.companyIds.includes(compId);
                                const assignedClients = Array.isArray(workspaceUserDraft.clientAccess?.[compId]) ? workspaceUserDraft.clientAccess[compId] : [];
                                return (
                                  <div key={compId} className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                                    {/* Company header checkbox */}
                                    <label className={`flex items-center gap-2.5 p-3 cursor-pointer select-none transition-all ${isCompChecked ? 'bg-blue-600/10' : 'bg-white dark:bg-white/5'}`}>
                                      <input
                                        type="checkbox"
                                        checked={isCompChecked}
                                        onChange={e => {
                                          setWorkspaceUserDraft(prev => {
                                            const list = prev.companyIds.filter(id => id !== 'all');
                                            const next = e.target.checked ? [...list, compId] : list.filter(id => id !== compId);
                                            const ca = { ...prev.clientAccess };
                                            if (!e.target.checked) delete ca[compId];
                                            return { ...prev, companyIds: next, clientAccess: ca };
                                          });
                                        }}
                                        className="w-4 h-4 rounded accent-blue-600"
                                      />
                                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${compConfig.theme?.bg || 'bg-blue-500'}`} />
                                      <span className={`text-xs font-black flex-1 ${isCompChecked ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-300'}`}>{compConfig.name}</span>
                                      <span className="text-[10px] text-slate-400">{compClients.length} {lang === 'ar' ? 'عميل' : 'clients'}</span>
                                    </label>
                                    {/* Clients list (shown only if company is checked) */}
                                    {isCompChecked && compClients.length > 0 && (
                                      <div className="px-3 pb-3 pt-1 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-200 dark:border-white/10">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-[10px] font-bold text-slate-500">{lang === 'ar' ? 'اختر العملاء المسموح بهم:' : 'Select allowed clients:'}</span>
                                          <button type="button" onClick={() => setWorkspaceUserDraft(prev => ({ ...prev, clientAccess: { ...prev.clientAccess, [compId]: assignedClients.length === compClients.length ? [] : [...compClients] } }))}
                                            className="text-[10px] font-black text-blue-500 hover:text-blue-400 transition-colors">
                                            {assignedClients.length === compClients.length ? (lang === 'ar' ? 'إلغاء الكل' : 'Deselect All') : (lang === 'ar' ? 'تحديد الكل' : 'Select All')}
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1.5">
                                          {compClients.map(clientName => {
                                            const isClientChecked = assignedClients.length === 0 || assignedClients.includes(String(clientName));
                                            return (
                                              <label key={clientName} className={`flex items-center gap-1.5 p-2 rounded-lg border cursor-pointer text-[11px] font-bold select-none transition-all ${isClientChecked ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500'}`}>
                                                <input type="checkbox" checked={isClientChecked}
                                                  onChange={e => {
                                                    setWorkspaceUserDraft(prev => {
                                                      const currentClients = Array.isArray(prev.clientAccess?.[compId]) && prev.clientAccess[compId].length > 0
                                                        ? [...prev.clientAccess[compId]]
                                                        : [...compClients];
                                                      const next = e.target.checked
                                                        ? [...new Set([...currentClients, String(clientName)])]
                                                        : currentClients.filter(c => c !== String(clientName));
                                                      return { ...prev, clientAccess: { ...prev.clientAccess, [compId]: next } };
                                                    });
                                                  }}
                                                  className="w-3 h-3 rounded accent-blue-600"
                                                />
                                                <span className="truncate">{clientName}</span>
                                              </label>
                                            );
                                          })}
                                        </div>
                                        {assignedClients.length > 0 && assignedClients.length < compClients.length && (
                                          <p className="text-[10px] text-amber-500 font-bold mt-2">
                                            ⚠️ {lang === 'ar' ? `سيرى ${assignedClients.length} عميل فقط من أصل ${compClients.length}` : `Will see ${assignedClients.length} of ${compClients.length} clients`}
                                          </p>
                                        )}
                                        {assignedClients.length === 0 && (
                                          <p className="text-[10px] text-emerald-500 font-bold mt-2">
                                            ✓ {lang === 'ar' ? 'سيرى جميع عملاء هذه الشركة' : 'Will see all clients of this company'}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>


                        <button
                          type="button"
                          onClick={handleAddWorkspaceUser}
                          className="solid-primary py-3 px-6 rounded-xl text-xs font-black flex items-center justify-center gap-2 shadow-lg cursor-pointer"
                        >
                          <UserPlus size={16} />
                          <span>{lang === 'ar' ? 'حفظ وإنشاء الحساب' : 'Create User Account'}</span>
                        </button>
                      </div>

                      {/* WORKSPACE USERS LIST */}
                      <div className="settings-card space-y-4">
                        <h5 className="font-extrabold text-sm text-slate-900 dark:text-white flex items-center gap-2">
                          <Users size={16} className="text-blue-500" />
                          {lang === 'ar' ? 'قائمة الحسابات المسجلة' : 'Registered User Accounts'}
                        </h5>

                        <div className="space-y-3">
                          {workspaceUsers.map((user) => {
                            const isCurrentUser = user.id === currentUser.id;
                            const allowedCompNames = (user.companyIds || []).includes('all') || user.accessRole === USER_ROLES.owner
                              ? (lang === 'ar' ? 'جميع الشركات والعملاء' : 'All Companies & Clients')
                              : (user.companyIds || []).map(id => companies[id]?.name).filter(Boolean).join(' ، ') || (lang === 'ar' ? 'لا يوجد عملاء مخصصين' : 'No clients assigned');

                            return (
                              <div
                                key={user.id}
                                className="rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 overflow-hidden transition-all"
                              >
                                {/* --- VIEW MODE --- */}
                                {editingUserId !== user.id ? (
                                  <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-blue-600 to-purple-600 text-white font-black text-sm flex items-center justify-center overflow-hidden shrink-0 shadow-md">
                                        {user.photo ? (
                                          <img src={user.photo} alt="User" className="w-full h-full object-cover" />
                                        ) : (
                                          (user.name || user.email || 'U')[0].toUpperCase()
                                        )}
                                      </div>
                                      <div className="min-w-0 space-y-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-black text-sm text-slate-900 dark:text-white">{user.name}</span>
                                          {isCurrentUser && (
                                            <span className="px-2 py-0.5 text-[10px] font-black bg-blue-500/15 text-blue-500 rounded-md">
                                              {lang === 'ar' ? 'أنت' : 'You'}
                                            </span>
                                          )}
                                          <span className="px-2 py-0.5 text-[10px] font-extrabold bg-slate-200 dark:bg-white/10 text-slate-700 dark:text-slate-300 rounded-md">
                                            {getRoleLabel(user.accessRole)}
                                          </span>
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate" dir="ltr">{user.email}</p>
                                        <p className="text-[11px] font-semibold text-blue-600 dark:text-blue-400 truncate">
                                          🔑 {lang === 'ar' ? 'الوصول المتاح:' : 'Access:'} {allowedCompNames}
                                        </p>
                                      </div>
                                    </div>
                                    {!isCurrentUser && (
                                      <div className="flex items-center gap-2 self-end md:self-center">
                                        <button
                                          type="button"
                                          onClick={() => { setEditingUserId(user.id); setEditingUserDraft({ name: user.name || '', email: user.email || '', password: '', accessRole: user.accessRole || USER_ROLES.designer, companyIds: user.companyIds || [], clientAccess: user.clientAccess || {} }); }}
                                          className="p-2.5 rounded-xl text-blue-500 hover:bg-blue-500/10 border border-blue-500/20 text-xs font-extrabold flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                        >
                                          <Edit size={14} />
                                          <span>{lang === 'ar' ? 'تعديل' : 'Edit'}</span>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => removeWorkspaceUser(user.id)}
                                          className="p-2.5 rounded-xl text-rose-500 hover:bg-rose-500/10 border border-rose-500/20 text-xs font-extrabold flex items-center justify-center gap-1.5 transition-all cursor-pointer"
                                        >
                                          <Trash2 size={14} />
                                          <span>{lang === 'ar' ? 'حذف' : 'Delete'}</span>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  /* --- EDIT MODE --- */
                                  <div className="p-4 space-y-4 border-t-2 border-blue-500/40 bg-blue-500/5">
                                    <p className="text-xs font-black text-blue-400 flex items-center gap-1.5">
                                      <Edit size={13} /> {lang === 'ar' ? `تعديل حساب: ${user.name}` : `Editing: ${user.name}`}
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">{lang === 'ar' ? 'الاسم' : 'Full Name'}</label>
                                        <input type="text" value={editingUserDraft.name} onChange={e => setEditingUserDraft(p => ({...p, name: e.target.value}))} className="settings-input text-sm" />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">{lang === 'ar' ? 'البريد الإلكتروني' : 'Email'}</label>
                                        <input type="email" value={editingUserDraft.email} onChange={e => setEditingUserDraft(p => ({...p, email: e.target.value}))} className="settings-input text-sm" dir="ltr" />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">{lang === 'ar' ? 'كلمة مرور جديدة (اتركها فارغة للإبقاء)' : 'New Password (leave blank to keep)'}</label>
                                        <input type="text" value={editingUserDraft.password} onChange={e => setEditingUserDraft(p => ({...p, password: e.target.value}))} className="settings-input text-sm font-mono" dir="ltr" placeholder="••••••••" />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 mb-1">{lang === 'ar' ? 'الرتبة' : 'Role'}</label>
                                        <select value={editingUserDraft.accessRole} onChange={e => setEditingUserDraft(p => ({...p, accessRole: e.target.value}))} className="settings-input text-sm">
                                          <option value={USER_ROLES.owner}>{lang === 'ar' ? 'مالك' : 'Owner'}</option>
                                          <option value={USER_ROLES.manager}>Account Manager</option>
                                          <option value={USER_ROLES.designer}>Designer</option>
                                          <option value={USER_ROLES.client}>{lang === 'ar' ? 'عميل' : 'Client'}</option>
                                        </select>
                                      </div>
                                    </div>
                                    {/* Company + Client access */}
                                    {editingUserDraft.accessRole !== USER_ROLES.owner && (
                                      <div className="space-y-2">
                                        <label className="block text-xs font-bold text-slate-500 dark:text-slate-400">📌 {lang === 'ar' ? 'الشركات والعملاء المسموح بهم:' : 'Allowed companies & clients:'}</label>
                                        {Object.entries(companies).map(([cId, cConf]) => {
                                          const compClients = Array.isArray(cConf.clients) ? cConf.clients : [];
                                          const isCompChecked = editingUserDraft.companyIds.includes(cId);
                                          const assignedClients = Array.isArray(editingUserDraft.clientAccess?.[cId]) ? editingUserDraft.clientAccess[cId] : [];
                                          return (
                                            <div key={cId} className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden">
                                              <label className={`flex items-center gap-2.5 p-2.5 cursor-pointer select-none transition-all ${isCompChecked ? 'bg-blue-600/10' : 'bg-white dark:bg-white/5'}`}>
                                                <input type="checkbox" checked={isCompChecked}
                                                  onChange={e => setEditingUserDraft(p => {
                                                    const list = p.companyIds.filter(x => x !== 'all');
                                                    const next = e.target.checked ? [...list, cId] : list.filter(x => x !== cId);
                                                    const ca = { ...p.clientAccess };
                                                    if (!e.target.checked) delete ca[cId];
                                                    return { ...p, companyIds: next, clientAccess: ca };
                                                  })}
                                                  className="w-3.5 h-3.5 accent-blue-600"
                                                />
                                                <span className={`w-2 h-2 rounded-full shrink-0 ${cConf.theme?.bg || 'bg-blue-500'}`} />
                                                <span className={`text-xs font-black flex-1 ${isCompChecked ? 'text-blue-500' : 'text-slate-500'}`}>{cConf.name}</span>
                                                <span className="text-[10px] text-slate-400">{compClients.length} {lang === 'ar' ? 'عميل' : 'clients'}</span>
                                              </label>
                                              {isCompChecked && compClients.length > 0 && (
                                                <div className="px-3 pb-2 pt-1 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-200 dark:border-white/10">
                                                  <div className="flex justify-between items-center mb-1.5">
                                                    <span className="text-[10px] font-bold text-slate-400">{lang === 'ar' ? 'العملاء:' : 'Clients:'}</span>
                                                    <button type="button" onClick={() => setEditingUserDraft(p => ({ ...p, clientAccess: { ...p.clientAccess, [cId]: assignedClients.length === compClients.length ? [] : [...compClients] } }))} className="text-[10px] font-black text-blue-500">
                                                      {assignedClients.length === compClients.length ? (lang === 'ar' ? 'إلغاء الكل' : 'Deselect All') : (lang === 'ar' ? 'تحديد الكل' : 'Select All')}
                                                    </button>
                                                  </div>
                                                  <div className="grid grid-cols-2 gap-1">
                                                    {compClients.map(cn => {
                                                      const isClientOn = assignedClients.length === 0 || assignedClients.includes(String(cn));
                                                      return (
                                                        <label key={cn} className={`flex items-center gap-1.5 p-1.5 rounded-lg border cursor-pointer text-[11px] font-bold select-none ${isClientOn ? 'bg-blue-500/10 border-blue-500/30 text-blue-500' : 'bg-white dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-500'}`}>
                                                          <input type="checkbox" checked={isClientOn}
                                                            onChange={e => setEditingUserDraft(p => {
                                                              const cur = Array.isArray(p.clientAccess?.[cId]) && p.clientAccess[cId].length > 0 ? [...p.clientAccess[cId]] : [...compClients];
                                                              const next = e.target.checked ? [...new Set([...cur, String(cn)])] : cur.filter(x => x !== String(cn));
                                                              return { ...p, clientAccess: { ...p.clientAccess, [cId]: next } };
                                                            })}
                                                            className="w-3 h-3 accent-blue-600"
                                                          />
                                                          <span className="truncate">{cn}</span>
                                                        </label>
                                                      );
                                                    })}
                                                  </div>
                                                  {assignedClients.length > 0 && assignedClients.length < compClients.length && (
                                                    <p className="text-[10px] text-amber-500 font-bold mt-1">⚠️ {lang === 'ar' ? `${assignedClients.length} من ${compClients.length} عميل فقط` : `${assignedClients.length}/${compClients.length} clients`}</p>
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 pt-1">
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          const updates = {
                                            name: editingUserDraft.name.trim() || user.name,
                                            email: editingUserDraft.email.trim() || user.email,
                                            accessRole: editingUserDraft.accessRole,
                                            companyIds: editingUserDraft.accessRole === USER_ROLES.owner ? Object.keys(companies) : (editingUserDraft.companyIds.length ? editingUserDraft.companyIds : Object.keys(companies)),
                                            clientAccess: editingUserDraft.accessRole === USER_ROLES.owner ? {} : (editingUserDraft.clientAccess || {}),
                                            ...(editingUserDraft.password.trim() ? { password: editingUserDraft.password.trim() } : {})
                                          };
                                          await updateWorkspaceUserAccess(user.id, updates);
                                          setEditingUserId(null);
                                          setActiveAlert({ title: t.success || 'Success', message: lang === 'ar' ? '✅ تم حفظ التعديلات بنجاح.' : '✅ Changes saved successfully.', type: 'success' });
                                        }}
                                        className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-black flex items-center justify-center gap-2 transition-all cursor-pointer"
                                      >
                                        <Save size={14} /> {lang === 'ar' ? 'حفظ التعديلات' : 'Save Changes'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setEditingUserId(null)}
                                        className="px-4 py-2.5 rounded-xl bg-slate-200 dark:bg-white/10 text-slate-600 dark:text-slate-300 text-xs font-bold transition-all cursor-pointer"
                                      >
                                        {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                      </div>
                    </div>
                  )}
                </section>
              </div>
            </div>
          </div>
        )
      }

      {/* HEALTH ALERT */}
      {
        showHealthAlert && (
          <div className="mindful-alert-overlay z-[70]">
            <div className="mindful-alert-card mindful-alert-health">
              <button onClick={() => setShowHealthAlert(false)} className="mindful-alert-close" aria-label={t.dismiss}><X size={18} /></button>
              <div className="mindful-alert-icon">
                <Activity size={34} />
              </div>
              <div className="mindful-alert-kicker">
                <Coffee size={15} />
                <span>{lang === 'ar' ? 'Short Break' : 'Short break'}</span>
              </div>
              <h2>{t.healthBreak}</h2>
              <p>{t.healthBreakMsg}</p>
              <div className="mindful-alert-meta">
                <span><Clock size={14} /> {healthSettings.interval} {lang === 'ar' ? 'minutes between alerts' : 'min routine'}</span>
                <span><Activity size={14} /> {lang === 'ar' ? 'Move a little' : 'Move a little'}</span>
              </div>
              <div className="mindful-alert-actions">
                <button onClick={() => { setHealthSettings(prev => updateSettingsField(prev, 'lastCheck', Date.now())); setShowHealthAlert(false); }} className="mindful-alert-primary">
                  <CheckCircle size={18} />
                  <span>{t.willMove}</span>
                </button>
                <button onClick={snoozeHealthAlert} className="mindful-alert-secondary">
                  <Bell size={16} />
                  <span>{t.remindAfter10}</span>
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* ALL PRAYERS LIST MODAL */}
      {
        showAllPrayersModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[90] flex items-center justify-center p-4 animate-in fade-in duration-300" onClick={() => setShowAllPrayersModal(false)}>
            <div className="prayer-modal-shell" onClick={e => e.stopPropagation()}>
              <button onClick={() => setShowAllPrayersModal(false)} className="detail-icon-button absolute top-5 left-5 w-10 h-10 rounded-xl"><X size={20} /></button>
              <div className="text-center mb-5">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-blue-600/12 border border-blue-500/25 flex items-center justify-center">
                  <img src={adhanIcon} alt="Prayers" className="w-7 h-7 object-contain" />
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white">{t.prayersList}</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm flex justify-center items-center gap-2 mt-2">
                  <MapPin size={14} /> {EGYPT_GOVERNORATES.find(g => g.id === governorate)?.[lang === 'ar' ? 'ar' : 'en']}
                </p>
              </div>

              <div className="prayer-summary-card">
                <div>
                  <p className="text-xs font-extrabold text-slate-500 dark:text-slate-400">{t.nextPrayer}</p>
                  <h4 className="text-xl font-black text-slate-950 dark:text-white mt-1">{nextPrayer?.name || '--'}</h4>
                </div>
                <div className="text-end">
                  <p className="font-mono text-2xl font-black text-blue-400">{getPrayerRemaining()}</p>
                  <p className="text-xs text-slate-500 mt-1">{nextPrayer?.time ? nextPrayer.time.toLocaleTimeString(lang === 'ar' ? 'ar-EG' : 'en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '--:--'}</p>
                </div>
              </div>

              <div className="space-y-2.5 mt-4">
                {['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'].map((key) => {
                  const isNext = nextPrayer?.key?.replace('Next', '') === key;
                  const isDone = prayerStatus[key];

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => togglePrayerStatus(key)}
                      className={`prayer-row ${isNext ? 'is-next' : ''} ${isDone ? 'is-done' : ''}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="prayer-row-check">
                          {isDone ? <CheckCircle size={15} /> : isNext ? <Bell size={14} /> : <Clock size={14} />}
                        </span>
                        <span className="font-extrabold text-base truncate">{t.prayers[key]}</span>
                      </div>
                      <div className="text-end">
                        <p className="font-mono font-black text-lg">{getPrayerTimeLabel(key)}</p>
                        <p className="text-[10px] font-bold opacity-70">{isDone ? 'Completed' : isNext ? t.upcomingPrayer : 'لم تتم بعد'}</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <button onClick={() => setIsMuted(!isMuted)} className="prayer-secondary-action">
                  {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                  <span>{isMuted ? 'Muted' : 'Prayer Alert Active'}</span>
                </button>
                <button onClick={() => { setActiveModal('settings'); setSettingsTab('sound'); setShowAllPrayersModal(false); }} className="prayer-secondary-action">
                  <Settings size={16} />
                  <span>Prayer Settings</span>
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* MOBILE FULL SCREEN NOTIFICATION PAGE (SMALL SCREENS ONLY) */}
      {isNotificationPanelOpen && (
        <div className="md:hidden fixed inset-0 z-[100] w-full h-full bg-white dark:bg-[#0c1017] text-slate-900 dark:text-slate-100 flex flex-col overflow-hidden animate-in fade-in duration-200 pb-20">
          <div
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#475569 transparent' }}
            className="w-full h-full p-4 space-y-4 font-sans flex flex-col overflow-y-auto flex-1"
          >
            {renderNotificationContent()}
          </div>
        </div>
      )}

      {/* TRELLO-LIKE TASK DETAILS MODAL */}
      {
        selectedTask && (
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-3 md:p-4 overflow-y-auto"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedTask(null);
                setActiveTaskPopover(null);
                cancelInlineTaskEdit();
              }
            }}
          >
            <div className="mobile-fullscreen-modal bg-white dark:bg-[#18191c] text-slate-800 dark:text-slate-100 md:rounded-xl shadow-2xl w-full md:max-w-3xl flex flex-col my-0 md:my-auto border-0 md:border border-slate-200 dark:border-slate-800 transition-all font-sans dir-ltr">
              
              {/* HEADER */}
              <div className="px-3 sm:px-4 md:px-6 pt-3.5 sm:pt-5 md:pt-6 pb-3 flex items-start justify-between gap-3 relative border-b border-slate-100 dark:border-slate-800/80">
                <div className="flex items-center gap-2.5 sm:gap-3.5 flex-1 min-w-0 flex-wrap sm:flex-nowrap">
                  <button 
                    type="button" 
                    onClick={() => {
                      const newStatus = selectedTask.status === 'done' ? 'in_progress' : 'done';
                      moveTask(selectedTask.id, newStatus);
                      setSelectedTask(prev => ({ ...prev, status: newStatus }));
                    }}
                    className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center shrink-0 transition-all cursor-pointer min-w-[24px] min-h-[24px] ${
                      selectedTask.status === 'done' 
                        ? 'bg-emerald-500 border-2 border-emerald-500 text-white shadow-md shadow-emerald-500/30' 
                        : 'border border-slate-300 dark:border-slate-500/80 hover:border-emerald-500 text-transparent hover:text-emerald-500/40'
                    }`}
                    title={lang === 'ar' ? 'تحديد كـ "مكتملة"' : 'Mark as Complete'}
                  >
                    <Check size={14} strokeWidth={2.5} />
                  </button>
                  
                  {inlineEditField === 'title' ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <input
                        type="text"
                        dir="auto"
                        value={inlineEditValue}
                        onChange={e => setInlineEditValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveInlineTaskField();
                          if (e.key === 'Escape') cancelInlineTaskEdit();
                        }}
                        className="flex-1 min-w-0 text-base sm:text-lg font-bold bg-slate-50 dark:bg-slate-800/80 border border-blue-500 rounded-xl px-3 py-1.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        autoFocus
                      />
                      <button onClick={saveInlineTaskField} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shrink-0 shadow-sm">{t.save}</button>
                      <button onClick={cancelInlineTaskEdit} className="px-3 py-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-lg transition-colors cursor-pointer shrink-0">{t.cancel}</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
                      <h2 
                        dir="auto"
                        onClick={() => startInlineTaskEdit('title')}
                        className="text-xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/60 px-2.5 py-1 rounded-xl transition-colors leading-snug flex items-center gap-2.5 group"
                        title={lang === 'ar' ? 'انقر لتعديل العنوان' : 'Click to edit title'}
                      >
                        <span className="truncate">{selectedTask.title}</span>
                        <Edit size={18} className="text-slate-400 group-hover:text-blue-500 transition-colors shrink-0" />
                      </h2>

                      {/* HEADER STATUS BADGE */}
                      {(() => {
                        const statusMap = {
                          scheduled: { label: 'New', style: 'bg-blue-500/20 text-blue-400 border-blue-500/40' },
                          ready: { label: 'Ready', style: 'bg-purple-500/20 text-purple-400 border-purple-500/40' },
                          in_progress: { label: 'Working', style: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
                          review: { label: 'Review', style: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/40' },
                          revision: { label: 'Edit', style: 'bg-orange-500/20 text-orange-400 border-orange-500/40' },
                          done: { label: 'Confirm', style: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' }
                        };
                        const isOverdue = isTaskOverdue(selectedTask) && selectedTask.status !== 'done';
                        const info = isOverdue
                          ? { label: lang === 'ar' ? 'متأخرة' : 'Overdue', style: 'bg-rose-500/20 text-rose-400 border-rose-500/40 animate-pulse' }
                          : (statusMap[selectedTask.status] || statusMap.scheduled);

                        return (
                          <span className={`px-3 py-1 rounded-full text-xs font-black border shadow-sm shrink-0 ${info.style}`}>
                            {info.label}
                          </span>
                        );
                      })()}
                    </div>
                  )}
                </div>

                <button 
                  onClick={() => { setSelectedTask(null); setActiveTaskPopover(null); cancelInlineTaskEdit(); }}
                  className="w-11 h-11 rounded-xl bg-slate-100 dark:bg-white/10 text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white flex items-center justify-center shrink-0 transition-colors min-w-[44px] min-h-[44px] cursor-pointer"
                  title={lang === 'ar' ? 'إغلاق' : 'Close'}
                >
                  <X size={22} />
                </button>
              </div>

              {/* ACTION BUTTONS / CAPSULES BAR */}
              <div className="px-3 sm:px-6 py-2.5 flex items-center gap-2 overflow-x-auto scrollbar-none no-scrollbar flex-nowrap border-b border-slate-100 dark:border-slate-800/80">

                {/* 1. KANBAN STATUS CAPSULE POPOVER */}
                <div className="relative shrink-0">
                  {(() => {
                    const statusLabels = {
                      scheduled: { label: lang === 'ar' ? 'جديدة' : 'New', style: 'bg-blue-500/15 text-blue-400 border-blue-500/40 shadow-sm' },
                      ready: { label: lang === 'ar' ? 'جاهزة' : 'Ready', style: 'bg-purple-500/15 text-purple-400 border-purple-500/40 shadow-sm' },
                      in_progress: { label: lang === 'ar' ? 'قيد العمل' : 'Working', style: 'bg-amber-500/15 text-amber-400 border-amber-500/40 shadow-sm' },
                      review: { label: lang === 'ar' ? 'مراجعة' : 'Review', style: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/40 shadow-sm' },
                      revision: { label: lang === 'ar' ? 'تعديل' : 'Edit', style: 'bg-orange-500/15 text-orange-400 border-orange-500/40 shadow-sm' },
                      done: { label: lang === 'ar' ? 'تأكيد' : 'Confirm', style: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40 shadow-sm' }
                    };
                    const currentStatusInfo = statusLabels[selectedTask.status] || statusLabels.scheduled;
                    return (
                      <button 
                        type="button"
                        onClick={() => setActiveTaskPopover(activeTaskPopover === 'labels' ? null : 'labels')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold border transition-all shrink-0 whitespace-nowrap cursor-pointer ${
                          activeTaskPopover === 'labels'
                            ? 'ring-2 ring-blue-500 ' + currentStatusInfo.style
                            : currentStatusInfo.style
                        }`}
                      >
                        <Tag size={14} />
                        <span>{lang === 'ar' ? `الحالة: ${currentStatusInfo.label}` : `Status: ${currentStatusInfo.label}`}</span>
                      </button>
                    );
                  })()}

                  {activeTaskPopover === 'labels' && (
                    <>
                      <div 
                        className="fixed inset-0 bg-black/60 z-[240] backdrop-blur-xs md:hidden animate-in fade-in duration-200"
                        onClick={() => setActiveTaskPopover(null)}
                      />

                      <div className="popover-mobile-sheet md:absolute md:top-full md:left-0 md:mt-2 md:w-72 md:p-4 bg-white dark:bg-[#222428] rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-[250] text-slate-800 dark:text-slate-200">
                        <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mb-3 md:hidden shrink-0" />

                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100 dark:border-slate-700">
                          <span className="font-extrabold text-base md:text-sm text-slate-800 dark:text-slate-100 w-full text-center">
                            {lang === 'ar' ? 'تغيير حالة المهمة' : 'Select Task Status'}
                          </span>
                          <button onClick={() => setActiveTaskPopover(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                            <X size={18} />
                          </button>
                        </div>

                        <div className="text-xs font-bold text-slate-500 dark:text-slate-400 mb-2">
                          {lang === 'ar' ? 'اختر الحالة الجديدة:' : 'Choose Status:'}
                        </div>

                        <div className="space-y-2.5 mb-2">
                          {[
                            { id: 'scheduled', name: lang === 'ar' ? 'جديدة' : 'New', color: 'bg-blue-500/20 text-blue-500 dark:text-blue-400 border border-blue-400/30' },
                            { id: 'ready', name: lang === 'ar' ? 'جاهزة' : 'Ready', color: 'bg-purple-500/20 text-purple-500 dark:text-purple-400 border border-purple-400/30' },
                            { id: 'in_progress', name: lang === 'ar' ? 'قيد العمل' : 'Working', color: 'bg-amber-500/20 text-amber-500 dark:text-amber-400 border border-amber-400/30' },
                            { id: 'review', name: lang === 'ar' ? 'مراجعة' : 'Review', color: 'bg-cyan-500/20 text-cyan-500 dark:text-cyan-400 border border-cyan-400/30' },
                            { id: 'revision', name: lang === 'ar' ? 'تعديل' : 'Edit', color: 'bg-orange-500/20 text-orange-500 dark:text-orange-400 border border-orange-400/30' },
                            { id: 'done', name: lang === 'ar' ? 'تأكيد' : 'Confirm', color: 'bg-emerald-500/20 text-emerald-500 dark:text-emerald-400 border border-emerald-400/30' },
                          ].map((lbl) => {
                            const isSelected = selectedTask.status === lbl.id;
                            return (
                              <div 
                                key={lbl.id} 
                                onClick={() => {
                                  moveTask(selectedTask.id, lbl.id);
                                  setSelectedTask(prev => ({ ...prev, status: lbl.id }));
                                  setActiveTaskPopover(null);
                                }}
                                className={`flex items-center gap-3 p-2 min-h-[48px] rounded-xl cursor-pointer transition-all hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
                                  isSelected ? 'ring-2 ring-blue-500 bg-blue-50/50 dark:bg-blue-950/30' : ''
                                }`}
                              >
                                <input
                                  type="radio"
                                  name="task_status_option"
                                  checked={isSelected}
                                  onChange={() => {}}
                                  className="w-5 h-5 text-blue-600 focus:ring-blue-500 cursor-pointer accent-blue-600"
                                />
                                <div className={`flex-1 min-h-[40px] px-3.5 rounded-xl flex items-center justify-between text-sm font-extrabold shadow-sm ${lbl.color}`}>
                                  <span>{lbl.name}</span>
                                  {isSelected && <CheckCircle size={17} className="shrink-0" />}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* 2. CLIENT CAPSULE POPOVER */}
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveTaskPopover(activeTaskPopover === 'client' ? null : 'client')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold border transition-all shrink-0 whitespace-nowrap cursor-pointer ${
                      activeTaskPopover === 'client'
                        ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50 ring-2 ring-emerald-500'
                        : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25'
                    }`}
                  >
                    <User size={14} className="shrink-0 text-emerald-400" />
                    <span>
                      {selectedTask.client 
                        ? (lang === 'ar' ? `العميل: ${selectedTask.client}` : `Client: ${selectedTask.client}`)
                        : (lang === 'ar' ? 'العميل: (بدون)' : 'Client: (None)')}
                    </span>
                    <ChevronDown size={12} className="text-emerald-400/70 shrink-0" />
                  </button>

                  {activeTaskPopover === 'client' && (
                    <>
                      <div 
                        className="fixed inset-0 bg-black/60 z-[240] backdrop-blur-xs md:hidden animate-in fade-in duration-200"
                        onClick={() => setActiveTaskPopover(null)}
                      />
                      <div className="popover-mobile-sheet md:absolute md:top-full md:left-0 md:mt-2 md:w-64 md:p-3 bg-white dark:bg-[#222428] rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-[250] text-slate-800 dark:text-slate-200 space-y-1.5">
                        <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mb-2 md:hidden shrink-0" />
                        <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-slate-700">
                          <span className="font-extrabold text-sm text-slate-800 dark:text-slate-100 w-full text-center">
                            {lang === 'ar' ? 'اختر العميل' : 'Select Client'}
                          </span>
                          <button onClick={() => setActiveTaskPopover(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg">
                            <X size={16} />
                          </button>
                        </div>

                        <div className="space-y-1 max-h-60 overflow-y-auto">
                          <button
                            type="button"
                            onClick={() => {
                              const updatedTask = { ...selectedTask, client: '' };
                              setSelectedTask(updatedTask);
                              setTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                              if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
                              FlowDeskRepository.saveTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                              setActiveTaskPopover(null);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-xl text-xs font-extrabold transition-all ${
                              !selectedTask.client ? 'bg-emerald-600 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                            }`}
                          >
                            — {lang === 'ar' ? 'بدون عميل' : 'No Client'} —
                          </button>

                          {getAllowedClientsForCompany(selectedTask.category).map((clientName) => {
                            const isAssigned = selectedTask.client === clientName;
                            return (
                              <button
                                key={clientName}
                                type="button"
                                onClick={() => {
                                  const updatedTask = { ...selectedTask, client: clientName };
                                  setSelectedTask(updatedTask);
                                  setTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                                  if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
                                  FlowDeskRepository.saveTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                                  setActiveTaskPopover(null);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-extrabold transition-all ${
                                  isAssigned ? 'bg-emerald-600 text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                }`}
                              >
                                <span className="truncate">{clientName}</span>
                                {isAssigned && <CheckCircle size={14} className="shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* 3. DEADLINE CAPSULE POPOVER (3 CHOICES) */}
                <div className="relative shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveTaskPopover(activeTaskPopover === 'deadline' ? null : 'deadline')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold border transition-all shrink-0 whitespace-nowrap cursor-pointer ${
                      activeTaskPopover === 'deadline'
                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/50 ring-2 ring-blue-500'
                        : 'bg-blue-500/15 text-blue-400 border-blue-500/30 hover:bg-blue-500/25'
                    }`}
                  >
                    <Calendar size={14} className="shrink-0 text-blue-400" />
                    <span>
                      {selectedTask.dueDate || selectedTask.deadlineDate || selectedTask.deadline
                        ? (lang === 'ar' ? `ديدلاين: ${selectedTask.dueDate || selectedTask.deadlineDate || selectedTask.deadline}` : `Deadline: ${selectedTask.dueDate || selectedTask.deadlineDate || selectedTask.deadline}`)
                        : (lang === 'ar' ? 'ديدلاين: (مفتوح)' : 'Deadline: (Set)')}
                    </span>
                    <ChevronDown size={12} className="text-blue-400/70 shrink-0" />
                  </button>

                  {activeTaskPopover === 'deadline' && (
                    <>
                      <div 
                        className="fixed inset-0 bg-black/60 z-[240] backdrop-blur-xs md:hidden animate-in fade-in duration-200"
                        onClick={() => setActiveTaskPopover(null)}
                      />
                      <div className="popover-mobile-sheet md:absolute md:top-full md:left-0 md:mt-2 md:w-64 md:p-3 bg-white dark:bg-[#222428] rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 z-[250] text-slate-800 dark:text-slate-200 space-y-1.5">
                        <div className="w-12 h-1.5 bg-slate-300 dark:bg-slate-600 rounded-full mx-auto mb-2 md:hidden shrink-0" />
                        <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-100 dark:border-slate-700">
                          <span className="font-extrabold text-sm text-slate-800 dark:text-slate-100 w-full text-center">
                            {lang === 'ar' ? 'حدد ميعاد التسليم (ديدلاين)' : 'Set Delivery Deadline'}
                          </span>
                          <button onClick={() => setActiveTaskPopover(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg">
                            <X size={16} />
                          </button>
                        </div>

                        {/* Option 1: اليوم (Today) */}
                        <button
                          type="button"
                          onClick={() => {
                            const todayStr = new Date().toISOString().split('T')[0];
                            const isoStr = new Date(`${todayStr}T23:59:59.999Z`).toISOString();
                            const updatedTask = { ...selectedTask, dueDate: todayStr, deadlineDate: todayStr, deadline: todayStr, deadlineISO: isoStr };
                            setSelectedTask(updatedTask);
                            setTasks(prev => prev.map(t => t.id === selectedTask.id ? updatedTask : t));
                            if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
                            FlowDeskRepository.saveTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                            setActiveTaskPopover(null);
                          }}
                          className="w-full flex items-center justify-between p-2.5 rounded-xl text-xs font-black text-slate-800 dark:text-slate-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 hover:text-emerald-500 transition-all cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                            <span>{lang === 'ar' ? 'اليوم' : 'Today'}</span>
                          </div>
                          <span className="text-[10px] font-bold text-slate-400 font-mono">
                            {new Date().toISOString().split('T')[0]}
                          </span>
                        </button>

                        {/* Option 2: غداً (Tomorrow) */}
                        <button
                          type="button"
                          onClick={() => {
                            const tomorrowStr = new Date(Date.now() + 86400000).toISOString().split('T')[0];
                            const isoStr = new Date(`${tomorrowStr}T23:59:59.999Z`).toISOString();
                            const updatedTask = { ...selectedTask, dueDate: tomorrowStr, deadlineDate: tomorrowStr, deadline: tomorrowStr, deadlineISO: isoStr };
                            setSelectedTask(updatedTask);
                            setTasks(prev => prev.map(t => t.id === selectedTask.id ? updatedTask : t));
                            if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
                            FlowDeskRepository.saveTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                            setActiveTaskPopover(null);
                          }}
                          className="w-full flex items-center justify-between p-2.5 rounded-xl text-xs font-black text-slate-800 dark:text-slate-200 hover:bg-purple-50 dark:hover:bg-purple-950/40 hover:text-purple-500 transition-all cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-purple-500" />
                            <span>{lang === 'ar' ? 'غداً' : 'Tomorrow'}</span>
                          </div>
                          <span className="text-[10px] font-bold text-slate-400 font-mono">
                            {new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                          </span>
                        </button>

                        {/* Option 3: تاريخ مخصص (Custom Date picker) */}
                        <div className="pt-1.5 border-t border-slate-100 dark:border-slate-800 space-y-1.5">
                          <label className="text-[11px] font-black text-slate-400 block px-1">
                            {lang === 'ar' ? 'تاريخ مخصص:' : 'Custom Date:'}
                          </label>
                          <input
                            type="date"
                            value={selectedTask.dueDate || selectedTask.deadlineDate || ''}
                            onChange={(e) => {
                              const customDateStr = e.target.value;
                              const isoStr = customDateStr ? new Date(`${customDateStr}T23:59:59.999Z`).toISOString() : '';
                              const updatedTask = { ...selectedTask, dueDate: customDateStr, deadlineDate: customDateStr, deadline: customDateStr, deadlineISO: isoStr };
                              setSelectedTask(updatedTask);
                              setTasks(prev => prev.map(t => t.id === selectedTask.id ? updatedTask : t));
                              if (typeof queueTaskSync === 'function') queueTaskSync(updatedTask);
                              FlowDeskRepository.saveTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                              setActiveTaskPopover(null);
                            }}
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 dark:text-white cursor-pointer focus:ring-2 focus:ring-blue-500/20"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>



              </div>

              {/* BODY CONTENT */}
              <div className="px-3 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1 min-h-0 max-h-[calc(100dvh-180px)] md:max-h-[75vh]">
                <section className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div 
                      onClick={() => {
                        setInlineEditField('description');
                        setInlineEditValue(selectedTask.description || selectedTask.brief || '');
                      }}
                      className="flex items-center gap-2 font-bold text-base text-slate-800 dark:text-slate-200 cursor-pointer group"
                    >
                      <AlignLeft size={18} />
                      <span>Description</span>
                      <Edit size={14} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
                    </div>

                    {inlineEditField !== 'description' ? (
                      <button
                        type="button"
                        onClick={() => {
                          setInlineEditField('description');
                          setInlineEditValue(selectedTask.description || selectedTask.brief || '');
                        }}
                        className="px-2.5 py-1 text-xs font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-500/20 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
                      >
                        <Edit size={13} />
                        <span>{lang === 'ar' ? 'تعديل' : 'Edit'}</span>
                      </button>
                    ) : (
                      <span className="px-2.5 py-0.5 text-[11px] font-extrabold text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded-full animate-pulse">
                        {lang === 'ar' ? 'وضع التعديل' : 'Editing Mode'}
                      </span>
                    )}
                  </div>

                  {inlineEditField !== 'description' ? (
                    /* VIEW MODE */
                    <div 
                      onClick={() => {
                        setInlineEditField('description');
                        setInlineEditValue(selectedTask.description || selectedTask.brief || '');
                      }}
                      className={`p-4 rounded-xl transition-all cursor-pointer group ${
                        (selectedTask.description || selectedTask.brief)
                          ? 'bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500/60 text-sm text-slate-800 dark:text-slate-200 leading-relaxed'
                          : 'bg-slate-50/50 dark:bg-slate-800/20 border border-dashed border-slate-300 dark:border-slate-700/80 hover:border-blue-400 dark:hover:border-blue-500 text-slate-400 dark:text-slate-500 text-sm font-medium italic flex items-center gap-2'
                      }`}
                    >
                      {(selectedTask.description || selectedTask.brief) ? (
                        <div dir="auto" className="prose dark:prose-invert max-w-none text-sm space-y-1 break-words whitespace-pre-wrap leading-relaxed">
                          {renderDescriptionMarkdown(selectedTask.description || selectedTask.brief)}
                        </div>
                      ) : (
                        <>
                          <Edit size={15} className="group-hover:text-blue-500 transition-colors shrink-0" />
                          <span>{lang === 'ar' ? 'لا يوجد وصف محدد. انقر لإضافة وصف...' : 'No description added. Tap to add one.'}</span>
                        </>
                      )}
                    </div>
                  ) : (
                    /* EDIT MODE */
                    <div className="space-y-2">
                      {/* Hidden File Inputs for Toolbar Image & Attachment */}
                      <input 
                        type="file" 
                        ref={descImageInputRef} 
                        accept="image/*" 
                        onChange={handleDescImageSelect} 
                        className="hidden" 
                      />
                      <input 
                        type="file" 
                        ref={descAttachInputRef} 
                        onChange={handleDescAttachmentSelect} 
                        className="hidden" 
                      />

                      {/* EDITOR BOX */}
                      <div className="border border-blue-500/80 dark:border-blue-500/60 rounded-xl overflow-hidden bg-white dark:bg-[#1a1c20] shadow-md ring-2 ring-blue-500/10">
                        {/* FORMATTING TOOLBAR */}
                        <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-xs">
                          {/* Heading Tt */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('heading')}
                            className="px-2 py-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded flex items-center gap-0.5 font-bold transition-colors"
                            title="Heading (###)"
                          >
                            <span>Tt</span> <ChevronDown size={12} />
                          </button>
                          <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 mx-0.5" />
                          
                          {/* Bold B */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('bold')}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded font-black transition-colors" 
                            title="Bold (**text**)"
                          >
                            B
                          </button>

                          {/* Italic I */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('italic')}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded italic font-serif transition-colors" 
                            title="Italic (*text*)"
                          >
                            I
                          </button>

                          {/* Strikethrough / More */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('strikethrough')}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded font-bold transition-colors" 
                            title="Strikethrough (~~text~~)"
                          >
                            ...
                          </button>
                          <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 mx-0.5" />

                          {/* List */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('list')}
                            className="px-1.5 py-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded flex items-center gap-0.5 transition-colors" 
                            title="Bullet List (- item)"
                          >
                            <span>::=</span> <ChevronDown size={12} />
                          </button>

                          {/* Link */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('link')}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors" 
                            title="Insert Link [text](url)"
                          >
                            <Link2 size={15} />
                          </button>

                          {/* Image */}
                          <button 
                            type="button" 
                            onClick={() => descImageInputRef.current?.click()}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors text-blue-600 dark:text-blue-400" 
                            title="Insert / Upload Image"
                          >
                            <ImageIcon size={15} />
                          </button>

                          {/* Quick Insert Quote / Plus */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('quote')}
                            className="px-1.5 py-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded flex items-center gap-0.5 transition-colors" 
                            title="Insert Quote (> text)"
                          >
                            <span>+</span> <ChevronDown size={12} />
                          </button>
                          <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 mx-0.5" />

                          {/* Attachment */}
                          <button 
                            type="button" 
                            onClick={() => descAttachInputRef.current?.click()}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors text-emerald-600 dark:text-emerald-400" 
                            title="Upload File Attachment"
                          >
                            <Paperclip size={15} />
                          </button>

                          {/* Markdown Code block */}
                          <button 
                            type="button" 
                            onClick={() => applyDescFormatting('codeblock')}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded font-mono font-bold transition-colors" 
                            title="Code Block (```)"
                          >
                            M&darr;
                          </button>

                          {/* Help */}
                          <button 
                            type="button" 
                            onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
                            className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors text-amber-500" 
                            title="Markdown Help"
                          >
                            <HelpCircle size={15} />
                          </button>
                        </div>

                        {/* Markdown Quick Help Banner */}
                        {showMarkdownHelp && (
                          <div className="p-3 bg-blue-50 dark:bg-slate-800/90 border-b border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-200 grid grid-cols-2 gap-2 animate-in fade-in">
                            <div><strong className="text-blue-600">**bold**</strong> → عريض</div>
                            <div><strong className="text-blue-600">*italic*</strong> → مائل</div>
                            <div><strong className="text-blue-600">### Header</strong> → عنوان</div>
                            <div><strong className="text-blue-600">- item</strong> → قائمة</div>
                            <div><strong className="text-blue-600">[title](url)</strong> → رابط</div>
                            <div><strong className="text-blue-600">&gt; quote</strong> → اقتباس</div>
                          </div>
                        )}

                        {/* TEXTAREA */}
                        <textarea
                          ref={descTextareaRef}
                          dir="auto"
                          rows={8}
                          value={inlineEditValue}
                          onChange={(e) => {
                            setInlineEditField('description');
                            setInlineEditValue(e.target.value);
                          }}
                          placeholder="Add a more detailed description..."
                          className="w-full p-4 bg-transparent text-slate-800 dark:text-slate-100 text-sm leading-relaxed outline-none resize-y min-h-[160px]"
                          autoFocus
                        />
                      </div>

                      {/* EDITOR ACTION BUTTONS (SAVE / CANCEL) */}
                      <div className="flex items-center justify-between pt-1">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={saveInlineTaskField}
                            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-colors min-h-[38px]"
                          >
                            {lang === 'ar' ? 'حفظ' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelInlineTaskEdit}
                            className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 font-semibold text-xs sm:text-sm transition-colors min-h-[38px]"
                          >
                            {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                {/* CHECKLIST / SUBTASKS SECTION */}
                {Boolean(selectedTask.subtasks?.length) && (
                  <section className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 font-bold text-base text-slate-800 dark:text-slate-200">
                        <CheckSquare size={18} />
                        <span>Checklist</span>
                      </div>
                      <button 
                        onClick={() => {
                          const updatedTask = { ...selectedTask, subtasks: [] };
                          setSelectedTask(updatedTask);
                          setTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                          queueTaskSync(updatedTask);
                        }}
                        className="px-3 py-1 bg-slate-100 dark:bg-slate-800 hover:bg-rose-100 hover:text-rose-600 text-slate-600 dark:text-slate-300 text-xs font-semibold rounded-md transition-colors"
                      >
                        Delete
                      </button>
                    </div>

                    {/* PROGRESS BAR */}
                    {(() => {
                      const total = selectedTask.subtasks.length;
                      const completed = selectedTask.subtasks.filter(s => s.completed).length;
                      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
                      return (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                            <span>{percent}%</span>
                          </div>
                          <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      );
                    })()}

                    {/* SUBTASK ITEMS */}
                    <div className="space-y-2">
                      {selectedTask.subtasks.map((st, index) => (
                        <div key={st.id || index} className="flex items-center justify-between group p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <label className="flex items-center gap-3 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              checked={Boolean(st.completed)}
                              onChange={() => toggleSubtask(selectedTask.id, st.id)}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                            <span dir="auto" className={`text-sm font-medium ${st.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-800 dark:text-slate-200'}`}>
                              {st.text}
                            </span>
                          </label>
                          <button 
                            onClick={() => removeSubtask(index, false)} 
                            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-500 p-1 transition-all"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* ADD AN ITEM INPUT */}
                    <div className="pt-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          dir="auto"
                          value={newSubtaskText}
                          onChange={(e) => setNewSubtaskText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newSubtaskText.trim()) {
                              const updatedTask = {
                                ...selectedTask,
                                subtasks: [...(selectedTask.subtasks || []), { id: Date.now(), text: newSubtaskText.trim(), completed: false }]
                              };
                              setSelectedTask(updatedTask);
                              setTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                              queueTaskSync(updatedTask);
                              setNewSubtaskText('');
                            }
                          }}
                          placeholder="Add an item"
                          className="flex-1 bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-1.5 text-sm outline-none text-slate-800 dark:text-slate-200 focus:border-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (newSubtaskText.trim()) {
                              const updatedTask = {
                                ...selectedTask,
                                subtasks: [...(selectedTask.subtasks || []), { id: Date.now(), text: newSubtaskText.trim(), completed: false }]
                              };
                              setSelectedTask(updatedTask);
                              setTasks(tasks.map(t => t.id === selectedTask.id ? updatedTask : t));
                              queueTaskSync(updatedTask);
                              setNewSubtaskText('');
                            }
                          }}
                          className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-md shadow-sm transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </section>
                )}

                {/* ATTACHMENTS SECTION */}
                <section className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-bold text-base text-slate-800 dark:text-slate-200">
                      <Paperclip size={18} />
                      <span>Attachments</span>
                    </div>

                    <label className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg cursor-pointer transition-colors shadow-sm flex items-center gap-1.5">
                      <Plus size={14} />
                      <span>Add File / Image</span>
                      <input type="file" multiple accept="image/*" onChange={(e) => handleImageUpload(e, 'edit')} className="hidden" />
                    </label>
                  </div>

                  {selectedTask.images && selectedTask.images.length > 0 ? (
                    <>
                      <div className="text-xs font-bold text-slate-500 dark:text-slate-400">Uploaded Files ({selectedTask.images.length})</div>

                      <div className="space-y-3">
                        {selectedTask.images.map((img, idx) => {
                          const fileName = typeof img === 'string' && img.includes('data:') 
                            ? `Attachment_${idx + 1}.jpg` 
                            : (typeof img === 'string' ? img.split('/').pop() : `Image_${idx + 1}.jpg`);
                          
                          return (
                            <div key={idx} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-800 group hover:border-slate-300 dark:hover:border-slate-700 transition-colors">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-16 h-14 rounded-lg bg-slate-200 dark:bg-slate-700 overflow-hidden shrink-0 border border-slate-200 dark:border-slate-600 flex items-center justify-center">
                                  <img 
                                    src={typeof img === 'string' ? img : ''} 
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    alt="attachment" 
                                    className="w-full h-full object-cover" 
                                  />
                                </div>
                                <div className="min-w-0">
                                  <div className="font-bold text-sm text-slate-800 dark:text-slate-200 truncate">
                                    {fileName}
                                  </div>
                                  <div className="text-xs text-slate-500 dark:text-slate-400">
                                    <span>Attachment # {idx + 1}</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-1 shrink-0">
                                <button 
                                  type="button"
                                  onClick={() => setPreviewImage(typeof img === 'string' ? img : '')} 
                                  className="p-2 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                  title="View Image"
                                >
                                  <ExternalLink size={16} />
                                </button>
                                <button 
                                  type="button"
                                  onClick={() => {
                                    const nextImages = selectedTask.images.filter((_, i) => i !== idx);
                                    setSelectedTask(prev => ({ ...prev, images: nextImages }));
                                    setTasks(prevTasks => prevTasks.map(t => t.id === selectedTask.id ? { ...t, images: nextImages } : t));
                                  }}
                                  className="p-2 text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors"
                                  title="Delete Attachment"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="p-4 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 text-center text-xs font-semibold text-slate-400">
                      No attachments yet. Click "Add File / Image" to attach images to this task.
                    </div>
                  )}
                </section>

                {/* COMMENTS SECTION */}
                <section className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 font-extrabold text-base text-slate-900 dark:text-slate-100">
                      <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
                        <MessageSquare size={16} />
                      </div>
                      <span>{lang === 'ar' ? 'التعليقات' : 'Comments'}</span>
                      {Boolean(selectedTask.comments?.length) && (
                        <span className="px-2 py-0.5 text-xs font-black bg-blue-500/15 text-blue-600 dark:text-blue-400 rounded-full border border-blue-500/20">
                          {selectedTask.comments.length}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* WRITE COMMENT INPUT */}
                  <div className="p-3.5 bg-slate-50/80 dark:bg-slate-800/40 rounded-xl border border-slate-200/90 dark:border-slate-800 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-black text-xs flex items-center justify-center shrink-0 shadow-sm overflow-hidden ring-2 ring-blue-500/20 mt-0.5">
                        {currentUser?.photo || userPhoto ? (
                          <img src={currentUser?.photo || userPhoto} alt="User" className="w-full h-full object-cover" />
                        ) : (
                          (currentUser?.name || userName || 'U')[0].toUpperCase()
                        )}
                      </div>

                      <div className="flex-1 space-y-2.5 min-w-0">
                        <textarea
                          dir="auto"
                          rows={2}
                          value={newCommentText}
                          onChange={(e) => handleCommentTextChange(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              handleAddComment();
                            }
                          }}
                          placeholder={lang === 'ar' ? 'اكتب تعليقاً... استعمل @ للمنشن' : 'Write a comment... use @ to mention'}
                          className="w-full p-3 bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-700/80 rounded-xl text-xs sm:text-sm text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all resize-y min-h-[72px] leading-relaxed"
                        />

                        <div className="flex items-center justify-between gap-2 pt-0.5">
                          <div className="flex items-center gap-2">
                            {/* MENTION BUTTON & ATTACHED DROPDOWN POPUP */}
                            <div className="relative inline-block">
                              {showMentionPopup && (
                                <div
                                  style={{
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#475569 transparent'
                                  }}
                                  className="absolute bottom-full mb-2 left-0 sm:left-0 bg-slate-900/95 dark:bg-slate-900/95 border border-slate-700/80 rounded-2xl shadow-2xl z-50 overflow-hidden w-60 max-w-[75vw] max-h-44 overflow-y-auto p-1.5 space-y-0.5 backdrop-blur-md text-slate-100 animate-fadeIn [::-webkit-scrollbar]:w-1.5 [::-webkit-scrollbar-track]:bg-transparent [::-webkit-scrollbar-thumb]:bg-slate-600/70 [::-webkit-scrollbar-thumb]:rounded-full"
                                >
                                  <div className="px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-slate-400 border-b border-slate-800 flex items-center gap-1.5">
                                    <AtSign size={13} className="text-blue-400 shrink-0" />
                                    <span>{lang === 'ar' ? 'منشن موظف (@Mention)' : 'Mention Employee'}</span>
                                  </div>
                                  {getMentionableUsers()
                                    .filter(u => u.toLowerCase().includes(mentionQuery))
                                    .map((user) => (
                                      <button
                                        key={user}
                                        type="button"
                                        onClick={() => handleSelectMentionUser(user)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs font-extrabold text-slate-200 hover:bg-blue-600/30 hover:text-blue-300 rounded-xl transition-all cursor-pointer text-right group"
                                      >
                                        <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 group-hover:bg-blue-500 group-hover:text-white flex items-center justify-center font-black text-[10px] shrink-0 border border-blue-500/30 transition-colors">
                                          {user[0].toUpperCase()}
                                        </div>
                                        <span className="truncate">@{user}</span>
                                      </button>
                                    ))}
                                </div>
                              )}

                              <button
                                type="button"
                                onClick={handleTriggerMention}
                                className="px-2.5 py-1 text-xs font-bold bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-lg transition-all flex items-center gap-1.5 cursor-pointer border border-blue-500/20"
                                title={lang === 'ar' ? 'منشن موظف (@)' : 'Mention Employee (@)'}
                              >
                                <AtSign size={13} className="text-blue-500 shrink-0" />
                                <span>{lang === 'ar' ? 'منشن' : 'Mention'}</span>
                              </button>
                            </div>

                            <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500 hidden sm:inline">
                              {lang === 'ar' ? 'Ctrl+Enter للإرسال' : 'Ctrl+Enter to send'}
                            </span>
                          </div>

                          <button
                            type="button"
                            disabled={!newCommentText.trim()}
                            onClick={handleAddComment}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 active:scale-95 disabled:opacity-40 disabled:hover:bg-blue-600 disabled:active:scale-100 text-white font-extrabold text-xs rounded-xl shadow-md shadow-blue-500/20 transition-all flex items-center gap-2 shrink-0 whitespace-nowrap cursor-pointer"
                          >
                            <Send size={14} className="shrink-0" />
                            <span className="whitespace-nowrap">{lang === 'ar' ? 'إضافة تعليق' : 'Add Comment'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* COMMENTS LIST (WHATSAPP CHAT BUBBLE STYLE) */}
                  {selectedTask.comments && selectedTask.comments.length > 0 ? (
                    <div className="space-y-3 pt-1">
                      {selectedTask.comments.map((comment) => {
                        const currentUserId = currentUser?.id || userName || 'user';
                        const likesList = Array.isArray(comment.likes) ? comment.likes : [];
                        const isLiked = likesList.includes(currentUserId);

                        return (
                          <div key={comment.id} className="flex items-start gap-2.5 group">
                            {/* Author Avatar */}
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 text-slate-800 dark:text-slate-100 font-extrabold text-xs flex items-center justify-center shrink-0 overflow-hidden shadow-sm border border-slate-200 dark:border-slate-700 mt-0.5">
                              {comment.authorPhoto ? (
                                <img src={comment.authorPhoto} alt={comment.author} className="w-full h-full object-cover" />
                              ) : (
                                (comment.author || 'U')[0].toUpperCase()
                              )}
                            </div>

                            {/* Formatted Sleek Chat Bubble */}
                            <div className="flex-1 min-w-0 rounded-2xl rounded-tl-xs p-3.5 sm:p-4 bg-slate-100/90 dark:bg-slate-800/90 border border-slate-200/90 dark:border-slate-700/80 text-slate-900 dark:text-slate-100 shadow-sm transition-all space-y-1.5">
                              {/* Top Row: Author Name & Actions (Left) vs Date Only (Far Right) */}
                              <div className="flex items-center justify-between w-full border-b border-slate-200/50 dark:border-slate-700/40 pb-1.5 mb-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-extrabold text-sm text-blue-600 dark:text-blue-400 truncate">
                                    {comment.author || (lang === 'ar' ? 'مستخدم' : 'User')}
                                  </span>

                                  {/* Edit & Delete Buttons (Visible on mobile/small screens & hover on desktop) */}
                                  <div className="flex items-center gap-1 opacity-90 sm:opacity-0 sm:group-hover:opacity-100 transition-all shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => handleStartEditComment(comment)}
                                      className="p-1 text-slate-400 hover:text-blue-500 rounded-lg transition-all cursor-pointer"
                                      title={lang === 'ar' ? 'تعديل التعليق' : 'Edit Comment'}
                                    >
                                      <Edit size={13} />
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleDeleteComment(comment.id)}
                                      className="p-1 text-slate-400 hover:text-rose-500 rounded-lg transition-all cursor-pointer"
                                      title={lang === 'ar' ? 'حذف التعليق' : 'Delete Comment'}
                                    >
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </div>

                                {/* Date Only on Far Right Edge */}
                                <span className="text-xs font-semibold text-slate-400 dark:text-slate-400 font-sans tracking-wide shrink-0">
                                  {formatCommentDateOnly(comment.createdAt)}
                                </span>
                              </div>

                              {/* Inline Delete Confirmation or Edit Form or Normal Content */}
                              {deletingCommentId === comment.id ? (
                                <div className="flex items-center justify-between gap-2 p-2 sm:p-2.5 bg-rose-500/10 border border-rose-500/30 rounded-xl text-rose-600 dark:text-rose-400 my-1">
                                  <span className="text-xs font-extrabold flex items-center gap-1">
                                    <AlertTriangle size={14} className="shrink-0" />
                                    {lang === 'ar' ? 'هل أنت تأكد من حذف التعليق؟' : 'Delete this comment?'}
                                  </span>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => setDeletingCommentId(null)}
                                      className="px-2.5 py-1 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-all cursor-pointer"
                                    >
                                      {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        confirmDeleteComment(comment.id);
                                        setDeletingCommentId(null);
                                      }}
                                      className="px-2.5 py-1 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-all cursor-pointer shadow-sm"
                                    >
                                      {lang === 'ar' ? 'تأكيد الحذف' : 'Delete'}
                                    </button>
                                  </div>
                                </div>
                              ) : editingCommentId === comment.id ? (
                                <div className="space-y-2 pt-1">
                                  <textarea
                                    value={editingCommentText}
                                    onChange={(e) => setEditingCommentText(e.target.value)}
                                    className="w-full p-2.5 text-sm sm:text-base font-semibold bg-white dark:bg-slate-900 border border-blue-500 dark:border-blue-400 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-slate-900 dark:text-slate-100"
                                    rows={2}
                                    autoFocus
                                  />
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={handleCancelEditComment}
                                      className="px-3 py-1 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-all cursor-pointer"
                                    >
                                      {lang === 'ar' ? 'إلغاء' : 'Cancel'}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleSaveEditComment(comment.id)}
                                      className="px-3 py-1 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all cursor-pointer shadow-sm flex items-center gap-1"
                                    >
                                      <Save size={12} />
                                      {lang === 'ar' ? 'حفظ' : 'Save'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div dir="auto" className="flex items-center gap-2 flex-wrap text-sm sm:text-base font-semibold text-slate-900 dark:text-slate-100 leading-relaxed pt-0.5">
                                  <span>{renderCommentTextWithMentions(comment.text)}</span>

                                  {/* Time + WhatsApp Double Checkmark (size 19) adjacent to text */}
                                  <button
                                    type="button"
                                    onClick={() => toggleCommentLike(comment.id)}
                                    className="inline-flex items-center gap-1.5 p-0.5 hover:bg-slate-200/60 dark:hover:bg-slate-700/60 rounded transition-colors cursor-pointer shrink-0 select-none"
                                    title={isLiked ? (lang === 'ar' ? 'تمت المشاهدة (العلامتان باللون الأزرق)' : 'Seen (Blue Ticks)') : (lang === 'ar' ? 'انقر لتأكيد المشاهدة' : 'Click to mark as seen')}
                                  >
                                    <span className="text-xs font-medium text-slate-400 dark:text-slate-400 font-sans">
                                      {formatCommentTimeOnly(comment.createdAt)}
                                    </span>
                                    <CheckCheck 
                                      size={19} 
                                      className={`transition-all ${isLiked ? 'text-sky-400 font-black drop-shadow-md scale-110' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`} 
                                    />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 text-center text-xs font-semibold text-slate-400 dark:text-slate-500">
                      {lang === 'ar' ? 'لا توجد تعليقات بعد. كن أول من يضيف تعليقاً!' : 'No comments yet. Be the first to leave a comment!'}
                    </div>
                  )}
                </section>
              </div>

              {/* FOOTER ACTIONS */}
              <div className="modal-footer-actions px-3 sm:px-6 py-3 bg-slate-50 dark:bg-[#151619] border-t border-slate-200 dark:border-slate-800 flex flex-nowrap items-center justify-between gap-2.5 rounded-b-xl sticky bottom-0 z-20">
                {hasPermission('deleteTasks') ? (
                  <button 
                    onClick={() => deleteTask(selectedTask.id)} 
                    className="text-rose-500 hover:text-rose-600 hover:bg-rose-500/10 p-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-colors shrink-0 min-w-[40px] min-h-[40px] cursor-pointer"
                    title={lang === 'ar' ? 'حذف المهمة' : 'Delete Task'}
                  >
                    <Trash2 size={18} />
                    <span className="hidden sm:inline whitespace-nowrap">{lang === 'ar' ? 'حذف' : 'Delete'}</span>
                  </button>
                ) : <div />}

                <div className="flex items-center gap-2.5 flex-nowrap overflow-x-auto scrollbar-none no-scrollbar">
                  {selectedTask.status === 'scheduled' && (
                    <button 
                      onClick={() => {
                        moveTask(selectedTask.id, 'in_progress');
                        setSelectedTask(prev => ({ ...prev, status: 'in_progress' }));
                        toggleTimer(selectedTask.id);
                      }} 
                      className="px-4 py-2.5 rounded-xl font-extrabold text-xs flex items-center gap-2 bg-blue-600 text-white hover:bg-blue-500 shadow-md transition-all cursor-pointer shrink-0 whitespace-nowrap"
                    >
                      <Play size={15} />
                      <span>{lang === 'ar' ? 'بدء العمل' : 'Start Work'}</span>
                    </button>
                  )}

                  {['in_progress', 'revision'].includes(selectedTask.status) && (
                    <>
                      <button 
                        onClick={() => toggleTimer(selectedTask.id)} 
                        className={`px-4 py-2.5 rounded-xl font-extrabold text-xs flex items-center gap-2 shadow-sm transition-all cursor-pointer shrink-0 whitespace-nowrap ${
                          selectedTask.isRunning ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-slate-200 dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-300 dark:hover:bg-slate-700'
                        }`}
                      >
                        {selectedTask.isRunning ? <><Pause size={15} /> <span>{lang === 'ar' ? 'إيقاف مؤقت' : 'Pause'}</span></> : <><Play size={15} /> <span>{lang === 'ar' ? 'استئناف' : 'Resume'}</span></>}
                      </button>
                      <button 
                        onClick={() => {
                          moveTask(selectedTask.id, 'review');
                          setSelectedTask(prev => ({ ...prev, status: 'review' }));
                        }} 
                        className="px-4 py-2.5 rounded-xl font-extrabold text-xs flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white shadow-md transition-all cursor-pointer shrink-0 whitespace-nowrap"
                      >
                        <Send size={15} />
                        <span>{lang === 'ar' ? 'إرسال للمراجعة' : 'Send for Review'}</span>
                      </button>
                    </>
                  )}

                  {selectedTask.status === 'review' && (
                    <>
                      <button 
                        onClick={() => {
                          moveTask(selectedTask.id, 'revision');
                          setSelectedTask(prev => ({ ...prev, status: 'revision' }));
                        }} 
                        className="px-4 py-2.5 rounded-xl font-bold text-xs flex items-center gap-2 bg-slate-200 dark:bg-slate-800 text-rose-500 dark:text-rose-400 hover:bg-rose-500/10 border border-rose-500/30 transition-all cursor-pointer shrink-0 whitespace-nowrap"
                      >
                        <RotateCcw size={15} />
                        <span>{lang === 'ar' ? 'طلب تعديل' : 'Request Revision'}</span>
                      </button>
                      <button 
                        onClick={() => {
                          moveTask(selectedTask.id, 'done');
                          setSelectedTask(prev => ({ ...prev, status: 'done' }));
                        }} 
                        className="px-4 py-2.5 rounded-xl font-extrabold text-xs flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white shadow-md transition-all cursor-pointer shrink-0 whitespace-nowrap"
                      >
                        <CheckCircle size={15} />
                        <span>{lang === 'ar' ? 'تأكيد واكتمال' : 'Mark Complete'}</span>
                      </button>
                    </>
                  )}

                  {selectedTask.status === 'done' && (
                    <button 
                      onClick={() => {
                        moveTask(selectedTask.id, 'in_progress');
                        setSelectedTask(prev => ({ ...prev, status: 'in_progress' }));
                      }} 
                      className="px-4 py-2.5 rounded-xl font-extrabold text-xs flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-200 transition-all cursor-pointer shadow-sm shrink-0 whitespace-nowrap"
                    >
                      <RotateCcw size={15} />
                      <span>{lang === 'ar' ? 'إعادة فتح المهمة' : 'Re-open Task'}</span>
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>
        )
      }
      {/* ADD TASK MODAL */}
      {
        activeModal === 'stats' && hasPermission('viewStats') && (() => {
          const analytics = getAnalytics();
          const labels = {
            reports: lang === 'ar' ? 'تقارير الأداء' : 'Performance Reports',
            subtitle: lang === 'ar' ? 'نظرة سريعة على ضغط الشغل والإنجاز داخل مساحة العمل.' : 'A quick view of workload, delivery, and workspace pressure.',
            totalTasks: lang === 'ar' ? 'إجمالي المهام' : 'Total Tasks',
            running: lang === 'ar' ? 'قيد التنفيذ' : 'In Progress',
            overdue: lang === 'ar' ? 'متأخر' : 'Overdue',
            review: 'Client Review',
            doneToday: lang === 'ar' ? 'إنجاز اليوم' : 'Done Today',
            doneMonth: lang === 'ar' ? 'إنجاز الشهر' : 'Done This Month',
            urgent: lang === 'ar' ? 'عاجل' : 'Urgent',
            workTime: lang === 'ar' ? 'وقت العمل النشط' : 'Active Work Time',
            avgTime: lang === 'ar' ? 'متوسط تنفيذ المهمة' : 'Average Task Time',
            topClient: lang === 'ar' ? 'أكثر عميل عليه شغل' : 'Top Client',
            companyPressure: lang === 'ar' ? 'ضغط Companies' : 'Company Pressure',
            noClient: lang === 'ar' ? 'لا يوجد عملاء بعد' : 'No clients yet',
            openTasks: lang === 'ar' ? 'مفتوحة' : 'Open',
            monthEarnings: lang === 'ar' ? 'أرباح الشهر' : 'Month Earnings',
            overtime: lang === 'ar' ? 'إضافي' : 'Overtime',
            noData: lang === 'ar' ? 'لسه مفيش بيانات كفاية للتقرير.' : 'Not enough data yet.'
          };
          const summaryCards = [
            { label: labels.totalTasks, value: analytics.statusCounts.total, icon: LayoutGrid, tone: 'text-blue-400 bg-blue-500/10 border-blue-400/20' },
            { label: labels.doneToday, value: analytics.statusCounts.doneToday, icon: CheckCircle, tone: 'text-emerald-400 bg-emerald-500/10 border-emerald-400/20' },
            { label: labels.running, value: analytics.statusCounts.in_progress, icon: Zap, tone: 'text-amber-400 bg-amber-500/10 border-amber-400/20' },
            { label: labels.overdue, value: analytics.statusCounts.overdue, icon: AlertTriangle, tone: 'text-rose-400 bg-rose-500/10 border-rose-400/20' }
          ];
          const statusRows = [
            { label: t.scheduled, value: analytics.statusCounts.scheduled, color: 'bg-blue-500' },
            { label: labels.running, value: analytics.statusCounts.in_progress, color: 'bg-amber-500' },
            { label: labels.review, value: analytics.statusCounts.review, color: 'bg-cyan-500' },
            { label: t.revision, value: analytics.statusCounts.revision, color: 'bg-orange-500' },
            { label: labels.urgent, value: analytics.statusCounts.urgent, color: 'bg-rose-500' },
            { label: labels.doneMonth, value: analytics.statusCounts.doneMonth, color: 'bg-emerald-500' }
          ];

          return (
            <div className="fixed inset-0 bg-black/65 backdrop-blur-xl z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-6xl max-h-[92vh] overflow-hidden rounded-[30px] bg-white/95 dark:bg-[#101720]/96 border border-slate-200 dark:border-white/10 shadow-2xl">
                <div className="p-5 md:p-6 border-b border-slate-200 dark:border-white/10 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/20 shrink-0"><PieChart size={22} /></div>
                    <div className="min-w-0">
                      <h3 className="text-2xl md:text-3xl font-black text-slate-950 dark:text-white">{labels.reports}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{labels.subtitle}</p>
                    </div>
                  </div>
                  <button onClick={() => setActiveModal(null)} className="detail-icon-button hover:border-rose-300 dark:hover:border-rose-400/30 hover:text-rose-500"><X size={20} /></button>
                </div>

                <div className="p-5 md:p-6 max-h-[calc(92vh-98px)] overflow-y-auto scrollbar-thin space-y-5">
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {summaryCards.map(card => {
                      const Icon = card.icon;
                      return (
                        <div key={card.label} className="rounded-2xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 p-4">
                          <div className={`w-11 h-11 rounded-2xl border flex items-center justify-center mb-4 ${card.tone}`}><Icon size={19} /></div>
                          <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{card.label}</p>
                          <p className="text-3xl font-black text-slate-950 dark:text-white mt-1">{card.value}</p>
                        </div>
                      );
                    })}
                  </div>

                  <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_.9fr] gap-5">
                    <section className="rounded-3xl bg-slate-50 dark:bg-white/[0.035] border border-slate-200 dark:border-white/10 p-5">
                      <div className="flex items-center justify-between gap-4 mb-5">
                        <h4 className="text-lg font-black text-slate-950 dark:text-white flex items-center gap-2"><BarChart2 size={18} className="text-blue-400" /> {lang === 'ar' ? 'توزيع الحالات' : 'Status Distribution'}</h4>
                        <span className="text-xs font-bold text-slate-500">{analytics.statusCounts.total}</span>
                      </div>
                      <div className="space-y-3">
                        {statusRows.map(row => {
                          const percent = analytics.statusCounts.total ? Math.round((row.value / analytics.statusCounts.total) * 100) : 0;
                          return (
                            <div key={row.label}>
                              <div className="flex items-center justify-between text-xs font-bold mb-1.5"><span className="text-slate-600 dark:text-slate-300">{row.label}</span><span className="text-slate-500">{row.value}</span></div>
                              <div className="h-2.5 rounded-full bg-white dark:bg-[#07101A] border border-slate-200 dark:border-white/10 overflow-hidden">
                                <div className={`h-full rounded-full ${row.color}`} style={{ width: `${percent}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </section>

                    <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="rounded-3xl bg-blue-500/10 border border-blue-400/20 p-5">
                        <Clock size={20} className="text-blue-400 mb-4" />
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{labels.workTime}</p>
                        <p className="text-2xl font-black text-blue-400 mt-1">{formatHours(analytics.activeSeconds)}</p>
                      </div>
                      <div className="rounded-3xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 p-5">
                        <Hourglass size={20} className="text-slate-500 dark:text-slate-300 mb-4" />
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{labels.avgTime}</p>
                        <p className="text-2xl font-black text-slate-950 dark:text-white mt-1">{formatHours(analytics.avgSeconds)}</p>
                      </div>
                      <div className="rounded-3xl bg-slate-50 dark:bg-white/[0.04] border border-slate-200 dark:border-white/10 p-5 sm:col-span-2">
                        <Users size={20} className="text-cyan-400 mb-4" />
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{labels.topClient}</p>
                        <div className="flex items-center justify-between gap-4 mt-1">
                          <p className="text-xl font-black text-slate-950 dark:text-white truncate">{analytics.topClient?.[0] || labels.noClient}</p>
                          {analytics.topClient && <span className="px-3 py-1 rounded-xl bg-cyan-500/10 text-cyan-400 text-xs font-black">{analytics.topClient[1]}</span>}
                        </div>
                      </div>
                      {analytics.hasHourlyRates && (
                        <div className="rounded-3xl bg-emerald-500/10 border border-emerald-400/20 p-5 sm:col-span-2">
                          <DollarSign size={20} className="text-emerald-400 mb-4" />
                          <p className="text-xs font-bold text-slate-500 dark:text-slate-400">{labels.monthEarnings}</p>
                          <p className="text-2xl font-black text-emerald-400 mt-1">{analytics.totalMonthEarnings.toFixed(0)}</p>
                        </div>
                      )}
                    </section>
                  </div>

                  <section className="rounded-3xl bg-slate-50 dark:bg-white/[0.035] border border-slate-200 dark:border-white/10 p-5">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <h4 className="text-lg font-black text-slate-950 dark:text-white flex items-center gap-2"><Briefcase size={18} className="text-blue-400" /> {labels.companyPressure}</h4>
                      <span className="text-xs font-bold text-slate-500">{analytics.companyAnalytics.length}</span>
                    </div>
                    {analytics.companyAnalytics.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 p-8 text-center text-sm text-slate-500">{labels.noData}</div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {analytics.companyAnalytics.map(item => (
                          <div key={item.key} className="rounded-2xl bg-white dark:bg-[#0B1118] border border-slate-200 dark:border-white/10 p-4">
                            <div className="flex items-center justify-between gap-3 mb-4">
                              <div className="flex items-center gap-3 min-w-0">
                                {item.company.logo ? <img src={item.company.logo} className="w-10 h-10 rounded-xl object-contain bg-white" alt="" /> : <div className={`w-10 h-10 rounded-xl ${item.company.theme.bg} text-white flex items-center justify-center font-black shrink-0`}>{item.company.name.charAt(0)}</div>}
                                <div className="min-w-0">
                                  <p className="font-black text-slate-950 dark:text-white truncate">{item.company.name}</p>
                                  <p className="text-xs text-slate-500">{item.open} {labels.openTasks}</p>
                                </div>
                              </div>
                              <span className={`px-3 py-1.5 rounded-xl text-xs font-black ${item.overdue > 0 ? 'bg-rose-500/10 text-rose-400' : 'bg-blue-500/10 text-blue-400'}`}>{item.pressure}</span>
                            </div>
                            <div className="grid grid-cols-4 gap-2 text-center">
                              <div className="rounded-xl bg-slate-50 dark:bg-white/[0.04] p-2"><p className="text-[10px] text-slate-500">{labels.doneToday}</p><p className="font-black text-emerald-400">{item.doneToday}</p></div>
                              <div className="rounded-xl bg-slate-50 dark:bg-white/[0.04] p-2"><p className="text-[10px] text-slate-500">{labels.review}</p><p className="font-black text-cyan-400">{item.review}</p></div>
                              <div className="rounded-xl bg-slate-50 dark:bg-white/[0.04] p-2"><p className="text-[10px] text-slate-500">{labels.overdue}</p><p className="font-black text-rose-400">{item.overdue}</p></div>
                              <div className="rounded-xl bg-slate-50 dark:bg-white/[0.04] p-2"><p className="text-[10px] text-slate-500">{labels.overtime}</p><p className="font-black text-amber-400">{item.overtime}h</p></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </div>
          );
        })()
      }



      {/* ADD COMPANY MODAL */}
      {
        activeModal === 'addCompany' && hasPermission('manageCompany') && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setActiveModal(null)}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleAddCompany();
              }}
              className="w-full max-w-md rounded-[28px] bg-white dark:bg-[#111821] border border-slate-200 dark:border-white/10 shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-slate-200 dark:border-white/10 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg"><Briefcase size={22} /></div>
                  <div className="min-w-0">
                    <p className="text-xs font-extrabold text-blue-400">{lang === 'ar' ? '\u0634\u0631\u0643\u0629 \u062c\u062f\u064a\u062f\u0629' : 'New Company'}</p>
                    <h3 className="text-xl font-black text-slate-950 dark:text-white">{lang === 'ar' ? '\u0625\u0636\u0627\u0641\u0629 \u0634\u0631\u0643\u0629' : 'Add Company'}</h3>
                  </div>
                </div>
                <button type="button" onClick={() => setActiveModal(null)} className="p-2 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-500 hover:text-slate-950 dark:hover:text-white hover-effect"><X size={20} /></button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-500 mb-2">{lang === 'ar' ? '\u0627\u0633\u0645 \u0627\u0644\u0634\u0631\u0643\u0629' : 'Company Name'}</label>
                  <input autoFocus value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value)} placeholder={lang === 'ar' ? '\u0645\u062b\u0627\u0644: Market Formers' : 'e.g. Market Formers'} className="settings-input text-base font-bold" />
                </div>
                <div className="rounded-2xl bg-blue-500/10 border border-blue-400/20 p-4 text-sm text-slate-500 dark:text-slate-300">
                  {lang === 'ar' ? '\u0628\u0639\u062f \u0627\u0644\u0625\u0636\u0627\u0641\u0629 \u0647\u062a\u062a\u0641\u062a\u062d \u0625\u0639\u062f\u0627\u062f\u0627\u062a \u0627\u0644\u0634\u0631\u0643\u0629 \u0639\u0634\u0627\u0646 \u062a\u0636\u064a\u0641 \u0627\u0644\u0639\u0645\u0644\u0627\u0621 \u0648\u0627\u0644\u0645\u0633\u0624\u0648\u0644\u064a\u0646.' : 'After adding it, company settings will open so you can add clients and managers.'}
                </div>
              </div>

              <div className="p-5 border-t border-slate-200 dark:border-white/10 flex gap-3">
                <button type="button" onClick={() => setActiveModal(null)} className="flex-1 py-3 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 font-bold hover-effect">{t.cancel || 'Cancel'}</button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-black shadow-lg shadow-blue-500/20 hover-effect">{t.add}</button>
              </div>
            </form>
          </div>
        )
      }

      {/* ATTENDANCE LOGS MODAL */}
      {
        attendanceLogsCompId && companies[attendanceLogsCompId] && (() => {
          const comp = companies[attendanceLogsCompId];
          const logs = comp.attendanceLogs || [];
          const totalSeconds = logs.reduce((sum, log) => sum + (Number(log.durationSeconds) || 0), 0);
          const formatSeconds = (seconds) => {
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = seconds % 60;
            return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
          };

          return (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[90] flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={() => setAttendanceLogsCompId(null)}>
              <div className="w-full max-w-3xl max-h-[86vh] overflow-hidden rounded-[30px] bg-white dark:bg-[#111821] border border-slate-200 dark:border-white/10 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="p-5 md:p-6 border-b border-slate-200 dark:border-white/10 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-lg shrink-0"><Fingerprint size={24} /></div>
                    <div className="min-w-0"><p className="text-xs font-extrabold text-blue-400">{lang === 'ar' ? '\u0633\u062c\u0644 \u0627\u0644\u062f\u0648\u0627\u0645' : 'Attendance Logs'}</p><h3 className="text-xl md:text-2xl font-black text-slate-950 dark:text-white truncate">{comp.name}</h3></div>
                  </div>
                  <button onClick={() => setAttendanceLogsCompId(null)} className="p-2.5 rounded-xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 text-slate-500 hover:text-slate-950 dark:hover:text-white hover-effect"><X size={20} /></button>
                </div>

                <div className="p-5 md:p-6 overflow-y-auto max-h-[calc(86vh-92px)] scrollbar-thin">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                    <div className="rounded-2xl bg-blue-500/10 border border-blue-400/20 p-4"><p className="text-xs font-bold text-slate-500">{lang === 'ar' ? '\u0639\u062f\u062f \u0627\u0644\u0633\u062c\u0644\u0627\u062a' : 'Entries'}</p><p className="text-2xl font-black text-blue-400 mt-1">{logs.length}</p></div>
                    <div className="rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs font-bold text-slate-500">{lang === 'ar' ? '\u0625\u062c\u0645\u0627\u0644\u064a \u0627\u0644\u0648\u0642\u062a' : 'Total Time'}</p><p className="text-2xl font-black text-slate-950 dark:text-white font-mono mt-1">{formatSeconds(totalSeconds)}</p></div>
                    <div className="rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4"><p className="text-xs font-bold text-slate-500">{lang === 'ar' ? '\u0622\u062e\u0631 \u062f\u0648\u0627\u0645' : 'Latest Shift'}</p><p className="text-sm font-black text-slate-950 dark:text-white mt-2 truncate">{logs[0]?.date || '--'}</p></div>
                  </div>

                  {logs.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex justify-end"><button onClick={() => handleClearAllAttendanceLogs(attendanceLogsCompId)} className="px-4 py-2 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs font-bold hover:bg-rose-500/15 hover-effect flex items-center gap-2"><Trash2 size={14} /> {lang === 'ar' ? '\u062d\u0630\u0641 \u0643\u0644 \u0627\u0644\u0633\u062c\u0644\u0627\u062a' : 'Clear All'}</button></div>
                      {logs.map((log) => (
                        <div key={log.id} className="rounded-2xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="min-w-0"><p className="text-sm font-black text-slate-950 dark:text-white">{log.date || '--'}</p><p className="text-xs text-slate-500 mt-1">{lang === 'ar' ? '\u062f\u062e\u0648\u0644' : 'In'}: <span className="font-mono text-slate-700 dark:text-slate-300">{log.clockIn || '--'}</span> ? {lang === 'ar' ? '\u062e\u0631\u0648\u062c' : 'Out'}: <span className="font-mono text-slate-700 dark:text-slate-300">{log.clockOut || '--'}</span></p></div>
                          <div className="flex items-center justify-between md:justify-end gap-3"><span className="px-3 py-2 rounded-xl bg-blue-500/10 text-blue-400 font-mono font-black text-sm">{formatSeconds(Number(log.durationSeconds) || 0)}</span><button onClick={() => handleClearSingleAttendanceLog(attendanceLogsCompId, log.id)} className="p-2 rounded-xl text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"><Trash2 size={16} /></button></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="min-h-[240px] flex flex-col items-center justify-center text-center rounded-3xl border border-dashed border-slate-300 dark:border-white/10 bg-slate-50 dark:bg-white/[0.025] p-8"><div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-400/20 text-blue-400 flex items-center justify-center mb-4"><Fingerprint size={26} /></div><h4 className="text-lg font-black text-slate-950 dark:text-white">{lang === 'ar' ? '\u0644\u0627 \u064a\u0648\u062c\u062f \u0633\u062c\u0644 \u062f\u0648\u0627\u0645' : 'No attendance logs yet'}</h4><p className="text-sm text-slate-500 mt-2 max-w-md">{lang === 'ar' ? '\u0627\u0639\u0645\u0644 Check In \u0648 Check Out \u0644\u062a\u0633\u062c\u064a\u0644 \u0645\u062f\u0629 \u0627\u0644\u062f\u0648\u0627\u0645 \u0647\u0646\u0627.' : 'Use Check In and Check Out to create shift records here.'}</p></div>
                  )}
                </div>
              </div>
            </div>
          );
        })()
      }

      {/* NEW: IMAGE PREVIEW MODAL */}
      {
        previewImage && (
          <div className="fixed inset-0 bg-black/90 z-[10000] flex items-center justify-center p-4 animate-in fade-in zoom-in duration-300" onClick={() => setPreviewImage(null)}>
            <button onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }} className="absolute top-6 right-6 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors hover-effect"><X size={24} /></button>
            <div className="relative max-w-5xl max-h-[90vh] w-full flex items-center justify-center group" onClick={(e) => e.stopPropagation()}>
              <img src={previewImage} alt="Full Preview" className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-4">
                <button onClick={(e) => handleDownload(e, previewImage)} className="flex items-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-full shadow-lg hover:bg-slate-200 transition-all transform hover:scale-105 hover-effect">
                  <Download size={20} /> Download
                </button>
              </div>
            </div>
          </div>
        )
      }


      {/* UPDATE MODAL */}




      {/* CROP MODAL */}
      {
        cropModal.isOpen && (
          <ImageCropper
            imageSrc={cropModal.imageSrc}
            onCancel={onCropCancel}
            onCropComplete={onCropComplete}
          />
        )
      }



      {/* CUSTOM MODERN ALERT/CONFIRM MODAL */}
      {
        activeAlert && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[999] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white dark:bg-[#1E232E] border border-slate-200 dark:border-white/10 w-full max-w-md rounded-3xl p-6 shadow-2xl scale-100 animate-in zoom-in-95 duration-200 relative overflow-hidden">
              {/* Decorative Effect */}
              <div className={`absolute top-0 inset-x-0 h-1.5 ${activeAlert.type === 'danger' ? 'bg-rose-500' :
                activeAlert.type === 'warning' ? 'bg-rose-500' :
                  activeAlert.type === 'success' ? 'bg-blue-500' : 'bg-blue-500'
                }`}></div>

              <div className="flex flex-col items-center text-center">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${activeAlert.type === 'danger' ? 'bg-rose-500/10 text-rose-500' :
                  activeAlert.type === 'warning' ? 'bg-rose-500/10 text-rose-500' :
                    activeAlert.type === 'success' ? 'bg-blue-500/10 text-blue-500' : 'bg-blue-500/10 text-blue-500'
                  }`}>
                  {activeAlert.type === 'danger' && <AlertTriangle size={32} />}
                  {activeAlert.type === 'warning' && <AlertTriangle size={32} />}
                  {activeAlert.type === 'success' && <CheckCircle size={32} />}
                  {(!activeAlert.type || activeAlert.type === 'info') && <CheckCircle size={32} />}
                </div>

                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{activeAlert.title}</h3>
                <p className="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">{formatErrorMessage(activeAlert.message)}</p>

                <div className="flex gap-3 w-full">
                  {activeAlert.showCancel && (
                    <button
                      onClick={() => setActiveAlert(null)}
                      className="flex-1 py-3 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 rounded-xl font-bold transition-colors cursor-pointer"
                    >
                      {activeAlert.cancelText || (lang === 'ar' ? 'إلغاء' : (t.cancel || t.later || "Cancel"))}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (activeAlert.onConfirm) activeAlert.onConfirm();
                      if (!activeAlert.onConfirm) setActiveAlert(null); // Auto close if just info
                    }}
                    className={`flex-1 py-3 text-white rounded-xl font-bold transition-colors shadow-lg hover-effect cursor-pointer ${activeAlert.type === 'danger' ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-500/20' :
                      activeAlert.type === 'warning' ? 'bg-rose-600 hover:bg-rose-500 shadow-rose-500/20' :
                        activeAlert.type === 'success' ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/20' : 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/20'
                      }`}
                  >
                    {activeAlert.confirmText || (lang === 'ar' ? 'تأكيد' : (t.confirm || "OK"))}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {
        updateStatus.status !== 'idle' && updateStatus.status !== 'error' && (
          <div className="fixed bottom-6 right-6 z-[60] animate-in slide-in-from-right-10 fade-in duration-300">
            <div className="bg-white dark:bg-[#1E232E] border border-slate-200 dark:border-white/10 p-5 rounded-2xl shadow-2xl w-[320px] relative overflow-hidden">
              {/* Background Glow */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl -translate-y-10 translate-x-10 pointer-events-none"></div>

              <div className="flex items-start justify-between mb-3 relative">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <Download size={20} />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white text-sm">
                      {updateStatus.status === 'available' && t.updateAvailable}
                      {updateStatus.status === 'downloading' && t.downloadingUpdate}
                      {updateStatus.status === 'ready' && t.updateReady}
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {updateStatus.status === 'available' && `v${updateStatus.info?.version} is available.`}
                      {updateStatus.status === 'downloading' && 'Please wait...'}
                      {updateStatus.status === 'ready' && 'Restart to install.'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={dismissUpdate}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-2 relative">
                {updateStatus.status === 'available' && (
                  <>
                    <button
                      onClick={handleDownloadUpdate}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Download size={14} /> {t.updateNow}
                    </button>
                    <button
                      onClick={dismissUpdate}
                      className="px-4 py-2.5 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 text-slate-600 dark:text-slate-300 text-xs font-bold rounded-lg transition-colors"
                    >
                      {t.later}
                    </button>
                  </>
                )}

                {updateStatus.status === 'downloading' && (
                  <div className="w-full mt-2">
                    <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                      <span>{t.downloadingUpdate}</span>
                      <span>{Math.round(updateStatus.progress || 0)}%</span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-white/5 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300 ease-out"
                        style={{ width: `${updateStatus.progress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {updateStatus.status === 'ready' && (
                  <button
                    onClick={handleRestartApp}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <RotateCcw size={14} /> {t.installNow || t.restartNow}
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* INSTALLING OVERLAY */}
      {
        updateStatus.status === 'installing' && (
          <div className="fixed inset-0 bg-black/90 z-[100] flex flex-col items-center justify-center text-white animate-in fade-in duration-500">
            <div className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full animate-spin mb-6"></div>
            <h2 className="text-2xl font-bold mb-2">{t.installingUpdate}</h2>
            <p className="text-slate-400">{t.restartDesc}</p>
          </div>
        )
      }
    </div >
  );

};

export default App;















